import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ButtonControl, ContextMenuControl, DialogControl, FieldControl, IconControl, SidePanelFormControl, TableControl, ToastNotifications } from "spark-core-dx/components";
import { useCorePermissions, useCoreRoute, useOperationDialog } from "spark-core-dx/hooks";
import { SystemTypes } from "spark-core-dx/services";
import { AI_PANEL_GRID_REFRESH_EVENT, dispatchAiPanelCommand } from "../../../components/AIChat/aiPanelCommand";
import ConvokeCommandBar from "../../../components/convoke/ConvokeCommandBar";
import RecentLogsControl from "../../../components/RecentLogsControl";
import ResourceMaturityBadge from "../../../components/ResourceMaturityBadge";
import { Operations } from "../../../services/ItemServices";
import { sanitizeHtmlFragment } from "../../../utilities/markupSanitizer";
import { sortOperations } from "../../../utilities/operationSort";
import SourceColumnSetManage from "../../convoke/screens/SourceColumnSetManage";
import { SOURCE_OBJECT_PROCESSOR_TYPES, getSourceObjectProcessorType, withSourceObjectProcessorOperationDefaults } from "../../convoke/sourceObjectProcessorType";
import MarketplaceAllowancePurchaseDialog from "../../marketplace/components/MarketplaceAllowancePurchaseDialog";
import MarketplaceRuntimeCapacityPurchaseDialog from "../../marketplace/components/MarketplaceRuntimeCapacityPurchaseDialog";
import { getProduct, getProductIconPath, getResourceIconPath, normalizeMarketplaceCatalog } from "../../marketplace/data/marketplaceCatalog";
import { MARKETPLACE_CATALOG_INVALIDATED_EVENT } from "../../marketplace/marketplaceCheckout";
import CardGridControl from "../components/CardGridControl";
import { ResourceCatalogListTab, ResourceCatalogPanel, ResourceCatalogRow } from "../components/ResourceCatalogPanel";
import PolicyFieldRepairDialog from "../components/PolicyFieldRepairDialog";
// Destination Schedule management screen wiring. Keep this import isolated for ResourceWorkspace merge work.
import ResourceConfigurationDestinationScheduleScreen from "../components/destinationSchedule/ResourceConfigurationDestinationScheduleScreen";
import { buildDestinationScheduleScopeKey } from "../components/destinationSchedule/destinationScheduleControlUtils";
import ResourceStateControl from "../components/ResourceStateControl";
import { buildEntityEndpointMetadata } from "../components/wizard/steps/EndpointScopeConfiguratorStep";
import {
  buildResourceOperationQueryParams,
  buildSourceObjectManagePanelContext,
  cloneSqlQueryTargetDraft,
  createSqlQueryTargetDraft,
  filterSourceTargetsForProductDefaults,
  getSourceObjectKey as getSourceObjectIdentityKey,
  getSourceTargetKey as getSourceTargetIdentityKey,
  isSourceObjectTypeQuery,
  isSqlQueryTargetSourceSystem,
  LOAD_SOURCE_OBJECTS_OPERATION,
  LOAD_SOURCE_SYSTEM_TARGETS_OPERATION,
  MANAGE_SOURCE_OBJECT_OPERATION,
  validateSourceTargetRows,
} from "../components/wizard/resourceConfigWizardUtils";
import { useResourceDomainContext } from "../ResourceDomainProvider";
import {
  RESOURCES_BASE_PATH,
  RESOURCES_SCHEDULES_PATH,
  RESOURCES_TRADING_PARTNERS_PATH,
  RESOURCE_WORKSPACE_BASE_PATH,
  buildResourceConfigurePath,
  buildResourceGroupPath
} from "../resourceRoutes";
import {
  DestinationDeploymentDeleteDialog,
  DestinationDeploymentActiveConfirmDialog,
  DestinationDeploymentManualRunDialog,
  DestinationEntityDeploymentDeleteDialog,
  DestinationEntityDeploymentPickerDialog,
  ResourceDestinationDeploymentEditPanel,
  ResourceDestinationEditPanel,
  ResourceDestinationEntityDeploymentEditPanel,
  WarehouseDeploymentCloneDialog,
  WarehouseDeploymentDeployDialog,
  WarehouseDeploymentDropDialog
} from "./ResourceWorkspaceDestinationPanels";
import {
  ResourceConfigurationDestinationDeploymentScreen,
  ResourceConfigurationDestinationDeploymentsScreen,
  ResourceConfigurationDestinationEntityDeploymentsScreen,
  ResourceConfigurationWarehouseViewDesignerScreen,
  ResourceConfigurationEndpointAuthenticationPanel,
  ResourceConfigurationEndpointScopeScreen,
  ResourceConfigurationDestinationScreen,
  EndpointRouteIcon
} from "./ResourceWorkspaceDestinationScreens";
import ResourceConfigurationClientMcpToolsScreen from "./ResourceWorkspaceMcpToolsScreens";
import {
  DESTINATION_DEPLOYMENT_NAVIGATION,
  DESTINATION_DEPLOYMENTS_SCREEN_KEY,
  DESTINATION_DEPLOYMENT_SCREEN_KEY,
  DESTINATION_ENTITY_DEPLOYMENTS_NAVIGATION,
  DESTINATION_ENTITY_DEPLOYMENTS_SCREEN_KEY,
  DESTINATION_WAREHOUSE_VIEWS_NAVIGATION,
  DESTINATION_WAREHOUSE_VIEWS_SCREEN_KEY,
  DESTINATION_NAVIGATIONS,
  DESTINATION_SCHEDULE_NAVIGATION,
  DESTINATION_SCHEDULES_SCREEN_KEY,
  DESTINATION_SCREEN_KEY,
  ENDPOINT_AUTHENTICATION_SCREEN_KEY,
  findDestinationDeploymentById,
  getDeploymentId,
  getDestinationDeployments,
  getDestinationEntityDeployments,
  getEndpointDeploymentSchemaScopes,
  getDestinationResourceDefaults,
  getDestinationDefaultsFromRecord,
  getDestinationSchemaId,
  getLinkedDestinationRecord,
  getWarehouseSchemaVersionOptions,
  isEndpointDeploymentMcpOnly,
  isDestinationLinkedRecord,
  isSchemaDestinationConfigEditable,
  isSchemaDestinationOnline
} from "./resourceWorkspace.destinationUtils";
import {
  CLIENT_MCP_SERVER_NAVIGATIONS,
  CLIENT_MCP_SERVER_RESOURCE_KEY,
  CLIENT_MCP_TOOLS_NAVIGATIONS,
  CLIENT_MCP_TOOLS_RESOURCE_KEY,
  isClientMcpServerResource,
  isClientMcpToolsResource,
  MCP_TOOLS_ACCESS_SCREEN_KEY,
  MCP_TOOLS_ACTIVE_SESSIONS_SCREEN_KEY,
  MCP_TOOLS_AUTH_SCREEN_KEY,
  MCP_TOOLS_CAPABILITIES_SCREEN_KEY,
  MCP_TOOLS_EXPOSURE_SCREEN_KEY,
  MCP_TOOLS_OVERVIEW_SCREEN_KEY,
  MCP_TOOLS_RULES_SCREEN_KEY
} from "./resourceWorkspace.mcpToolsUtils";
import { useDestinationWorkspaceController } from "./useDestinationWorkspaceController";
import {
  RESOURCE_ACTION_PERMISSIONS,
  RESOURCE_SCREEN_PERMISSION,
  canAccessManagedNavigation,
  filterManagedNavigations
} from "./resourceWorkspace.permissions";
import { shouldRenderResourceWorkspaceIconAsImage } from "./resourceWorkspace.iconUtils";
import "./ResourceWorkspace.scss";

const PRODUCT_MARKETPLACE_CATALOG_ITEM = "ProductMarketplaceCatalog";
const RESOURCE_NAVIGATION_TARGET_KEY = "resources";
const RESOURCE_FALLBACK_ICON = "application";
const EDI_RESOURCE_GROUP_KEY = "edi";
const EDI_RESOURCE_GROUP_TITLE = "Inbound/Outbound EDI";
const EDI_RESOURCE_GROUP_ICON_PATH = "/content/marketplace/resourceIcons/marketplace-resource-EdiGroup.svg";

const resourceUsesProductIcon = (resource) => {
  const productKey = resource?.productKey || getString(resource?.product, "ProductKey", "productKey", "key");
  const resourceKey = resource?.productResourceKey || getString(resource?.productResource, "ProductResourceKey", "productResourceKey", "key");
  return Boolean(productKey && resourceKey && normalizeCompare(productKey) === normalizeCompare(resourceKey));
};

const ResourceWorkspaceIcon = ({ iconKey, iconPath, label, preferImage = false, width = "3.25rem", height = "3.25rem" }) => {
  if (shouldRenderResourceWorkspaceIconAsImage({ iconKey, iconPath, preferImage })) {
    return <img src={iconPath} alt="" title={label} />;
  }
  if (normalizeCompare(iconKey) === "clock") {
    return (
      <span
        className="material-symbols-rounded resource-workspace-material-icon"
        title={label}
        style={{ width, height, fontSize: width }}
      >
        schedule
      </span>
    );
  }
  return <IconControl iconKey={iconKey || RESOURCE_FALLBACK_ICON} title={label} width={width} height={height} />;
};

const getResourceWorkspaceIconPath = (resource) => {
  const explicitIconPath = getString(resource?.productResource, "IconPath", "iconPath");
  if (explicitIconPath) return explicitIconPath;

  const productKey = resource?.productKey || getString(resource?.product, "ProductKey", "productKey", "key");
  const resourceKey = resource?.productResourceKey || getString(resource?.productResource, "ProductResourceKey", "productResourceKey", "key");
  if (resourceUsesProductIcon(resource)) {
    return getString(resource?.product, "IconPath", "iconPath") || getProductIconPath(productKey);
  }

  return resourceKey ? getResourceIconPath(resourceKey) : (productKey ? getProductIconPath(productKey) : "");
};

const OVERVIEW_NAVIGATION = { key: "overview", displayName: "Resource", icon: "info", sequence: 1 };
const EVENTS_NAVIGATION = { key: "events", displayName: "Events", icon: "eventscreen", sequence: 3 };
const LOGS_NAVIGATION = { key: "logs", displayName: "Logs", icon: "logscreen", sequence: 4 };
const CONFIGURATION_MANAGE_NAVIGATION = { key: "manage", displayName: "Manage", icon: "manage", sequence: 1 };
const SOURCE_INTEGRATION_NAVIGATIONS = [
  { key: "integration", displayName: "Integration", icon: "integrationtab", sequence: 10 },
  { key: "source-system", displayName: "Source System", icon: "system", sequence: 20 }
];
const SOURCE_SYSTEM_SCREEN_KEY = "source-system";
const INBOUND_EDI_TARGETS_SCREEN_KEY = "edi-targets";
const INBOUND_EDI_SOURCE_OBJECTS_SCREEN_KEY = "source-objects";
const INBOUND_EDI_MAPPING_SCREEN_KEY = "mapping";
const INBOUND_EDI_PARTNERS_SCREEN_KEY = "trading-partners";
const OUTBOUND_EDI_ENTITY_OBJECTS_SCREEN_KEY = "edi-entity-objects";
const INBOUND_EDI_NAVIGATIONS = [
  { key: "integration", displayName: "EDI Integration", icon: "integrationtab", sequence: 10 },
  { key: SOURCE_SYSTEM_SCREEN_KEY, displayName: "EDI Source System", icon: "system", sequence: 20 },
  { key: "entity-schema", displayName: "Entity Schema", icon: "schema", sequence: 50 },
  { key: INBOUND_EDI_PARTNERS_SCREEN_KEY, displayName: "Trading Partners", icon: "clientscreen", sequence: 70 }
];
const OUTBOUND_EDI_NAVIGATIONS = [
  { key: OUTBOUND_EDI_ENTITY_OBJECTS_SCREEN_KEY, displayName: "EDI Entity Objects", icon: "schema", sequence: 20 },
  { key: DESTINATION_SCREEN_KEY, displayName: "File Destination", icon: "target", sequence: 30 },
  { key: DESTINATION_SCHEDULES_SCREEN_KEY, displayName: "Schedule", icon: "clock", sequence: 60 },
  { key: INBOUND_EDI_PARTNERS_SCREEN_KEY, displayName: "Trading Partners", icon: "clientscreen", sequence: 70 }
];
const INBOUND_EDI_SOURCE_SYSTEM_SCREEN_KEYS = [
  SOURCE_SYSTEM_SCREEN_KEY,
  INBOUND_EDI_TARGETS_SCREEN_KEY,
  INBOUND_EDI_SOURCE_OBJECTS_SCREEN_KEY
];
const ENTITY_SCHEMA_NAVIGATIONS = [
  { key: "entity-schema", displayName: "Schema", icon: "schema", sequence: 10 }
];
const ENTITY_SCHEMA_SCREEN_KEY = "entity-schema";
const INBOUND_EDI_ENTITY_SCHEMA_SCREEN_KEYS = [
  ENTITY_SCHEMA_SCREEN_KEY,
  INBOUND_EDI_MAPPING_SCREEN_KEY
];
const OUTBOUND_EDI_ENTITY_SCHEMA_SCREEN_KEYS = [
  ENTITY_SCHEMA_SCREEN_KEY,
  OUTBOUND_EDI_ENTITY_OBJECTS_SCREEN_KEY
];
const LEGACY_ENTITY_OBJECTS_SCREEN_KEY = "entity-objects";
const MODIFY_ENTITY_OPERATION = "ModifyEntity";
const ENTITY_OBJECT_FIELD_MAPPING_OPERATION = Operations.EntityObjectItem?.EntityObjectFieldMapping || "EntityObjectFieldMapping";
const MAP_ENTITY_OBJECT_OPERATION = Operations.EntityObjectItem?.MapEntityObject || "MapEntityObject";
const SEARCH_ENTITY_OBJECT_OPERATION = Operations.EntityObjectItem?.SearchEntityObject || "SearchEntityObject";
const SCHEMA_ITEM_SERVICE = "Schema";
const SCHEMA_ITEM_NAME = "SchemaItem";
const ENTITY_OBJECT_ITEM_SERVICE = "EntityObject";
const ENTITY_OBJECT_ITEM_NAME = "EntityObjectItem";
const ENTITY_OBJECT_IMPORT_FROM_SCHEMA_OPERATION_NAME =
  Operations.SchemaItem?.ImportEntityObjectFromExternalSchema
  || "ImportEntityObjectFromExternalSchema";
const SOURCE_SYSTEM_ITEM_SERVICE = "SourceSystem";
const SOURCE_OBJECT_ITEM_SERVICE = "SourceObject";
const SOURCE_OBJECT_ITEM_NAME = "SourceObjectItem";
const SOURCE_OBJECT_SEARCH_OPERATION_NAME = Operations.SourceObjectItem.SearchSourceObject;
const SOURCE_OBJECT_EXPORT_ACTION_NAME = "ExportSourceObject";
const SOURCE_OBJECT_IMPORT_FROM_SCHEMA_OPERATION_NAME = Operations.SourceSystemItem.ImportSourceObjectFromExternalSchema;
const SOURCE_OBJECT_SEARCH_ACTION = "search-source-object";
const SOURCE_OBJECT_EXPORT_ACTION = "export-source-object";
const SOURCE_OBJECT_IMPORT_FROM_SCHEMA_ACTION = "import-source-object-from-schema";
const SOURCE_OBJECT_IMPORT_FROM_SCHEMA_LABEL = "Import From Schema";
const ENTITY_OBJECT_SEARCH_ACTION = "destination-entity-search";
const DESTINATION_ENTITY_SEARCH_EXECUTION_SOURCE = "source";
const DESTINATION_ENTITY_SEARCH_EXECUTION_WAREHOUSE = "warehouse";
const DESTINATION_ENTITY_SEARCH_EXECUTION_OPTIONS = [
  {
    Text: "Source",
    Value: DESTINATION_ENTITY_SEARCH_EXECUTION_SOURCE
  },
  {
    Text: "Warehouse",
    Value: DESTINATION_ENTITY_SEARCH_EXECUTION_WAREHOUSE
  }
];
const DESTINATION_ENTITY_SEARCH_EXPORT_OPTIONS = [
  { Text: "No Export", Value: "none" },
  { Text: "Excel", Value: "xlsx" },
  { Text: "CSV", Value: "csv" }
];
const SOURCE_OBJECT_TARGET_METADATA_ID = "86609dc1-1519-40ce-84f3-b767c319b485";
const SOURCE_SYSTEM_CATALOG_TAB_SOURCE_OBJECTS = "source-objects";
const SOURCE_SYSTEM_CATALOG_TAB_TARGETS = "targets";
const INBOUND_EDI_READINESS_OPERATION = Operations.ResourceConfigurationItem?.ValidateResourceConfigurationReadiness || "ValidateResourceConfigurationReadiness";
const INBOUND_EDI_SUMMARY_OPERATION = Operations.ResourceConfigurationItem?.LoadResourceConfigurationManagementSummary || "LoadResourceConfigurationManagementSummary";
const INBOUND_EDI_CONNECTION_OPERATION = Operations.ResourceConfigurationItem?.ValidateInboundEdiConnection || "ValidateInboundEdiConnection";
const INBOUND_EDI_TEST_PARSE_OPERATION = Operations.ResourceConfigurationItem?.RunInboundEdiTestParseValidation || "RunInboundEdiTestParseValidation";
const RESOURCE_CONFIGURATION_RECONFIGURE_OPERATION = Operations.ResourceConfigurationItem?.ReconfigureResourceConfiguration || "ReconfigureResourceConfiguration";
const INBOUND_EDI_PARTNER_MODE_OPTIONS = [
  { Text: "Unassigned", Value: "Unassigned" },
  { Text: "Partner", Value: "Partner" },
  { Text: "Partner Group", Value: "PartnerGroup" }
];
const SOURCE_OBJECT_MANAGE_ROUTE_SEGMENT = "source-objects";
const SOURCE_OBJECT_MANAGE_SCREEN_KEY = "manage";
const SOURCE_OBJECT_OPERATION_NAMES = new Set([
  "SearchSourceObject",
  "LoadSourceObjectKeys",
  "SetSourceObjectUniqueFields",
  "ValidateSourceObjectKeys"
]);
const EXCLUDED_SOURCE_OBJECT_OPERATION_IDS = new Set([
  MANAGE_SOURCE_OBJECT_OPERATION,
  "AIQuestionSourceObjectManage",
  "AIQuestionEDIDiagnostics"
]);
const EXCLUDED_SOURCE_OBJECT_OPERATION_LABELS = new Set([
  "Manage Source",
  "Manage Source Object",
  "Manage Source Configuration",
  "Analyze EDI Diagnostics"
]);
const EXCLUDED_SCHEMA_OPERATION_IDS = new Set([
  Operations.SchemaItem?.CreateEDISchema,
  Operations.SchemaItem?.CreateEntitySchemaFromSource,
  Operations.SchemaItem?.CreateEntitySchemaFromDestinationSchema,
  "CreateEntitySchemaFromDestinationSchema",
  Operations.SchemaItem?.CreateSchema
].filter(Boolean));
const EXCLUDED_SCHEMA_OPERATION_LABELS = new Set([
  "Create EDI Schema",
  "Create Entity Schema From Source",
  "Create Entity Schema From Destination Schema",
  "Create Schema"
]);
const EXCLUDED_ENTITY_OBJECT_OPERATION_IDS = new Set([
  MODIFY_ENTITY_OPERATION,
  ENTITY_OBJECT_FIELD_MAPPING_OPERATION,
  MAP_ENTITY_OBJECT_OPERATION,
  "AIQuestionEntityObjectModify",
  "AIQuestionEntityObject"
].filter(Boolean));
const EXCLUDED_ENTITY_OBJECT_OPERATION_LABELS = new Set([
  "Modify Entity",
  "Modify Entity Configuration",
  "Map Entity Object",
  "Ask Your Warehouse Data"
]);
const SOURCE_OBJECT_EXPORT_FORMATS = [
  {
    value: "csv",
    label: "CSV",
    description: "Comma-separated export for spreadsheets and integrations.",
    iconKey: "fileexport"
  },
  {
    value: "xlsx",
    label: "XLSX",
    description: "Excel workbook export for review and sharing.",
    iconKey: "xlsx"
  }
];
const getCurrentBackUrlQueryParams = () => {
  if (typeof window === "undefined" || !window.location) return undefined;
  const backUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  return backUrl ? { backUrl } : undefined;
};
const getResourceModeHardRedirectQueryParams = () => {
  if (typeof window === "undefined" || !window.location) return undefined;
  const redirectBaseUrl = window.location.origin || "";
  if (!redirectBaseUrl) return getCurrentBackUrlQueryParams();
  return {
    ...(getCurrentBackUrlQueryParams() || {}),
    uiRedirectMode: "hard",
    redirectBaseUrl
  };
};
const runEntityObjectRedirectOperation = async (entityObjectManager, entityObject, setError, operationName = MODIFY_ENTITY_OPERATION) => {
  if (!entityObjectManager?.RunOperation) {
    setError?.("Entity Object operation service is not available.");
    return false;
  }
  const entityObjectId = getId(entityObject);
  if (!entityObjectId) {
    setError?.("Entity Object is missing an id.");
    return false;
  }
  try {
    setError?.("");
    const result = await entityObjectManager.RunOperation({
      operationName,
      id: entityObjectId,
      queryParams: getResourceModeHardRedirectQueryParams(),
      item: entityObject,
      isItem: true
    });
    const operationResult = getOperationResult(result);
    if (operationResult?.Success === false || operationResult?.success === false) {
      throw new Error(getOperationResultMessage(result, "Entity Object editor could not be opened."));
    }
    return true;
  } catch (ex) {
    setError?.(ex?.message || "Entity Object editor could not be opened.");
    return false;
  }
};
const CONFIGURATIONS_NAVIGATION = {
  key: "configurations",
  displayName: "Configurations",
  icon: "application",
  sequence: 2,
  subNavigations: [CONFIGURATION_MANAGE_NAVIGATION]
};
const CUSTOM_FIELD_METADATA_FLAG = "__CustomFieldUIMetadataField";
const LOG_LEVEL_LABELS = {
  0: "Debug",
  1: "Info",
  2: "Timing",
  3: "Warning",
  4: "Error"
};
const SENSITIVE_CONNECTION_FIELD_PATTERN = /(password|passwd|pwd|secret|token|credential|private|clientsecret|apikey|api key|accesskey|access key|auth|authorization)$/i;
const DIRECT_INTEGRATION_CONNECTION_FIELDS = [
  "Protocol",
  "Host",
  "Server",
  "Port",
  "Url",
  "BaseUrl",
  "Path",
  "RootFolder",
  "DBName",
  "DatabaseName",
  "ApiName",
  "Schema",
  "Tenant",
  "Company",
  "User",
  "UserName",
  "Username",
  "Login"
];

const DEFAULT_RESOURCE_NAVIGATIONS = [
  OVERVIEW_NAVIGATION,
  CONFIGURATIONS_NAVIGATION,
  EVENTS_NAVIGATION,
  LOGS_NAVIGATION
];

const buildSelectListField = (fieldName, displayName, options, placeHolder = "Please Select", ariaLabel = "") => ({
  FieldType: SystemTypes.UIFieldType.SelectList,
  FieldName: fieldName,
  DisplayName: displayName,
  DataJson: options,
  PlaceHolder: placeHolder,
  AriaLabel: ariaLabel
});

const buildSearchSelectListField = (fieldName, displayName, options, placeHolder = "Please Select", ariaLabel = "") => ({
  FieldType: SystemTypes.UIFieldType.SearchSelectList,
  FieldName: fieldName,
  DisplayName: displayName,
  DataJson: options,
  PlaceHolder: placeHolder,
  AriaLabel: ariaLabel
});

const buildFilterTextBoxField = (fieldName, placeHolder, ariaLabel) => ({
  FieldType: SystemTypes.UIFieldType.TextBox,
  FieldName: fieldName,
  DisplayName: "",
  PlaceHolder: placeHolder,
  AriaLabel: ariaLabel
});

const SOURCE_CATALOG_STATUS_OPTIONS = [
  { Text: "Active", Value: "active" },
  { Text: "Inactive", Value: "inactive" },
  { Text: "All", Value: "all" }
];

const matchesSourceCatalogStatusFilter = (item, statusFilter) => {
  if (statusFilter === "all") return true;
  if (statusFilter === "inactive") return !isRecordActive(item);
  return isRecordActive(item);
};

const buildTextBoxField = (fieldName, displayName, description = "") => ({
  FieldType: SystemTypes.UIFieldType.TextBox,
  FieldName: fieldName,
  DisplayName: displayName,
  Description: description,
  IsRequired: true
});

const buildTextAreaField = (fieldName, displayName, description = "") => ({
  FieldType: SystemTypes.UIFieldType.TextArea,
  FieldName: fieldName,
  DisplayName: displayName,
  Description: description
});

const RESOURCE_CONFIGURATION_EDIT_PANEL_METADATA = {
  Name: "ResourceConfiguration",
  DisplayName: "Resource Configuration",
  SidePanelFormWidth: 34,
  Fields: [
    buildTextBoxField("DisplayName", "Name", "Configuration display name."),
    buildTextAreaField("Description", "Description", "Optional notes for this configuration.")
  ]
};

const SCHEMA_VERSION_EDIT_PANEL_METADATA = {
  Name: "SchemaVersion",
  DisplayName: "Schema Version",
  SidePanelFormWidth: 34,
  Fields: [
    {
      FieldType: SystemTypes.UIFieldType.TextBox,
      FieldName: "Version",
      DisplayName: "Version",
      IsReadOnly: true
    },
    buildTextBoxField("Title", "Title", "User-facing schema version title."),
    buildTextAreaField("Comment", "Comments", "Summary of changes in this schema version.")
  ]
};

const endpointEntityOptionSupportsWarehouse = (option) =>
  normalizeCompare(getString(option, "Icon", "icon")) === "database";

const normalizeEndpointEntityPanelExecutionMode = (panelData, entityObjectOptions = []) => {
  if (!panelData) return panelData;

  const selectedEntityObjectId = getString(panelData, "__EndpointEntityObjectSelectionId", "EntityObjectId", "entityObjectId");
  const selectedEntityObjectOption = entityObjectOptions.find((option) =>
    normalizeCompare(getString(option, "Value", "value")) === normalizeCompare(selectedEntityObjectId)
  );
  if (!selectedEntityObjectOption) {
    return panelData;
  }

  const supportsWarehouse = endpointEntityOptionSupportsWarehouse(selectedEntityObjectOption);
  const currentExecutionMode = String(getString(panelData, "ExecutionMode", "executionMode") || "1");
  const normalizedExecutionMode = supportsWarehouse ? currentExecutionMode : "1";
  const warehouseQueryIsEnabled = getValue(
    panelData,
    "EnableWarehouseNativeQuery",
    "enableWarehouseNativeQuery"
  ) === true;
  const warehouseQueryIsAllowed = supportsWarehouse && normalizedExecutionMode === "2";
  if (
    normalizedExecutionMode === currentExecutionMode
    && (!warehouseQueryIsEnabled || warehouseQueryIsAllowed)
  ) {
    return panelData;
  }

  return {
    ...panelData,
    ...(normalizedExecutionMode !== currentExecutionMode ? { ExecutionMode: normalizedExecutionMode } : {}),
    ...(warehouseQueryIsEnabled && !warehouseQueryIsAllowed ? { EnableWarehouseNativeQuery: false } : {})
  };
};

const buildEndpointEntityEndpointPanelMetadata = ({
  entityObjectOptions = [],
  schemaScopes = [],
  isAddMode = false,
  selectedEntityObjectId = "",
  selectedExecutionMode = "1"
} = {}) => {
  const activeSchemaScopes = schemaScopes.filter((scope) =>
    getValue(scope, "IsDeleted", "isDeleted") !== true
    && getValue(scope, "IsActive", "isActive") !== false
    && getString(scope, "SchemaScopeIdentifier", "schemaScopeIdentifier")
    && getString(scope, "SchemaVersionId", "schemaVersionId")
  );
  const schemaScopeOptions = activeSchemaScopes.map((scope) => {
    const scopeIdentifier = getString(scope, "SchemaScopeIdentifier", "schemaScopeIdentifier");
    const schemaName = getString(scope, "SchemaName", "schemaName") || "Schema";
    const schemaVersion = getString(scope, "SchemaVersion", "schemaVersion") || "Active version";
    return {
      Text: `${scopeIdentifier} — ${schemaName} — ${schemaVersion}`,
      Value: scopeIdentifier
    };
  });
  const activeSchemaScopeIdentifiers = new Set(activeSchemaScopes
    .map((scope) => normalizeCompare(getString(scope, "SchemaScopeIdentifier", "schemaScopeIdentifier")))
    .filter(Boolean));
  const entityOptions = entityObjectOptions.map((option) => ({
    Text: getString(option, "Text", "text"),
    Value: getString(option, "Value", "value"),
    FilterValue: getString(option, "FilterValue", "filterValue"),
    Icon: getString(option, "Icon", "icon"),
    Disabled: getValue(option, "Disabled", "disabled") === true
  })).filter((option) =>
    option.Value
    && option.FilterValue
    && activeSchemaScopeIdentifiers.has(normalizeCompare(option.FilterValue))
  );
  const selectedEntityObjectOption = entityOptions.find((option) =>
    normalizeCompare(option.Value) === normalizeCompare(selectedEntityObjectId)
  );
  const warehouseIsUnavailable = Boolean(
    selectedEntityObjectOption && !endpointEntityOptionSupportsWarehouse(selectedEntityObjectOption)
  );
  const warehouseModeIsSelected = String(selectedExecutionMode || "1") === "2";
  const metadata = buildEntityEndpointMetadata({
    Name: "SchemaAPIEntityEndpoint",
    DisplayName: "Endpoint Entity Scope",
    SidePanelFormWidth: 34
  });
  return {
    ...metadata,
    Fields: [
      {
        FieldName: "SchemaScopeIdentifier",
        DisplayName: "Schema Scope",
        Description: "Schema scope and its active version used to filter Entity Objects.",
        FieldType: SystemTypes.UIFieldType.SearchSelectList,
        DataJson: schemaScopeOptions,
        IsRequired: true,
        IsReadOnly: !isAddMode,
        ShowOnGrid: true,
        ShowOnCard: true,
        DisplaySequence: 1,
        GridSequence: 1,
        TabId: "Endpoint|1",
        PlaceHolder: "Select schema scope"
      },
      ...(metadata.Fields || [])
        .filter((field) => field.FieldName !== "SchemaScopeIdentifier" && field.FieldName !== "EntitySchemaVersion")
        .map((field) => {
          if (field.FieldName === "ExecutionMode") {
            return {
              ...field,
              Disabled: warehouseIsUnavailable,
              DataJson: (field.DataJson || []).map((option) => ({
                ...option,
                Disabled: String(getString(option, "Value", "value")) === "2" && warehouseIsUnavailable
              })),
              DisplaySequence: Number(field.DisplaySequence || 0) + 1,
              GridSequence: Number(field.GridSequence || 0) + 1
            };
          }
          if (field.FieldName === "EnableWarehouseNativeQuery") {
            return {
              ...field,
              Disabled: warehouseIsUnavailable || !warehouseModeIsSelected,
              DisplaySequence: Number(field.DisplaySequence || 0) + 1,
              GridSequence: Number(field.GridSequence || 0) + 1
            };
          }
          if (field.FieldName !== "EntityObjectId") {
            return {
              ...field,
              DisplaySequence: Number(field.DisplaySequence || 0) + 1,
              GridSequence: Number(field.GridSequence || 0) + 1
            };
          }
          return {
            ...field,
            FieldName: "__EndpointEntityObjectSelectionId",
            FieldType: SystemTypes.UIFieldType.SearchSelectList,
            DataJson: entityOptions,
            DataOperationFilterProperty: "SchemaScopeIdentifier",
            IsReadOnly: !isAddMode,
            DisplaySequence: 2,
            GridSequence: 2,
            PlaceHolder: "Select entity object"
          };
        })
    ]
  };
};

const CLIENT_RESOURCE_STATUS_LABELS = {
  4: "Active",
  6: "Suspended",
  8: "Cancelled",
  9: "Retired",
  10: "Pending Approval"
};

const LEGACY_CLIENT_RESOURCE_STATUS_LABELS = {
  1: "Pending Approval",
  2: "Pending Approval",
  3: "Pending Approval",
  5: "Suspended",
  7: "Suspended"
};

const APPROVAL_STATUS_LABELS = {
  1: "Not Submitted",
  2: "Pending Review",
  3: "Approved",
  4: "Rejected",
  5: "Changes Requested",
  6: "Cancelled"
};

const RESOURCE_CONFIGURATION_STATUS_LABELS = {
  1: "Draft",
  2: "Configuring",
  3: "Pending Review",
  4: "Active",
  5: "Failed",
  6: "Retired",
  7: "Configured",
  8: "Ready",
  9: "Disabled"
};

const LINKED_CONFIGURATION_RECORD_LABELS = {
  SystemConfig: "System Config",
  Integration: "Integration",
  SourceSystem: "Source System",
  SourceObjectTarget: "EDI Target",
  SourceObject: "Source Object",
  Schema: "Entity Schema",
  Warehouse: "Warehouse Destination",
  FileStorage: "File Destination",
  ExternalSystem: "External System Destination"
};

const IMPORT_ENTITY_SCHEMA_RESOURCE_CONFIGURATION_OPERATION =
  Operations.ResourceConfigurationItem?.ImportEntitySchemaResourceConfiguration
  || "ImportEntitySchemaResourceConfiguration";

const normalizeCompare = (value) => String(value ?? "").trim().toLowerCase();

const getValue = (item, ...keys) => {
  if (!item) return "";
  for (const key of keys) {
    const value = item[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return "";
};

const getString = (item, ...keys) => String(getValue(item, ...keys) ?? "").trim();

const getNumber = (item, ...keys) => {
  const value = getValue(item, ...keys);
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getBoolean = (item, ...keys) => {
  const value = getValue(item, ...keys);
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  const normalized = normalizeCompare(value);
  return normalized === "true" || normalized === "yes" || normalized === "1";
};

const isSingleInstanceProductResource = (...sources) => sources.some((source) => {
  const productResource = getValue(source, "productResource", "ProductResource") || source;
  return getBoolean(productResource, "IsSingleInstance", "isSingleInstance");
});

const getArray = (item, ...keys) => {
  for (const key of keys) {
    const value = item?.[key];
    if (Array.isArray(value)) return value;
  }
  return [];
};

const firstItem = (items) => {
  if (!items) return null;
  if (typeof items.first === "function") return items.first();
  return Array.isArray(items) ? items[0] : null;
};

const getOperationResult = (response) => {
  const firstResponse = firstItem(response);
  return firstResponse?.Items || firstResponse?.Success !== undefined || firstResponse?.success !== undefined
    ? firstResponse
    : response;
};

const getResultMessageText = (value) => {
  if (value === undefined || value === null || value === "") return "";
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) return value.map(getResultMessageText).filter(Boolean).join("\n");
  if (typeof value === "object") {
    const message = getString(value, "DisplayMessage", "displayMessage", "Content", "content", "Message", "message", "Text", "text", "Info", "info");
    if (message) return message;
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value).trim();
};

const getOperationResultMessage = (response, fallbackMessage) => {
  const result = getOperationResult(response);
  return getResultMessageText(getValue(result, "DisplayMessage", "displayMessage"))
    || getResultMessageText(getValue(result, "Message", "message"))
    || getResultMessageText(getValue(result, "Value", "value"))
    || fallbackMessage;
};

const getSourceObjectExportDispatchMessage = (response, sourceObject) => {
  const processorType = getSourceObjectProcessorType(sourceObject);
  if (processorType === SOURCE_OBJECT_PROCESSOR_TYPES.REAL_TIME) return "";

  if (processorType === SOURCE_OBJECT_PROCESSOR_TYPES.ASYNC) {
    return "Export queued for background processing. Open the Background section in the upper right of the screen to monitor it. When processing completes, the file download will be available from the completed event.";
  }

  if (processorType === SOURCE_OBJECT_PROCESSOR_TYPES.BACKGROUND) {
    return "Export queued for background processing. Open the Background section in the upper right of the screen to monitor it. When processing completes, the file download will be available from the completed event.";
  }

  return "";
};

const getOperationResultDetails = (response) => {
  const result = getOperationResult(response);
  return [
    ...getArray(result, "MessageDetails", "messageDetails"),
    ...getArray(result, "Details", "details"),
    ...getArray(result, "Messages", "messages")
  ].map(getResultMessageText).filter(Boolean);
};

const getOperationItems = (response) => {
  const result = getOperationResult(response);
  const resultItems = getArray(result, "Items", "items");
  if (result === response) return resultItems.filter(Boolean);

  return [...resultItems, ...getArray(response, "Items", "items")].filter(Boolean);
};

const getOperationItem = (response) => {
  const result = getOperationResult(response);
  return firstItem(result?.Items)
    || firstItem(result?.items)
    || result?.Item
    || result?.item
    || result?.Value
    || result?.value
    || null;
};

const getImportedSourceObjectRecordId = (response) => {
  const visited = new Set();
  const visit = (value) => {
    if (!value) return "";
    if (Array.isArray(value)) {
      for (const item of value) {
        const sourceObjectId = visit(item);
        if (sourceObjectId) return sourceObjectId;
      }
      return "";
    }
    if (typeof value !== "object" || visited.has(value)) return "";
    visited.add(value);

    const sourceObjectId = getString(value, "SourceObjectId", "sourceObjectId");
    if (sourceObjectId) return getString(value, "_id", "Id", "id");

    for (const key of ["Item", "item", "Items", "items", "InnerResults", "innerResults"]) {
      const nestedSourceObjectId = visit(value[key]);
      if (nestedSourceObjectId) return nestedSourceObjectId;
    }
    return "";
  };

  return visit(getOperationResult(response)) || visit(response);
};

const dispatchAiPanelCommandFromOperationResponse = (response) => {
  const result = getOperationResult(response);
  const commands = getValue(result, "UICommands", "uiCommands");
  if (!commands) return false;
  return dispatchAiPanelCommand(commands, result);
};

const getOperationName = (operation) =>
  getString(operation, "Name", "name", "OperationTypeId", "operationTypeId", "Value", "value", "_id", "id");

const getOperationIcon = (metadata, operationName, fallbackIcon = "") => {
  const normalizedOperationName = normalizeCompare(operationName);
  const operationSources = [
    ...getArray(metadata, "Operations", "operations"),
    ...getArray(metadata, "OperationTypes", "operationTypes"),
    ...getArray(metadata, "ItemOperations", "itemOperations"),
    ...getArray(metadata, "SupportedOperations", "supportedOperations"),
    ...getArray(metadata, "UIOperations", "uiOperations"),
    ...getArray(metadata, "Commands", "commands"),
    ...getArray(metadata, "UICommands", "uiCommands")
  ];
  const operation = operationSources.find((item) => normalizeCompare(getOperationName(item)) === normalizedOperationName);
  return getString(operation, "Icon", "icon", "IconKey", "iconKey") || fallbackIcon;
};

const getOperationDisplayName = (operation) =>
  getString(operation, "DisplayName", "displayName", "Text", "text") || getOperationName(operation);

const normalizeOperationForDialog = (operation) => {
  const name = getOperationName(operation);
  return {
    ...operation,
    Name: name,
    OperationTypeId: getString(operation, "OperationTypeId", "operationTypeId") || name,
    DisplayName: getOperationDisplayName(operation)
  };
};

const normalizeSourceSystemLoadTargetsOperation = (operation) => ({
  ...normalizeOperationForDialog(operation),
  CanSelectProcessor: false,
  canSelectProcessor: false
});

const isSourceObjectSearchOperation = (operation) =>
  normalizeCompare(getOperationName(operation)) === normalizeCompare(SOURCE_OBJECT_SEARCH_OPERATION_NAME);

const toSourceObjectSearchAction = (operation) => {
  const normalized = normalizeOperationForDialog(operation);
  return {
    ...normalized,
    Name: SOURCE_OBJECT_SEARCH_OPERATION_NAME,
    OperationTypeId: SOURCE_OBJECT_SEARCH_OPERATION_NAME,
    DisplayName: "Search Source",
    Icon: getString(normalized, "Icon", "icon", "IconKey", "iconKey") || "search",
    CanSelectProcessor: false,
    canSelectProcessor: false,
    Fields: [],
    fields: [],
    ResourceOperationAction: SOURCE_OBJECT_SEARCH_ACTION
  };
};

const toSourceObjectExportAction = (operation) => {
  const normalized = normalizeOperationForDialog(operation);
  const sequence = Number(getValue(normalized, "Sequence", "sequence"));
  return {
    ...normalized,
    Name: SOURCE_OBJECT_EXPORT_ACTION_NAME,
    OperationTypeId: SOURCE_OBJECT_SEARCH_OPERATION_NAME,
    DisplayName: "Export Source",
    Icon: "export",
    CanSelectProcessor: false,
    canSelectProcessor: false,
    Fields: [],
    fields: [],
    Sequence: Number.isFinite(sequence) ? sequence + 0.1 : getValue(normalized, "Sequence", "sequence"),
    ResourceOperationAction: SOURCE_OBJECT_EXPORT_ACTION
  };
};

const toSourceObjectImportFromSchemaAction = (operation) => {
  const normalized = normalizeOperationForDialog(operation);
  const fields = getArray(normalized, "Fields", "fields")
    .filter((field) => normalizeCompare(getString(field, "FieldName", "fieldName")) !== "targetid");
  return {
    ...normalized,
    DisplayName: SOURCE_OBJECT_IMPORT_FROM_SCHEMA_LABEL,
    Fields: fields,
    fields,
    ResourceOperationAction: SOURCE_OBJECT_IMPORT_FROM_SCHEMA_ACTION
  };
};

const normalizeSourceObjectOperations = (operations = []) => {
  const baseOperations = sortOperations(operations
    .filter(isSourceObjectOperation)
    .filter((operation) => !isExcludedSourceObjectOperation(operation))
    .map(normalizeOperationForDialog));

  return baseOperations.flatMap((operation) => (
    isSourceObjectSearchOperation(operation)
      ? [toSourceObjectSearchAction(operation), toSourceObjectExportAction(operation)]
      : [operation]
  ));
};

const isSourceObjectSearchAction = (operation) =>
  getString(operation, "ResourceOperationAction", "resourceOperationAction") === SOURCE_OBJECT_SEARCH_ACTION;

const isSourceObjectExportAction = (operation) =>
  getString(operation, "ResourceOperationAction", "resourceOperationAction") === SOURCE_OBJECT_EXPORT_ACTION;

const isSourceObjectImportFromSchemaAction = (operation) =>
  getString(operation, "ResourceOperationAction", "resourceOperationAction") === SOURCE_OBJECT_IMPORT_FROM_SCHEMA_ACTION;

const isSourceObjectOperation = (operation) => {
  const operationName = getOperationName(operation);
  if (!operationName) return false;
  const itemName = getString(operation, "ItemName", "itemName", "MetadataRefItemName", "metadataRefItemName");
  return SOURCE_OBJECT_OPERATION_NAMES.has(operationName)
    || (
      getBoolean(operation, "SupportsOperationItem", "supportsOperationItem")
      && normalizeCompare(itemName) === normalizeCompare(SOURCE_OBJECT_ITEM_NAME)
    );
};

const isExcludedSourceObjectOperation = (operation) =>
  EXCLUDED_SOURCE_OBJECT_OPERATION_IDS.has(getOperationName(operation))
  || EXCLUDED_SOURCE_OBJECT_OPERATION_LABELS.has(getOperationDisplayName(operation));

const getSourceObjectOperations = (operations = []) => {
  const sourceObjectOperations = normalizeSourceObjectOperations(operations);
  const importFromSchemaOperation = operations.find((operation) =>
    normalizeCompare(getOperationName(operation)) === normalizeCompare(SOURCE_OBJECT_IMPORT_FROM_SCHEMA_OPERATION_NAME)
  );

  return importFromSchemaOperation
    ? [...sourceObjectOperations, toSourceObjectImportFromSchemaAction(importFromSchemaOperation)]
    : sourceObjectOperations;
};

const isSchemaOperation = (operation) => {
  const operationName = getOperationName(operation);
  if (!operationName || EXCLUDED_SCHEMA_OPERATION_IDS.has(operationName)) return false;
  if (EXCLUDED_SCHEMA_OPERATION_LABELS.has(getOperationDisplayName(operation))) return false;
  const itemName = getString(operation, "ItemName", "itemName", "MetadataRefItemName", "metadataRefItemName");
  const normalizedItemName = normalizeCompare(itemName);
  return getBoolean(operation, "SupportsOperationItem", "supportsOperationItem")
    && (normalizedItemName === normalizeCompare(SCHEMA_ITEM_NAME) || normalizedItemName === normalizeCompare(SCHEMA_ITEM_SERVICE));
};

const getSchemaOperations = (operations = []) =>
  sortOperations(operations
    .filter(isSchemaOperation)
    .map(normalizeOperationForDialog));

const isEntityObjectImportFromSchemaOperation = (operation) =>
  normalizeCompare(getOperationName(operation)) === normalizeCompare(ENTITY_OBJECT_IMPORT_FROM_SCHEMA_OPERATION_NAME);

const isEntityObjectOperation = (operation) => {
  const operationName = getOperationName(operation);
  if (!operationName || EXCLUDED_ENTITY_OBJECT_OPERATION_IDS.has(operationName)) return false;
  if (EXCLUDED_ENTITY_OBJECT_OPERATION_LABELS.has(getOperationDisplayName(operation))) return false;
  const itemName = getString(operation, "ItemName", "itemName", "MetadataRefItemName", "metadataRefItemName");
  const normalizedItemName = normalizeCompare(itemName);
  return getBoolean(operation, "SupportsOperationItem", "supportsOperationItem")
    && (normalizedItemName === normalizeCompare(ENTITY_OBJECT_ITEM_NAME) || normalizedItemName === normalizeCompare(ENTITY_OBJECT_ITEM_SERVICE));
};

const getEntityObjectOperations = (operations = []) =>
  sortOperations(operations
    .filter(isEntityObjectOperation)
    .map(normalizeOperationForDialog));

const copyTextToClipboard = async (text) => {
  if (!text) return;
  const fallbackCopy = () => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.setAttribute("readonly", "");
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.select();
    textArea.setSelectionRange(0, textArea.value.length);
    const copied = document.execCommand("copy");
    document.body.removeChild(textArea);
    if (!copied) {
      throw new Error("Copy command was blocked.");
    }
  };

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      fallbackCopy();
      return;
    }
  }

  fallbackCopy();
};

const getId = (item) => getString(item, "_id", "Id", "id", "UniqueId", "uniqueId");

const getDisplayName = (item, fallback = "") =>
  getString(item, "DisplayName", "displayName", "ResourceName", "resourceName", "ProductName", "productName", "name") || fallback;

const RESOURCE_CATALOG_DISPLAY_NAMES_BY_KEY = Object.freeze({
  apiendpoint: "Client API: Query Data",
  clientmcpserver: "Client MCP: Server",
  clientmcptoolsendpoint: "Client MCP: Tools",
  mcpdataqueryendpoint: "Client MCP: Query Data",
  mcptoolendpoint: "Client MCP: Tools"
});

const getResourceCatalogDisplayName = (resource) => {
  const productResourceKey = resource?.productResourceKey
    || getString(resource?.clientResource, "ProductResourceKey", "productResourceKey")
    || getString(resource?.productResource, "ProductResourceKey", "productResourceKey");
  return RESOURCE_CATALOG_DISPLAY_NAMES_BY_KEY[normalizeCompare(productResourceKey)]
    || resource?.displayName
    || getDisplayName(resource?.clientResource, getDisplayName(resource?.productResource, productResourceKey));
};

const formatDisplayLabel = (value) =>
  String(value ?? "")
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");

const formatToolKey = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const withoutDomain = raw.includes("|") ? raw.split("|").pop() : raw;
  const trimmed = withoutDomain.replace(/^\/+|\/+$/g, "");
  const parts = trimmed.split("/").filter(Boolean);
  return normalizeCompare(parts[parts.length - 1] || trimmed);
};

const cloneNavigation = (navigation) => ({
  ...navigation,
  subNavigations: getArray(navigation, "subNavigations", "SubNavigations").map(cloneNavigation)
});

const mapResourceNavigation = (navigation, index = 0) => {
  const configuredDisplayName = getDisplayName(navigation, "Resource tool");
  const key = formatToolKey(getString(navigation, "ScreenKey", "screenKey", "Key", "key")) || formatToolKey(configuredDisplayName);
  if (!key) return null;
  const displayName = key === OVERVIEW_NAVIGATION.key
    ? OVERVIEW_NAVIGATION.displayName
    : configuredDisplayName;

  return {
    key,
    displayName,
    icon: getString(navigation, "Icon", "icon") || RESOURCE_FALLBACK_ICON,
    permissions: getArray(navigation, "Permission", "permission", "Permissions", "permissions"),
    sequence: getNumber(navigation, "Sequence", "sequence") ?? index + 1,
    subNavigations: getArray(navigation, "SubNavigations", "subNavigations")
      .map((subNavigation, subIndex) => mapResourceNavigation(subNavigation, subIndex))
      .filter(Boolean),
    raw: navigation
  };
};

const isSourceIntegrationResource = (productResource) =>
  normalizeCompare(getString(productResource, "ProductKey", "productKey")) === "sourceintegration";

const isEntitySchemaResource = (productResource) =>
  normalizeCompare(getString(productResource, "ProductKey", "productKey")) === "entityschema"
  || normalizeCompare(getString(productResource, "ProductResourceKey", "productResourceKey")) === "entityschema";

const isInboundEdiResource = (productResource, selectedResource = null, configuration = null) => {
  const productKey = normalizeCompare(
    getString(productResource, "ProductKey", "productKey")
    || selectedResource?.productKey
    || getString(selectedResource?.product, "ProductKey", "productKey")
    || getString(configuration, "ProductKey", "productKey")
  );
  const resourceKey = normalizeCompare(
    getString(productResource, "ProductResourceKey", "productResourceKey")
    || selectedResource?.productResourceKey
    || getString(selectedResource?.productResource, "ProductResourceKey", "productResourceKey")
    || getString(configuration, "ProductResourceKey", "productResourceKey")
  );
  const setupPathKey = normalizeCompare(
    getString(productResource, "SetupPathKey", "setupPathKey")
    || getString(selectedResource?.productResource, "SetupPathKey", "setupPathKey")
    || getString(configuration, "SetupPathKey", "setupPathKey")
  );
  return productKey === "inboundedi"
    || setupPathKey === "inboundedisetup"
    || /inboundedi$/i.test(resourceKey);
};

const isOutboundEdiResource = (productResource, selectedResource = null, configuration = null) => {
  const productKey = normalizeCompare(
    getString(productResource, "ProductKey", "productKey")
    || selectedResource?.productKey
    || getString(selectedResource?.product, "ProductKey", "productKey")
    || getString(configuration, "ProductKey", "productKey")
  );
  const resourceKey = normalizeCompare(
    getString(productResource, "ProductResourceKey", "productResourceKey")
    || selectedResource?.productResourceKey
    || getString(selectedResource?.productResource, "ProductResourceKey", "productResourceKey")
    || getString(configuration, "ProductResourceKey", "productResourceKey")
  );
  const setupPathKey = normalizeCompare(
    getString(productResource, "SetupPathKey", "setupPathKey")
    || getString(selectedResource?.productResource, "SetupPathKey", "setupPathKey")
    || getString(configuration, "SetupPathKey", "setupPathKey")
  );
  return productKey === "outboundedi"
    || setupPathKey === "outboundedisetup"
    || /outboundedi$/i.test(resourceKey);
};

const getEdiResourceDirection = (resource) => {
  if (isInboundEdiResource(resource?.productResource, resource)) return "inbound";
  if (isOutboundEdiResource(resource?.productResource, resource)) return "outbound";
  return "";
};

const isEdiResource = (resource) => Boolean(getEdiResourceDirection(resource));

const isEdiResourceConfiguration = (productResource, selectedResource = null, configuration = null) =>
  isInboundEdiResource(productResource, selectedResource, configuration)
  || isOutboundEdiResource(productResource, selectedResource, configuration);

const getEdiConfigurationDirection = (productResource, selectedResource = null, configuration = null) =>
  isOutboundEdiResource(productResource, selectedResource, configuration) ? "Outbound" : "Inbound";

const buildEdiGroupSummary = (resources) => {
  const count = resources.length;
  const inboundCount = resources.filter((resource) => getEdiResourceDirection(resource) === "inbound").length;
  const outboundCount = resources.filter((resource) => getEdiResourceDirection(resource) === "outbound").length;
  const resourceLabel = count === 1 ? "EDI resource" : "EDI resources";
  return `${count} ${resourceLabel}: ${inboundCount} inbound, ${outboundCount} outbound`;
};

const resolveProductResourceForClientResource = (clientResource, resourcesById, resourcesByKey) => {
  const productResourceKey = getString(clientResource, "ProductResourceKey", "productResourceKey");
  const productResourceDefinitionId = getString(
    clientResource,
    "ProductResourceDefinitionId",
    "productResourceDefinitionId"
  );
  if (isClientMcpToolsResource(clientResource)) {
    const clientMcpToolsResource = resourcesByKey.get(normalizeCompare(CLIENT_MCP_TOOLS_RESOURCE_KEY));
    if (clientMcpToolsResource) return clientMcpToolsResource;
  }
  if (isClientMcpServerResource(clientResource)) {
    const clientMcpServerResource = resourcesByKey.get(normalizeCompare(CLIENT_MCP_SERVER_RESOURCE_KEY));
    if (clientMcpServerResource) return clientMcpServerResource;
  }

  return resourcesById.get(normalizeCompare(productResourceDefinitionId))
    || resourcesByKey.get(normalizeCompare(productResourceKey))
    || null;
};

const ENDPOINT_RESOURCE_NAVIGATIONS = [
  { key: DESTINATION_SCREEN_KEY, displayName: "Overview", icon: "target", sequence: 10 },
  { key: DESTINATION_DEPLOYMENTS_SCREEN_KEY, displayName: "Deployments", icon: "target", sequence: 20 }
];

const ensureConfigurationsNavigation = (navigations, productResource, selectedResource = null) => {
  const nextNavigations = navigations.map(cloneNavigation);
  const configurations = nextNavigations.find((navigation) => navigation.key === CONFIGURATIONS_NAVIGATION.key);
  const destinationDefaults = getDestinationResourceDefaults(productResource);
  const isOutboundEdiNavigation = isOutboundEdiResource(productResource, selectedResource);
  const productSpecificNavigations = isInboundEdiResource(productResource, selectedResource)
    ? INBOUND_EDI_NAVIGATIONS
    : isOutboundEdiNavigation
    ? OUTBOUND_EDI_NAVIGATIONS
    : isSourceIntegrationResource(productResource)
    ? SOURCE_INTEGRATION_NAVIGATIONS
    : isEntitySchemaResource(productResource)
      ? ENTITY_SCHEMA_NAVIGATIONS
      : isClientMcpServerResource(productResource, selectedResource)
      ? CLIENT_MCP_SERVER_NAVIGATIONS
      : isClientMcpToolsResource(productResource, selectedResource)
      ? CLIENT_MCP_TOOLS_NAVIGATIONS
      : destinationDefaults
      ? [
        ...(destinationDefaults.isEndpoint ? ENDPOINT_RESOURCE_NAVIGATIONS : DESTINATION_NAVIGATIONS)
      ]
      : [];
  const requiredSubNavigations = [CONFIGURATION_MANAGE_NAVIGATION, ...productSpecificNavigations];
  const isClientMcpNavigation = isClientMcpServerResource(productResource, selectedResource)
    || isClientMcpToolsResource(productResource, selectedResource);

  if (!configurations) {
    return [
      ...nextNavigations,
      {
        ...cloneNavigation(CONFIGURATIONS_NAVIGATION),
        subNavigations: requiredSubNavigations.map(cloneNavigation)
      }
    ];
  }

  const subNavigations = getArray(configurations, "subNavigations", "SubNavigations")
    .filter((navigation) =>
      navigation.key !== DESTINATION_SCHEDULES_SCREEN_KEY
      && (
        !isOutboundEdiNavigation
        || (
          navigation.key !== ENTITY_SCHEMA_SCREEN_KEY
          && navigation.key !== DESTINATION_DEPLOYMENTS_SCREEN_KEY
          && navigation.key !== DESTINATION_ENTITY_DEPLOYMENTS_SCREEN_KEY
        )
      )
      && (
        !isEntitySchemaResource(productResource)
        || navigation.key !== LEGACY_ENTITY_OBJECTS_SCREEN_KEY
      )
      && (
        !isClientMcpNavigation
        || navigation.key === CONFIGURATION_MANAGE_NAVIGATION.key
        || productSpecificNavigations.some((requiredNavigation) => requiredNavigation.key === navigation.key)
      )
    );
  const missingSubNavigations = requiredSubNavigations
    .filter((requiredNavigation) => !subNavigations.some((navigation) => navigation.key === requiredNavigation.key))
    .map(cloneNavigation);

  configurations.subNavigations = [...missingSubNavigations, ...subNavigations]
    .sort((left, right) =>
      (left.sequence || 0) - (right.sequence || 0)
      || left.displayName.localeCompare(right.displayName)
    );

  return nextNavigations;
};

const readResourceNavigations = (productResource, selectedResource = null) => {
  const configuredNavigations = getArray(productResource, "ResourceNavigations", "resourceNavigations");
  let navigations = configuredNavigations.length > 0
    ? configuredNavigations.map(mapResourceNavigation).filter(Boolean)
    : DEFAULT_RESOURCE_NAVIGATIONS.map(cloneNavigation);

  if (!navigations.some((navigation) => navigation.key === OVERVIEW_NAVIGATION.key)) {
    navigations = [cloneNavigation(OVERVIEW_NAVIGATION), ...navigations];
  }
  if (!navigations.some((navigation) => navigation.key === LOGS_NAVIGATION.key)) {
    navigations = [...navigations, cloneNavigation(LOGS_NAVIGATION)];
  }
  if (!navigations.some((navigation) => navigation.key === EVENTS_NAVIGATION.key)) {
    navigations = [...navigations, cloneNavigation(EVENTS_NAVIGATION)];
  }
  navigations = ensureConfigurationsNavigation(navigations, productResource, selectedResource);

  return [...navigations].sort((left, right) =>
    (left.key === OVERVIEW_NAVIGATION.key ? -1 : 0) - (right.key === OVERVIEW_NAVIGATION.key ? -1 : 0)
    ||
    (left.sequence || 0) - (right.sequence || 0)
    || left.displayName.localeCompare(right.displayName)
  );
};

const buildCatalogModel = (catalog) => {
  const products = getArray(catalog, "Products", "products");
  const productResources = getArray(catalog, "ProductResources", "productResources");
  const clientResources = getArray(catalog, "ClientResources", "clientResources");
  const clientResourceAccess = getArray(catalog, "ClientResourceAccess", "clientResourceAccess");
  const resourceConfigurations = getArray(catalog, "ResourceConfigurations", "resourceConfigurations");
  const effectiveResourceAllowances = getArray(catalog, "EffectiveResourceAllowances", "effectiveResourceAllowances");
  const resourceLogs = getArray(catalog, "ResourceLogs", "resourceLogs");
  const resourceEvents = getArray(catalog, "ResourceEvents", "resourceEvents", "Events", "events");

  const productsByKey = new Map(products.map((product) => [
    normalizeCompare(getString(product, "ProductKey", "productKey", "key")),
    product
  ]));
  const resourcesByKey = new Map(productResources.map((resource) => [
    normalizeCompare(getString(resource, "ProductResourceKey", "productResourceKey", "key")),
    resource
  ]));
  const resourcesById = new Map(productResources.map((resource) => [
    normalizeCompare(getId(resource)),
    resource
  ]));
  const effectiveAllowanceByClientResourceId = new Map(effectiveResourceAllowances.map((allowance) => [
    normalizeCompare(getString(allowance, "ClientResourceId", "clientResourceId")),
    allowance
  ]));
  const accessByClientResourceId = new Map(clientResourceAccess.map((access) => [
    normalizeCompare(getString(access, "ClientResourceId", "clientResourceId")),
    access
  ]));

  const enrichedClientResources = clientResources
    .map((clientResource) => {
      const productResourceKey = getString(clientResource, "ProductResourceKey", "productResourceKey");
      const productResource = resolveProductResourceForClientResource(clientResource, resourcesById, resourcesByKey);
      const productKey = getString(clientResource, "ProductKey", "productKey")
        || getString(productResource, "ProductKey", "productKey");
      const product = productsByKey.get(normalizeCompare(productKey)) || null;
      const id = getId(clientResource);
      const access = accessByClientResourceId.get(normalizeCompare(id)) || null;
      return {
        clientResource,
        productResource,
        product,
        id,
        displayName: getDisplayName(clientResource, getDisplayName(productResource, productResourceKey)),
        productName: getDisplayName(product, productKey),
        productResourceKey,
        productKey,
        hasAccess: access ? getBoolean(access, "HasAccess", "hasAccess") : true,
        accessMessage: access
          ? getString(access, "AccessMessage", "accessMessage")
          : "",
        rootPermission: access
          ? getString(access, "ProductResourceKey", "productResourceKey")
          : productResourceKey,
        effectiveAllowance: effectiveAllowanceByClientResourceId.get(normalizeCompare(id)) || null,
        environmentKey: getString(clientResource, "EnvironmentKey", "environmentKey")
          || getString(catalog, "EnvironmentKey", "environmentKey")
      };
    })
    .filter((resource) => resource.id)
    .sort((left, right) =>
      left.productName.localeCompare(right.productName)
      || left.displayName.localeCompare(right.displayName)
      || left.productResourceKey.localeCompare(right.productResourceKey)
    );

  return {
    products,
    productResources,
    clientResources: enrichedClientResources,
    resourceConfigurations,
    effectiveResourceAllowances,
    resourceLogs,
    resourceEvents,
    clientResourceAccess,
    environmentKey: getString(catalog, "EnvironmentKey", "environmentKey")
  };
};

const findSelectedClientResource = (clientResources, clientResourceId) => {
  const selectedId = normalizeCompare(clientResourceId);
  if (!selectedId) return null;
  return clientResources.find((resource) => {
    const clientResource = resource.clientResource;
    return [
      resource.id,
      getString(clientResource, "_id"),
      getString(clientResource, "Id", "id"),
      getString(clientResource, "UniqueId", "uniqueId")
    ].some((value) => normalizeCompare(value) === selectedId);
  }) || null;
};

const findResourceConfigurations = (configurations, selectedClientResource) => {
  if (!selectedClientResource) return [];
  const clientIds = new Set([
    selectedClientResource.id,
    getString(selectedClientResource.clientResource, "_id"),
    getString(selectedClientResource.clientResource, "Id", "id"),
    getString(selectedClientResource.clientResource, "UniqueId", "uniqueId")
  ].map(normalizeCompare).filter(Boolean));

  return configurations
    .filter((configuration) => {
      const configurationClientId = normalizeCompare(getString(configuration, "ClientResourceId", "clientResourceId"));
      return clientIds.has(configurationClientId);
    })
    .sort((left, right) =>
      String(getValue(right, "UpdatedDate", "updatedDate", "CreatedDate", "createdDate"))
        .localeCompare(String(getValue(left, "UpdatedDate", "updatedDate", "CreatedDate", "createdDate")))
    );
};

const getConfigurationLinkedRecords = (configuration) =>
  getArray(configuration, "LinkedRecords", "linkedRecords");

const getEndpointResourceTypeLabel = (resourceContext) => {
  const contextLabel = normalizeCompare([
    getString(resourceContext, "ProductResourceKey", "productResourceKey"),
    getString(resourceContext, "ProductKey", "productKey"),
    getDisplayName(resourceContext),
    getString(resourceContext?.productResource, "ProductResourceKey", "productResourceKey"),
    getString(resourceContext?.productResource, "ProductKey", "productKey"),
    getDisplayName(resourceContext?.productResource),
    getString(resourceContext?.clientResource, "ProductResourceKey", "productResourceKey"),
    getString(resourceContext?.clientResource, "ProductKey", "productKey"),
    getDisplayName(resourceContext?.clientResource)
  ].filter(Boolean).join(" "));

  if (contextLabel.includes("mcp")) return "MCP Endpoint";
  if (contextLabel.includes("api endpoint")
    || contextLabel.includes("apiendpoint")
    || contextLabel.includes("hosted api")) return "API Endpoint";
  return "";
};

const getSchemaApiLinkedRecordTypeLabel = (record, resourceContext) => {
  const resourceTypeLabel = getEndpointResourceTypeLabel(resourceContext);
  if (resourceTypeLabel) return resourceTypeLabel;

  const deployments = getArray(record, "Deployments", "deployments");
  const surfaceRecords = deployments.length > 0 ? deployments : [record];
  let hasApiSurface = false;
  let hasMcpSurface = false;

  surfaceRecords.forEach((surfaceRecord) => {
    const mode = Number(getValue(surfaceRecord, "PublicSurfaceMode", "publicSurfaceMode"));
    if (mode === 1 || mode === 3) hasApiSurface = true;
    if (mode === 2 || mode === 3) hasMcpSurface = true;
    if (!mode) {
      hasApiSurface = hasApiSurface || getBoolean(surfaceRecord, "HostedApiEnabled", "hostedApiEnabled");
      hasMcpSurface = hasMcpSurface
        || getBoolean(surfaceRecord, "McpEnabled", "mcpEnabled")
        || Boolean(getString(surfaceRecord, "McpConnectorUrl", "mcpConnectorUrl"));
    }
  });

  if (hasApiSurface) return "API Endpoint";
  if (hasMcpSurface) return "MCP Endpoint";
  return "Endpoint";
};

const getLinkedRecordTypeLabel = (record, resourceContext) => {
  const itemName = getString(record, "ItemName", "itemName");
  if (isClientMcpServerLinkedRecord(record)) return "Client MCP Server";
  if (normalizeCompare(itemName) === "schemaapi") return getSchemaApiLinkedRecordTypeLabel(record, resourceContext);
  return LINKED_CONFIGURATION_RECORD_LABELS[itemName] || formatDisplayLabel(itemName) || "Linked item";
};

const isClientMcpServerLinkedRecord = (record) =>
  normalizeCompare(getString(record, "ItemName", "itemName")) === "resourceconfiguration"
  && normalizeCompare(getString(record, "Relationship", "relationship")) === "clientmcpserver";

const getLinkedRecordRelationshipLabel = (record) =>
  formatDisplayLabel(getString(record, "Relationship", "relationship"));

const getSeverityIconKey = (severity) => {
  const normalizedSeverity = normalizeCompare(severity);
  if (["error", "blocker", "failed", "failure"].includes(normalizedSeverity)) return "error";
  if (["warning", "warn"].includes(normalizedSeverity)) return "warning";
  return "check";
};

const getIssuePresentationSeverity = (issue) => {
  const code = normalizeCompare(getString(issue, "Code", "code"));
  if (code === "acknowledgement-disabled") return "Success";

  const severity = getString(issue, "Severity", "severity") || "Warning";
  const normalizedSeverity = normalizeCompare(severity);
  if (["info", "ok", "valid", "accepted"].includes(normalizedSeverity)) return "Success";

  return severity;
};

const getLinkedRecordDisplayName = (record) => {
  const systemConfigKey = normalizeCompare([
    getLinkedRecordItemId(record),
    getString(record, "Name", "name", "Key", "key")
  ].join(" "));

  if (isSystemConfigLinkedRecord(record) && systemConfigKey.includes("mcptools")) {
    return "MCP Tools Configuration";
  }

  return getDisplayName(record, getLinkedRecordTypeLabel(record));
};

const getLinkedRecordDescription = (record) => {
  const description = getString(record, "Description", "description");
  if (normalizeCompare(getString(record, "ItemName", "itemName")) !== "schemaapi") return description;

  const displayName = getLinkedRecordDisplayName(record);
  if (normalizeCompare(description) === normalizeCompare(`SchemaAPI ${displayName}`)) return "";
  return description.replace(/^schemaapi\s+/i, "").trim();
};

const isSystemConfigLinkedRecord = (record) =>
  normalizeCompare(getString(record, "ItemName", "itemName")) === "systemconfig";

const isClientMcpToolsSystemConfigRecord = (record, configuration) =>
  isSystemConfigLinkedRecord(record)
  && isClientMcpToolsResource(configuration)
  && normalizeCompare(getLinkedRecordItemId(record)).includes("mcptools");

const getLinkedRecordIconKey = (record, configuration, fallbackIcon) => {
  if (isClientMcpServerLinkedRecord(record)) return "mcptab";
  if (isSystemConfigLinkedRecord(record)) return "settings";
  if (isEntitySchemaLinkedRecord(record)) return "schema";
  if (isDestinationLinkedRecord(record)) return "endpointtab";
  return fallbackIcon || RESOURCE_FALLBACK_ICON;
};

const getLinkedRecordScreenKey = (record, configuration) => {
  if (isClientMcpServerLinkedRecord(record)) return MCP_TOOLS_OVERVIEW_SCREEN_KEY;
  if (isClientMcpToolsSystemConfigRecord(record, configuration)) return MCP_TOOLS_OVERVIEW_SCREEN_KEY;
  if (isDestinationLinkedRecord(record)) return DESTINATION_SCREEN_KEY;

  const recordType = normalizeCompare([
    getString(record, "ItemName", "itemName"),
    getString(record, "Relationship", "relationship"),
    getLinkedRecordTypeLabel(record)
  ].join(" "));

  if (recordType.includes("integration")) return "integration";
  if (recordType.includes("sourcesystem") || recordType.includes("source system")) return "source-system";
  if (isInboundEdiResource(null, null, configuration) && (recordType.includes("sourceobjecttarget") || recordType.includes("inboundeditarget") || recordType.includes("edi target"))) return INBOUND_EDI_TARGETS_SCREEN_KEY;
  if (isInboundEdiResource(null, null, configuration) && (recordType.includes("sourceobject") || recordType.includes("inboundedisourceobject") || recordType.includes("source object"))) return INBOUND_EDI_SOURCE_OBJECTS_SCREEN_KEY;
  if (isOutboundEdiResource(null, null, configuration) && (recordType.includes("entityobject") || recordType.includes("entity object") || recordType.includes("outboundedientityobject") || recordType.includes("edi entity object"))) return OUTBOUND_EDI_ENTITY_OBJECTS_SCREEN_KEY;
  if (isEntitySchemaLinkedRecord(record)) return "entity-schema";
  return "";
};

const getLinkedRecordItemId = (record) =>
  getString(record, "ItemId", "itemId", "LinkedItemId", "linkedItemId", "RecordId", "recordId");

const getLinkedIntegrationRecord = (configuration) =>
  getConfigurationLinkedRecords(configuration).find((record) =>
    normalizeCompare(getString(record, "ItemName", "itemName")) === "integration"
    || normalizeCompare(getString(record, "Relationship", "relationship")).includes("integration")
  ) || null;

const getLinkedSourceSystemRecord = (configuration) =>
  getConfigurationLinkedRecords(configuration).find((record) =>
    normalizeCompare(getString(record, "ItemName", "itemName")) === "sourcesystem"
    || normalizeCompare(getString(record, "Relationship", "relationship")).includes("sourcesystem")
    || normalizeCompare(getString(record, "Relationship", "relationship")).includes("source system")
  ) || null;

const getLinkedEntitySchemaRecord = (configuration) =>
  getConfigurationLinkedRecords(configuration).find((record) =>
    isEntitySchemaLinkedRecord(record)
  ) || null;

const findConfigurationByLinkedEntitySchemaId = (configurations, schemaId) => {
  const normalizedSchemaId = normalizeCompare(schemaId);
  if (!normalizedSchemaId) return null;

  return getArray({ configurations }, "configurations").find((configuration) =>
    getConfigurationLinkedRecords(configuration).some((record) =>
      isEntitySchemaLinkedRecord(record)
      && normalizeCompare(getLinkedRecordItemId(record)) === normalizedSchemaId
    )
  ) || null;
};

const findClientResourceForConfiguration = (clientResources, configuration) => {
  const normalizedClientResourceId = normalizeCompare(getString(configuration, "ClientResourceId", "clientResourceId"));
  if (!normalizedClientResourceId) return null;

  return getArray({ clientResources }, "clientResources").find((resource) => {
    const clientResource = resource?.clientResource || resource;
    return [
      resource?.id,
      getString(clientResource, "_id"),
      getString(clientResource, "Id", "id"),
      getString(clientResource, "UniqueId", "uniqueId")
    ].some((value) => normalizeCompare(value) === normalizedClientResourceId);
  }) || null;
};

const isEntitySchemaLinkedRecord = (record) => {
  if (isDestinationLinkedRecord(record)) return false;
  const itemName = normalizeCompare(getString(record, "ItemName", "itemName"));
  const relationship = normalizeCompare(getString(record, "Relationship", "relationship"));
  return itemName === "schema" && (relationship.includes("entityschema") || relationship.includes("entity schema"));
};

const isRecordDeleted = (item) => getBoolean(item, "IsDeleted", "isDeleted");

const isRecordActive = (item) => {
  const activeValue = getValue(item, "IsActive", "isActive");
  if (activeValue === undefined || activeValue === null || activeValue === "") return true;
  return getBoolean(item, "IsActive", "isActive");
};

const isCustomQueryTarget = (target) => getBoolean(target, "IsCustomQuery", "isCustomQuery");

const getUniqueRowsByKey = (rows, getKey) => {
  const seen = new Set();
  return getArray({ rows }, "rows").filter((row, index) => {
    const key = String(getKey?.(row, index) || `row-${index}`).toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getSourceSystemTargets = (sourceSystem) =>
  getUniqueRowsByKey(getValue(sourceSystem, "SourceObjectTargets", "sourceObjectTargets") || [], getSourceTargetIdentityKey);

const getSourceSystemTargetId = (target, index = 0) =>
  getString(target, "SourceObjectTargetId", "sourceObjectTargetId", "UniqueId", "uniqueId", "_id", "id")
  || getSourceTargetIdentityKey(target, index);

const getSourceTargetOperationId = (target) =>
  getString(target, "SourceObjectTargetId", "sourceObjectTargetId", "UniqueId", "uniqueId", "_id", "id");

const getSourceTargetComparableKeys = (target, index = 0) =>
  [
    getSourceSystemTargetId(target, index),
    getString(target, "ObjectName", "objectName"),
    getString(target, "SystemObjectType", "systemObjectType"),
    getDisplayName(target)
  ].map(normalizeCompare).filter(Boolean);

const getSourceObjectTargetComparableKeys = (sourceObject) =>
  [
    getString(sourceObject, "SourceObjectTargetId", "sourceObjectTargetId"),
    getString(sourceObject, "ObjectName", "objectName"),
    getString(sourceObject, "SystemName", "systemName"),
    getString(sourceObject, "InternalName", "internalName"),
    getDisplayName(sourceObject)
  ].map(normalizeCompare).filter(Boolean);

const findSourceObjectTarget = (targets, sourceObject) => {
  const objectTargetKeys = new Set(getSourceObjectTargetComparableKeys(sourceObject));
  if (objectTargetKeys.size > 0) {
    const matched = targets.find((target, index) =>
      getSourceTargetComparableKeys(target, index).some((targetKey) => objectTargetKeys.has(targetKey))
    );
    if (matched) return matched;
  }

  const objectName = normalizeCompare(getString(sourceObject, "SystemName", "systemName", "InternalName", "internalName", "DisplayName", "displayName"));
  return targets.find((target) =>
    normalizeCompare(getString(target, "ObjectName", "objectName", "DisplayName", "displayName")) === objectName
  ) || null;
};

const findSourceObjectForTarget = (sourceObjects, target, targetIndex = 0) => {
  const targetKeys = new Set(getSourceTargetComparableKeys(target, targetIndex));
  if (targetKeys.size === 0) return null;

  return getArray({ sourceObjects }, "sourceObjects")
    .filter((sourceObject) => !isRecordDeleted(sourceObject))
    .find((sourceObject) =>
      getSourceObjectTargetComparableKeys(sourceObject).some((sourceObjectKey) => targetKeys.has(sourceObjectKey))
    ) || null;
};

const findLatestActiveSourceObjectForTarget = (sourceObjects, targetId) => {
  const normalizedTargetId = normalizeCompare(targetId);
  if (!normalizedTargetId) return null;

  return getArray({ sourceObjects }, "sourceObjects")
    .filter((sourceObject) =>
      !isRecordDeleted(sourceObject)
      && isRecordActive(sourceObject)
      && normalizeCompare(getString(sourceObject, "SourceObjectTargetId", "sourceObjectTargetId")) === normalizedTargetId
    )
    .sort((left, right) =>
      (getNumber(right, "Version", "version") ?? 0) - (getNumber(left, "Version", "version") ?? 0)
    )[0] || null;
};

const getAvailableSourceObjectTargets = (targets, sourceObjects, options = {}) => {
  const includeRepresentedTargets = Boolean(options.includeRepresentedTargets);
  const representedTargetKeys = new Set();
  getArray({ sourceObjects }, "sourceObjects")
    .filter((sourceObject) => !isRecordDeleted(sourceObject))
    .forEach((sourceObject) => {
      getSourceObjectTargetComparableKeys(sourceObject).forEach((key) => representedTargetKeys.add(key));
    });

  return targets.filter((target, index) =>
    isRecordActive(target)
    && !isRecordDeleted(target)
    && Boolean(getSourceTargetOperationId(target))
    && (includeRepresentedTargets || !getSourceTargetComparableKeys(target, index).some((targetKey) => representedTargetKeys.has(targetKey)))
  );
};

const getSourceSystemTypeLabel = (sourceSystem, metadata) => {
  const typeValue = getValue(sourceSystem, "SourceSystemType", "sourceSystemType");
  return getMetadataOptionText(metadata, "SourceSystemType", typeValue)
    || formatDisplayLabel(getString(sourceSystem, "SourceSystemType", "sourceSystemType"))
    || "Source System";
};

const getSourceSystemTargetStats = (targets) => {
  const targetRows = getArray({ targets }, "targets").filter((target) => !isRecordDeleted(target));
  const activeTargets = targetRows.filter(isRecordActive);
  return {
    total: targetRows.length,
    active: activeTargets.length
  };
};

const getSourceObjectStats = (sourceObjects) => {
  const objectRows = getArray({ sourceObjects }, "sourceObjects").filter((sourceObject) => !isRecordDeleted(sourceObject));
  const activeObjects = objectRows.filter(isRecordActive);
  return {
    total: objectRows.length,
    active: activeObjects.length
  };
};

const getSourceObjectVersionLabel = (sourceObject) =>
  getString(sourceObject, "DisplayVersion", "displayVersion", "Version", "version") || "Current";

const getSourceObjectFieldCount = (sourceObject) =>
  getArray(sourceObject, "Fields", "fields", "SourceObjectFields", "sourceObjectFields").length;

const getEntityObjectFieldCount = (entityObject) =>
  getArray(entityObject, "Fields", "fields", "EntityObjectFields", "entityObjectFields").length;

const getEntityObjectVersionLabel = (entityObject, schemaVersionLabel = "") =>
  getString(entityObject, "Version", "version")
  || schemaVersionLabel
  || "Not set";

const getEntityObjectSourceObjectId = (entityObject) =>
  getString(entityObject, "SourceObjectOfRecordId", "sourceObjectOfRecordId", "SourceObjectId", "sourceObjectId");

const getSourceObjectComparableKeys = (sourceObject, index = 0) =>
  [
    getId(sourceObject),
    getString(sourceObject, "SourceObjectId", "sourceObjectId", "EntityObjectId", "entityObjectId", "SourceObjectID", "EntityObjectID"),
    getSourceObjectIdentityKey(sourceObject, index)
  ].map(normalizeCompare).filter(Boolean);

const getSourceObjectOperationId = (sourceObject) =>
  getId(sourceObject)
  || getString(sourceObject, "SourceObjectId", "sourceObjectId", "EntityObjectId", "entityObjectId", "SourceObjectID", "EntityObjectID");

const findSourceObjectById = (sourceObjects, sourceObjectId) => {
  const requestedId = normalizeCompare(sourceObjectId);
  if (!requestedId) return null;
  return getArray({ sourceObjects }, "sourceObjects").find((sourceObject, index) =>
    getSourceObjectComparableKeys(sourceObject, index).includes(requestedId)
  ) || null;
};

const getSelectedSourceObjectsForEntityObjects = (entityObjects, sourceObjects) => {
  const selectedSourceIds = new Set(
    getArray({ entityObjects }, "entityObjects")
      .map(getEntityObjectSourceObjectId)
      .map(normalizeCompare)
      .filter(Boolean)
  );

  if (selectedSourceIds.size === 0) return [];

  return getUniqueRowsByKey(
    getArray({ sourceObjects }, "sourceObjects")
      .filter((sourceObject, index) =>
        getSourceObjectComparableKeys(sourceObject, index).some((key) => selectedSourceIds.has(key))
      ),
    getSourceObjectIdentityKey
  );
};

const buildSourceObjectSearchText = (sourceObject, target) =>
  [
    getDisplayName(sourceObject),
    getString(sourceObject, "SystemName", "systemName", "InternalName", "internalName", "Description", "description"),
    getDisplayName(target),
    getString(target, "ObjectName", "objectName", "ObjectType", "objectType")
  ].map(normalizeCompare).join(" ");

const buildSourceTargetSearchText = (target) =>
  [
    getDisplayName(target),
    getString(target, "ObjectName", "objectName"),
    getString(target, "ObjectType", "objectType"),
    getString(target, "SystemObjectType", "systemObjectType"),
    getString(target, "SchemaIdentifier", "schemaIdentifier"),
    getString(target, "Query", "query")
  ].map(normalizeCompare).join(" ");

const parseDataOptions = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const parseJsonObject = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  if (!value || typeof value !== "string") return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const getMetadataField = (metadata, fieldName) => {
  const normalizedFieldName = normalizeCompare(fieldName);
  return getArray(metadata, "Fields", "fields").find((field) =>
    normalizeCompare(getString(field, "FieldName", "fieldName", "Name", "name")) === normalizedFieldName
  ) || null;
};

const getMetadataOptions = (metadata, fieldName) => {
  const field = getMetadataField(metadata, fieldName);
  return [
    ...parseDataOptions(getValue(field, "DataJson", "dataJson")),
    ...parseDataOptions(getValue(field, "Options", "options"))
  ];
};

const getMetadataOptionText = (metadata, fieldName, value) => {
  const options = getMetadataOptions(metadata, fieldName);
  const normalizedValue = normalizeCompare(value);
  return options.find((option) => normalizeCompare(getValue(option, "Value", "value")) === normalizedValue)?.Text
    || options.find((option) => normalizeCompare(getValue(option, "Value", "value")) === normalizedValue)?.text
    || "";
};

const toValueList = (value) => {
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null || value === "") return [];
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      // Fall back to delimited text below.
    }
    return trimmed.split(/[;,|]/).map((part) => part.trim()).filter(Boolean);
  }
  return [value];
};

const getMetadataOptionTexts = (metadata, fieldName, value) =>
  toValueList(value)
    .map((optionValue) =>
      getMetadataOptionText(metadata, fieldName, optionValue)
      || LOG_LEVEL_LABELS[Number(optionValue)]
      || formatDisplayLabel(optionValue)
    )
    .filter(Boolean)
    .filter((label, index, labels) =>
      labels.findIndex((candidate) => String(candidate).toLowerCase() === String(label).toLowerCase()) === index
    );

const resolveSourceTargetMetadata = (metadataSource) => {
  const metadataItems = getOperationItems(metadataSource);
  return metadataItems.find((item) =>
    normalizeCompare(getId(item)) === normalizeCompare(SOURCE_OBJECT_TARGET_METADATA_ID)
    || normalizeCompare(getString(item, "UIItemId", "uiItemId", "UniqueId", "uniqueId")) === normalizeCompare(SOURCE_OBJECT_TARGET_METADATA_ID)
    || normalizeCompare(getString(item, "Name", "name", "ItemName", "itemName")) === "source system target"
    || normalizeCompare(getString(item, "DisplayName", "displayName")) === "source system target"
  ) || null;
};

const getIntegrationTypeLabel = (integration, metadata) => {
  const typeValue = getValue(integration, "IntegrationType", "integrationType");
  return getMetadataOptionText(metadata, "IntegrationType", typeValue)
    || formatDisplayLabel(getString(integration, "IntegrationType", "integrationType"))
    || "Integration";
};

const getIntegrationLogLevelLabels = (integration, metadata) => {
  const labels = getMetadataOptionTexts(metadata, "LogLevels", getValue(integration, "LogLevels", "logLevels"));
  return labels.length > 0 ? labels.join(", ") : "None";
};

const getIntegrationRuntimeStatus = (integration) => {
  if (!integration) return "Unavailable";
  if (getBoolean(integration, "IsOnline", "isOnline")) return "Online";
  return "Offline";
};

const matchesLogField = (log, fieldNames, expectedValue) => {
  const normalizedExpected = normalizeCompare(expectedValue);
  if (!normalizedExpected) return false;
  return fieldNames.some((fieldName) =>
    normalizeCompare(getString(log, fieldName, fieldName.charAt(0).toLowerCase() + fieldName.slice(1))) === normalizedExpected
  );
};

const getResourceScopedRecords = (records = [], selectedResource) => {
  const resourceIds = getSelectedClientResourceIds(selectedResource).map(normalizeCompare).filter(Boolean);
  if (resourceIds.length === 0) return [];
  const resourceIdSet = new Set(resourceIds);
  return records.filter((record) => resourceIdSet.has(normalizeCompare(getString(record, "ClientResourceId", "clientResourceId"))));
};

const getResourceScopedLogs = (resourceLogs = [], selectedResource) =>
  getResourceScopedRecords(resourceLogs, selectedResource);

const getResourceScopedEvents = (resourceEvents = [], selectedResource) =>
  getResourceScopedRecords(resourceEvents, selectedResource);

const getSelectedClientResourceIds = (selectedResource) => [
  selectedResource?.id,
  getString(selectedResource?.clientResource, "_id"),
  getString(selectedResource?.clientResource, "Id", "id"),
  getString(selectedResource?.clientResource, "UniqueId", "uniqueId")
].filter(Boolean);

const getSelectedClientResourceId = (selectedResource) => getSelectedClientResourceIds(selectedResource)[0] || "";

const buildResourceScopeFilterNode = (fieldName, method, value) => ({
  Variable: "x",
  Name: fieldName,
  Method: method,
  Value: value,
  IsConjunction: false
});

const combineResourceScopeFilterNodes = (left, right) => {
  if (!left) return right || null;
  if (!right) return left;

  return {
    Variable: "x",
    Method: "&&",
    IsConjunction: true,
    Left: left,
    Right: right
  };
};

const getRootFilterNode = (query) => query?.RootFilterNode || query?.rootFilterNode || null;

const getPositiveNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

const buildLockedClientResourceQuery = (tableQuery, selectedClientResourceId) => {
  const lockedFilter = buildResourceScopeFilterNode("ClientResourceId", "==", selectedClientResourceId);
  const rootFilterNode = getRootFilterNode(tableQuery);
  const nextQuery = { ...(tableQuery || {}) };

  delete nextQuery.rootFilterNode;
  nextQuery.RootFilterNode = combineResourceScopeFilterNodes(lockedFilter, rootFilterNode);
  nextQuery.Skip = getPositiveNumber(nextQuery.Skip ?? nextQuery.skip, 0);
  nextQuery.Take = getPositiveNumber(nextQuery.Take ?? nextQuery.take, 50) || 50;
  nextQuery.SortBy = nextQuery.SortBy || nextQuery.sortBy || "CreatedDate";
  nextQuery.SortOrder = nextQuery.SortOrder || nextQuery.sortOrder || "DESC";

  return nextQuery;
};

const buildLockedClientResourceLogQuery = (tableQuery, selectedClientResourceId) =>
  buildLockedClientResourceQuery(tableQuery, selectedClientResourceId);

const buildLockedClientResourceEventQuery = (tableQuery, selectedClientResourceId) =>
  buildLockedClientResourceQuery(tableQuery, selectedClientResourceId);

const buildScheduleTableQuery = (tableQuery) => {
  const activeFilter = buildResourceScopeFilterNode("IsActive", "==", true);
  const notDeletedFilter = buildResourceScopeFilterNode("IsDeleted", "==", false);
  const rootFilterNode = getRootFilterNode(tableQuery);
  const nextQuery = { ...(tableQuery || {}) };

  delete nextQuery.rootFilterNode;
  nextQuery.RootFilterNode = combineResourceScopeFilterNodes(
    combineResourceScopeFilterNodes(activeFilter, notDeletedFilter),
    rootFilterNode
  );
  nextQuery.Skip = getPositiveNumber(nextQuery.Skip ?? nextQuery.skip, 0);
  nextQuery.Take = getPositiveNumber(nextQuery.Take ?? nextQuery.take, 50) || 50;
  nextQuery.SortBy = nextQuery.SortBy || nextQuery.sortBy || "CreatedDate";
  nextQuery.SortOrder = nextQuery.SortOrder || nextQuery.sortOrder || "DESC";

  return nextQuery;
};

const buildLockedResourceConfigurationQuery = (tableQuery, selectedClientResourceId, resourceConfigurationId) => {
  const clientFilter = selectedClientResourceId
    ? buildResourceScopeFilterNode("ClientResourceId", "==", selectedClientResourceId)
    : null;
  const configurationFilter = resourceConfigurationId
    ? buildResourceScopeFilterNode("ResourceConfigurationId", "==", resourceConfigurationId)
    : null;
  const rootFilterNode = getRootFilterNode(tableQuery);
  const nextQuery = { ...(tableQuery || {}) };

  delete nextQuery.rootFilterNode;
  nextQuery.RootFilterNode = combineResourceScopeFilterNodes(
    combineResourceScopeFilterNodes(clientFilter, configurationFilter),
    rootFilterNode
  );
  nextQuery.Skip = getPositiveNumber(nextQuery.Skip ?? nextQuery.skip, 0);
  nextQuery.Take = getPositiveNumber(nextQuery.Take ?? nextQuery.take, 5) || 5;
  nextQuery.SortBy = nextQuery.SortBy || nextQuery.sortBy || "CreatedDate";
  nextQuery.SortOrder = nextQuery.SortOrder || nextQuery.sortOrder || "DESC";

  return nextQuery;
};

const getConfigurationScopedRecords = (records = [], configuration) => {
  const configurationId = getId(configuration);
  if (!configurationId) return [];
  return records.filter((record) =>
    matchesLogField(record, ["ResourceConfigurationId"], configurationId)
  );
};

const getConfigurationScopedLogs = (resourceLogs = [], configuration) =>
  getConfigurationScopedRecords(resourceLogs, configuration);

const getConfigurationScopedEvents = (resourceEvents = [], configuration) =>
  getConfigurationScopedRecords(resourceEvents, configuration);

const CONFIGURATION_ACTIVITY_ITEM_LIMIT = 5;
const CONFIGURATION_ACTIVITY_DISPLAY_LIMIT = CONFIGURATION_ACTIVITY_ITEM_LIMIT * 2;

const getQueryResultItems = (response) => {
  const firstResponse = firstItem(response);
  if (firstResponse && (firstResponse.Items || firstResponse.items || firstResponse.Success !== undefined || firstResponse.success !== undefined)) {
    return getArray(firstResponse, "Items", "items");
  }
  if (Array.isArray(response)) return response;
  return getArray(response, "Items", "items");
};

const queryConfigurationScopedRecords = async (manager, selectedClientResourceId, resourceConfigurationId, take = CONFIGURATION_ACTIVITY_ITEM_LIMIT) => {
  if (!manager?.Query || !resourceConfigurationId) return [];

  const lockedQuery = buildLockedResourceConfigurationQuery(
    { Take: take, SortBy: "CreatedDate", SortOrder: "DESC" },
    selectedClientResourceId,
    resourceConfigurationId
  );
  const result = await manager.Query({
    model: lockedQuery,
    skip: lockedQuery.Skip,
    take: lockedQuery.Take,
    sortBy: lockedQuery.SortBy,
    sortOrder: lockedQuery.SortOrder
  });

  if (result?.Success === false || result?.success === false) {
    throw new Error(getOperationResultMessage(result, "Configuration activity could not be loaded."));
  }

  return getQueryResultItems(result);
};

const getCaseInsensitiveObjectValue = (item, key) => {
  if (!item || typeof item !== "object" || !key) return undefined;
  if (Object.prototype.hasOwnProperty.call(item, key)) return item[key];

  const normalizedKey = normalizeCompare(key);
  const matchingKey = Object.keys(item).find((itemKey) => normalizeCompare(itemKey) === normalizedKey);
  return matchingKey ? item[matchingKey] : undefined;
};

const unwrapConnectionFieldValue = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  const directValue = getCaseInsensitiveObjectValue(value, "Value");
  if (directValue !== undefined) return directValue;
  const textValue = getCaseInsensitiveObjectValue(value, "Text");
  if (textValue !== undefined) return textValue;
  return value;
};

const isDisplayableConnectionField = (fieldName, value) => {
  const normalizedFieldName = normalizeCompare(fieldName);
  if (!normalizedFieldName || normalizedFieldName.includes("connectionstring")) return false;
  if (normalizedFieldName.startsWith("__")) return false;
  if (normalizedFieldName.includes("securecustomfields")) return false;
  if (normalizedFieldName.endsWith("id") || normalizedFieldName === "_id") return false;
  if (SENSITIVE_CONNECTION_FIELD_PATTERN.test(String(fieldName || ""))) return false;
  const unwrappedValue = unwrapConnectionFieldValue(value);
  if (unwrappedValue === undefined || unwrappedValue === null) return false;
  if (typeof unwrappedValue === "string" && !unwrappedValue.trim()) return false;
  if (typeof unwrappedValue === "object") return false;
  return true;
};

const sanitizeConnectionFieldValue = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return text;

  try {
    const url = new URL(text);
    if (url.password) {
      url.password = "";
      return url.toString();
    }
  } catch {
    // Fall through to lightweight key/value redaction.
  }

  return text.replace(/\b(password|passwd|pwd)\s*=\s*([^;\n&\s]+)/gi, "$1=********");
};

const formatConnectionFieldValue = (value) => {
  const unwrappedValue = unwrapConnectionFieldValue(value);
  if (typeof unwrappedValue === "boolean") return unwrappedValue ? "Yes" : "No";
  return sanitizeConnectionFieldValue(unwrappedValue);
};

const isCustomConnectionMetadataField = (field) =>
  getBoolean(getValue(field, "CustomFields", "customFields"), CUSTOM_FIELD_METADATA_FLAG);

const getIntegrationCustomFieldValue = (integration, fieldName) => {
  const customFieldContainers = [
    getValue(integration, "CustomFields", "customFields"),
    getValue(integration, "CustomFieldValues", "customFieldValues")
  ];

  for (const container of customFieldContainers) {
    const value = getCaseInsensitiveObjectValue(container, fieldName);
    if (value !== undefined) return unwrapConnectionFieldValue(value);

    for (const nestedValue of Object.values(container || {})) {
      if (!nestedValue || typeof nestedValue !== "object") continue;
      const nestedFieldValue = getCaseInsensitiveObjectValue(nestedValue, fieldName);
      if (nestedFieldValue !== undefined) return unwrapConnectionFieldValue(nestedFieldValue);
    }
  }

  return undefined;
};

const parseConnectionStringDetailRows = (connectionString) => {
  const source = String(connectionString || "").trim();
  if (!source) return [];

  try {
    const parsed = JSON.parse(source);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return Object.entries(parsed)
        .filter(([key, value]) => isDisplayableConnectionField(key, value))
        .map(([key, value]) => ({ label: formatDisplayLabel(key), value: formatConnectionFieldValue(value) }));
    }
  } catch {
    // Fall back to common connection-string formats below.
  }

  try {
    const url = new URL(source);
    const searchRows = Array.from(url.searchParams.entries())
      .filter(([key, value]) => isDisplayableConnectionField(key, value))
      .map(([key, value]) => ({ label: formatDisplayLabel(key), value }));
    return [
      { label: "Protocol", value: url.protocol.replace(":", "") },
      url.username ? { label: "Username", value: decodeURIComponent(url.username) } : null,
      { label: "Host", value: url.hostname },
      url.port ? { label: "Port", value: url.port } : null,
      url.pathname && url.pathname !== "/" ? { label: "Path", value: decodeURIComponent(url.pathname.replace(/^\/+/, "")) } : null,
      ...searchRows
    ].filter((row) => row && isDisplayableConnectionField(row.label, row.value));
  } catch {
    // Fall back to key/value parsing below.
  }

  return source
    .split(/[;\n&]/)
    .map((part) => part.trim())
    .filter((part) => part.includes("="))
    .map((part) => {
      const separatorIndex = part.indexOf("=");
      return {
        key: part.slice(0, separatorIndex).trim(),
        value: part.slice(separatorIndex + 1).trim()
      };
    })
    .filter(({ key, value }) => isDisplayableConnectionField(key, value))
    .map(({ key, value }) => ({ label: formatDisplayLabel(key), value }));
};

const getIntegrationConnectionDetailRows = (integration, metadata) => {
  const rows = [];
  const seenLabels = new Set();
  const addRow = (label, value) => {
    if (!isDisplayableConnectionField(label, value)) return;
    const formattedLabel = formatDisplayLabel(label);
    const normalizedLabel = normalizeCompare(formattedLabel);
    if (!formattedLabel || seenLabels.has(normalizedLabel)) return;
    rows.push({ label: formattedLabel, value: formatConnectionFieldValue(value) });
    seenLabels.add(normalizedLabel);
  };

  DIRECT_INTEGRATION_CONNECTION_FIELDS
    .forEach((fieldName) => addRow(fieldName, getCaseInsensitiveObjectValue(integration, fieldName)));

  getArray(metadata, "Fields", "fields")
    .filter(isCustomConnectionMetadataField)
    .forEach((field) => {
      const fieldName = getString(field, "FieldName", "fieldName", "Name", "name");
      const label = getString(field, "DisplayName", "displayName", "Title", "title") || fieldName;
      addRow(label, getIntegrationCustomFieldValue(integration, fieldName));
    });

  Object.entries(getValue(integration, "CustomFields", "customFields") || {})
    .forEach(([key, value]) => addRow(key, value));

  parseConnectionStringDetailRows(getString(integration, "ConnectionString", "connectionString"))
    .forEach((row) => addRow(row.label, row.value));

  return rows;
};

const formatConnectionStringKey = (label) =>
  String(label || "")
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();

const buildIntegrationConnectionStringClipboardValue = (rows) => {
  const parts = rows
    .map((row) => {
      const key = formatConnectionStringKey(row.label);
      const value = String(row.value ?? "").trim();
      return key && value ? `${key}=${value}` : "";
    })
    .filter(Boolean);

  if (parts.length === 0) return "";

  parts.push("password=<password>");
  return parts.join(";");
};

const formatNumber = (value, fallback = "0") => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed.toLocaleString() : fallback;
};

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const formatConfigurationNotes = (value) => {
  const source = String(value || "").trim();
  if (!source) return [];

  const normalized = source
    .replace(/\s+by profile\s+[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "")
    .replace(/\bDetailsUpdated\b/g, "Details updated")
    .replace(/\b([A-Za-z]+)Updated\b/g, (_, label) => `${formatDisplayLabel(label)} updated`)
    .replace(
      /\bat\s+(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?)/gi,
      (_, timestamp) => `on ${formatDateTime(timestamp)}`
    )
    .replace(/\s+([.,])/g, "$1")
    .replace(/\s+/g, " ")
    .trim();

  return normalized
    .split(/\.\s+/)
    .map((entry, index, entries) => {
      const text = entry.trim();
      if (!text) return "";
      return index < entries.length - 1 && !text.endsWith(".") ? `${text}.` : text;
    })
    .filter(Boolean);
};

const renderConfigurationNoteParagraphs = (notes, fallback) => (
  notes.length > 0
    ? notes.map((note, index) => <p key={`${note}-${index}`}>{note}</p>)
    : <p>{fallback}</p>
);

const getConfigurationUsageSnapshot = (configuration) =>
  getValue(configuration, "UsageEstimateSnapshot", "usageEstimateSnapshot") || {};

const getLogMessageTitle = (entry, fallbackType) => {
  const messages = getArray(entry, "Messages", "messages");
  if (messages.length > 0) return String(messages[0] ?? "").trim();

  return getString(entry, "DisplayMessage", "displayMessage", "Title", "title", "Message", "message", "Name", "name")
    || fallbackType;
};

const normalizeCompactLabel = (value) =>
  String(value ?? "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

const getActivityTypeLabel = (type, title) => {
  const formattedType = formatDisplayLabel(type);
  const compactType = normalizeCompactLabel(type);
  if (!compactType || formattedType.includes(" ") || !/^[A-Z0-9_ -]+$/.test(String(type ?? ""))) {
    return formattedType || "Event";
  }

  const titleWords = String(title ?? "").trim().split(/\s+/).filter(Boolean);
  let titlePrefix = "";
  for (const word of titleWords) {
    titlePrefix = titlePrefix ? `${titlePrefix} ${word}` : word;
    const compactPrefix = normalizeCompactLabel(titlePrefix);
    if (compactPrefix === compactType) return titlePrefix;
    if (compactPrefix.length > compactType.length) break;
  }

  return formattedType || "Event";
};

const getConfigurationActivityEntries = (configuration, scopedLogs = [], scopedEvents = [], limit = CONFIGURATION_ACTIVITY_DISPLAY_LIMIT) => {
  const candidateEntries = [
    ...getArray(configuration, "Events", "events"),
    ...getArray(configuration, "Logs", "logs"),
    ...getArray(configuration, "Activities", "activities"),
    ...getArray(configuration, "Activity", "activity"),
    ...scopedLogs,
    ...scopedEvents
  ];

  return candidateEntries
    .map((entry, index) => {
      const timestamp = getString(
        entry,
        "TimestampUtc",
        "timestampUtc",
        "Timestamp",
        "timestamp",
        "CreatedDate",
        "createdDate",
        "UpdatedDate",
        "updatedDate"
      );
      const logLevel = getNumber(entry, "LogLevel", "logLevel");
      const rawType = getString(entry, "EventName", "eventName", "Type", "type", "Level", "level", "EventType", "eventType", "Category", "category")
        || LOG_LEVEL_LABELS[logLevel]
        || "Event";
      const title = getLogMessageTitle(entry, rawType);
      const type = getActivityTypeLabel(rawType, title);
      const description = getString(entry, "Description", "description", "Details", "details", "Summary", "summary", "StackTrace", "stackTrace", "SystemInfo", "systemInfo");

      return {
        id: getId(entry) || `${type}-${timestamp}-${index}`,
        type,
        title,
        description,
        timestamp
      };
    })
    .sort((left, right) => String(right.timestamp).localeCompare(String(left.timestamp)))
    .slice(0, limit);
};

const getConfigurationStatusLabel = (configuration) => {
  const status = getNumber(configuration, "ConfigurationStatus", "configurationStatus");
  return RESOURCE_CONFIGURATION_STATUS_LABELS[status] || "Unknown";
};

const isConfigurationComplete = (configuration) =>
  [4, 7, 8, 9].includes(getNumber(configuration, "ConfigurationStatus", "configurationStatus"));

const isConfigurationActive = (configuration) =>
  getNumber(configuration, "ConfigurationStatus", "configurationStatus") === 4;

const isConfigurationRetired = (configuration) =>
  getNumber(configuration, "ConfigurationStatus", "configurationStatus") === 6;

const isConfigurationConfigured = (configuration) =>
  getNumber(configuration, "ConfigurationStatus", "configurationStatus") === 7;

const isConfigurationReady = (configuration) =>
  getNumber(configuration, "ConfigurationStatus", "configurationStatus") === 8;

const isConfigurationDisabled = (configuration) =>
  getNumber(configuration, "ConfigurationStatus", "configurationStatus") === 9;

const isConfigurationConfiguring = (configuration) =>
  getNumber(configuration, "ConfigurationStatus", "configurationStatus") === 2;

const canContinueConfigurationSetup = (configuration) =>
  !isConfigurationComplete(configuration) && !isConfigurationRetired(configuration);

const getConfigurationStatusClassName = (configuration) =>
  isConfigurationConfiguring(configuration)
    ? "resource-workspace-configuration-status resource-workspace-configuration-status--warning"
    : isConfigurationReady(configuration)
      ? "resource-workspace-configuration-status resource-workspace-configuration-status--ready"
      : isConfigurationDisabled(configuration)
        ? "resource-workspace-configuration-status resource-workspace-configuration-status--disabled"
    : "resource-workspace-configuration-status";

const resolveResourceAllowanceState = (selectedResource, configurations = []) => {
  const clientResource = selectedResource?.clientResource || {};
  const allowance = getValue(clientResource, "ResourceAllowance", "resourceAllowance") || {};
  const effectiveAllowance = selectedResource?.effectiveAllowance || {};
  const isUnlimitedAllowance = Boolean(getValue(effectiveAllowance, "IsUnlimited", "isUnlimited"));
  const allowedCount = isUnlimitedAllowance
    ? Number.POSITIVE_INFINITY
    : getNumber(effectiveAllowance, "AllowedCount", "allowedCount")
      ?? getNumber(allowance, "AllowedResourceConfigurationCount", "allowedResourceConfigurationCount");
  const reservedCount = getNumber(effectiveAllowance, "ReservedCount", "reservedCount") ?? configurations.length;
  const remainingCount = isUnlimitedAllowance
    ? Number.POSITIVE_INFINITY
    : getNumber(effectiveAllowance, "RemainingCount", "remainingCount")
      ?? (allowedCount === null ? null : Math.max(0, allowedCount - reservedCount));

  return {
    allowedCount,
    reservedCount,
    remainingCount,
    hasRemainingConfigurationAllowance: remainingCount === Number.POSITIVE_INFINITY
      || (remainingCount !== null && remainingCount > 0)
  };
};

const RESOURCE_CONFIGURATION_IMPORT_SUPPORT = {
  entityschema: {
    key: "EntitySchema",
    label: "Entity Schema",
    operationName: IMPORT_ENTITY_SCHEMA_RESOURCE_CONFIGURATION_OPERATION
  }
};

const RESOURCE_IMPORT_OFFER_CARD_FIELDS = [
  { FieldName: "ProductOfferName", DisplayName: "Product Offer", ShowOnCard: true, GridSequence: 1 },
  { FieldName: "RemainingLabel", DisplayName: "Remaining", ShowOnCard: true, GridSequence: 2 },
  { FieldName: "AllowanceLabel", DisplayName: "Allowance", ShowOnCard: true, GridSequence: 3 },
  { FieldName: "QuantityLabel", DisplayName: "Quantity", ShowOnCard: true, GridSequence: 4 }
];

const getResourceConfigurationImportSupport = (selectedResource) => {
  const productResource = selectedResource?.productResource || selectedResource;
  if (isEntitySchemaResource(productResource)) {
    return RESOURCE_CONFIGURATION_IMPORT_SUPPORT.entityschema;
  }

  return null;
};

const isActiveSchemaImportCandidate = (schema) => {
  const activeSchemaVersionId = getString(schema, "ActiveSchemaVersionId", "activeSchemaVersionId");
  const versions = getArray(schema, "SchemaVersions", "schemaVersions");
  return getBoolean(schema, "IsActive", "isActive")
    && !getBoolean(schema, "IsDeleted", "isDeleted")
    && Boolean(activeSchemaVersionId)
    && versions.some((version) => {
      const versionActiveValue = getValue(version, "IsActive", "isActive");
      return normalizeCompare(getString(version, "SchemaVersionId", "schemaVersionId", "UniqueId", "uniqueId")) === normalizeCompare(activeSchemaVersionId)
        && !getBoolean(version, "IsDeleted", "isDeleted")
        && getBoolean(version, "IsPublished", "isPublished")
        && (versionActiveValue === "" || getBoolean(version, "IsActive", "isActive"));
    });
};

const getResourceImportOfferRows = (selectedResource) => {
  const resourceKey = normalizeCompare(selectedResource?.productResourceKey);
  const effectiveAllowance = selectedResource?.effectiveAllowance || {};
  return getArray(effectiveAllowance, "PurchasedOffers", "purchasedOffers", "Grants", "grants")
    .filter((offer) => {
      const offerId = getString(offer, "ClientProductOfferId", "clientProductOfferId", "_id", "id");
      const offerResourceKey = normalizeCompare(getString(offer, "ProductResourceKey", "productResourceKey"));
      const isUnlimited = getBoolean(offer, "IsUnlimited", "isUnlimited");
      const remainingCount = isUnlimited
        ? Number.POSITIVE_INFINITY
        : getNumber(offer, "RemainingCount", "remainingCount");

      return Boolean(offerId)
        && (isUnlimited || offerResourceKey === resourceKey)
        && (isUnlimited || (remainingCount !== null && remainingCount > 0));
    });
};

const buildResourceImportOfferCardRows = (selectedResource) =>
  getResourceImportOfferRows(selectedResource).map((offer) => {
    const isUnlimited = getBoolean(offer, "IsUnlimited", "isUnlimited");
    const remainingCount = isUnlimited
      ? "Unlimited"
      : getNumber(offer, "RemainingCount", "remainingCount");
    const allowedCount = isUnlimited
      ? "Unlimited"
      : getNumber(offer, "AllowedCount", "allowedCount", "AllowedResourceConfigurationCount", "allowedResourceConfigurationCount");
    const quantity = getNumber(offer, "Quantity", "quantity");
    const productOfferName = getDisplayName(offer, getString(offer, "ProductOfferKey", "productOfferKey") || "Product Offer");
    const productOfferKey = getString(offer, "ProductOfferKey", "productOfferKey");
    const productOfferSku = getString(offer, "ProductOfferSku", "productOfferSku", "Sku", "sku");
    return {
      ClientProductOfferId: getString(offer, "ClientProductOfferId", "clientProductOfferId", "_id", "id"),
      ProductOfferName: productOfferName,
      ProductOfferKey: productOfferKey,
      ProductOfferSku: productOfferSku,
      RemainingLabel: remainingCount === "Unlimited" ? "Unlimited" : `${remainingCount} remaining`,
      AllowanceLabel: allowedCount === "Unlimited" ? "Unlimited" : `${allowedCount ?? "Limited"}`,
      QuantityLabel: quantity === null ? "1" : `${quantity}`,
      SourceOffer: offer
    };
  });

const buildSchemaImportOptions = (schemas = []) =>
  schemas
    .filter(isActiveSchemaImportCandidate)
    .map((schema) => ({
      Text: getDisplayName(schema, getString(schema, "Name", "name") || "Entity Schema"),
      Value: getString(schema, "_id", "id", "SchemaId", "schemaId", "UniqueId", "uniqueId")
    }));

const getStatusLabel = (labels, item, ...keys) => {
  const status = getNumber(item, ...keys);
  return labels[status] || "Unknown";
};

const getClientResourceStatusLabel = (resource) =>
  getClientResourceStatusLabelFromItem(resource.clientResource);

const getClientResourceStatusLabelFromItem = (clientResource) => {
  const status = getNumber(clientResource, "LifecycleStatus", "lifecycleStatus");
  return CLIENT_RESOURCE_STATUS_LABELS[status]
    || LEGACY_CLIENT_RESOURCE_STATUS_LABELS[status]
    || "Unknown";
};

const isClientResourceSetupEnabled = (resource) => {
  const clientResource = resource?.clientResource;
  return getNumber(clientResource, "LifecycleStatus", "lifecycleStatus") === 4
    && getNumber(clientResource, "ApprovalStatus", "approvalStatus") === 3;
};

const getResourceConfigurationCount = (resource, resourceConfigurations = []) =>
  findResourceConfigurations(resourceConfigurations, resource).length;

const canRemoveUnstartedClientResource = (resource, resourceConfigurations = []) =>
  !isClientResourceSetupEnabled(resource)
    && getResourceConfigurationCount(resource, resourceConfigurations) === 0;

const getResourceTypeLabel = (resource) =>
  [resource.productName, resource.productResourceKey].filter(Boolean).join(" - ") || "Unknown type";

const getResourceCatalogNote = (resource) =>
  getString(resource?.clientResource, "Notes", "notes", "Description", "description")
  || getString(resource?.productResource, "Notes", "notes", "Description", "description", "Details", "details", "Summary", "summary")
  || resource?.productName
  || "";

const getSortedUniqueOptions = (values) =>
  Array.from(new Set(values.filter(Boolean))).sort((left, right) => left.localeCompare(right));

const buildResourcePath = (clientResourceId, toolKey = "overview") =>
  `${RESOURCE_WORKSPACE_BASE_PATH}/${encodeURIComponent(clientResourceId)}/${encodeURIComponent(toolKey)}`;

const buildResourceRootPath = (clientResourceId) =>
  `${RESOURCE_WORKSPACE_BASE_PATH}/${encodeURIComponent(clientResourceId)}`;

const buildResourceNavigationPath = (clientResourceId, toolKey) =>
  toolKey === OVERVIEW_NAVIGATION.key
    ? buildResourceRootPath(clientResourceId)
    : buildResourcePath(clientResourceId, toolKey);

const buildResourceNavigationActivePaths = (clientResourceId, toolKey) =>
  toolKey === OVERVIEW_NAVIGATION.key
    ? [buildResourceRootPath(clientResourceId), buildResourcePath(clientResourceId, OVERVIEW_NAVIGATION.key)]
    : [buildResourcePath(clientResourceId, toolKey)];

const splitToolPath = (toolPath) =>
  String(toolPath || "")
    .split("/")
    .map((segment) => segment.trim())
    .filter(Boolean);

const parseConfigurationRoute = (toolPath, configurationNavigations = []) => {
  const segments = splitToolPath(toolPath);
  if (segments.length === 0) {
    return {
      resourceConfigurationId: "",
      screenKey: CONFIGURATION_MANAGE_NAVIGATION.key
    };
  }

  const firstKey = formatToolKey(segments[0]);
  const knownScreenKeys = new Set([
    ...configurationNavigations.map((navigation) => navigation.key),
    DESTINATION_DEPLOYMENTS_SCREEN_KEY,
    DESTINATION_DEPLOYMENT_SCREEN_KEY,
    DESTINATION_ENTITY_DEPLOYMENTS_SCREEN_KEY
  ]);
  if (firstKey === CONFIGURATION_MANAGE_NAVIGATION.key || knownScreenKeys.has(firstKey)) {
    return {
      resourceConfigurationId: "",
      screenKey: firstKey
    };
  }

  return {
    resourceConfigurationId: segments[0],
    screenKey: formatToolKey(segments[1]) || CONFIGURATION_MANAGE_NAVIGATION.key,
    sourceObjectManagerSourceObjectId: formatToolKey(segments[1]) === SOURCE_SYSTEM_SCREEN_KEY
      && formatToolKey(segments[2]) === SOURCE_OBJECT_MANAGE_ROUTE_SEGMENT
      && segments[3]
      && formatToolKey(segments[4]) === SOURCE_OBJECT_MANAGE_SCREEN_KEY
      ? decodeURIComponent(segments[3])
      : ""
  };
};

const buildResourceConfigurationManagePath = (clientResourceId) =>
  `${buildResourcePath(clientResourceId, CONFIGURATIONS_NAVIGATION.key)}/${CONFIGURATION_MANAGE_NAVIGATION.key}`;

const buildResourceConfigurationScreenPath = (clientResourceId, resourceConfigurationId, screenKey = "") => {
  const defaultScreenKey = formatToolKey(screenKey);
  const basePath = `${buildResourcePath(clientResourceId, CONFIGURATIONS_NAVIGATION.key)}/${encodeURIComponent(resourceConfigurationId)}`;
  return defaultScreenKey ? `${basePath}/${encodeURIComponent(defaultScreenKey)}` : basePath;
};

// Destination schedule deep links are Resource workspace-local filters, not domain route definitions.
const buildDestinationScheduleScreenPath = (clientResourceId, resourceConfigurationId, {
  deploymentId = "",
  entityObjectIds = [],
  scopeKey = ""
} = {}) => {
  const path = buildResourceConfigurationScreenPath(clientResourceId, resourceConfigurationId, DESTINATION_SCHEDULES_SCREEN_KEY);
  const query = new URLSearchParams();
  if (deploymentId) query.set("deploymentId", deploymentId);
  const normalizedEntityObjectIds = (Array.isArray(entityObjectIds) ? entityObjectIds : [entityObjectIds])
    .map((entityObjectId) => String(entityObjectId || "").trim())
    .filter(Boolean);
  if (normalizedEntityObjectIds.length) query.set("entityObjectIds", normalizedEntityObjectIds.join(","));
  if (scopeKey) query.set("scopeKey", scopeKey);
  const queryText = query.toString();
  return queryText ? `${path}?${queryText}` : path;
};

// Destination scope deep links are Resource workspace-local filters, not domain route definitions.
const buildDestinationEntityDeploymentsScreenPath = (clientResourceId, resourceConfigurationId, {
  deploymentId = ""
} = {}) => {
  const path = buildResourceConfigurationScreenPath(clientResourceId, resourceConfigurationId, DESTINATION_ENTITY_DEPLOYMENTS_SCREEN_KEY);
  const query = new URLSearchParams();
  if (deploymentId) query.set("deploymentId", deploymentId);
  const queryText = query.toString();
  return queryText ? `${path}?${queryText}` : path;
};

// Destination deployment detail deep links are Resource workspace-local filters, not domain route definitions.
const buildDestinationDeploymentScreenPath = (clientResourceId, resourceConfigurationId, {
  deploymentId = ""
} = {}) => {
  const path = buildResourceConfigurationScreenPath(clientResourceId, resourceConfigurationId, DESTINATION_DEPLOYMENT_SCREEN_KEY);
  const query = new URLSearchParams();
  if (deploymentId) query.set("deploymentId", deploymentId);
  const queryText = query.toString();
  return queryText ? `${path}?${queryText}` : path;
};

const buildDestinationWarehouseViewsScreenPath = (clientResourceId, resourceConfigurationId, {
  deploymentId = ""
} = {}) => {
  const path = buildResourceConfigurationScreenPath(clientResourceId, resourceConfigurationId, DESTINATION_WAREHOUSE_VIEWS_SCREEN_KEY);
  const query = new URLSearchParams();
  if (deploymentId) query.set("deploymentId", deploymentId);
  const queryText = query.toString();
  return queryText ? `${path}?${queryText}` : path;
};

const buildSourceObjectManagerPath = (clientResourceId, resourceConfigurationId, sourceObjectId) =>
  `${buildResourceConfigurationScreenPath(clientResourceId, resourceConfigurationId, SOURCE_SYSTEM_SCREEN_KEY)}/${SOURCE_OBJECT_MANAGE_ROUTE_SEGMENT}/${encodeURIComponent(sourceObjectId)}/${SOURCE_OBJECT_MANAGE_SCREEN_KEY}`;

const buildSelectedConfigurationPath = (clientResourceId, resourceConfigurationId, screenKey = CONFIGURATION_MANAGE_NAVIGATION.key) =>
  screenKey === CONFIGURATION_MANAGE_NAVIGATION.key
    ? buildResourceConfigurationScreenPath(clientResourceId, resourceConfigurationId)
    : buildResourceConfigurationScreenPath(clientResourceId, resourceConfigurationId, screenKey);

const withSelectedDestinationPath = (path, destinationId) =>
  destinationId ? `${path}?destinationId=${encodeURIComponent(destinationId)}` : path;

const buildConfigurationNavigationPath = (clientResourceId, screenKey, selectedConfigurationId, selectedDestinationId = "") => {
  if (screenKey === CONFIGURATION_MANAGE_NAVIGATION.key) {
    return selectedConfigurationId
      ? withSelectedDestinationPath(
          buildSelectedConfigurationPath(clientResourceId, selectedConfigurationId, screenKey),
          selectedDestinationId
        )
      : buildResourceConfigurationManagePath(clientResourceId);
  }

  if (selectedConfigurationId) {
    return withSelectedDestinationPath(
      buildResourceConfigurationScreenPath(clientResourceId, selectedConfigurationId, screenKey),
      selectedDestinationId
    );
  }

  return `${buildResourcePath(clientResourceId, CONFIGURATIONS_NAVIGATION.key)}/${encodeURIComponent(screenKey)}`;
};

const buildConfigurationNavigationActivePaths = (clientResourceId, screenKey, selectedConfigurationId) => {
  if (screenKey === CONFIGURATION_MANAGE_NAVIGATION.key) {
    const paths = [
      buildResourcePath(clientResourceId, CONFIGURATIONS_NAVIGATION.key),
      buildResourceConfigurationManagePath(clientResourceId)
    ];
    if (selectedConfigurationId) {
      paths.push(buildSelectedConfigurationPath(clientResourceId, selectedConfigurationId, CONFIGURATION_MANAGE_NAVIGATION.key));
      paths.push(buildResourceConfigurationScreenPath(clientResourceId, selectedConfigurationId, CONFIGURATION_MANAGE_NAVIGATION.key));
    }
    return paths;
  }

  const paths = [`${buildResourcePath(clientResourceId, CONFIGURATIONS_NAVIGATION.key)}/${encodeURIComponent(screenKey)}`];
  if (selectedConfigurationId) {
    paths.push(buildResourceConfigurationScreenPath(clientResourceId, selectedConfigurationId, screenKey));
    if (screenKey === DESTINATION_DEPLOYMENTS_SCREEN_KEY) {
      paths.push(buildResourceConfigurationScreenPath(clientResourceId, selectedConfigurationId, DESTINATION_DEPLOYMENT_SCREEN_KEY));
      paths.push(buildResourceConfigurationScreenPath(clientResourceId, selectedConfigurationId, DESTINATION_ENTITY_DEPLOYMENTS_SCREEN_KEY));
    }
  }
  return paths;
};

const findConfigurationById = (configurations, resourceConfigurationId) => {
  const normalizedId = normalizeCompare(resourceConfigurationId);
  if (!normalizedId) return null;
  return configurations.find((configuration) => normalizeCompare(getId(configuration)) === normalizedId) || null;
};

const ResourceWorkspace = () => {
  const routeParams = useParams();
  const { clientResourceId, toolKey, resourceGroupKey } = routeParams;
  const toolPath = routeParams["*"] || "";
  const navigate = useNavigate();
  const location = useLocation();
  const { currentRoute } = useCoreRoute("Resource");
  const { canAccess, canExecute, canView, canViewOnNav } = useCorePermissions();
  const canViewResourceScreen = canView(currentRoute?.Key || RESOURCE_SCREEN_PERMISSION);
  const canAddMarketplaceResource = canExecute(RESOURCE_ACTION_PERMISSIONS.addClientResource);
  const canConfigureResource = canExecute(RESOURCE_ACTION_PERMISSIONS.configureResource);
  const canManageClientResourceApproval = canExecute(RESOURCE_ACTION_PERMISSIONS.manageClientResourceApproval);
  const isSchedulesRoute = normalizeCompare(location.pathname) === normalizeCompare(RESOURCES_SCHEDULES_PATH);
  const isEdiResourceGroupRoute = normalizeCompare(resourceGroupKey) === EDI_RESOURCE_GROUP_KEY;
  const resourceDomainContext = useResourceDomainContext();
  const coreContext = resourceDomainContext?.coreContext;
  const coreState = resourceDomainContext?.coreState;
  const marketplaceCatalogMgr = resourceDomainContext?.managers?.productMarketplaceCatalog;
  const clientResourceMgr = resourceDomainContext?.managers?.clientResource;
  const clientMgr = resourceDomainContext?.managers?.client;
  const resourceConfigurationMgr = resourceDomainContext?.managers?.resourceConfiguration;
  const integrationMgr = resourceDomainContext?.managers?.integration;
  const sourceSystemMgr = resourceDomainContext?.managers?.sourceSystem;
  const sourceObjectMgr = resourceDomainContext?.managers?.sourceObject;
  const schemaMgr = resourceDomainContext?.managers?.schema;
  const entityObjectMgr = resourceDomainContext?.managers?.entityObject;
  const warehouseMgr = resourceDomainContext?.managers?.warehouse;
  const fileStorageMgr = resourceDomainContext?.managers?.fileStorage;
  const externalSystemMgr = resourceDomainContext?.managers?.externalSystem;
  const schemaAPIMgr = resourceDomainContext?.managers?.schemaAPI;
  const logMgr = resourceDomainContext?.managers?.log;
  const eventMgr = resourceDomainContext?.managers?.event;
  const destinationScheduleMgr = resourceDomainContext?.managers?.destinationSchedule;
  const marketplaceCatalogMgrRef = useRef(marketplaceCatalogMgr);
  const clientResourceMgrRef = useRef(clientResourceMgr);
  const clientMgrRef = useRef(clientMgr);
  const resourceConfigurationMgrRef = useRef(resourceConfigurationMgr);
  const integrationMgrRef = useRef(integrationMgr);
  const sourceSystemMgrRef = useRef(sourceSystemMgr);
  const sourceObjectMgrRef = useRef(sourceObjectMgr);
  const schemaMgrRef = useRef(schemaMgr);
  const entityObjectMgrRef = useRef(entityObjectMgr);
  const warehouseMgrRef = useRef(warehouseMgr);
  const fileStorageMgrRef = useRef(fileStorageMgr);
  const externalSystemMgrRef = useRef(externalSystemMgr);
  const schemaAPIMgrRef = useRef(schemaAPIMgr);
  const pendingSourceObjectSchemaImportRef = useRef("");
  const sourceSystemOperationDialog = useOperationDialog(SOURCE_SYSTEM_ITEM_SERVICE);
  const sourceObjectOperationDialog = useOperationDialog(SOURCE_OBJECT_ITEM_SERVICE);
  const schemaOperationDialog = useOperationDialog(SCHEMA_ITEM_SERVICE);
  const entityObjectOperationDialog = useOperationDialog(ENTITY_OBJECT_ITEM_SERVICE);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [purchaseDialogResource, setPurchaseDialogResource] = useState(null);
  const [purchaseDialogOfferKey, setPurchaseDialogOfferKey] = useState("");
  const [deleteConfigurationTarget, setDeleteConfigurationTarget] = useState(null);
  const [deleteConfigurationConfirmText, setDeleteConfigurationConfirmText] = useState("");
  const [deleteConfigurationState, setDeleteConfigurationState] = useState({ busy: false, error: "" });
  const [deactivateConfigurationTarget, setDeactivateConfigurationTarget] = useState(null);
  const [configurationActionState, setConfigurationActionState] = useState({ busyAction: "", error: "", message: "" });
  const [configurationOperationResultDialog, setConfigurationOperationResultDialog] = useState(null);
  const [resourceConfigurationImportDialog, setResourceConfigurationImportDialog] = useState({
    open: false,
    support: null,
    schemaId: "",
    clientProductOfferId: "",
    schemas: [],
    loadingSchemas: false,
    busy: false,
    error: ""
  });
  const [destinationScheduleActionState, setDestinationScheduleActionState] = useState(null);
  const [endpointDeploymentsSelectionState, setEndpointDeploymentsSelectionState] = useState(null);
  const [destinationEntityScopeActionState, setDestinationEntityScopeActionState] = useState(null);
  const [warehouseViewActionState, setWarehouseViewActionState] = useState(null);
  const [connectionResultDialog, setConnectionResultDialog] = useState(null);
  const [sourceObjectExportDialog, setSourceObjectExportDialog] = useState({
    open: false,
    sourceObject: null,
    operation: null,
    selectedFormat: "csv",
    busy: false,
    error: "",
    resultMessage: ""
  });
  const [destinationEntitySearchDialog, setDestinationEntitySearchDialog] = useState({
    open: false,
    entityDeployment: null,
    entityObject: null,
    executionMode: DESTINATION_ENTITY_SEARCH_EXECUTION_SOURCE,
    exportFormat: "none",
    warehouseContext: null,
    busy: false,
    error: ""
  });
  const [editConfigurationPanelOpen, setEditConfigurationPanelOpen] = useState(false);
  const [editConfigurationPanelData, setEditConfigurationPanelData] = useState(null);
  const [integrationScreenState, setIntegrationScreenState] = useState({
    linkedRecordId: "",
    integration: null,
    metadata: null,
    loading: false,
    error: ""
  });
  const [sourceSystemScreenState, setSourceSystemScreenState] = useState({
    linkedRecordId: "",
    sourceSystem: null,
    metadata: null,
    sourceTargetMetadata: null,
    sourceObjectMetadata: null,
    sourceObjects: [],
    loading: false,
    error: ""
  });
  const [schemaScreenState, setSchemaScreenState] = useState({
    linkedRecordId: "",
    schema: null,
    metadata: null,
    selectedSchemaVersionId: "",
    activeSchemaVersionId: "",
    entityObjects: [],
    sourceObjects: [],
    sourceObjectError: "",
    loading: false,
    error: ""
  });
  const [recentLogsRefreshKey, setRecentLogsRefreshKey] = useState(0);
  const [sourceSystemRefreshKey, setSourceSystemRefreshKey] = useState(0);
  const [schemaRefreshKey, setSchemaRefreshKey] = useState(0);
  const [sourceObjectEditRequestKey, setSourceObjectEditRequestKey] = useState(0);
  const [editIntegrationPanelOpen, setEditIntegrationPanelOpen] = useState(false);
  const [editIntegrationPanelData, setEditIntegrationPanelData] = useState(null);
  const [editSourceSystemPanelOpen, setEditSourceSystemPanelOpen] = useState(false);
  const [editSourceSystemPanelData, setEditSourceSystemPanelData] = useState(null);
  const [editSchemaPanelOpen, setEditSchemaPanelOpen] = useState(false);
  const [editSchemaPanelData, setEditSchemaPanelData] = useState(null);
  const [selectedSchemaVersionId, setSelectedSchemaVersionId] = useState("");
  const [editSchemaVersionPanelOpen, setEditSchemaVersionPanelOpen] = useState(false);
  const [editSchemaVersionPanelData, setEditSchemaVersionPanelData] = useState(null);
  const [publishSchemaVersionDialog, setPublishSchemaVersionDialog] = useState({
    schema: null,
    version: null,
    action: "",
    makePrimary: false,
    busy: false,
    error: ""
  });
  const [editSourceTargetPanelOpen, setEditSourceTargetPanelOpen] = useState(false);
  const [editSourceTargetPanelData, setEditSourceTargetPanelData] = useState(null);
  const [editSourceTargetPanelState, setEditSourceTargetPanelState] = useState({ busy: false, error: "" });
  const [sourceTargetStatusDialog, setSourceTargetStatusDialog] = useState({
    target: null,
    isActive: false,
    busy: false,
    error: ""
  });
  const [addSourceObjectDialogOpen, setAddSourceObjectDialogOpen] = useState(false);
  const [addSourceObjectTargetIds, setAddSourceObjectTargetIds] = useState([]);
  const [addSourceObjectState, setAddSourceObjectState] = useState({ busy: false, error: "", confirmReload: false });
  const catalogLoadKey = [
    coreContext?.Profile?._id,
    coreContext?.Profile?.ClientId,
    coreContext?.Profile?.AppId
  ].filter(Boolean).join("|");
  const profileContextReady = Boolean(coreContext?.Profile?.ClientId && coreContext?.Profile?.AppId);
  const catalogRequestKey = useMemo(
    () => isSchedulesRoute
      ? "resources:schedules"
      : `resources:${catalogLoadKey || "pending-profile"}:${clientResourceId || "list"}`,
    [catalogLoadKey, clientResourceId, isSchedulesRoute]
  );
  const [loadedCatalogRequestKey, setLoadedCatalogRequestKey] = useState("");
  const isSystemAdmin = useMemo(
    () => Array.isArray(coreContext?.ProfileRoles) && coreContext.ProfileRoles.some((role) => role?.IsSystemAdmin === true),
    [coreContext?.ProfileRoles]
  );

  useEffect(() => {
    marketplaceCatalogMgrRef.current = marketplaceCatalogMgr;
  }, [marketplaceCatalogMgr]);

  useEffect(() => {
    clientResourceMgrRef.current = clientResourceMgr;
  }, [clientResourceMgr]);

  useEffect(() => {
    clientMgrRef.current = clientMgr;
  }, [clientMgr]);

  useEffect(() => {
    resourceConfigurationMgrRef.current = resourceConfigurationMgr;
  }, [resourceConfigurationMgr]);

  useEffect(() => {
    integrationMgrRef.current = integrationMgr;
  }, [integrationMgr]);

  useEffect(() => {
    sourceSystemMgrRef.current = sourceSystemMgr;
  }, [sourceSystemMgr]);

  useEffect(() => {
    sourceObjectMgrRef.current = sourceObjectMgr;
  }, [sourceObjectMgr]);

  useEffect(() => {
    schemaMgrRef.current = schemaMgr;
  }, [schemaMgr]);

  useEffect(() => {
    entityObjectMgrRef.current = entityObjectMgr;
  }, [entityObjectMgr]);

  useEffect(() => {
    warehouseMgrRef.current = warehouseMgr;
  }, [warehouseMgr]);

  useEffect(() => {
    fileStorageMgrRef.current = fileStorageMgr;
  }, [fileStorageMgr]);

  useEffect(() => {
    externalSystemMgrRef.current = externalSystemMgr;
  }, [externalSystemMgr]);

  useEffect(() => {
    schemaAPIMgrRef.current = schemaAPIMgr;
  }, [schemaAPIMgr]);

  const loadCatalog = useCallback(async (isMounted = () => true) => {
    if (!canViewResourceScreen) {
      if (isMounted()) {
        setCatalog(null);
        setLoadedCatalogRequestKey(catalogRequestKey);
        setLoading(false);
        setError("");
      }
      return;
    }

    if (!profileContextReady) {
      if (isMounted()) {
        setLoadedCatalogRequestKey("");
        setLoading(true);
        setError("");
      }
      return;
    }

    if (isMounted()) {
      setLoadedCatalogRequestKey("");
      setLoading(true);
      setError("");
    }

    try {
      const activeMarketplaceCatalogMgr = marketplaceCatalogMgrRef.current;
      if (!activeMarketplaceCatalogMgr?.RunOperation) {
        throw new Error("Marketplace catalog service is not available.");
      }
      const catalogRequestItem = { ItemName: PRODUCT_MARKETPLACE_CATALOG_ITEM };
      const response = await activeMarketplaceCatalogMgr.RunOperation({
        operationName: Operations.ProductMarketplaceCatalogItem.LoadProductMarketplaceCatalog,
        item: catalogRequestItem,
        queryParams: buildResourceOperationQueryParams({
          clientResourceId,
          item: catalogRequestItem
        }),
        isItem: true,
        isWarning: true,
        supressAllErrors: false
      });
      const result = getOperationResult(response);
      if (result?.Success === false || result?.success === false) {
        throw new Error(getResultMessage(response, "Marketplace catalog could not be loaded."));
      }
      const catalogItem = getOperationItem(response);
      if (!catalogItem) {
        throw new Error("Marketplace catalog could not be loaded.");
      }
      if (isMounted()) {
        setCatalog(catalogItem);
        setLoadedCatalogRequestKey(catalogRequestKey);
      }
    } catch (loadError) {
      if (isMounted()) {
        setCatalog(null);
        setError(isSchedulesRoute ? "" : getResultMessageText(loadError?.message || loadError) || "Marketplace catalog could not be loaded.");
        setLoadedCatalogRequestKey(catalogRequestKey);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [canViewResourceScreen, catalogRequestKey, clientResourceId, isSchedulesRoute, profileContextReady]);

  useEffect(() => {
    let mounted = true;
    loadCatalog(() => mounted);

    return () => {
      mounted = false;
    };
  }, [catalogLoadKey, loadCatalog]);

  useEffect(() => {
    const handleCatalogInvalidated = () => void loadCatalog();
    window.addEventListener(MARKETPLACE_CATALOG_INVALIDATED_EVENT, handleCatalogInvalidated);
    return () => window.removeEventListener(MARKETPLACE_CATALOG_INVALIDATED_EVENT, handleCatalogInvalidated);
  }, [loadCatalog]);

  const catalogModel = useMemo(() => buildCatalogModel(catalog), [catalog]);
  const marketplaceCatalog = useMemo(() => catalog ? normalizeMarketplaceCatalog(catalog) : null, [catalog]);
  const selectedResource = useMemo(
    () => findSelectedClientResource(catalogModel.clientResources, clientResourceId),
    [catalogModel.clientResources, clientResourceId]
  );
  const selectedMarketplaceResource = useMemo(() => {
    const selectedResourceKey = normalizeCompare(selectedResource?.productResourceKey);
    if (!selectedResourceKey) return null;
    return (marketplaceCatalog?.resources || []).find((resource) =>
      normalizeCompare(resource.key) === selectedResourceKey
    ) || null;
  }, [marketplaceCatalog?.resources, selectedResource?.productResourceKey]);
  const resourceConfigurationImportSupport = useMemo(
    () => getResourceConfigurationImportSupport(selectedResource),
    [selectedResource]
  );
  const resourceConfigurationImportOfferCardRows = useMemo(
    () => buildResourceImportOfferCardRows(selectedResource),
    [selectedResource]
  );
  const allResourceNavigations = useMemo(
    () => readResourceNavigations(selectedResource?.productResource, selectedResource),
    [selectedResource]
  );
  const resourceNavigations = useMemo(
    () => filterManagedNavigations(allResourceNavigations, { canAccess, canViewOnNav }),
    [allResourceNavigations, canAccess, canViewOnNav]
  );
  const selectedToolKey = formatToolKey(toolKey) || "";
  const activeToolKey = selectedToolKey || resourceNavigations[0]?.key || allResourceNavigations[0]?.key || "overview";
  const selectedNavigation = allResourceNavigations.find((navigation) => navigation.key === activeToolKey) || null;
  const configurationNavigations = useMemo(() =>
    allResourceNavigations.find((navigation) => navigation.key === CONFIGURATIONS_NAVIGATION.key)?.subNavigations || [CONFIGURATION_MANAGE_NAVIGATION],
    [allResourceNavigations]
  );
  const configurationRoute = useMemo(
    () => parseConfigurationRoute(toolPath, configurationNavigations),
    [configurationNavigations, toolPath]
  );
  const selectedConfigurationNavigation = selectedNavigation?.key === CONFIGURATIONS_NAVIGATION.key
    ? configurationNavigations.find((navigation) => navigation.key === configurationRoute.screenKey) || null
    : null;
  const canAccessSelectedResourceNavigation = !selectedNavigation
    || canAccessManagedNavigation(selectedNavigation, { canAccess });
  const canAccessSelectedConfigurationNavigation = !selectedConfigurationNavigation
    || canAccessManagedNavigation(selectedConfigurationNavigation, { canAccess });
  const canAccessDirectResourceNavigation = canAccessSelectedResourceNavigation
    && canAccessSelectedConfigurationNavigation;
  const destinationEntityDeploymentRouteFilters = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return {
      deploymentId: params.get("deploymentId") || "all"
    };
  }, [location.search]);
  const destinationDeploymentRouteFilters = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return {
      deploymentId: params.get("deploymentId") || ""
    };
  }, [location.search]);
  const selectedDestinationRouteId = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("destinationId") || params.get("endpointId") || "";
  }, [location.search]);
  const configurations = useMemo(
    () => findResourceConfigurations(catalogModel.resourceConfigurations, selectedResource),
    [catalogModel.resourceConfigurations, selectedResource]
  );
  const selectedConfiguration = useMemo(
    () => {
      const routedConfiguration = findConfigurationById(configurations, configurationRoute.resourceConfigurationId);
      if (routedConfiguration || configurationRoute.resourceConfigurationId) return routedConfiguration;
      if (configurations.length === 1 && isClientMcpServerResource(selectedResource)) return configurations[0];
      return null;
    },
    [configurationRoute.resourceConfigurationId, configurations, selectedResource]
  );
  const selectedProductResourceDefaults = useMemo(
    () => parseJsonObject(getValue(selectedResource?.productResource, "SetupDefaultsJson", "setupDefaultsJson")),
    [selectedResource?.productResource]
  );
  useEffect(() => {
    if (selectedNavigation?.key !== CONFIGURATIONS_NAVIGATION.key) return;
    if (configurationRoute.screenKey !== CONFIGURATION_MANAGE_NAVIGATION.key) return;
    if (configurationRoute.resourceConfigurationId || configurations.length !== 1) return;

    const resourceConfigurationId = getId(configurations[0]);
    const configurationClientResourceId = selectedResource?.id || clientResourceId;
    if (!resourceConfigurationId || !configurationClientResourceId) return;

    navigate(
      buildSelectedConfigurationPath(configurationClientResourceId, resourceConfigurationId),
      { replace: true }
    );
  }, [
    clientResourceId,
    configurationRoute.resourceConfigurationId,
    configurationRoute.screenKey,
    configurations,
    navigate,
    selectedNavigation?.key,
    selectedResource?.id
  ]);
  useEffect(() => {
    if (!configurationRoute.resourceConfigurationId || !selectedResource?.id || !selectedConfiguration) return;
    if (isConfigurationComplete(selectedConfiguration)) return;

    navigate(
      buildResourceConfigurePath(selectedResource.id, getId(selectedConfiguration)),
      { replace: true }
    );
  }, [configurationRoute.resourceConfigurationId, navigate, selectedConfiguration, selectedResource?.id]);
  const buildWorkspaceResourceQueryParams = useCallback((queryParams, item, configuration = selectedConfiguration) => buildResourceOperationQueryParams({
    queryParams,
    item,
    resourceConfiguration: configuration,
    clientResourceId: selectedResource?.id || clientResourceId,
    resourceConfigurationId: getId(configuration)
  }), [clientResourceId, selectedConfiguration, selectedResource?.id]);
  const openDestinationScheduleContext = useCallback(({
    deploymentId = "",
    entityObjectIds = [],
    scopeKey = ""
  } = {}) => {
    const scheduleClientResourceId = selectedResource?.id || clientResourceId;
    const scheduleResourceConfigurationId = configurationRoute.resourceConfigurationId || getId(selectedConfiguration);
    if (!scheduleClientResourceId || !scheduleResourceConfigurationId) return;

    navigate(buildDestinationScheduleScreenPath(scheduleClientResourceId, scheduleResourceConfigurationId, {
      deploymentId,
      entityObjectIds,
      scopeKey
    }));
  }, [clientResourceId, configurationRoute.resourceConfigurationId, navigate, selectedConfiguration, selectedResource?.id]);
  const openDestinationScheduleForDeployment = useCallback((deployment) => {
    const deploymentId = getDeploymentId(deployment);
    if (!deploymentId) return;
    openDestinationScheduleContext({
      deploymentId,
      scopeKey: buildDestinationScheduleScopeKey(deploymentId)
    });
  }, [openDestinationScheduleContext]);
  const openDestinationScheduleForEntityDeployment = useCallback((entityDeployment) => {
    const deploymentId = getString(entityDeployment, "DeploymentId", "deploymentId", "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId");
    const entityObjectId = getString(entityDeployment, "EntityObjectId", "entityObjectId");
    if (!deploymentId) return;
    const entityObjectIds = entityObjectId ? [entityObjectId] : [];
    openDestinationScheduleContext({
      deploymentId,
      entityObjectIds,
      scopeKey: buildDestinationScheduleScopeKey(deploymentId, entityObjectIds)
    });
  }, [openDestinationScheduleContext]);
  const openDestinationEntityDeploymentContext = useCallback(({
    deploymentId = ""
  } = {}) => {
    const scopeClientResourceId = selectedResource?.id || clientResourceId;
    const scopeResourceConfigurationId = configurationRoute.resourceConfigurationId || getId(selectedConfiguration);
    if (!scopeClientResourceId || !scopeResourceConfigurationId) return;

    navigate(buildDestinationEntityDeploymentsScreenPath(scopeClientResourceId, scopeResourceConfigurationId, {
      deploymentId
    }));
  }, [clientResourceId, configurationRoute.resourceConfigurationId, navigate, selectedConfiguration, selectedResource?.id]);
  const openDestinationScopeForDeployment = useCallback((deployment) => {
    const deploymentId = getDeploymentId(deployment);
    if (!deploymentId) return;
    openDestinationEntityDeploymentContext({ deploymentId });
  }, [openDestinationEntityDeploymentContext]);
  const openDestinationDeploymentManagement = useCallback((deployment) => {
    const deploymentId = getDeploymentId(deployment);
    const deploymentClientResourceId = selectedResource?.id || clientResourceId;
    const deploymentResourceConfigurationId = configurationRoute.resourceConfigurationId || getId(selectedConfiguration);
    if (!deploymentId || !deploymentClientResourceId || !deploymentResourceConfigurationId) return;

    navigate(buildDestinationDeploymentScreenPath(deploymentClientResourceId, deploymentResourceConfigurationId, { deploymentId }));
  }, [clientResourceId, configurationRoute.resourceConfigurationId, navigate, selectedConfiguration, selectedResource?.id]);
  const openWarehouseViewsForDeployment = useCallback(async (deployment) => {
    const deploymentId = getDeploymentId(deployment);
    const deploymentClientResourceId = selectedResource?.id || clientResourceId;
    const deploymentResourceConfigurationId = configurationRoute.resourceConfigurationId || getId(selectedConfiguration);
    if (!deploymentId || !deploymentClientResourceId || !deploymentResourceConfigurationId) return;

    const destination = getLinkedDestinationRecord(selectedConfiguration);
    const warehouseId = getId(destination);
    const warehouseManager = warehouseMgrRef.current;
    if (warehouseManager?.RunOperation && destination) {
      try {
        await warehouseManager.RunOperation({
          operationName: Operations.WarehouseItem?.OpenWarehouseViewDesigner || "OpenWarehouseViewDesigner",
          item: destination,
          queryParams: {
            wid: warehouseId,
            wId: warehouseId,
            warehouseId,
            wdid: deploymentId,
            wdId: deploymentId,
            warehouseDeploymentId: deploymentId,
            referenceid: deploymentId
          },
          isItem: true,
          isWarning: true,
          supressAllErrors: true
        });
      } catch {
        // Navigation below remains the deterministic local route for the designer screen.
      }
    }

    navigate(buildDestinationWarehouseViewsScreenPath(deploymentClientResourceId, deploymentResourceConfigurationId, { deploymentId }));
  }, [clientResourceId, configurationRoute.resourceConfigurationId, navigate, selectedConfiguration, selectedResource?.id]);
  const selectedIntegrationLinkedRecord = useMemo(
    () => getLinkedIntegrationRecord(selectedConfiguration),
    [selectedConfiguration]
  );
  const selectedIntegrationId = getLinkedRecordItemId(selectedIntegrationLinkedRecord);
  const selectedSourceSystemLinkedRecord = useMemo(
    () => getLinkedSourceSystemRecord(selectedConfiguration),
    [selectedConfiguration]
  );
  const selectedSourceSystemId = getLinkedRecordItemId(selectedSourceSystemLinkedRecord);
  const selectedSchemaLinkedRecord = useMemo(
    () => getLinkedEntitySchemaRecord(selectedConfiguration),
    [selectedConfiguration]
  );
  const selectedSchemaId = getLinkedRecordItemId(selectedSchemaLinkedRecord);
  const selectedDestinationLinkedRecord = useMemo(() => {
    const records = getConfigurationLinkedRecords(selectedConfiguration).filter(isDestinationLinkedRecord);
    if (selectedDestinationRouteId) {
      return records.find((record) => normalizeCompare(getLinkedRecordItemId(record)) === normalizeCompare(selectedDestinationRouteId)) || null;
    }
    if (configurationRoute.screenKey === CONFIGURATION_MANAGE_NAVIGATION.key) {
      return records.length === 1 ? records[0] : null;
    }
    return records[0] || null;
  }, [configurationRoute.screenKey, selectedConfiguration, selectedDestinationRouteId]);
  const selectedDestinationId = getLinkedRecordItemId(selectedDestinationLinkedRecord);
  const selectedDestinationDefaults = useMemo(
    () => getDestinationDefaultsFromRecord(selectedDestinationLinkedRecord, selectedResource?.productResource),
    [selectedDestinationLinkedRecord, selectedResource?.productResource]
  );
  const isIntegrationConfigurationScreen = selectedNavigation?.key === CONFIGURATIONS_NAVIGATION.key
    && configurationRoute.screenKey === SOURCE_INTEGRATION_NAVIGATIONS[0].key
    && Boolean(selectedConfiguration);
  const isSourceSystemConfigurationScreen = selectedNavigation?.key === CONFIGURATIONS_NAVIGATION.key
    && INBOUND_EDI_SOURCE_SYSTEM_SCREEN_KEYS.includes(configurationRoute.screenKey)
    && Boolean(selectedConfiguration);
  const isEntitySchemaConfigurationScreen = selectedNavigation?.key === CONFIGURATIONS_NAVIGATION.key
    && [
      ...INBOUND_EDI_ENTITY_SCHEMA_SCREEN_KEYS,
      ...OUTBOUND_EDI_ENTITY_SCHEMA_SCREEN_KEYS
    ].includes(configurationRoute.screenKey)
    && Boolean(selectedConfiguration);
  const isDestinationConfigurationScreen = selectedNavigation?.key === CONFIGURATIONS_NAVIGATION.key
    && [
      DESTINATION_SCREEN_KEY,
      DESTINATION_DEPLOYMENTS_SCREEN_KEY,
      DESTINATION_DEPLOYMENT_SCREEN_KEY,
      DESTINATION_ENTITY_DEPLOYMENTS_SCREEN_KEY,
      DESTINATION_WAREHOUSE_VIEWS_SCREEN_KEY,
      ENDPOINT_AUTHENTICATION_SCREEN_KEY,
      DESTINATION_SCHEDULES_SCREEN_KEY
    ].includes(configurationRoute.screenKey)
    && Boolean(selectedConfiguration);

  useEffect(() => {
    if (configurationRoute.screenKey !== DESTINATION_SCHEDULES_SCREEN_KEY) {
      setDestinationScheduleActionState(null);
    }
  }, [configurationRoute.screenKey]);
  const sourceSystemLoadTargetsOperation = useMemo(() => {
    const operations = Array.isArray(coreContext?.Operations) ? coreContext.Operations : [];
    const operation = operations.find((candidate) =>
      normalizeCompare(getOperationName(candidate)) === normalizeCompare(LOAD_SOURCE_SYSTEM_TARGETS_OPERATION)
    );
    return normalizeSourceSystemLoadTargetsOperation(operation || {
        Name: LOAD_SOURCE_SYSTEM_TARGETS_OPERATION,
        OperationTypeId: LOAD_SOURCE_SYSTEM_TARGETS_OPERATION,
        DisplayName: "Load Targets",
        Details: "Load available source targets for this Source System."
      });
  }, [coreContext?.Operations]);
  const sourceSystemLoadObjectsOperation = useMemo(() => {
    const operations = Array.isArray(coreContext?.Operations) ? coreContext.Operations : [];
    const operation = operations.find((candidate) =>
      normalizeCompare(getOperationName(candidate)) === normalizeCompare(LOAD_SOURCE_OBJECTS_OPERATION)
    );
    return operation
      ? normalizeOperationForDialog(operation)
      : {
        Name: LOAD_SOURCE_OBJECTS_OPERATION,
        OperationTypeId: LOAD_SOURCE_OBJECTS_OPERATION,
        DisplayName: "Load Source Objects",
        Details: "Load Source Objects for selected Source System targets."
      };
  }, [coreContext?.Operations]);
  const sourceObjectOperations = useMemo(
    () => getSourceObjectOperations(Array.isArray(coreContext?.Operations) ? coreContext.Operations : []),
    [coreContext?.Operations]
  );
  const schemaOperations = useMemo(
    () => getSchemaOperations(Array.isArray(coreContext?.Operations) ? coreContext.Operations : []),
    [coreContext?.Operations]
  );
  const entityObjectOperations = useMemo(
    () => getEntityObjectOperations(Array.isArray(coreContext?.Operations) ? coreContext.Operations : []),
    [coreContext?.Operations]
  );
  const sourceSystemScreenStateWithOperations = useMemo(
    () => ({
      ...sourceSystemScreenState,
      loadSourceObjectsOperation: sourceSystemLoadObjectsOperation
    }),
    [sourceSystemLoadObjectsOperation, sourceSystemScreenState]
  );
  const saveNewLinkedDestination = useCallback(async (endpoint) => {
    const activeSchemaApiManager = schemaAPIMgrRef.current;
    const resourceConfigurationId = getId(selectedConfiguration);
    const setupClientResourceId = selectedResource?.id || clientResourceId;
    if (!activeSchemaApiManager?.RunOperation || !resourceConfigurationId || !setupClientResourceId) {
      throw new Error("Endpoint link service is not available.");
    }

    const response = await activeSchemaApiManager.RunOperation({
      operationName: Operations.ResourceConfigurationItem?.SaveResourceConfigurationLinkedEndpoint
        || "SaveResourceConfigurationLinkedEndpoint",
      item: endpoint,
      queryParams: buildWorkspaceResourceQueryParams(null, endpoint, selectedConfiguration),
      isItem: true,
      isWarning: true,
      supressAllErrors: false
    });
    const result = getOperationResult(response);
    if (result?.Success === false || result?.success === false) {
      const error = new Error(getOperationResultMessage(response, "Endpoint could not be saved and linked."));
      error.response = response;
      throw error;
    }

    const savedEndpoint = getOperationItem(response) || endpoint;
    const savedEndpointId = getId(savedEndpoint);
    await loadCatalog();
    if (savedEndpointId) {
      navigate(withSelectedDestinationPath(
        buildSelectedConfigurationPath(setupClientResourceId, resourceConfigurationId, DESTINATION_SCREEN_KEY),
        savedEndpointId
      ));
    }
    return response;
  }, [
    buildWorkspaceResourceQueryParams,
    clientResourceId,
    loadCatalog,
    navigate,
    selectedConfiguration,
    selectedResource?.id
  ]);
  const destinationWorkspace = useDestinationWorkspaceController({
    isDestinationConfigurationScreen,
    profileContextReady,
    selectedDestinationId,
    selectedDestinationDefaults,
    coreContext,
    schemaMgrRef,
    entityObjectMgrRef,
    sourceObjectMgrRef,
    clientMgrRef,
    warehouseMgrRef,
    fileStorageMgrRef,
    externalSystemMgrRef,
    schemaAPIMgrRef,
    coreState,
    buildWorkspaceResourceQueryParams,
    setConfigurationActionState,
    setConnectionResultDialog,
    setRecentLogsRefreshKey,
    loadCatalog,
    saveNewLinkedDestination
  });
  const { destinationScreenState, refreshDestinationWorkspace } = destinationWorkspace;
  const openDestinationEntityPickerForRoute = useCallback(
    () => destinationWorkspace.openDestinationEntityPicker(destinationEntityDeploymentRouteFilters?.deploymentId),
    [destinationEntityDeploymentRouteFilters?.deploymentId, destinationWorkspace.openDestinationEntityPicker]
  );
  const endpointEntityEndpointMetadata = useMemo(
    () => {
      const panelData = destinationWorkspace.editEndpointEntityEndpointPanelData;
      const deploymentId = getString(panelData, "__DeploymentId", "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId");
      const deployment = findDestinationDeploymentById(
        destinationScreenState.destination,
        destinationScreenState.defaults,
        deploymentId
      );
      return buildEndpointEntityEndpointPanelMetadata({
        entityObjectOptions: destinationWorkspace.endpointEntityObjectSelectOptionsByDeployment?.[normalizeCompare(deploymentId)] || [],
        schemaScopes: getEndpointDeploymentSchemaScopes(deployment),
        isAddMode: panelData?.__EndpointEntityMode === "add",
        selectedEntityObjectId: getString(panelData, "__EndpointEntityObjectSelectionId", "EntityObjectId", "entityObjectId"),
        selectedExecutionMode: getString(panelData, "ExecutionMode", "executionMode") || "1"
      });
    },
    [
      destinationScreenState.defaults,
      destinationScreenState.destination,
      destinationWorkspace.endpointEntityObjectSelectOptionsByDeployment,
      destinationWorkspace.editEndpointEntityEndpointPanelData
    ]
  );
  useEffect(() => {
    const panelData = destinationWorkspace.editEndpointEntityEndpointPanelData;
    if (!destinationWorkspace.editEndpointEntityEndpointPanelOpen || !panelData) return;

    const deploymentId = getString(panelData, "__DeploymentId", "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId");
    const entityObjectOptions = destinationWorkspace.endpointEntityObjectSelectOptionsByDeployment?.[
      normalizeCompare(deploymentId)
    ] || [];
    const normalizedPanelData = normalizeEndpointEntityPanelExecutionMode(panelData, entityObjectOptions);
    if (normalizedPanelData !== panelData) {
      destinationWorkspace.setEditEndpointEntityEndpointPanelData(normalizedPanelData);
    }
  }, [
    destinationWorkspace.editEndpointEntityEndpointPanelData,
    destinationWorkspace.editEndpointEntityEndpointPanelOpen,
    destinationWorkspace.endpointEntityObjectSelectOptionsByDeployment,
    destinationWorkspace.setEditEndpointEntityEndpointPanelData
  ]);
  const findDestinationEntityObjectForScope = useCallback((entityDeployment) => {
    const entityObjectId = getString(entityDeployment, "EntityObjectId", "entityObjectId");
    if (!entityObjectId) return null;

    const entityObjects = Array.isArray(destinationScreenState?.entityObjects) ? destinationScreenState.entityObjects : [];
    return entityObjects.find((entityObject) =>
      normalizeCompare(getId(entityObject)) === normalizeCompare(entityObjectId)
      || normalizeCompare(getString(entityObject, "EntityObjectId", "entityObjectId")) === normalizeCompare(entityObjectId)
      || normalizeCompare(getString(entityObject, "UniqueId", "uniqueId")) === normalizeCompare(entityObjectId)
    ) || null;
  }, [destinationScreenState?.entityObjects]);
  const loadDestinationEntityObjectForScope = useCallback(async (entityDeployment) => {
    const entityObjectId = getString(entityDeployment, "EntityObjectId", "entityObjectId");
    if (!entityObjectId) {
      return { entityObject: null, error: "Entity Deployment Scope row is missing an Entity Object id." };
    }

    const matchedEntityObject = findDestinationEntityObjectForScope(entityDeployment);
    if (matchedEntityObject) return { entityObject: matchedEntityObject, error: "" };

    const activeEntityObjectManager = entityObjectMgrRef.current;
    if (!activeEntityObjectManager?.Query) {
      return { entityObject: null, error: "Entity Object operation service is not available." };
    }

    try {
      const entityObjectResult = await activeEntityObjectManager.Query({
        model: "x => x.IsDeleted == false && (x._id == entityObjectId || x.EntityObjectId == entityObjectId || x.UniqueId == entityObjectId)",
        params: { entityObjectId }
      });
      const operationResult = getOperationResult(entityObjectResult);
      if (operationResult?.Success === false || operationResult?.success === false) {
        return {
          entityObject: null,
          error: getOperationResultMessage(entityObjectResult, "Entity Object could not be loaded for this Entity Scope row.")
        };
      }

      const entityObject = getOperationItems(entityObjectResult).find((item) =>
        normalizeCompare(getId(item)) === normalizeCompare(entityObjectId)
        || normalizeCompare(getString(item, "EntityObjectId", "entityObjectId")) === normalizeCompare(entityObjectId)
        || normalizeCompare(getString(item, "UniqueId", "uniqueId")) === normalizeCompare(entityObjectId)
      ) || null;

      if (!entityObject) {
        return { entityObject: null, error: "Entity Object could not be loaded for this Entity Scope row." };
      }

      return { entityObject, error: "" };
    } catch (entityObjectError) {
      return {
        entityObject: null,
        error: entityObjectError?.message || "Entity Object could not be loaded for this Entity Scope row."
      };
    }
  }, [findDestinationEntityObjectForScope]);
  const openDestinationEntityDeploymentEntityAction = useCallback(async (entityDeployment, operationName) => {
    const entityObjectId = getString(entityDeployment, "EntityObjectId", "entityObjectId");
    const actionKey = operationName === ENTITY_OBJECT_FIELD_MAPPING_OPERATION ? "destination-entity-map" : "destination-entity-manage";
    setConfigurationActionState({ busyAction: `${actionKey}:${entityObjectId || ""}`, error: "" });

    const { entityObject, error } = await loadDestinationEntityObjectForScope(entityDeployment);
    if (!entityObject) {
      setConfigurationActionState({
        busyAction: "",
        error
      });
      return;
    }

    const opened = await runEntityObjectRedirectOperation(
      entityObjectMgrRef.current,
      entityObject,
      (message) => {
        if (message) setConfigurationActionState({ busyAction: "", error: message });
      },
      operationName
    );
    if (opened) setConfigurationActionState({ busyAction: "", error: "" });
  }, [loadDestinationEntityObjectForScope]);
  const openDestinationEntityDeploymentEntityManage = useCallback(
    (entityDeployment) => openDestinationEntityDeploymentEntityAction(entityDeployment, MODIFY_ENTITY_OPERATION),
    [openDestinationEntityDeploymentEntityAction]
  );
  const openDestinationEntityDeploymentEntityMap = useCallback(
    (entityDeployment) => openDestinationEntityDeploymentEntityAction(entityDeployment, ENTITY_OBJECT_FIELD_MAPPING_OPERATION),
    [openDestinationEntityDeploymentEntityAction]
  );

  const resolveDestinationEntitySearchWarehouseContext = useCallback((entityDeployment) => {
    const destination = destinationScreenState.destination || selectedDestinationLinkedRecord;
    const defaults = destinationScreenState.defaults || selectedDestinationDefaults;
    const deploymentId = getString(
      entityDeployment,
      "DeploymentId",
      "deploymentId",
      "SchemaDestinationDeploymentId",
      "schemaDestinationDeploymentId",
      "WarehouseDeploymentId",
      "warehouseDeploymentId"
    );
    const deployment = findDestinationDeploymentById(destination, defaults, deploymentId);
    const deploymentWarehouseId = getString(deployment, "WarehouseId", "warehouseId", "wId", "wid")
      || getString(entityDeployment, "WarehouseId", "warehouseId", "wId", "wid");
    const deploymentWarehouseDeploymentId = getString(deployment, "WarehouseDeploymentId", "warehouseDeploymentId", "wdId", "wdid")
      || getString(entityDeployment, "WarehouseDeploymentId", "warehouseDeploymentId", "wdId", "wdid");

    if (deploymentWarehouseId && deploymentWarehouseDeploymentId) {
      return {
        warehouseId: deploymentWarehouseId,
        warehouseDeploymentId: deploymentWarehouseDeploymentId
      };
    }

    if (normalizeCompare(defaults?.managerKey) === "warehouse") {
      const warehouseId = getId(destination) || selectedDestinationId;
      const warehouseDeploymentId = deploymentId || getDeploymentId(deployment);
      if (warehouseId && warehouseDeploymentId) {
        return {
          warehouseId,
          warehouseDeploymentId
        };
      }
    }

    return {
      warehouseId: "",
      warehouseDeploymentId: ""
    };
  }, [destinationScreenState.defaults, destinationScreenState.destination, selectedDestinationDefaults, selectedDestinationId, selectedDestinationLinkedRecord]);

  const openDestinationEntityDeploymentEntitySearch = useCallback(async (entityDeployment) => {
    const initialEntityObjectId = getString(entityDeployment, "EntityObjectId", "entityObjectId");
    setConfigurationActionState({ busyAction: `${ENTITY_OBJECT_SEARCH_ACTION}:${initialEntityObjectId || ""}`, error: "" });

    const { entityObject, error } = await loadDestinationEntityObjectForScope(entityDeployment);
    if (!entityObject) {
      setConfigurationActionState({
        busyAction: "",
        error
      });
      return null;
    }

    const initialExecutionMode = String(getString(entityDeployment, "ExecutionMode", "executionMode") || "") === "2"
      ? DESTINATION_ENTITY_SEARCH_EXECUTION_WAREHOUSE
      : DESTINATION_ENTITY_SEARCH_EXECUTION_SOURCE;

    setDestinationEntitySearchDialog({
      open: true,
      entityDeployment,
      entityObject,
      executionMode: initialExecutionMode,
      exportFormat: "none",
      warehouseContext: resolveDestinationEntitySearchWarehouseContext(entityDeployment),
      busy: false,
      error: ""
    });
    setConfigurationActionState({ busyAction: "", error: "" });
    return entityObject;
  }, [loadDestinationEntityObjectForScope, resolveDestinationEntitySearchWarehouseContext]);

  const closeDestinationEntitySearchDialog = useCallback(() => {
    setDestinationEntitySearchDialog((previous) => ({
      ...previous,
      open: false,
      busy: false,
      error: ""
    }));
  }, []);

  const updateDestinationEntitySearchDialog = useCallback((updates) => {
    setDestinationEntitySearchDialog((previous) => ({
      ...previous,
      ...updates,
      error: ""
    }));
  }, []);

  const runDestinationEntityDeploymentEntitySearch = useCallback(async () => {
    const { entityObject, executionMode, exportFormat, warehouseContext } = destinationEntitySearchDialog;
    const entityObjectId = getId(entityObject);
    const useWarehouse = executionMode === DESTINATION_ENTITY_SEARCH_EXECUTION_WAREHOUSE;
    const selectedExportFormat = exportFormat || "none";
    const activeEntityObjectManager = entityObjectMgrRef.current;

    if (!entityObjectId) {
      setDestinationEntitySearchDialog((previous) => ({
        ...previous,
        error: "Entity Object Search/Export could not start because the Entity Object is missing an id."
      }));
      return null;
    }
    if (!activeEntityObjectManager?.RunOperation) {
      setDestinationEntitySearchDialog((previous) => ({
        ...previous,
        error: "Entity Object operation service is not available."
      }));
      return null;
    }
    if (useWarehouse && (!warehouseContext?.warehouseId || !warehouseContext?.warehouseDeploymentId)) {
      setDestinationEntitySearchDialog((previous) => ({
        ...previous,
        error: "Warehouse mode requires a selected Warehouse and Warehouse Deployment on this destination."
      }));
      return null;
    }

    setDestinationEntitySearchDialog((previous) => ({ ...previous, busy: true, error: "" }));
    setConfigurationActionState({ busyAction: `${ENTITY_OBJECT_SEARCH_ACTION}:${entityObjectId}`, error: "" });
    try {
      const response = await activeEntityObjectManager.RunOperation({
        operationName: SEARCH_ENTITY_OBJECT_OPERATION,
        item: entityObject,
        queryParams: buildWorkspaceResourceQueryParams({
          id: entityObjectId,
          eoid: entityObjectId,
          export: selectedExportFormat,
          ...(useWarehouse ? {
            wId: warehouseContext.warehouseId,
            wdId: warehouseContext.warehouseDeploymentId,
            warehouseId: warehouseContext.warehouseId,
            warehouseDeploymentId: warehouseContext.warehouseDeploymentId
          } : {})
        }, entityObject),
        isItem: true
      });
      setRecentLogsRefreshKey((current) => current + 1);
      dispatchAiPanelCommandFromOperationResponse(response);
      setDestinationEntitySearchDialog({
        open: false,
        entityDeployment: null,
        entityObject: null,
        executionMode: DESTINATION_ENTITY_SEARCH_EXECUTION_SOURCE,
        exportFormat: "none",
        warehouseContext: null,
        busy: false,
        error: ""
      });
      setConfigurationActionState({ busyAction: "", error: "" });
      return response;
    } catch (searchError) {
      setDestinationEntitySearchDialog((previous) => ({
        ...previous,
        busy: false,
        error: searchError?.message || "Entity Object Search/Export could not be started."
      }));
      setConfigurationActionState({ busyAction: "", error: "" });
      return null;
    }
  }, [buildWorkspaceResourceQueryParams, destinationEntitySearchDialog]);

  useEffect(() => {
    let mounted = true;

    const loadLinkedIntegration = async () => {
      if (!isIntegrationConfigurationScreen) {
        setIntegrationScreenState((previous) => ({
          ...previous,
          linkedRecordId: "",
          integration: null,
          loading: false,
          error: ""
        }));
        return;
      }

      if (!selectedIntegrationId) {
        setIntegrationScreenState((previous) => ({
          ...previous,
          linkedRecordId: "",
          integration: null,
          loading: false,
          error: ""
        }));
        return;
      }

      if (!profileContextReady) {
        setIntegrationScreenState((previous) => ({
          ...previous,
          linkedRecordId: selectedIntegrationId,
          integration: null,
          loading: true,
          error: ""
        }));
        return;
      }

      const activeIntegrationMgr = integrationMgrRef.current;
      if (!activeIntegrationMgr?.getItem || !activeIntegrationMgr?.GetMetadata) {
        setIntegrationScreenState((previous) => ({
          ...previous,
          linkedRecordId: selectedIntegrationId,
          integration: null,
          loading: false,
          error: "Integration service is not available."
        }));
        return;
      }

      setIntegrationScreenState((previous) => ({
        ...previous,
        linkedRecordId: selectedIntegrationId,
        integration: null,
        loading: true,
        error: ""
      }));

      try {
        const [integrationResult, metadataResult] = await Promise.all([
          activeIntegrationMgr.getItem(selectedIntegrationId),
          activeIntegrationMgr.GetMetadata({ forceReload: true })
        ]);
        const loadedIntegration = getOperationItem(integrationResult) || firstItem(integrationResult?.Items) || null;
        const loadedMetadata = firstItem(metadataResult?.Items)
          || firstItem(metadataResult)
          || firstItem(activeIntegrationMgr.uiMetadata)
          || null;

        if (!mounted) return;
        setIntegrationScreenState((previous) => ({
          ...previous,
          linkedRecordId: selectedIntegrationId,
          integration: loadedIntegration,
          metadata: loadedMetadata,
          loading: false,
          error: loadedIntegration ? "" : "The linked Integration could not be loaded."
        }));
      } catch (loadIntegrationError) {
        if (!mounted) return;
        setIntegrationScreenState((previous) => ({
          ...previous,
          linkedRecordId: selectedIntegrationId,
          integration: null,
          loading: false,
          error: loadIntegrationError?.message || "The linked Integration could not be loaded."
        }));
      }
    };

    loadLinkedIntegration();

    return () => {
      mounted = false;
    };
  }, [isIntegrationConfigurationScreen, profileContextReady, selectedIntegrationId]);

  useEffect(() => {
    let mounted = true;

    const loadLinkedSourceSystem = async () => {
      if (!isSourceSystemConfigurationScreen) {
        setSourceSystemScreenState((previous) => ({
          ...previous,
          linkedRecordId: "",
          sourceSystem: null,
          sourceTargetMetadata: null,
          loading: false,
          error: ""
        }));
        return;
      }

      if (!selectedSourceSystemId) {
        setSourceSystemScreenState((previous) => ({
          ...previous,
          linkedRecordId: "",
          sourceSystem: null,
          sourceTargetMetadata: null,
          sourceObjectMetadata: null,
          sourceObjects: [],
          loading: false,
          error: ""
        }));
        return;
      }

      if (!profileContextReady) {
        setSourceSystemScreenState((previous) => ({
          ...previous,
          linkedRecordId: selectedSourceSystemId,
          sourceSystem: null,
          sourceTargetMetadata: null,
          sourceObjectMetadata: null,
          sourceObjects: [],
          loading: true,
          error: ""
        }));
        return;
      }

      const activeSourceSystemMgr = sourceSystemMgrRef.current;
      const activeSourceObjectMgr = sourceObjectMgrRef.current;
      if (!activeSourceSystemMgr?.GetMetadata || (!activeSourceSystemMgr?.getItem && !activeSourceSystemMgr?.Query)) {
        setSourceSystemScreenState((previous) => ({
          ...previous,
          linkedRecordId: selectedSourceSystemId,
          sourceSystem: null,
          sourceTargetMetadata: null,
          sourceObjectMetadata: null,
          sourceObjects: [],
          loading: false,
          error: "Source System service is not available."
        }));
        return;
      }

      if (!activeSourceObjectMgr?.Query || !activeSourceObjectMgr?.GetMetadata) {
        setSourceSystemScreenState((previous) => ({
          ...previous,
          linkedRecordId: selectedSourceSystemId,
          sourceSystem: null,
          sourceTargetMetadata: null,
          sourceObjectMetadata: null,
          sourceObjects: [],
          loading: false,
          error: "Source Object service is not available."
        }));
        return;
      }

      setSourceSystemScreenState((previous) => ({
        ...previous,
        linkedRecordId: selectedSourceSystemId,
        sourceSystem: null,
        sourceObjects: [],
        loading: true,
        error: ""
      }));

      try {
        const sourceSystemPromise = activeSourceSystemMgr.getItem
          ? activeSourceSystemMgr.getItem(selectedSourceSystemId)
          : activeSourceSystemMgr.Query({ model: "x => x.IsDeleted == false" });
        const [
          sourceSystemResult,
          sourceSystemMetadataResult,
          sourceObjectResult,
          sourceObjectMetadataResult
        ] = await Promise.all([
          sourceSystemPromise,
          activeSourceSystemMgr.GetMetadata({ forceReload: true }),
          activeSourceObjectMgr.Query({
            model: "x => x.IsDeleted == false && x.SourceSystemId == sourceSystemId",
            params: { sourceSystemId: selectedSourceSystemId }
          }),
          activeSourceObjectMgr.GetMetadata({ forceReload: true })
        ]);

        const sourceSystemItems = getOperationItems(sourceSystemResult);
        const loadedSourceSystem = getOperationItem(sourceSystemResult)
          || sourceSystemItems.find((item) => normalizeCompare(getId(item)) === normalizeCompare(selectedSourceSystemId))
          || null;
        const sourceSystemMetadataItems = getOperationItems(sourceSystemMetadataResult);
        const loadedSourceSystemMetadata = sourceSystemMetadataItems.find((item) => normalizeCompare(getString(item, "Name", "name", "ItemName", "itemName")) === "sourcesystem")
          || firstItem(sourceSystemMetadataResult?.Items)
          || firstItem(sourceSystemMetadataResult)
          || firstItem(activeSourceSystemMgr.uiMetadata)
          || null;
        const loadedSourceTargetMetadata = resolveSourceTargetMetadata(sourceSystemMetadataResult)
          || resolveSourceTargetMetadata(activeSourceSystemMgr.uiMetadata);
        const sourceObjectMetadataItems = getOperationItems(sourceObjectMetadataResult);
        const loadedSourceObjectMetadata = sourceObjectMetadataItems.find((item) => normalizeCompare(getString(item, "Name", "name", "ItemName", "itemName")) === "sourceobject")
          || firstItem(sourceObjectMetadataResult?.Items)
          || firstItem(sourceObjectMetadataResult)
          || firstItem(activeSourceObjectMgr.uiMetadata)
          || null;
        const loadedSourceObjects = getUniqueRowsByKey(
          getOperationItems(sourceObjectResult)
            .filter((item) => normalizeCompare(getString(item, "SourceSystemId", "sourceSystemId")) === normalizeCompare(selectedSourceSystemId)),
          getSourceObjectIdentityKey
        );

        if (!mounted) return;
        setSourceSystemScreenState((previous) => ({
          ...previous,
          linkedRecordId: selectedSourceSystemId,
          sourceSystem: loadedSourceSystem
            ? {
              ...loadedSourceSystem,
              SourceObjectTargets: getSourceSystemTargets(loadedSourceSystem)
            }
            : null,
          metadata: loadedSourceSystemMetadata,
          sourceTargetMetadata: loadedSourceTargetMetadata,
          sourceObjectMetadata: loadedSourceObjectMetadata,
          sourceObjects: loadedSourceObjects,
          loading: false,
          error: loadedSourceSystem ? "" : "The linked Source System could not be loaded."
        }));
      } catch (loadSourceSystemError) {
        if (!mounted) return;
        setSourceSystemScreenState((previous) => ({
          ...previous,
          linkedRecordId: selectedSourceSystemId,
          sourceSystem: null,
          sourceTargetMetadata: null,
          sourceObjectMetadata: null,
          sourceObjects: [],
          loading: false,
          error: loadSourceSystemError?.message || "The linked Source System could not be loaded."
        }));
      }
    };

    loadLinkedSourceSystem();

    return () => {
      mounted = false;
    };
  }, [isSourceSystemConfigurationScreen, profileContextReady, selectedSourceSystemId, sourceSystemRefreshKey]);

  useEffect(() => {
    let mounted = true;
    const isSchemaWorkspaceScreen = isEntitySchemaConfigurationScreen;

    const loadLinkedSchema = async () => {
      if (!isSchemaWorkspaceScreen) {
        setSchemaScreenState({
          linkedRecordId: "",
          schema: null,
          metadata: null,
          selectedSchemaVersionId: "",
          activeSchemaVersionId: "",
          entityObjects: [],
          sourceObjects: [],
          sourceObjectError: "",
          loading: false,
          error: ""
        });
        return;
      }

      if (!selectedSchemaId) {
        setSchemaScreenState({
          linkedRecordId: "",
          schema: null,
          metadata: null,
          selectedSchemaVersionId: "",
          activeSchemaVersionId: "",
          entityObjects: [],
          sourceObjects: [],
          sourceObjectError: "",
          loading: false,
          error: "No Entity Schema is linked to this Resource Configuration."
        });
        return;
      }

      if (!profileContextReady) {
        setSchemaScreenState((previous) => ({
          ...previous,
          linkedRecordId: selectedSchemaId,
          schema: null,
          metadata: null,
          selectedSchemaVersionId: "",
          activeSchemaVersionId: "",
          entityObjects: [],
          sourceObjects: [],
          sourceObjectError: "",
          loading: true,
          error: ""
        }));
        return;
      }

      const activeSchemaMgr = schemaMgrRef.current;
      const activeEntityObjectMgr = entityObjectMgrRef.current;
      if (!activeSchemaMgr?.Query || !activeEntityObjectMgr?.Query) {
        setSchemaScreenState((previous) => ({
          ...previous,
          linkedRecordId: selectedSchemaId,
          schema: null,
          metadata: null,
          selectedSchemaVersionId: "",
          activeSchemaVersionId: "",
          entityObjects: [],
          sourceObjects: [],
          sourceObjectError: "",
          loading: false,
          error: "Entity Schema workspace services are not available."
        }));
        return;
      }

      setSchemaScreenState((previous) => ({
        ...previous,
        linkedRecordId: selectedSchemaId,
        loading: true,
        error: "",
        sourceObjectError: ""
      }));

      try {
        const [workspaceState, schemaMetadataResult] = await Promise.all([
          loadLinkedEntitySchemaWorkspaceState({
            schemaRecord: selectedSchemaLinkedRecord,
            schemaManager: activeSchemaMgr,
            entityObjectManager: activeEntityObjectMgr,
            sourceObjectManager: sourceObjectMgrRef.current,
            selectedSchemaVersionId
          }),
          activeSchemaMgr.GetMetadata ? activeSchemaMgr.GetMetadata({ forceReload: true }) : Promise.resolve(null)
        ]);
        const schemaMetadataItems = getOperationItems(schemaMetadataResult);
        const loadedSchemaMetadata = schemaMetadataItems.find((item) => normalizeCompare(getString(item, "Name", "name", "ItemName", "itemName")) === "schema")
          || firstItem(schemaMetadataResult?.Items)
          || firstItem(schemaMetadataResult)
          || firstItem(activeSchemaMgr.uiMetadata)
          || null;

        if (!mounted) return;
        setSchemaScreenState({
          ...workspaceState,
          linkedRecordId: selectedSchemaId,
          metadata: loadedSchemaMetadata,
          loading: false
        });
      } catch (loadSchemaError) {
        if (!mounted) return;
        setSchemaScreenState({
          linkedRecordId: selectedSchemaId,
          schema: null,
          metadata: null,
          selectedSchemaVersionId: "",
          activeSchemaVersionId: "",
          entityObjects: [],
          sourceObjects: [],
          sourceObjectError: "",
          loading: false,
          error: loadSchemaError?.message || "Entity Schema workspace could not be loaded."
        });
      }
    };

    loadLinkedSchema();

    return () => {
      mounted = false;
    };
  }, [
    isEntitySchemaConfigurationScreen,
    profileContextReady,
    schemaRefreshKey,
    selectedSchemaId,
    selectedSchemaVersionId,
    selectedSchemaLinkedRecord
  ]);

  useEffect(() => {
    if (!isSourceSystemConfigurationScreen || typeof window === "undefined") return undefined;

    const handleSourceObjectRefresh = (event) => {
      const detail = event?.detail ?? {};
      if (detail.itemName !== "SourceObject") return;
      const detailSourceSystemId = detail.sourceSystemId || detail.SourceSystemId || "";
      if (detailSourceSystemId && selectedSourceSystemId && normalizeCompare(detailSourceSystemId) !== normalizeCompare(selectedSourceSystemId)) {
        return;
      }
      event.preventDefault?.();
      setSourceSystemRefreshKey((current) => current + 1);
    };

    window.addEventListener(AI_PANEL_GRID_REFRESH_EVENT, handleSourceObjectRefresh);
    return () => window.removeEventListener(AI_PANEL_GRID_REFRESH_EVENT, handleSourceObjectRefresh);
  }, [isSourceSystemConfigurationScreen, selectedSourceSystemId]);

  const resourceNavigationItems = useMemo(() => {
    if (!selectedResource?.id) return [];

    const hasManageableConfiguration = configurations.some(isConfigurationComplete);
    const selectedResourceDestinationDefaults = getDestinationResourceDefaults(selectedResource?.productResource);
    const selectedNavigationDestinationId = selectedDestinationRouteId || selectedDestinationId;
    const resourceAccessDenied = selectedResource.hasAccess === false;

    const mapNavigationItem = (navigation, index = 0, parentKey = "") => {
      const isConfigurationChild = parentKey === CONFIGURATIONS_NAVIGATION.key;
      const isConfigurationManageNavigation = isConfigurationChild
        && navigation.key === CONFIGURATION_MANAGE_NAVIGATION.key;
      const requiresSelectedEndpoint = isConfigurationChild
        && selectedResourceDestinationDefaults?.isEndpoint
        && [DESTINATION_SCREEN_KEY, DESTINATION_DEPLOYMENTS_SCREEN_KEY].includes(navigation.key);
      const isConfigurationChildDisabled = isConfigurationChild && (
        (isConfigurationManageNavigation && configurations.length > 0 && !hasManageableConfiguration)
        || (!isConfigurationManageNavigation && !isConfigurationActive(selectedConfiguration))
        || (requiresSelectedEndpoint && !selectedNavigationDestinationId)
      );
      const isConfigurationsNavigation = !parentKey
        && navigation.key === CONFIGURATIONS_NAVIGATION.key;
      const isResourceAccessDisabled = resourceAccessDenied && (
        isConfigurationChild
          ? !isConfigurationManageNavigation
          : !isConfigurationsNavigation
      );
      const routePath = isConfigurationChild
        ? buildConfigurationNavigationPath(
            selectedResource.id,
            navigation.key,
            configurationRoute.resourceConfigurationId,
            selectedNavigationDestinationId
          )
        : buildResourceNavigationPath(selectedResource.id, navigation.key);
      const activeRoutePaths = isConfigurationChild
        ? buildConfigurationNavigationActivePaths(
            selectedResource.id,
            navigation.key,
            configurationRoute.resourceConfigurationId
          )
        : buildResourceNavigationActivePaths(selectedResource.id, navigation.key);

      return {
        _id: `resource-workspace-${selectedResource.id}-${parentKey ? `${parentKey}-` : ""}${navigation.key}`,
        Key: `resource-workspace-${selectedResource.id}-${parentKey ? `${parentKey}-` : ""}${navigation.key}`,
        DisplayName: navigation.displayName,
        Icon: navigation.icon || RESOURCE_FALLBACK_ICON,
        RoutePath: routePath,
        ActiveRoutePaths: activeRoutePaths,
        Disabled: isResourceAccessDisabled || (!resourceAccessDenied && isConfigurationChildDisabled),
        Sequence: navigation.sequence ?? index + 1,
        Placement: parentKey ? undefined : "child",
        SubNavigations: (navigation.subNavigations || []).map((subNavigation, subIndex) =>
          mapNavigationItem(subNavigation, subIndex, navigation.key)
        )
      };
    };

    return resourceNavigations.map((navigation, index) => mapNavigationItem(navigation, index));
  }, [
    configurationRoute.resourceConfigurationId,
    configurations,
    resourceNavigations,
    selectedConfiguration,
    selectedDestinationId,
    selectedDestinationRouteId,
    selectedResource?.hasAccess,
    selectedResource?.productResource,
    selectedResource?.id
  ]);
  const openConfigurationSetup = useCallback((configuration) => {
    if (!canConfigureResource || selectedResource?.hasAccess === false || !isClientResourceSetupEnabled(selectedResource)) return;
    const resourceConfigurationId = getId(configuration);
    const setupClientResourceId = selectedResource?.id || clientResourceId;
    if (!setupClientResourceId || !resourceConfigurationId) return;

    navigate(buildResourceConfigurePath(setupClientResourceId, resourceConfigurationId));
  }, [canConfigureResource, clientResourceId, navigate, selectedResource]);
  const openResourcePurchaseDialog = useCallback(() => {
    if (!canAddMarketplaceResource || selectedResource?.hasAccess === false || !selectedMarketplaceResource) return;
    setPurchaseDialogResource(selectedMarketplaceResource);
    setPurchaseDialogOfferKey(selectedMarketplaceResource.selectedProductOfferKey || "");
  }, [canAddMarketplaceResource, selectedMarketplaceResource, selectedResource?.hasAccess]);
  const openNewConfigurationSetup = useCallback(() => {
    if (!canConfigureResource || selectedResource?.hasAccess === false || !isClientResourceSetupEnabled(selectedResource)) return;
    const setupClientResourceId = selectedResource?.id || clientResourceId;
    if (!setupClientResourceId) return;

    navigate(`${buildResourceConfigurePath(setupClientResourceId)}?new=1`);
  }, [canConfigureResource, clientResourceId, navigate, selectedResource]);
  const openResourceConfigurationImportDialog = useCallback(async () => {
    if (!canConfigureResource || selectedResource?.hasAccess === false || !isClientResourceSetupEnabled(selectedResource) || !resourceConfigurationImportSupport) return;

    const offerRows = buildResourceImportOfferCardRows(selectedResource);
    const firstOfferId = offerRows.find((offer) => offer.ClientProductOfferId)?.ClientProductOfferId || "";
    setResourceConfigurationImportDialog({
      open: true,
      support: resourceConfigurationImportSupport,
      schemaId: "",
      clientProductOfferId: firstOfferId,
      schemas: [],
      loadingSchemas: true,
      busy: false,
      error: ""
    });

    try {
      const activeSchemaMgr = schemaMgrRef.current;
      if (!activeSchemaMgr?.Query) {
        throw new Error("Entity Schema service is not available.");
      }

      const response = await activeSchemaMgr.Query({ params: { isActive: true } });
      const schemas = getOperationItems(response);
      setResourceConfigurationImportDialog((previous) => ({
        ...previous,
        schemas,
        loadingSchemas: false,
        error: ""
      }));
    } catch (loadSchemasError) {
      setResourceConfigurationImportDialog((previous) => ({
        ...previous,
        loadingSchemas: false,
        error: loadSchemasError?.message || "Entity Schemas could not be loaded."
      }));
    }
  }, [canConfigureResource, resourceConfigurationImportSupport, selectedResource]);
  const closeResourceConfigurationImportDialog = useCallback(() => {
    setResourceConfigurationImportDialog((previous) => {
      if (previous.busy) return previous;
      return {
        open: false,
        support: null,
        schemaId: "",
        clientProductOfferId: "",
        schemas: [],
        loadingSchemas: false,
        busy: false,
        error: ""
      };
    });
  }, []);
  const updateResourceConfigurationImportDialog = useCallback((updates) => {
    setResourceConfigurationImportDialog((previous) => ({
      ...previous,
      ...updates,
      error: ""
    }));
  }, []);
  const confirmResourceConfigurationImport = useCallback(async () => {
    const setupClientResourceId = selectedResource?.id || clientResourceId;
    const support = resourceConfigurationImportDialog.support || resourceConfigurationImportSupport;
    const schemaId = resourceConfigurationImportDialog.schemaId;
    const clientProductOfferId = resourceConfigurationImportDialog.clientProductOfferId;

    if (!setupClientResourceId || !support) return;

    if (!schemaId || !clientProductOfferId) {
      setResourceConfigurationImportDialog((previous) => ({
        ...previous,
        error: "Select an Entity Schema and Product Offer allowance."
      }));
      return;
    }

    try {
      const activeResourceConfigurationMgr = resourceConfigurationMgrRef.current;
      if (!activeResourceConfigurationMgr?.RunOperation) {
        throw new Error("Resource configuration service is not available.");
      }

      const requestItem = {
        ClientResourceId: setupClientResourceId,
        AllowanceClientProductOfferId: clientProductOfferId,
        LinkedRecords: [
          {
            ItemName: "Schema",
            Relationship: "EntitySchema",
            ItemId: schemaId
          }
        ]
      };

      setResourceConfigurationImportDialog((previous) => ({
        ...previous,
        busy: true,
        error: ""
      }));

      const response = await activeResourceConfigurationMgr.RunOperation({
        operationName: support.operationName,
        item: requestItem,
        queryParams: buildWorkspaceResourceQueryParams({
          clientResourceId: setupClientResourceId,
          schemaId,
          clientProductOfferId
        }, requestItem, requestItem),
        isItem: true,
        isWarning: true,
        supressAllErrors: true
      });
      const result = getOperationResult(response);
      if (result?.Success === false || result?.success === false) {
        throw new Error(getOperationResultMessage(response, "Resource Configuration could not be imported."));
      }

      const importedConfiguration = getOperationItem(response);
      const importedConfigurationId = getId(importedConfiguration);
      setResourceConfigurationImportDialog({
        open: false,
        support: null,
        schemaId: "",
        clientProductOfferId: "",
        schemas: [],
        loadingSchemas: false,
        busy: false,
        error: ""
      });
      await loadCatalog();
      if (importedConfigurationId) {
        navigate(buildSelectedConfigurationPath(setupClientResourceId, importedConfigurationId, CONFIGURATION_MANAGE_NAVIGATION.key));
      }
    } catch (importError) {
      setResourceConfigurationImportDialog((previous) => ({
        ...previous,
        busy: false,
        error: importError?.message || "Resource Configuration could not be imported."
      }));
    }
  }, [
    buildWorkspaceResourceQueryParams,
    clientResourceId,
    loadCatalog,
    navigate,
    resourceConfigurationImportDialog.clientProductOfferId,
    resourceConfigurationImportDialog.schemaId,
    resourceConfigurationImportDialog.support,
    resourceConfigurationImportSupport,
    selectedResource?.id
  ]);
  const selectConfigurationScreen = useCallback((resourceConfigurationId, screenKey = CONFIGURATION_MANAGE_NAVIGATION.key, linkedRecord = null) => {
    const setupClientResourceId = selectedResource?.id || clientResourceId;
    if (!setupClientResourceId) return;

    if (!resourceConfigurationId) {
      navigate(buildResourceConfigurationManagePath(setupClientResourceId));
      return;
    }

    if (isClientMcpServerLinkedRecord(linkedRecord)) {
      const linkedConfigurationId = getLinkedRecordItemId(linkedRecord);
      const linkedConfiguration = catalogModel.resourceConfigurations.find((candidate) =>
        normalizeCompare(getId(candidate)) === normalizeCompare(linkedConfigurationId));
      const linkedClientResource = findClientResourceForConfiguration(catalogModel.clientResources, linkedConfiguration);
      const linkedClientResourceId = linkedClientResource?.id || getString(linkedConfiguration, "ClientResourceId", "clientResourceId");
      if (linkedClientResourceId && linkedConfigurationId) {
        navigate(buildSelectedConfigurationPath(linkedClientResourceId, linkedConfigurationId, screenKey || MCP_TOOLS_OVERVIEW_SCREEN_KEY));
        return;
      }
    }

    const destinationId = getLinkedRecordItemId(linkedRecord)
      || (screenKey !== CONFIGURATION_MANAGE_NAVIGATION.key ? selectedDestinationRouteId || selectedDestinationId : "");
    navigate(withSelectedDestinationPath(
      buildSelectedConfigurationPath(setupClientResourceId, resourceConfigurationId, screenKey),
      destinationId
    ));
  }, [catalogModel.clientResources, catalogModel.resourceConfigurations, clientResourceId, navigate, selectedDestinationId, selectedDestinationRouteId, selectedResource?.id]);
  const openEntitySchemaConfigurationFromScope = useCallback((scope) => {
    const schemaId = getString(scope, "SchemaId", "schemaId") || getDestinationSchemaId(destinationScreenState?.destination);
    const schemaConfiguration = findConfigurationByLinkedEntitySchemaId(catalogModel.resourceConfigurations, schemaId);
    const schemaClientResource = findClientResourceForConfiguration(catalogModel.clientResources, schemaConfiguration);
    const schemaClientResourceId = schemaClientResource?.id || getString(schemaConfiguration, "ClientResourceId", "clientResourceId");
    const schemaConfigurationId = getId(schemaConfiguration);

    if (!schemaClientResourceId || !schemaConfigurationId) {
      setConfigurationActionState({
        busyAction: "",
        error: "No Entity Schema resource configuration is linked to this schema."
      });
      return;
    }

    navigate(buildSelectedConfigurationPath(schemaClientResourceId, schemaConfigurationId, ENTITY_SCHEMA_SCREEN_KEY));
  }, [
    catalogModel.clientResources,
    catalogModel.resourceConfigurations,
    destinationScreenState?.destination,
    navigate,
    setConfigurationActionState
  ]);
  const openSelectedDestinationDeploymentScreen = useCallback(() => {
    const setupClientResourceId = selectedResource?.id || clientResourceId;
    const resourceConfigurationId = configurationRoute.resourceConfigurationId || getId(selectedConfiguration);
    const deploymentId = destinationDeploymentRouteFilters?.deploymentId || "";
    if (!setupClientResourceId || !resourceConfigurationId || !deploymentId) return;

    navigate(buildDestinationDeploymentScreenPath(setupClientResourceId, resourceConfigurationId, { deploymentId }));
  }, [
    clientResourceId,
    configurationRoute.resourceConfigurationId,
    destinationDeploymentRouteFilters?.deploymentId,
    navigate,
    selectedConfiguration,
    selectedResource?.id
  ]);
  const openConfigurationManage = useCallback((configuration) => {
    const setupClientResourceId = selectedResource?.id || clientResourceId;
    const resourceConfigurationId = getId(configuration);
    if (!setupClientResourceId || !resourceConfigurationId) return;

    navigate(buildSelectedConfigurationPath(setupClientResourceId, resourceConfigurationId, CONFIGURATION_MANAGE_NAVIGATION.key));
  }, [clientResourceId, navigate, selectedResource?.id]);
  const openDeleteConfigurationDialog = useCallback((configuration) => {
    setDeleteConfigurationTarget(configuration);
    setDeleteConfigurationConfirmText("");
    setDeleteConfigurationState({ busy: false, error: "" });
  }, []);
  const openEditConfigurationPanel = useCallback((configuration) => {
    if (!configuration) return;

    setEditConfigurationPanelData({
      ...configuration,
      DisplayName: getDisplayName(configuration, "Resource configuration"),
      Description: getString(configuration, "Description", "description")
    });
    setEditConfigurationPanelOpen(true);
    setConfigurationActionState({ busyAction: "", error: "" });
  }, []);
  const closeEditConfigurationPanel = useCallback(() => {
    setEditConfigurationPanelOpen(false);
    setEditConfigurationPanelData(null);
  }, []);
  const closeDeleteConfigurationDialog = useCallback(() => {
    if (deleteConfigurationState.busy) return;
    setDeleteConfigurationTarget(null);
    setDeleteConfigurationConfirmText("");
    setDeleteConfigurationState({ busy: false, error: "" });
  }, [deleteConfigurationState.busy]);
  const runConfigurationOperation = useCallback(async (
    configuration,
    operationName,
    actionKey,
    fallbackErrorMessage
  ) => {
    if (!configuration || !operationName) return;

    try {
      const activeResourceConfigurationMgr = resourceConfigurationMgrRef.current;
      if (!activeResourceConfigurationMgr?.RunOperation) {
        throw new Error("Resource configuration service is not available.");
      }

      setConfigurationOperationResultDialog(null);
      setConfigurationActionState({ busyAction: actionKey, error: "", message: "" });
      const response = await activeResourceConfigurationMgr.RunOperation({
        operationName,
        item: configuration,
        queryParams: buildWorkspaceResourceQueryParams(null, configuration, configuration),
        isItem: true,
        isWarning: true,
        supressAllErrors: false
      });
      const result = getOperationResult(response);
      if (result?.Success === false || result?.success === false) {
        throw new Error(getOperationResultMessage(response, fallbackErrorMessage));
      }

      const successMessage = getOperationResultMessage(response, "Resource Configuration updated.");
      setConfigurationActionState({
        busyAction: "",
        error: "",
        message: successMessage
      });
      window.setTimeout(() => {
        setConfigurationActionState((current) => current.message === successMessage
          ? { ...current, message: "" }
          : current);
      }, 5500);
      await loadCatalog();
      return response;
    } catch (operationError) {
      const errorMessage = operationError?.message || fallbackErrorMessage;
      setConfigurationActionState({
        busyAction: "",
        error: errorMessage,
        message: ""
      });
      setConfigurationOperationResultDialog({
        status: "error",
        title: "Resource Configuration Operation Failed",
        message: errorMessage
      });
      return undefined;
    }
  }, [buildWorkspaceResourceQueryParams, loadCatalog]);
  const validateConfigurationReadiness = useCallback((configuration) => {
    runConfigurationOperation(
      configuration,
      INBOUND_EDI_READINESS_OPERATION,
      `validate:${getId(configuration)}`,
      "Resource Configuration readiness could not be validated."
    );
  }, [runConfigurationOperation]);
  const activateConfiguration = useCallback((configuration) => {
    runConfigurationOperation(
      configuration,
      Operations.ResourceConfigurationItem?.ResourceActivationOperation || "ResourceActivationOperation",
      `activate:${getId(configuration)}`,
      "Resource Configuration could not be activated."
    );
  }, [runConfigurationOperation]);
  const requestDeactivateConfiguration = useCallback((configuration) => {
    setConfigurationActionState({ busyAction: "", error: "", message: "" });
    setDeactivateConfigurationTarget(configuration || null);
  }, []);
  const closeDeactivateConfigurationDialog = useCallback(() => {
    if (configurationActionState.busyAction?.startsWith("deactivate:")) return;
    setDeactivateConfigurationTarget(null);
  }, [configurationActionState.busyAction]);
  const confirmDeactivateConfiguration = useCallback(async () => {
    const configuration = deactivateConfigurationTarget;
    if (!configuration) return;
    const response = await runConfigurationOperation(
      configuration,
      Operations.ResourceConfigurationItem?.ResourceDeactivationOperation || "ResourceDeactivationOperation",
      `deactivate:${getId(configuration)}`,
      "Resource Configuration could not be deactivated."
    );
    if (response) setDeactivateConfigurationTarget(null);
  }, [deactivateConfigurationTarget, runConfigurationOperation]);
  const reconfigureConfiguration = useCallback((configuration) => {
    runConfigurationOperation(
      configuration,
      RESOURCE_CONFIGURATION_RECONFIGURE_OPERATION,
      `reconfigure:${getId(configuration)}`,
      "Resource Configuration could not be reconfigured."
    );
  }, [runConfigurationOperation]);
  const saveConfigurationDetails = useCallback(async (updatedConfiguration) => {
    const payload = {
      ...editConfigurationPanelData,
      ...(updatedConfiguration || {})
    };
    const configurationId = getId(payload);
    const displayName = getString(payload, "DisplayName", "displayName");
    if (!configurationId || !displayName) {
      setConfigurationActionState({
        busyAction: "",
        error: "Resource Configuration name is required."
      });
      return;
    }

    try {
      const activeResourceConfigurationMgr = resourceConfigurationMgrRef.current;
      if (!activeResourceConfigurationMgr?.RunOperation) {
        throw new Error("Resource configuration service is not available.");
      }

      setConfigurationActionState({ busyAction: `edit:${configurationId}`, error: "" });
      const requestItem = {
        ...payload,
        DisplayName: displayName,
        Description: getString(payload, "Description", "description")
      };
      const response = await activeResourceConfigurationMgr.RunOperation({
        operationName: Operations.ResourceConfigurationItem.UpdateResourceConfigurationDetails,
        item: requestItem,
        queryParams: buildWorkspaceResourceQueryParams(null, requestItem, requestItem),
        isItem: true,
        isWarning: true,
        supressAllErrors: false
      });
      const result = getOperationResult(response);
      if (result?.Success === false || result?.success === false) {
        throw new Error(result?.Message || result?.message || "Resource Configuration could not be saved.");
      }

      setConfigurationActionState({ busyAction: "", error: "" });
      closeEditConfigurationPanel();
      loadCatalog();
      return response;
    } catch (saveError) {
      setConfigurationActionState({
        busyAction: "",
        error: saveError?.message || "Resource Configuration could not be saved."
      });
      return undefined;
    }
  }, [buildWorkspaceResourceQueryParams, closeEditConfigurationPanel, editConfigurationPanelData, loadCatalog]);
  const closeConnectionResultDialog = useCallback(() => {
    setConnectionResultDialog(null);
  }, []);
  const openEditIntegrationPanel = useCallback(() => {
    const integration = integrationScreenState.integration;
    if (!getId(integration)) {
      setConfigurationActionState({
        busyAction: "",
        error: "No linked Integration is available to edit."
      });
      return;
    }
    if (!integrationScreenState.metadata) {
      setConfigurationActionState({
        busyAction: "",
        error: "Integration metadata is not available yet."
      });
      return;
    }

    setEditIntegrationPanelData({
      ...integration,
      IsNew: false
    });
    setEditIntegrationPanelOpen(true);
    setConfigurationActionState({ busyAction: "", error: "" });
  }, [integrationScreenState.integration, integrationScreenState.metadata]);
  const closeEditIntegrationPanel = useCallback(() => {
    setEditIntegrationPanelOpen(false);
    setEditIntegrationPanelData(null);
  }, []);
  const saveIntegrationDetails = useCallback(async (updatedIntegration) => {
    const integrationId = getId(updatedIntegration) || getId(editIntegrationPanelData);
    if (!integrationId) {
      setConfigurationActionState({
        busyAction: "",
        error: "Integration could not be saved because it is missing an id."
      });
      return undefined;
    }

    try {
      const activeIntegrationMgr = integrationMgrRef.current;
      if (!activeIntegrationMgr?.saveItem) {
        throw new Error("Integration service is not available.");
      }

      const payload = {
        ...editIntegrationPanelData,
        ...(updatedIntegration || {}),
        IsNew: false
      };

      setConfigurationActionState({ busyAction: `integration-edit:${integrationId}`, error: "" });
      const response = await activeIntegrationMgr.saveItem(payload);
      const result = getOperationResult(response);
      if (result?.Success === false || result?.success === false) {
        throw new Error(result?.Message || result?.message || "Integration could not be saved.");
      }

      const savedIntegration = getOperationItem(response)
        || firstItem(response?.Items)
        || payload;
      setIntegrationScreenState((previous) => ({
        ...previous,
        integration: savedIntegration,
        error: ""
      }));
      setConfigurationActionState({ busyAction: "", error: "" });
      closeEditIntegrationPanel();
      loadCatalog();
      return response;
    } catch (saveIntegrationError) {
      setConfigurationActionState({
        busyAction: "",
        error: saveIntegrationError?.message || "Integration could not be saved."
      });
      return undefined;
    }
  }, [closeEditIntegrationPanel, editIntegrationPanelData, loadCatalog]);
  const runIntegrationConnectionCheck = useCallback(async () => {
    const integration = integrationScreenState.integration;
    const integrationId = getId(integration);
    const configurationId = getId(selectedConfiguration);
    if (!integrationId) {
      setConfigurationActionState({
        busyAction: "",
        error: "No linked Integration is available to check."
      });
      return;
    }

    try {
      const useInboundEdiConnectionValidation = isInboundEdiResource(selectedResource?.productResource, selectedResource, selectedConfiguration);
      const activeManager = useInboundEdiConnectionValidation
        ? resourceConfigurationMgrRef.current
        : integrationMgrRef.current;
      if (!activeManager?.RunOperation) {
        throw new Error(useInboundEdiConnectionValidation
          ? "Resource Configuration management service is not available."
          : "Integration service is not available.");
      }

      const operationItem = useInboundEdiConnectionValidation ? selectedConfiguration : integration;
      const operationQueryParams = useInboundEdiConnectionValidation
        ? buildResourceOperationQueryParams({
          item: selectedConfiguration,
          resourceConfiguration: selectedConfiguration,
          clientResourceId: selectedResource?.id || clientResourceId,
          resourceConfigurationId: configurationId,
          queryParams: {
            integrationId
          }
        })
        : buildWorkspaceResourceQueryParams({
          clientResourceId: selectedResource?.id || clientResourceId,
          resourceConfigurationId: configurationId
        }, integration);
      setConfigurationActionState({ busyAction: `integration-check:${integrationId}`, error: "" });
      const response = await activeManager.RunOperation({
        operationName: useInboundEdiConnectionValidation ? INBOUND_EDI_CONNECTION_OPERATION : Operations.IntegrationItem.ConnectionCheck,
        item: operationItem,
        queryParams: operationQueryParams,
        isItem: true,
        isWarning: false,
        supressAllErrors: true
      });
      const result = getOperationResult(response);
      if (result?.Success === false || result?.success === false) {
        const message = getOperationResultMessage(response, "Connection check failed.");
        setConfigurationActionState({
          busyAction: "",
          error: message
        });
        setConnectionResultDialog({
          status: "error",
          title: "Connection Check Failed",
          message,
          details: getOperationResultDetails(response)
        });
        setRecentLogsRefreshKey((current) => current + 1);
        return;
      }

      setConfigurationActionState({ busyAction: "", error: "" });
      setConnectionResultDialog({
        status: "success",
        title: "Connection Check Complete",
        message: getOperationResultMessage(response, "Connection check completed successfully."),
        details: getOperationResultDetails(response)
      });
      setRecentLogsRefreshKey((current) => current + 1);
    } catch (checkError) {
      const message = checkError?.message || "Connection check failed.";
      setConfigurationActionState({
        busyAction: "",
        error: message
      });
      setConnectionResultDialog({
        status: "error",
        title: "Connection Check Failed",
        message,
        details: []
      });
      setRecentLogsRefreshKey((current) => current + 1);
    }
  }, [buildWorkspaceResourceQueryParams, clientResourceId, integrationScreenState.integration, selectedConfiguration, selectedResource]);
  const setIntegrationOnline = useCallback(async (isOnline) => {
    const integration = integrationScreenState.integration;
    const integrationId = getId(integration);
    if (!integrationId) {
      setConfigurationActionState({
        busyAction: "",
        error: "No linked Integration is available to update."
      });
      return;
    }

    try {
      const activeIntegrationMgr = integrationMgrRef.current;
      if (!activeIntegrationMgr?.saveItem) {
        throw new Error("Integration service is not available.");
      }

      const payload = {
        ...integration,
        IsOnline: isOnline,
        isOnline,
        IsNew: false
      };
      const actionKey = isOnline ? "integration-enable" : "integration-disable";
      setConfigurationActionState({ busyAction: `${actionKey}:${integrationId}`, error: "" });
      const response = await activeIntegrationMgr.saveItem(payload);
      const result = getOperationResult(response);
      if (result?.Success === false || result?.success === false) {
        throw new Error(result?.Message || result?.message || "Integration status could not be updated.");
      }

      const savedIntegration = getOperationItem(response)
        || firstItem(response?.Items)
        || payload;
      setIntegrationScreenState((previous) => ({
        ...previous,
        integration: savedIntegration,
        error: ""
      }));
      setConfigurationActionState({ busyAction: "", error: "" });
      loadCatalog();
    } catch (statusError) {
      setConfigurationActionState({
        busyAction: "",
        error: statusError?.message || "Integration status could not be updated."
      });
    }
  }, [integrationScreenState.integration, loadCatalog]);
  const refreshConfigurationWorkspace = useCallback(() => {
    loadCatalog();
    if (isSourceSystemConfigurationScreen) {
      setSourceSystemRefreshKey((current) => current + 1);
      setRecentLogsRefreshKey((current) => current + 1);
    } else if (isIntegrationConfigurationScreen) {
      setRecentLogsRefreshKey((current) => current + 1);
    } else if (isEntitySchemaConfigurationScreen) {
      setSchemaRefreshKey((current) => current + 1);
      setRecentLogsRefreshKey((current) => current + 1);
    } else if (isDestinationConfigurationScreen) {
      refreshDestinationWorkspace();
      setRecentLogsRefreshKey((current) => current + 1);
    }
  }, [isDestinationConfigurationScreen, isEntitySchemaConfigurationScreen, isIntegrationConfigurationScreen, isSourceSystemConfigurationScreen, loadCatalog, refreshDestinationWorkspace]);
  const openEditSourceSystemPanel = useCallback(() => {
    const sourceSystem = sourceSystemScreenState.sourceSystem;
    if (!getId(sourceSystem)) {
      setConfigurationActionState({
        busyAction: "",
        error: "No linked Source System is available to edit."
      });
      return;
    }
    if (!sourceSystemScreenState.metadata) {
      setConfigurationActionState({
        busyAction: "",
        error: "Source System metadata is not available yet."
      });
      return;
    }

    setEditSourceSystemPanelData({
      ...sourceSystem,
      IsNew: false
    });
    setEditSourceSystemPanelOpen(true);
    setConfigurationActionState({ busyAction: "", error: "" });
  }, [sourceSystemScreenState.metadata, sourceSystemScreenState.sourceSystem]);
  const closeEditSourceSystemPanel = useCallback(() => {
    setEditSourceSystemPanelOpen(false);
    setEditSourceSystemPanelData(null);
  }, []);
  const saveSourceSystemDetails = useCallback(async (updatedSourceSystem) => {
    const sourceSystemId = getId(updatedSourceSystem) || getId(editSourceSystemPanelData);
    if (!sourceSystemId) {
      setConfigurationActionState({
        busyAction: "",
        error: "Source System could not be saved because it is missing an id."
      });
      return undefined;
    }

    try {
      const activeSourceSystemMgr = sourceSystemMgrRef.current;
      if (!activeSourceSystemMgr?.saveItem) {
        throw new Error("Source System service is not available.");
      }

      const payload = {
        ...editSourceSystemPanelData,
        ...(updatedSourceSystem || {}),
        IsNew: false
      };

      setConfigurationActionState({ busyAction: `source-system-edit:${sourceSystemId}`, error: "" });
      const response = await activeSourceSystemMgr.saveItem(payload);
      const result = getOperationResult(response);
      if (result?.Success === false || result?.success === false) {
        throw new Error(result?.Message || result?.message || "Source System could not be saved.");
      }

      const savedSourceSystem = getOperationItem(response)
        || firstItem(response?.Items)
        || payload;
      setSourceSystemScreenState((previous) => ({
        ...previous,
        sourceSystem: {
          ...savedSourceSystem,
          SourceObjectTargets: getSourceSystemTargets(savedSourceSystem)
        },
        error: ""
      }));
      setConfigurationActionState({ busyAction: "", error: "" });
      closeEditSourceSystemPanel();
      refreshConfigurationWorkspace();
      return response;
    } catch (saveSourceSystemError) {
      setConfigurationActionState({
        busyAction: "",
        error: saveSourceSystemError?.message || "Source System could not be saved."
      });
      return undefined;
    }
  }, [closeEditSourceSystemPanel, editSourceSystemPanelData, refreshConfigurationWorkspace]);
  const openEditSchemaPanel = useCallback((schemaOverride = null, metadataOverride = null) => {
    const schema = schemaOverride || schemaScreenState.schema;
    if (!getId(schema)) {
      setConfigurationActionState({
        busyAction: "",
        error: "No linked Entity Schema is available to edit."
      });
      return;
    }
    const metadata = metadataOverride || schemaScreenState.metadata;
    if (!metadata) {
      setConfigurationActionState({
        busyAction: "",
        error: "Entity Schema metadata is not available yet."
      });
      return;
    }

    setEditSchemaPanelData({
      ...schema,
      IsNew: false
    });
    setEditSchemaPanelOpen(true);
    setConfigurationActionState({ busyAction: "", error: "" });
  }, [schemaScreenState.metadata, schemaScreenState.schema]);
  const closeEditSchemaPanel = useCallback(() => {
    setEditSchemaPanelOpen(false);
    setEditSchemaPanelData(null);
  }, []);
  const saveSchemaDetails = useCallback(async (updatedSchema) => {
    const schemaId = getId(updatedSchema) || getId(editSchemaPanelData);
    if (!schemaId) {
      setConfigurationActionState({
        busyAction: "",
        error: "Entity Schema could not be saved because it is missing an id."
      });
      return undefined;
    }

    try {
      const activeSchemaMgr = schemaMgrRef.current;
      if (!activeSchemaMgr?.saveItem) {
        throw new Error("Entity Schema service is not available.");
      }

      const payload = {
        ...editSchemaPanelData,
        ...(updatedSchema || {}),
        IsNew: false
      };

      setConfigurationActionState({ busyAction: `schema-edit:${schemaId}`, error: "" });
      const response = await activeSchemaMgr.saveItem(payload);
      const result = getOperationResult(response);
      if (result?.Success === false || result?.success === false) {
        throw new Error(result?.Message || result?.message || "Entity Schema could not be saved.");
      }

      const savedSchema = getOperationItem(response)
        || firstItem(response?.Items)
        || payload;
      setSchemaScreenState((previous) => ({
        ...previous,
        schema: savedSchema,
        error: ""
      }));
      setConfigurationActionState({ busyAction: "", error: "" });
      closeEditSchemaPanel();
      setSchemaRefreshKey((current) => current + 1);
      loadCatalog();
      return response;
    } catch (saveSchemaError) {
      setConfigurationActionState({
        busyAction: "",
        error: saveSchemaError?.message || "Entity Schema could not be saved."
      });
      return undefined;
    }
  }, [closeEditSchemaPanel, editSchemaPanelData, loadCatalog]);

  const selectSchemaVersion = useCallback((schemaVersion) => {
    const schemaVersionId = typeof schemaVersion === "string"
      ? schemaVersion
      : getSchemaVersionId(schemaVersion);
    if (!schemaVersionId) return;
    setSelectedSchemaVersionId(schemaVersionId);
  }, []);

  const openEditSchemaVersionPanel = useCallback((schemaVersion) => {
    const schemaVersionId = getSchemaVersionId(schemaVersion);
    if (!schemaVersionId) {
      setConfigurationActionState({
        busyAction: "",
        error: "Schema Version metadata is not available to edit."
      });
      return;
    }

    setEditSchemaVersionPanelData({
      ...schemaVersion,
      SchemaVersionId: schemaVersionId,
      IsNew: false
    });
    setEditSchemaVersionPanelOpen(true);
    setConfigurationActionState({ busyAction: "", error: "" });
  }, []);

  const closeEditSchemaVersionPanel = useCallback(() => {
    setEditSchemaVersionPanelOpen(false);
    setEditSchemaVersionPanelData(null);
  }, []);

  const saveSchemaVersionDetails = useCallback(async (updatedVersion) => {
    const schemaVersionId = getSchemaVersionId(updatedVersion) || getSchemaVersionId(editSchemaVersionPanelData);
    const schema = schemaScreenState.schema;
    if (!schema || !schemaVersionId) {
      setConfigurationActionState({
        busyAction: "",
        error: "Schema Version could not be saved because its schema context is missing."
      });
      return undefined;
    }

    try {
      const activeSchemaMgr = schemaMgrRef.current;
      if (!activeSchemaMgr?.saveItem) {
        throw new Error("Entity Schema service is not available.");
      }

      const payload = updateSchemaVersionOnSchema(schema, {
        ...editSchemaVersionPanelData,
        ...(updatedVersion || {}),
        SchemaVersionId: schemaVersionId,
        IsNew: false
      });

      setConfigurationActionState({ busyAction: `schema-version-edit:${schemaVersionId}`, error: "" });
      const response = await activeSchemaMgr.saveItem(payload);
      const result = getOperationResult(response);
      if (result?.Success === false || result?.success === false) {
        throw new Error(result?.Message || result?.message || "Schema Version could not be saved.");
      }

      const savedSchema = getOperationItem(response)
        || firstItem(response?.Items)
        || payload;
      setSchemaScreenState((previous) => ({
        ...previous,
        schema: savedSchema,
        error: ""
      }));
      setConfigurationActionState({ busyAction: "", error: "" });
      closeEditSchemaVersionPanel();
      setSelectedSchemaVersionId(schemaVersionId);
      setSchemaRefreshKey((current) => current + 1);
      loadCatalog();
      return response;
    } catch (saveSchemaVersionError) {
      setConfigurationActionState({
        busyAction: "",
        error: saveSchemaVersionError?.message || "Schema Version could not be saved."
      });
      return undefined;
    }
  }, [closeEditSchemaVersionPanel, editSchemaVersionPanelData, loadCatalog, schemaScreenState.schema]);

  const openPublishSchemaVersionDialog = useCallback((schemaVersion, requestedAction) => {
    const schemaVersionId = getSchemaVersionId(schemaVersion);
    if (!schemaScreenState.schema || !schemaVersionId) {
      setConfigurationActionState({
        busyAction: "",
        error: "Schema Version could not be published because its schema context is missing."
      });
      return;
    }

    const published = getBoolean(schemaVersion, "IsPublished", "isPublished");
    const action = requestedAction || (published ? "unpublish" : "publish");

    setPublishSchemaVersionDialog({
      schema: schemaScreenState.schema,
      version: schemaVersion,
      action,
      makePrimary: false,
      busy: false,
      error: ""
    });
  }, [schemaScreenState.schema]);

  const closePublishSchemaVersionDialog = useCallback(() => {
    setPublishSchemaVersionDialog({
      schema: null,
      version: null,
      action: "",
      makePrimary: false,
      busy: false,
      error: ""
    });
  }, []);

  const confirmPublishSchemaVersion = useCallback(async () => {
    const schemaVersion = publishSchemaVersionDialog.version;
    const schemaVersionId = getSchemaVersionId(schemaVersion);
    const schema = publishSchemaVersionDialog.schema || schemaScreenState.schema;
    if (!schema || !schemaVersionId) return undefined;
    const action = publishSchemaVersionDialog.action || "publish";
    const actionLabel = action === "unpublish"
      ? "unpublished"
      : action === "make-primary"
        ? "made Primary"
        : publishSchemaVersionDialog.makePrimary
          ? "published and made Primary"
          : "published";

    setPublishSchemaVersionDialog((previous) => ({ ...previous, busy: true, error: "" }));
    setConfigurationActionState({ busyAction: `schema-version-${action}:${schemaVersionId}`, error: "" });

    try {
      const activeSchemaMgr = schemaMgrRef.current;
      if (!activeSchemaMgr?.saveItem) {
        throw new Error("Entity Schema service is not available.");
      }

      const payload = action === "unpublish"
        ? unpublishSchemaVersionOnSchema(schema, schemaVersionId)
        : action === "make-primary"
          ? makePrimarySchemaVersionOnSchema(schema, schemaVersionId)
          : publishSchemaVersionOnSchema(schema, schemaVersionId, publishSchemaVersionDialog.makePrimary);
      const response = await activeSchemaMgr.saveItem(payload);
      const result = getOperationResult(response);
      if (result?.Success === false || result?.success === false) {
        throw new Error(result?.Message || result?.message || `Schema Version could not be ${actionLabel}.`);
      }

      const savedSchema = getOperationItem(response)
        || firstItem(response?.Items)
        || payload;
      setSchemaScreenState((previous) => ({
        ...previous,
        schema: savedSchema,
        activeSchemaVersionId: getSchemaActiveVersionId(savedSchema),
        selectedSchemaVersionId: schemaVersionId,
        error: ""
      }));
      setConfigurationActionState({ busyAction: "", error: "" });
      setSelectedSchemaVersionId(schemaVersionId);
      closePublishSchemaVersionDialog();
      setSchemaRefreshKey((current) => current + 1);
      loadCatalog();
      return response;
    } catch (publishSchemaVersionError) {
      const errorMessage = publishSchemaVersionError?.message || `Schema Version could not be ${actionLabel}.`;
      setPublishSchemaVersionDialog((previous) => ({ ...previous, busy: false, error: errorMessage }));
      setConfigurationActionState({ busyAction: "", error: errorMessage });
      return undefined;
    }
  }, [closePublishSchemaVersionDialog, loadCatalog, publishSchemaVersionDialog.action, publishSchemaVersionDialog.makePrimary, publishSchemaVersionDialog.schema, publishSchemaVersionDialog.version, schemaScreenState.schema]);

  const openSchemaOperation = useCallback(async (schema, operation) => {
    if (!schema || !operation) return;
    const isEntityObjectSchemaImport = isEntityObjectImportFromSchemaOperation(operation);
    const schemaId = getId(schema);
    if (!schemaId) {
      setConfigurationActionState({
        busyAction: "",
        error: "Schema operation could not start because the Entity Schema is missing an id."
      });
      return;
    }
    const schemaDisplayName = getString(schema, "DisplayName", "displayName", "Name", "name");
    if (!schemaDisplayName) {
      setConfigurationActionState({
        busyAction: "",
        error: "Schema operation could not start because the selected Schema is missing a name."
      });
      return;
    }

    const operationFields = getArray(operation, "Fields", "fields");
    const acceptsSchemaSelection = operationFields.some((field) =>
      normalizeCompare(getString(field, "FieldName", "fieldName")) === "schemaid"
    );
    const acceptsEntityObjectSelection = operationFields.some((field) =>
      normalizeCompare(getString(field, "FieldName", "fieldName")) === "entityobjectid"
    );
    const operationQueryParams = buildWorkspaceResourceQueryParams(
      operation.queryParams || operation.QueryParams,
      schema
    );
    let initialQueryParams = acceptsSchemaSelection
      ? { ...operationQueryParams, schemaId }
      : operationQueryParams;
    if (isEntityObjectSchemaImport) {
      const schemaVersionId = getString(
        schemaScreenState,
        "selectedSchemaVersionId",
        "SelectedSchemaVersionId"
      );
      if (!schemaVersionId) {
        setConfigurationActionState({
          busyAction: "",
          error: "Schema import could not start because no Schema Version is selected."
        });
        return;
      }
      initialQueryParams = { ...initialQueryParams, schemaVersionId };
    }
    const schemaSelectData = [{
      Text: schemaDisplayName,
      Value: schemaId
    }];
    const entityObjectSelectData = getArray(schemaScreenState, "entityObjects", "EntityObjects")
      .map((entityObject) => ({
        Text: getString(entityObject, "DisplayName", "displayName", "ObjectName", "objectName", "SystemName", "systemName", "Name", "name"),
        Value: getString(entityObject, "_id", "Id", "id", "EntityObjectId", "entityObjectId", "UniqueId", "uniqueId")
      }))
      .filter((option) => option.Text && option.Value);
    const selectDataByField = {
      ...(acceptsSchemaSelection ? { schemaId: schemaSelectData } : {}),
      ...(acceptsEntityObjectSelection ? { entityObjectId: entityObjectSelectData } : {})
    };

    setConfigurationActionState({ busyAction: getOperationName(operation) || "schema-operation", error: "" });
    schemaOperationDialog.setQueryParams?.(initialQueryParams);
    await schemaOperationDialog.handleOperationDialog?.(
      {
        ...operation,
        itemData: schema,
        queryParams: initialQueryParams
      },
      {
        closeOnComplete: isEntityObjectSchemaImport,
        selectDataByField
      }
    );
    setConfigurationActionState({ busyAction: "", error: "" });
  }, [buildWorkspaceResourceQueryParams, schemaOperationDialog, schemaScreenState]);
  const executeSchemaDialogOperation = useCallback(async () => {
    const isEntityObjectSchemaImport = isEntityObjectImportFromSchemaOperation(schemaOperationDialog.activeOperation);
    const response = await schemaOperationDialog.executeOperation?.();
    const operationResult = getOperationResult(response);
    const operationFailed = operationResult?.Success === false || operationResult?.success === false;
    if (!isEntityObjectSchemaImport || operationFailed) {
      setSchemaRefreshKey((current) => current + 1);
      loadCatalog();
    }
    setRecentLogsRefreshKey((current) => current + 1);
    return response;
  }, [loadCatalog, schemaOperationDialog]);
  const openEntityObjectOperation = useCallback(async (entityObject, operation) => {
    if (!entityObject || !operation) return;
    setConfigurationActionState({ busyAction: getOperationName(operation) || "entity-object-operation", error: "" });
    await entityObjectOperationDialog.handleOperationDialog?.(
      {
        ...operation,
        itemData: entityObject,
        queryParams: buildWorkspaceResourceQueryParams(operation.queryParams || operation.QueryParams, entityObject)
      },
      { closeOnComplete: false }
    );
    setConfigurationActionState({ busyAction: "", error: "" });
  }, [buildWorkspaceResourceQueryParams, entityObjectOperationDialog]);
  const executeEntityObjectDialogOperation = useCallback(async () => {
    const response = await entityObjectOperationDialog.executeOperation?.();
    setSchemaRefreshKey((current) => current + 1);
    setRecentLogsRefreshKey((current) => current + 1);
    loadCatalog();
    return response;
  }, [entityObjectOperationDialog, loadCatalog]);
  const setSourceSystemActive = useCallback(async (isActive) => {
    const sourceSystem = sourceSystemScreenState.sourceSystem;
    const sourceSystemId = getId(sourceSystem);
    if (!sourceSystemId) {
      setConfigurationActionState({
        busyAction: "",
        error: "No linked Source System is available to update."
      });
      return;
    }

    try {
      const activeSourceSystemMgr = sourceSystemMgrRef.current;
      if (!activeSourceSystemMgr?.saveItem) {
        throw new Error("Source System service is not available.");
      }

      const payload = {
        ...sourceSystem,
        IsActive: isActive,
        isActive,
        IsNew: false
      };
      const actionKey = isActive ? "source-system-enable" : "source-system-disable";
      setConfigurationActionState({ busyAction: `${actionKey}:${sourceSystemId}`, error: "" });
      const response = await activeSourceSystemMgr.saveItem(payload);
      const result = getOperationResult(response);
      if (result?.Success === false || result?.success === false) {
        throw new Error(result?.Message || result?.message || "Source System status could not be updated.");
      }

      const savedSourceSystem = getOperationItem(response)
        || firstItem(response?.Items)
        || payload;
      setSourceSystemScreenState((previous) => ({
        ...previous,
        sourceSystem: {
          ...savedSourceSystem,
          SourceObjectTargets: getSourceSystemTargets(savedSourceSystem)
        },
        error: ""
      }));
      setConfigurationActionState({ busyAction: "", error: "" });
      refreshConfigurationWorkspace();
    } catch (statusError) {
      setConfigurationActionState({
        busyAction: "",
        error: statusError?.message || "Source System status could not be updated."
      });
    }
  }, [refreshConfigurationWorkspace, sourceSystemScreenState.sourceSystem]);
  const setCurrentSourceObjectActive = useCallback(async (isActive) => {
    const sourceObjectId = configurationRoute.sourceObjectManagerSourceObjectId;
    const sourceObject = findSourceObjectById(sourceSystemScreenState.sourceObjects, sourceObjectId);
    const effectiveSourceObjectId = getId(sourceObject) || sourceObjectId;
    if (!sourceObject || !effectiveSourceObjectId) {
      setConfigurationActionState({
        busyAction: "",
        error: "No Source Object is available to update."
      });
      return;
    }

    try {
      const activeSourceObjectMgr = sourceObjectMgrRef.current;
      if (!activeSourceObjectMgr?.saveItem) {
        throw new Error("Source Object service is not available.");
      }

      const payload = {
        ...sourceObject,
        IsActive: isActive,
        isActive,
        IsNew: false
      };
      const actionKey = isActive ? "source-object-enable" : "source-object-disable";
      setConfigurationActionState({ busyAction: `${actionKey}:${effectiveSourceObjectId}`, error: "" });
      const response = await activeSourceObjectMgr.saveItem(payload);
      const result = getOperationResult(response);
      if (result?.Success === false || result?.success === false) {
        throw new Error(result?.Message || result?.message || "Source Object status could not be updated.");
      }

      const savedSourceObject = getOperationItem(response)
        || firstItem(response?.Items)
        || payload;
      setSourceSystemScreenState((previous) => ({
        ...previous,
        sourceObjects: getArray(previous, "sourceObjects").map((item, index) =>
          getSourceObjectComparableKeys(item, index).includes(normalizeCompare(effectiveSourceObjectId))
            ? savedSourceObject
            : item
        ),
        error: ""
      }));
      setConfigurationActionState({ busyAction: "", error: "" });
      refreshConfigurationWorkspace();
    } catch (statusError) {
      setConfigurationActionState({
        busyAction: "",
        error: statusError?.message || "Source Object status could not be updated."
      });
    }
  }, [configurationRoute.sourceObjectManagerSourceObjectId, refreshConfigurationWorkspace, sourceSystemScreenState.sourceObjects]);
  const openSourceSystemLoadTargetsOperation = useCallback(async () => {
    const sourceSystem = sourceSystemScreenState.sourceSystem;
    const sourceSystemId = getId(sourceSystem);
    if (!sourceSystemId) {
      setConfigurationActionState({
        busyAction: "",
        error: "No linked Source System is available for target loading."
      });
      return;
    }

    setConfigurationActionState({ busyAction: "", error: "" });
    pendingSourceObjectSchemaImportRef.current = "";
    await sourceSystemOperationDialog.handleOperationDialog?.(
      {
        ...sourceSystemLoadTargetsOperation,
        itemData: sourceSystem,
        queryParams: buildWorkspaceResourceQueryParams(sourceSystemLoadTargetsOperation.queryParams || sourceSystemLoadTargetsOperation.QueryParams, sourceSystem)
      },
      { closeOnComplete: false }
    );
  }, [buildWorkspaceResourceQueryParams, sourceSystemLoadTargetsOperation, sourceSystemOperationDialog, sourceSystemScreenState.sourceSystem]);
  const completeSourceObjectSchemaImport = useCallback(async (response) => {
    const importedTargetId = pendingSourceObjectSchemaImportRef.current;
    if (!importedTargetId) return;

    let importedSourceObjectId = getImportedSourceObjectRecordId(response);
    const sourceSystemId = getId(sourceSystemScreenState.sourceSystem) || selectedSourceSystemId;
    const activeSourceObjectMgr = sourceObjectMgrRef.current;
    if (sourceSystemId && activeSourceObjectMgr?.Query) {
      try {
        const sourceObjectResult = await activeSourceObjectMgr.Query({
          model: "x => x.IsDeleted == false && x.SourceSystemId == sourceSystemId",
          params: { sourceSystemId }
        });
        const refreshedSourceObjects = getUniqueRowsByKey(
          getOperationItems(sourceObjectResult)
            .filter((item) =>
              normalizeCompare(getString(item, "SourceSystemId", "sourceSystemId")) === normalizeCompare(sourceSystemId)
            ),
          getSourceObjectIdentityKey
        );
        const latestActiveSourceObject = findLatestActiveSourceObjectForTarget(
          refreshedSourceObjects,
          importedTargetId
        );
        importedSourceObjectId = getId(latestActiveSourceObject) || importedSourceObjectId;
        if (refreshedSourceObjects.length > 0) {
          setSourceSystemScreenState((previous) => ({
            ...previous,
            sourceObjects: refreshedSourceObjects
          }));
        }
      } catch {
        // Fall back to the version record returned by the completed operation.
      }
    }

    pendingSourceObjectSchemaImportRef.current = "";
    if (importedSourceObjectId) {
      sourceSystemOperationDialog.cancelOperation?.();
      const resourceConfigurationId = getId(selectedConfiguration) || configurationRoute.resourceConfigurationId;
      if (resourceConfigurationId) {
        const path = buildSourceObjectManagerPath(
          selectedResource?.id || clientResourceId,
          resourceConfigurationId,
          importedSourceObjectId
        );
        if (configurationRoute.sourceObjectManagerSourceObjectId) {
          navigate(path, { replace: true });
        } else {
          navigate(path);
        }
      }
    }
  }, [
    clientResourceId,
    configurationRoute.resourceConfigurationId,
    configurationRoute.sourceObjectManagerSourceObjectId,
    navigate,
    selectedConfiguration,
    selectedSourceSystemId,
    selectedResource?.id,
    sourceSystemOperationDialog,
    sourceSystemScreenState.sourceSystem
  ]);
  const executeSourceSystemDialogOperation = useCallback(async () => {
    const isSourceObjectSchemaImport = Boolean(pendingSourceObjectSchemaImportRef.current);
    const response = await sourceSystemOperationDialog.executeOperation?.();
    if (!isSourceObjectSchemaImport) {
      setSourceSystemRefreshKey((current) => current + 1);
      loadCatalog();
    }
    setRecentLogsRefreshKey((current) => current + 1);
    return response;
  }, [loadCatalog, sourceSystemOperationDialog]);
  const cancelSourceSystemDialogOperation = useCallback(() => {
    pendingSourceObjectSchemaImportRef.current = "";
    sourceSystemOperationDialog.cancelOperation?.();
  }, [sourceSystemOperationDialog]);
  const executeSourceObjectDialogOperation = useCallback(async () => {
    const response = await sourceObjectOperationDialog.executeOperation?.();
    setSourceSystemRefreshKey((current) => current + 1);
    setRecentLogsRefreshKey((current) => current + 1);
    return response;
  }, [sourceObjectOperationDialog]);
  const closeAddSourceObjectDialog = useCallback(() => {
    if (addSourceObjectState.busy) return;
    setAddSourceObjectDialogOpen(false);
    setAddSourceObjectTargetIds([]);
    setAddSourceObjectState({ busy: false, error: "", confirmReload: false });
  }, [addSourceObjectState.busy]);
  const openAddSourceObjectDialog = useCallback(() => {
    setAddSourceObjectDialogOpen(true);
    setAddSourceObjectTargetIds([]);
    setAddSourceObjectState({ busy: false, error: "", confirmReload: false });
  }, []);
  const returnToAddSourceObjectSelection = useCallback(() => {
    if (addSourceObjectState.busy) return;
    setAddSourceObjectState({ busy: false, error: "", confirmReload: false });
  }, [addSourceObjectState.busy]);
  const runLoadSourceObjectsForTargets = useCallback(async (reloadConfirmed = false) => {
    const sourceSystem = sourceSystemScreenState.sourceSystem;
    const sourceSystemId = getId(sourceSystem);
    if (!sourceSystemId || addSourceObjectTargetIds.length === 0) return;

    const selectedTargetIdSet = new Set(addSourceObjectTargetIds.map(normalizeCompare));
    const selectedTargets = getSourceSystemTargets(sourceSystem).filter((target) =>
      selectedTargetIdSet.has(normalizeCompare(getSourceTargetOperationId(target)))
    );
    const reloadTargetIds = selectedTargets
      .filter((target, index) => findSourceObjectForTarget(sourceSystemScreenState.sourceObjects, target, index))
      .map(getSourceTargetOperationId)
      .filter(Boolean);
    const reloadTargetIdSet = new Set(reloadTargetIds.map(normalizeCompare));
    const newTargetIds = addSourceObjectTargetIds.filter((targetId) => !reloadTargetIdSet.has(normalizeCompare(targetId)));

    if (reloadTargetIds.length > 0 && !reloadConfirmed) {
      setAddSourceObjectState({ busy: false, error: "", confirmReload: true });
      return;
    }

    try {
      const activeSourceSystemMgr = sourceSystemMgrRef.current;
      if (!activeSourceSystemMgr?.RunOperation) {
        throw new Error("Source System service is not available.");
      }

      setAddSourceObjectState({ busy: true, error: "", confirmReload: reloadTargetIds.length > 0 });
      const targetGroups = [
        { targetIds: newTargetIds, reload: false },
        { targetIds: reloadTargetIds, reload: true }
      ].filter((group) => group.targetIds.length > 0);

      for (const targetGroup of targetGroups) {
        const response = await activeSourceSystemMgr.RunOperation({
          operationName: LOAD_SOURCE_OBJECTS_OPERATION,
          item: sourceSystem,
          queryParams: buildWorkspaceResourceQueryParams({
            targetId: targetGroup.targetIds.join(","),
            reload: targetGroup.reload
          }, sourceSystem),
          isItem: true,
          isWarning: true,
          supressAllErrors: false
        });
        const result = getOperationResult(response);
        if (result?.Success === false || result?.success === false) {
          throw new Error(result?.DisplayMessage || result?.Message || result?.message || "Source Objects could not be loaded.");
        }
      }

      setAddSourceObjectDialogOpen(false);
      setAddSourceObjectTargetIds([]);
      setAddSourceObjectState({ busy: false, error: "", confirmReload: false });
      setSourceSystemRefreshKey((current) => current + 1);
      setRecentLogsRefreshKey((current) => current + 1);
    } catch (loadError) {
      setAddSourceObjectState({
        busy: false,
        error: loadError?.message || "Source Objects could not be loaded.",
        confirmReload: reloadTargetIds.length > 0
      });
    }
  }, [addSourceObjectTargetIds, buildWorkspaceResourceQueryParams, sourceSystemScreenState.sourceObjects, sourceSystemScreenState.sourceSystem]);
  const openEditSourceTargetPanel = useCallback((sourceObject) => {
    const sourceSystem = sourceSystemScreenState.sourceSystem;
    const targets = getSourceSystemTargets(sourceSystem);
    const directTargetKey = getSourceTargetIdentityKey(sourceObject);
    const target = targets.find((candidate, index) =>
      normalizeCompare(getSourceTargetIdentityKey(candidate, index)) === normalizeCompare(directTargetKey)
    ) || findSourceObjectTarget(targets, sourceObject);
    if (!target) {
      setConfigurationActionState({
        busyAction: "",
        error: "No Source Object Target is available for this Source Object."
      });
      return;
    }

    setEditSourceTargetPanelData({
      ...target,
      __SourceTargetKey: getSourceTargetIdentityKey(target)
    });
    setEditSourceTargetPanelOpen(true);
    setEditSourceTargetPanelState({ busy: false, error: "" });
    setConfigurationActionState({ busyAction: "", error: "" });
  }, [sourceSystemScreenState.sourceSystem]);
  const openAddQueryTargetPanel = useCallback(() => {
    const sourceSystem = sourceSystemScreenState.sourceSystem;
    if (!isSqlQueryTargetSourceSystem(sourceSystem)) {
      setConfigurationActionState({
        busyAction: "",
        error: "Query targets can only be added to supported SQL Source Systems."
      });
      return;
    }

    const targets = getSourceSystemTargets(sourceSystem);
    const draftTarget = createSqlQueryTargetDraft(sourceSystem, targets);
    setEditSourceTargetPanelData({
      ...draftTarget,
      __SourceTargetKey: getSourceTargetIdentityKey(draftTarget),
      __SourceTargetMode: "create"
    });
    setEditSourceTargetPanelOpen(true);
    setEditSourceTargetPanelState({ busy: false, error: "" });
    setConfigurationActionState({ busyAction: "", error: "" });
  }, [sourceSystemScreenState.sourceSystem]);
  const openCloneQueryTargetPanel = useCallback((target) => {
    const sourceSystem = sourceSystemScreenState.sourceSystem;
    if (!isSqlQueryTargetSourceSystem(sourceSystem) || !isSourceObjectTypeQuery(target)) {
      setConfigurationActionState({
        busyAction: "",
        error: "Only supported query targets can be cloned."
      });
      return;
    }

    const targets = getSourceSystemTargets(sourceSystem);
    const draftTarget = cloneSqlQueryTargetDraft(sourceSystem, targets, target);
    setEditSourceTargetPanelData({
      ...draftTarget,
      __SourceTargetKey: getSourceTargetIdentityKey(draftTarget),
      __SourceTargetMode: "create"
    });
    setEditSourceTargetPanelOpen(true);
    setEditSourceTargetPanelState({ busy: false, error: "" });
    setConfigurationActionState({ busyAction: "", error: "" });
  }, [sourceSystemScreenState.sourceSystem]);
  const closeEditSourceTargetPanel = useCallback(() => {
    setEditSourceTargetPanelOpen(false);
    setEditSourceTargetPanelData(null);
    setEditSourceTargetPanelState({ busy: false, error: "" });
  }, []);
  const openSourceTargetStatusDialog = useCallback((target, isActive) => {
    setSourceTargetStatusDialog({
      target,
      isActive,
      busy: false,
      error: ""
    });
  }, []);
  const closeSourceTargetStatusDialog = useCallback(() => {
    setSourceTargetStatusDialog((previous) => (
      previous.busy
        ? previous
        : { target: null, isActive: false, busy: false, error: "" }
    ));
  }, []);
  const saveSourceTargetDetails = useCallback(async (updatedTarget) => {
    const sourceSystem = sourceSystemScreenState.sourceSystem;
    const sourceSystemId = getId(sourceSystem);
    if (!sourceSystemId) {
      setEditSourceTargetPanelState({
        busy: false,
        error: "Source Object Target could not be saved because the Source System is missing."
      });
      return undefined;
    }

    try {
      const activeSourceSystemMgr = sourceSystemMgrRef.current;
      if (!activeSourceSystemMgr?.saveItem) {
        throw new Error("Source System service is not available.");
      }

      const currentTargets = getSourceSystemTargets(sourceSystem);
      const sourceTargetKey = updatedTarget?.__SourceTargetKey || editSourceTargetPanelData?.__SourceTargetKey;
      const sourceTargetMode = updatedTarget?.__SourceTargetMode || editSourceTargetPanelData?.__SourceTargetMode;
      const nextTarget = {
        ...editSourceTargetPanelData,
        ...(updatedTarget || {})
      };
      delete nextTarget.__SourceTargetKey;
      delete nextTarget.__SourceTargetMode;
      const validationMessage = validateSourceTargetRows(
        [nextTarget],
        sourceSystemScreenState.sourceTargetMetadata?.Fields || sourceSystemScreenState.sourceTargetMetadata?.fields || [],
        "Source Object Target"
      );
      if (validationMessage) {
        setEditSourceTargetPanelState({
          busy: false,
          error: validationMessage
        });
        return undefined;
      }

      const nextTargets = sourceTargetMode === "create"
        ? [...currentTargets, nextTarget]
        : currentTargets.map((target, index) =>
          normalizeCompare(getSourceTargetIdentityKey(target, index)) === normalizeCompare(sourceTargetKey)
            ? nextTarget
            : target
        );
      const payload = {
        ...sourceSystem,
        IsNew: false,
        SourceObjectTargets: nextTargets
      };

      setEditSourceTargetPanelState({ busy: true, error: "" });
      const response = await activeSourceSystemMgr.saveItem(payload);
      const result = getOperationResult(response);
      if (result?.Success === false || result?.success === false) {
        throw new Error(result?.Message || result?.message || "Source Object Target could not be saved.");
      }

      const savedSourceSystem = getOperationItem(response)
        || firstItem(response?.Items)
        || payload;
      setSourceSystemScreenState((previous) => ({
        ...previous,
        sourceSystem: {
          ...savedSourceSystem,
          SourceObjectTargets: getSourceSystemTargets(savedSourceSystem)
        },
        error: ""
      }));
      setEditSourceTargetPanelState({ busy: false, error: "" });
      closeEditSourceTargetPanel();
      setSourceSystemRefreshKey((current) => current + 1);
      return response;
    } catch (saveTargetError) {
      setEditSourceTargetPanelState({
        busy: false,
        error: saveTargetError?.message || "Source Object Target could not be saved."
      });
      return undefined;
    }
  }, [closeEditSourceTargetPanel, editSourceTargetPanelData, sourceSystemScreenState.sourceSystem, sourceSystemScreenState.sourceTargetMetadata]);
  const setSourceTargetActive = useCallback(async (targetToUpdate, isActive) => {
    const sourceSystem = sourceSystemScreenState.sourceSystem;
    const sourceSystemId = getId(sourceSystem);
    const sourceTargetKey = getSourceTargetIdentityKey(targetToUpdate);
    if (!sourceSystemId || !sourceTargetKey) {
      setConfigurationActionState({
        busyAction: "",
        error: "Source Object Target status could not be updated because the target is missing."
      });
      return undefined;
    }

    try {
      const activeSourceSystemMgr = sourceSystemMgrRef.current;
      if (!activeSourceSystemMgr?.saveItem) {
        throw new Error("Source System service is not available.");
      }

      const currentTargets = getSourceSystemTargets(sourceSystem);
      const nextTargets = currentTargets.map((target, index) =>
        normalizeCompare(getSourceTargetIdentityKey(target, index)) === normalizeCompare(sourceTargetKey)
          ? {
            ...target,
            IsActive: isActive,
            isActive
          }
          : target
      );
      const payload = {
        ...sourceSystem,
        IsNew: false,
        SourceObjectTargets: nextTargets
      };
      const sourceTargetId = getSourceTargetOperationId(targetToUpdate) || sourceTargetKey;
      const actionKey = isActive ? "source-target-enable" : "source-target-disable";
      setConfigurationActionState({ busyAction: `${actionKey}:${sourceTargetId}`, error: "" });
      const response = await activeSourceSystemMgr.saveItem(payload);
      const result = getOperationResult(response);
      if (result?.Success === false || result?.success === false) {
        throw new Error(result?.Message || result?.message || "Source Object Target status could not be updated.");
      }

      const savedSourceSystem = getOperationItem(response)
        || firstItem(response?.Items)
        || payload;
      setSourceSystemScreenState((previous) => ({
        ...previous,
        sourceSystem: {
          ...savedSourceSystem,
          SourceObjectTargets: getSourceSystemTargets(savedSourceSystem)
        },
        error: ""
      }));
      setConfigurationActionState({ busyAction: "", error: "" });
      setSourceSystemRefreshKey((current) => current + 1);
      return response;
    } catch (statusError) {
      setConfigurationActionState({
        busyAction: "",
        error: statusError?.message || "Source Object Target status could not be updated."
      });
      return undefined;
    }
  }, [sourceSystemScreenState.sourceSystem]);
  const confirmSourceTargetStatusChange = useCallback(async () => {
    const { target, isActive } = sourceTargetStatusDialog;
    if (!target) return undefined;

    setSourceTargetStatusDialog((previous) => ({
      ...previous,
      busy: true,
      error: ""
    }));

    const response = await setSourceTargetActive(target, isActive);
    if (response) {
      setSourceTargetStatusDialog({ target: null, isActive: false, busy: false, error: "" });
      return undefined;
    }

    setSourceTargetStatusDialog((previous) => ({
      ...previous,
      busy: false,
      error: "Source Object Target status could not be updated."
    }));
    return undefined;
  }, [setSourceTargetActive, sourceTargetStatusDialog]);
  const closeSourceObjectManager = useCallback(() => {
    const resourceConfigurationId = getId(selectedConfiguration) || configurationRoute.resourceConfigurationId;
    if (!resourceConfigurationId) return;
    setSourceSystemRefreshKey((current) => current + 1);
    navigate(buildResourceConfigurationScreenPath(
      selectedResource?.id || clientResourceId,
      resourceConfigurationId,
      SOURCE_SYSTEM_SCREEN_KEY
    ));
  }, [clientResourceId, configurationRoute.resourceConfigurationId, navigate, selectedConfiguration, selectedResource?.id]);
  const openSourceObjectManager = useCallback((sourceObject) => {
    const panelContext = buildSourceObjectManagePanelContext(sourceObject, {
      sourceSystemId: getId(sourceSystemScreenState.sourceSystem) || selectedSourceSystemId,
      clientResourceId: selectedResource?.id || clientResourceId,
      resourceConfigurationId: getId(selectedConfiguration)
    });
    if (!panelContext) {
      setConfigurationActionState({
        busyAction: "",
        error: "Source Object Manager could not open because the Source Object is missing an id."
      });
      return;
    }

    navigate(buildSourceObjectManagerPath(
      panelContext.clientResourceId,
      panelContext.resourceConfigurationId,
      panelContext.sourceObjectId || panelContext.id
    ));
  }, [clientResourceId, navigate, selectedConfiguration, selectedResource?.id, selectedSourceSystemId, sourceSystemScreenState.sourceSystem]);
  const requestCurrentSourceObjectEdit = useCallback(() => {
    setSourceObjectEditRequestKey((current) => current + 1);
  }, []);
  const openEntityObjectManager = useCallback(async (entityObject) => {
    const entityObjectId = getId(entityObject);
    setConfigurationActionState({ busyAction: `${MODIFY_ENTITY_OPERATION}:${entityObjectId || ""}`, error: "" });
    const opened = await runEntityObjectRedirectOperation(
      entityObjectMgrRef.current,
      entityObject,
      (message) => setConfigurationActionState({ busyAction: "", error: message || "" })
    );
    if (opened) setConfigurationActionState({ busyAction: "", error: "" });
  }, []);
  const runSourceObjectSearchOperation = useCallback(async (sourceObject) => {
    const activeSourceObjectMgr = sourceObjectMgrRef.current;
    const sourceObjectId = getSourceObjectOperationId(sourceObject);
    if (!sourceObjectId) {
      setConfigurationActionState({
        busyAction: "",
        error: "Source search could not start because the Source Object is missing an id."
      });
      return null;
    }
    if (!activeSourceObjectMgr?.RunOperation) {
      setConfigurationActionState({
        busyAction: "",
        error: "Source Object service is not available."
      });
      return null;
    }

    setConfigurationActionState({ busyAction: SOURCE_OBJECT_SEARCH_ACTION, error: "" });
    try {
      const response = await activeSourceObjectMgr.RunOperation({
        operationName: SOURCE_OBJECT_SEARCH_OPERATION_NAME,
        item: sourceObject,
        queryParams: buildWorkspaceResourceQueryParams({
          id: sourceObjectId,
          soid: sourceObjectId
        }, sourceObject),
        isItem: true,
        processorType: getSourceObjectProcessorType(sourceObject)
      });
      setRecentLogsRefreshKey((current) => current + 1);
      dispatchAiPanelCommandFromOperationResponse(response);
      setConfigurationActionState({ busyAction: "", error: "" });
      return response;
    } catch (searchError) {
      setConfigurationActionState({
        busyAction: "",
        error: searchError?.message || "Source search could not be started."
      });
      return null;
    }
  }, [buildWorkspaceResourceQueryParams]);
  const openSourceObjectExportDialog = useCallback((sourceObject, operation) => {
    setSourceObjectExportDialog({
      open: true,
      sourceObject,
      operation,
      selectedFormat: "csv",
      busy: false,
      error: "",
      resultMessage: ""
    });
  }, []);
  const closeSourceObjectExportDialog = useCallback(() => {
    setSourceObjectExportDialog((previous) => (
      previous.busy
        ? previous
        : { open: false, sourceObject: null, operation: null, selectedFormat: "csv", busy: false, error: "", resultMessage: "" }
    ));
  }, []);
  const selectSourceObjectExportFormat = useCallback((format) => {
    setSourceObjectExportDialog((previous) => ({
      ...previous,
      selectedFormat: format,
      error: "",
      resultMessage: ""
    }));
  }, []);
  const executeSourceObjectExport = useCallback(async () => {
    const activeSourceObjectMgr = sourceObjectMgrRef.current;
    const { sourceObject, selectedFormat } = sourceObjectExportDialog;
    const sourceObjectId = getSourceObjectOperationId(sourceObject);
    if (!sourceObjectId || !selectedFormat) return undefined;
    if (!activeSourceObjectMgr?.RunOperation) {
      setSourceObjectExportDialog((previous) => ({
        ...previous,
        error: "Source Object service is not available."
      }));
      return undefined;
    }

    setSourceObjectExportDialog((previous) => ({ ...previous, busy: true, error: "" }));
    try {
      const response = await activeSourceObjectMgr.RunOperation({
        operationName: SOURCE_OBJECT_SEARCH_OPERATION_NAME,
        id: sourceObjectId,
        item: sourceObject,
        queryParams: buildWorkspaceResourceQueryParams({
          id: sourceObjectId,
          soid: sourceObjectId,
          export: selectedFormat
        }, sourceObject),
        isItem: true,
        processorType: getSourceObjectProcessorType(sourceObject)
      });
      const result = getOperationResult(response);
      if (result?.Success === false || result?.success === false) {
        throw new Error(getOperationResultMessage(response, "Source export could not be completed."));
      }
      setRecentLogsRefreshKey((current) => current + 1);
      setSourceObjectExportDialog((previous) => ({
        ...previous,
        busy: false,
        error: "",
        resultMessage: getSourceObjectExportDispatchMessage(response, sourceObject)
          || getResultMessageText(getValue(result, "Value", "value"))
          || getOperationResultMessage(response, "Source export completed.")
      }));
      return undefined;
    } catch (exportError) {
      setSourceObjectExportDialog((previous) => ({
        ...previous,
        busy: false,
        error: exportError?.message || "Source export could not be completed.",
        resultMessage: ""
      }));
      return undefined;
    }
  }, [buildWorkspaceResourceQueryParams, sourceObjectExportDialog]);
  const openSourceObjectOperation = useCallback(async (sourceObject, operation) => {
    if (!sourceObject || !operation) return;
    if (isSourceObjectImportFromSchemaAction(operation)) {
      const sourceSystem = sourceSystemScreenState.sourceSystem;
      const sourceObjectTarget = findSourceObjectTarget(getSourceSystemTargets(sourceSystem), sourceObject);
      const targetId = getSourceTargetOperationId(sourceObjectTarget);
      if (!getId(sourceSystem) || !targetId) {
        setConfigurationActionState({
          busyAction: "",
          error: "The selected Source Object does not have a matching structured target for schema import."
        });
        return;
      }

      setConfigurationActionState({ busyAction: "", error: "" });
      pendingSourceObjectSchemaImportRef.current = targetId;
      try {
        await sourceSystemOperationDialog.handleOperationDialog?.(
          {
            ...operation,
            itemData: sourceSystem,
            queryParams: {
              ...buildWorkspaceResourceQueryParams(operation.queryParams || operation.QueryParams, sourceSystem),
              targetId
            }
          },
          {
            closeOnComplete: true,
            resultsCallback: completeSourceObjectSchemaImport
          }
        );
      } catch (operationError) {
        pendingSourceObjectSchemaImportRef.current = "";
        throw operationError;
      }
      return;
    }
    if (isSourceObjectSearchAction(operation)) {
      await runSourceObjectSearchOperation(sourceObject);
      return;
    }
    if (isSourceObjectExportAction(operation)) {
      openSourceObjectExportDialog(sourceObject, operation);
      return;
    }
    await sourceObjectOperationDialog.handleOperationDialog?.(
      {
        ...operation,
        ...withSourceObjectProcessorOperationDefaults(operation, sourceObject),
        itemData: sourceObject,
        queryParams: buildWorkspaceResourceQueryParams(operation.queryParams || operation.QueryParams, sourceObject)
      },
      { closeOnComplete: false }
    );
  }, [buildWorkspaceResourceQueryParams, completeSourceObjectSchemaImport, openSourceObjectExportDialog, runSourceObjectSearchOperation, sourceObjectOperationDialog, sourceSystemOperationDialog, sourceSystemScreenState.sourceSystem]);
  const confirmDeleteConfiguration = useCallback(async () => {
    const configuration = deleteConfigurationTarget;
    const configurationName = getDisplayName(configuration, "Resource configuration");
    if (!configuration || deleteConfigurationConfirmText.trim() !== configurationName) return;

    try {
      const activeResourceConfigurationMgr = resourceConfigurationMgrRef.current;
      if (!activeResourceConfigurationMgr?.RunOperation) {
        throw new Error("Resource configuration service is not available.");
      }

      setDeleteConfigurationState({ busy: true, error: "" });
      const response = await activeResourceConfigurationMgr.RunOperation({
        operationName: Operations.ResourceConfigurationItem.DeleteResourceConfiguration || "DeleteResourceConfiguration",
        item: configuration,
        queryParams: buildWorkspaceResourceQueryParams(null, configuration, configuration),
        isItem: true,
        isWarning: true,
        supressAllErrors: false
      });
      const result = getOperationResult(response);
      if (result?.Success === false || result?.success === false) {
        throw new Error(result?.Message || result?.message || "Resource Configuration could not be deleted.");
      }

      setDeleteConfigurationTarget(null);
      setDeleteConfigurationConfirmText("");
      setDeleteConfigurationState({ busy: false, error: "" });
      loadCatalog();
    } catch (deleteError) {
      setDeleteConfigurationState({
        busy: false,
        error: deleteError?.message || "Resource Configuration could not be deleted."
      });
    }
  }, [buildWorkspaceResourceQueryParams, deleteConfigurationConfirmText, deleteConfigurationTarget, loadCatalog]);
  const closeResourcePurchaseDialog = useCallback(() => {
    setPurchaseDialogResource(null);
    setPurchaseDialogOfferKey("");
  }, []);
  const handleResourcePurchased = useCallback(() => {
    closeResourcePurchaseDialog();
    loadCatalog();
  }, [closeResourcePurchaseDialog, loadCatalog]);
  const removeUnstartedClientResource = useCallback(async (resource) => {
    if (!canManageClientResourceApproval) {
      throw new Error("You do not have permission to remove this Client Resource.");
    }
    if (resource?.hasAccess === false) {
      throw new Error(resource.accessMessage || "Access denied for this Client Resource.");
    }
    const clientResource = resource?.clientResource;
    const removableClientResourceId = resource?.id || getId(clientResource);
    if (!clientResource || !removableClientResourceId) {
      throw new Error("Client Resource is not available to remove.");
    }

    const activeClientResourceMgr = clientResourceMgrRef.current;
    if (!activeClientResourceMgr?.RunOperation) {
      throw new Error("Client Resource service is not available.");
    }

    const response = await activeClientResourceMgr.RunOperation({
      operationName: Operations.ClientResourceItem.CancelClientResource,
      item: {
        ...clientResource,
        _id: removableClientResourceId,
        StatusReason: "Removed before setup was started."
      },
      queryParams: buildResourceOperationQueryParams({
        clientResourceId: removableClientResourceId,
        item: clientResource,
        queryParams: {
          reason: "Removed before setup was started."
        }
      }),
      isItem: true,
      isWarning: true,
      supressAllErrors: false
    });
    const result = getOperationResult(response);
    if (result?.Success === false || result?.success === false) {
      throw new Error(getOperationResultMessage(response, "Client Resource could not be removed."));
    }

    await loadCatalog();
  }, [canManageClientResourceApproval, loadCatalog]);
  const openClientResourceManager = useCallback((resource) => {
    if (!resource?.id) return;
    navigate(resource.hasAccess === false
      ? buildConfigurationNavigationPath(resource.id, CONFIGURATION_MANAGE_NAVIGATION.key)
      : buildResourceRootPath(resource.id));
  }, [navigate]);

  useEffect(() => {
    const setCustomNavigationItems = coreContext?.setCustomNavigationItems;
    if (!setCustomNavigationItems) return;

    if (!clientResourceId || !selectedResource) {
      setCustomNavigationItems(RESOURCE_NAVIGATION_TARGET_KEY, []);
      return;
    }

    setCustomNavigationItems(RESOURCE_NAVIGATION_TARGET_KEY, resourceNavigationItems);

    return () => {
      setCustomNavigationItems(RESOURCE_NAVIGATION_TARGET_KEY, []);
    };
  }, [
    clientResourceId,
    coreContext?.setCustomNavigationItems,
    resourceNavigationItems,
    selectedResource
  ]);

  const waitingForCurrentCatalog = !isSchedulesRoute
    && (loading || !profileContextReady || loadedCatalogRequestKey !== catalogRequestKey);

  if (!canViewResourceScreen) {
    return (
      <main className="resource-workspace" aria-label="Resource workspace">
        <ResourceStateControl
          tone="warning"
          title="Resources unavailable"
          message="You do not have permission to view Resources."
        />
      </main>
    );
  }

  if (waitingForCurrentCatalog) {
    return (
      <main className="resource-workspace" aria-label="Resource workspace">
        <ResourceStateControl loading loadingText="Loading resources..." />
      </main>
    );
  }

  if (error && !isSchedulesRoute) {
    return (
      <main className="resource-workspace" aria-label="Resource workspace">
        <ResourceStateControl
          tone="error"
          title="Resources could not be loaded"
          message={error}
          actions={<ButtonControl type="standard" onClick={() => loadCatalog()}>Retry</ButtonControl>}
        />
      </main>
    );
  }

  if (isSchedulesRoute) {
    return (
      <ResourceSchedulesScreen
        destinationScheduleManager={destinationScheduleMgr}
        environmentKey={marketplaceCatalog?.environmentKey || catalogModel.environmentKey}
        clientProductOffers={marketplaceCatalog?.clientProductOffers || []}
        isSelfHostedResourceMode={marketplaceCatalog?.isSelfHostedResourceMode}
        onRuntimeCapacityPurchased={loadCatalog}
      />
    );
  }

  if (resourceGroupKey && !isEdiResourceGroupRoute) {
    return (
      <main className="resource-workspace" aria-label="Resource workspace">
        <ResourceStateControl
          iconKey={RESOURCE_FALLBACK_ICON}
          title="Resource group not found"
          message="The selected resource group is not available."
          actions={(
            <ButtonControl type="standard" onClick={() => navigate(RESOURCES_BASE_PATH)}>
              All resources
            </ButtonControl>
          )}
        />
      </main>
    );
  }

  if (isEdiResourceGroupRoute) {
    return (
      <ResourceListScreen
        title={EDI_RESOURCE_GROUP_TITLE}
        eyebrow="Resources"
        resources={catalogModel.clientResources.filter(isEdiResource)}
        resourceConfigurations={catalogModel.resourceConfigurations}
        onBuy={() => navigate("/marketplace")}
        onSelect={openClientResourceManager}
        onAddConfiguration={(resource) => navigate(`${buildResourceConfigurePath(resource.id)}?new=1`)}
        onRemoveResource={removeUnstartedClientResource}
        canAddResource={canAddMarketplaceResource}
        canConfigureResource={canConfigureResource}
        canRemoveResource={canManageClientResourceApproval}
        emptyTitle="No EDI resources"
        emptyMessage="No EDI resources are available."
        backAction={(
          <ButtonControl
            type="standard"
            className="resource-workspace-list-action"
            onClick={() => navigate(RESOURCES_BASE_PATH)}
          >
            Back
          </ButtonControl>
        )}
      />
    );
  }

  if (!clientResourceId) {
    return (
      <ResourceListScreen
        resources={catalogModel.clientResources}
        resourceConfigurations={catalogModel.resourceConfigurations}
        onBuy={() => navigate("/marketplace")}
        onSelect={openClientResourceManager}
        onAddConfiguration={(resource) => navigate(`${buildResourceConfigurePath(resource.id)}?new=1`)}
        onRemoveResource={removeUnstartedClientResource}
        canAddResource={canAddMarketplaceResource}
        canConfigureResource={canConfigureResource}
        canRemoveResource={canManageClientResourceApproval}
        showEdiGroup
        onSelectEdiGroup={() => navigate(buildResourceGroupPath(EDI_RESOURCE_GROUP_KEY))}
      />
    );
  }

  if (!selectedResource) {
    return (
      <main className="resource-workspace" aria-label="Resource workspace">
        <ResourceStateControl
          iconKey={RESOURCE_FALLBACK_ICON}
          title="Resource not found"
          message="The selected Client Resource is not available in your product resource catalog."
          actions={(
            <ButtonControl type="standard" onClick={() => navigate(RESOURCES_BASE_PATH)}>
              All resources
            </ButtonControl>
          )}
        />
      </main>
    );
  }

  const isConfigurationWorkspaceScreen = selectedNavigation?.key === CONFIGURATIONS_NAVIGATION.key;
  const resourceAccessDenied = selectedResource.hasAccess === false;
  const isResourceManagerScreen = isConfigurationWorkspaceScreen
    && configurationRoute.screenKey === CONFIGURATION_MANAGE_NAVIGATION.key;
  const resourceAccessMessage = selectedResource.accessMessage
    || "Access denied. Assign the Resource Administrator role for this Client Resource in Roles & Permissions.";

  if (!canAccessDirectResourceNavigation) {
    const requestedNavigationName = selectedConfigurationNavigation?.displayName
      || selectedNavigation?.displayName
      || "Resource workspace";
    return (
      <main className="resource-workspace" aria-label="Resource workspace">
        <ResourceWorkspaceHeader selectedResource={selectedResource} />
        <div className="resource-workspace-body">
          <section className="resource-workspace-screen">
            <ResourceStateControl
              tone="error"
              title={`${requestedNavigationName} unavailable`}
              message="You do not have permission to access this Resource screen."
            />
          </section>
        </div>
      </main>
    );
  }

  if (resourceAccessDenied && !isResourceManagerScreen) {
    return (
      <main className="resource-workspace" aria-label="Resource workspace">
        <ResourceWorkspaceHeader selectedResource={selectedResource} />
        <div className="resource-workspace-body">
          <section className="resource-workspace-screen">
            <ResourceStateControl
              tone="error"
              title="Access Denied"
              message={resourceAccessMessage}
            />
          </section>
        </div>
      </main>
    );
  }

  if (resourceAccessDenied && isResourceManagerScreen) {
    return (
      <main className="resource-workspace" aria-label="Resource workspace">
        <div className="resource-workspace-body">
          <section className="resource-workspace-screen">
            <ResourceConfigurationScreenShell
              selectedResource={selectedResource}
              configurations={[]}
              selectedConfiguration={null}
              screenKey={CONFIGURATION_MANAGE_NAVIGATION.key}
              canOpenConfigurationSetup={false}
            >
              <ResourceStateControl
                tone="error"
                title="Access Denied"
                message={resourceAccessMessage}
              />
            </ResourceConfigurationScreenShell>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className="resource-workspace" aria-label="Resource workspace">
      {configurationActionState.message ? (
        <ToastNotifications position="top" message={configurationActionState.message} />
      ) : null}
      {!isConfigurationWorkspaceScreen ? (
        <ResourceWorkspaceHeader
          selectedResource={selectedResource}
          configurationCount={configurations.length}
        />
      ) : null}
      <div className="resource-workspace-body">
        <section className="resource-workspace-screen">
          {selectedNavigation?.key === "overview" ? (
            <ResourceOverviewScreen
              selectedResource={selectedResource}
              configurations={configurations}
              canOpenConfigurationSetup={canConfigureResource && isClientResourceSetupEnabled(selectedResource)}
              onOpenConfigurationSetup={openConfigurationSetup}
              onManageConfiguration={openConfigurationManage}
              onSelectConfigurationScreen={selectConfigurationScreen}
              canBuyResourceAllowance={canAddMarketplaceResource && Boolean(selectedMarketplaceResource)}
              onBuyResourceAllowance={openResourcePurchaseDialog}
              onAddResourceConfiguration={openNewConfigurationSetup}
              importSupport={resourceConfigurationImportSupport}
              onImportResourceConfiguration={openResourceConfigurationImportDialog}
            />
          ) : selectedNavigation?.key === "logs" ? (
            <ResourceLogsScreen
              selectedResource={selectedResource}
              resourceLogs={catalogModel.resourceLogs}
              logManager={logMgr}
            />
          ) : selectedNavigation?.key === EVENTS_NAVIGATION.key ? (
            <ResourceEventsScreen
              selectedResource={selectedResource}
              resourceEvents={catalogModel.resourceEvents}
              eventManager={eventMgr}
            />
          ) : selectedNavigation?.key === CONFIGURATIONS_NAVIGATION.key ? (
            <ResourceConfigurationsScreen
              selectedResource={selectedResource}
              configurations={configurations}
              resourceConfigurations={catalogModel.resourceConfigurations}
              resourceLogs={catalogModel.resourceLogs}
              resourceEvents={catalogModel.resourceEvents}
              configurationNavigations={configurationNavigations}
              configurationRoute={configurationRoute}
              selectedConfiguration={selectedConfiguration}
              environmentKey={marketplaceCatalog?.environmentKey || catalogModel.environmentKey}
              clientProductOffers={marketplaceCatalog?.clientProductOffers || []}
              canOpenConfigurationSetup={canConfigureResource && isClientResourceSetupEnabled(selectedResource)}
              onOpenConfigurationSetup={openConfigurationSetup}
              onManageConfiguration={openConfigurationManage}
              onAddResourceConfiguration={openNewConfigurationSetup}
              onAddLinkedEndpoint={() => {
                const endpointDefaults = getDestinationResourceDefaults(selectedResource?.productResource);
                destinationWorkspace.openAddDestinationPanel(
                  endpointDefaults
                    ? { ...endpointDefaults, label: getSchemaApiLinkedRecordTypeLabel({}, selectedResource) }
                    : endpointDefaults,
                  { templateDestinationId: getLinkedRecordItemId(getLinkedDestinationRecord(selectedConfiguration)) }
                );
              }}
              onSelectConfigurationScreen={selectConfigurationScreen}
              onDeleteConfiguration={openDeleteConfigurationDialog}
              onEditConfiguration={openEditConfigurationPanel}
              onValidateConfiguration={validateConfigurationReadiness}
              onActivateConfiguration={activateConfiguration}
              onDisableConfiguration={requestDeactivateConfiguration}
              onReconfigureConfiguration={reconfigureConfiguration}
              onRefreshConfigurations={refreshConfigurationWorkspace}
              onBackToDestinationDeployment={openSelectedDestinationDeploymentScreen}
              configurationActionState={configurationActionState}
              integrationScreenState={integrationScreenState}
              sourceSystemScreenState={sourceSystemScreenStateWithOperations}
              logManager={logMgr}
              recentLogsRefreshKey={recentLogsRefreshKey}
              onEditIntegration={openEditIntegrationPanel}
              onCheckIntegrationConnection={runIntegrationConnectionCheck}
              onSetIntegrationOnline={setIntegrationOnline}
              onEditSourceSystem={openEditSourceSystemPanel}
              onSetSourceSystemActive={setSourceSystemActive}
              onSetSourceObjectActive={setCurrentSourceObjectActive}
              schemaScreenState={schemaScreenState}
              schemaOperations={schemaOperations}
              onEditSchema={openEditSchemaPanel}
              onRunSchemaOperation={openSchemaOperation}
              onSelectSchemaVersion={selectSchemaVersion}
              onEditSchemaVersion={openEditSchemaVersionPanel}
              onPublishSchemaVersion={openPublishSchemaVersionDialog}
              onRefreshSchemaWorkspace={() => {
                setSchemaRefreshKey((current) => current + 1);
              }}
              onSetSourceObjectTargetActive={openSourceTargetStatusDialog}
              onLoadSourceSystemTargets={openSourceSystemLoadTargetsOperation}
              onOpenAddSourceObjects={openAddSourceObjectDialog}
              onEditSourceObjectTarget={openEditSourceTargetPanel}
              onAddQueryTarget={openAddQueryTargetPanel}
              onCloneQueryTarget={openCloneQueryTargetPanel}
              onEditSourceObject={openSourceObjectManager}
              onEditCurrentSourceObject={requestCurrentSourceObjectEdit}
              sourceObjectEditRequestKey={sourceObjectEditRequestKey}
              sourceObjectManagerSourceObjectId={configurationRoute.sourceObjectManagerSourceObjectId}
              onCloseSourceObjectManager={closeSourceObjectManager}
              sourceObjectOperations={sourceObjectOperations}
              onRunSourceObjectOperation={openSourceObjectOperation}
              entityObjectOperations={entityObjectOperations}
              onRunEntityObjectOperation={openEntityObjectOperation}
              onOpenEntityObjectPanel={openEntityObjectManager}
              entityObjectManager={entityObjectMgr}
              destinationScreenState={destinationScreenState}
              onRefreshDestinationWorkspace={refreshDestinationWorkspace}
              destinationDeploymentRouteFilters={destinationDeploymentRouteFilters}
              destinationEntityDeploymentRouteFilters={destinationEntityDeploymentRouteFilters}
              destinationScheduleActionState={destinationScheduleActionState}
              onDestinationScheduleActionsChange={setDestinationScheduleActionState}
              endpointDeploymentsSelectionState={endpointDeploymentsSelectionState}
              onEndpointDeploymentsSelectionChange={setEndpointDeploymentsSelectionState}
              destinationEntityScopeActionState={destinationEntityScopeActionState}
              onDestinationEntityScopeActionsChange={setDestinationEntityScopeActionState}
              warehouseViewActionState={warehouseViewActionState}
              onWarehouseViewActionsChange={setWarehouseViewActionState}
              onEditDestination={destinationWorkspace.openEditDestinationPanel}
              onSetDestinationActive={destinationWorkspace.setDestinationActive}
              onCheckDestinationConnection={destinationWorkspace.runDestinationConnectionCheck}
              onManualRunDestinationDeployment={destinationWorkspace.openDestinationDeploymentManualRun}
              onDeployDestinationDeployment={destinationWorkspace.requestDeployEndpointDeployment}
              onUndeployDestinationDeployment={destinationWorkspace.requestUndeployEndpointDeployment}
              onDeployWarehouseDeployment={destinationWorkspace.openWarehouseDeploymentDeployDialog}
              onDropWarehouseDeployment={destinationWorkspace.openWarehouseDeploymentDropDialog}
              onCloneWarehouseDeployment={destinationWorkspace.openWarehouseDeploymentCloneDialog}
              onAddDestinationDeployment={destinationWorkspace.openAddDestinationDeploymentPanel}
              onManageDestinationDeployment={openDestinationDeploymentManagement}
              onEditDestinationDeployment={destinationWorkspace.openEditDestinationDeploymentPanel}
              onSaveDestinationDeploymentSchemaScopes={destinationWorkspace.saveDestinationDeploymentSchemaScopes}
              onOpenEntitySchemaConfigurationFromScope={openEntitySchemaConfigurationFromScope}
              onSetDestinationDeploymentActive={destinationWorkspace.setDestinationDeploymentActive}
              onDeleteDestinationDeployment={destinationWorkspace.deleteDestinationDeployment}
              onOpenDestinationDeploymentSchedule={openDestinationScheduleForDeployment}
              onOpenDestinationDeploymentScope={openDestinationScopeForDeployment}
              onOpenDestinationDeploymentViews={openWarehouseViewsForDeployment}
              onOpenDestinationEntityPicker={openDestinationEntityPickerForRoute}
              onOpenEndpointEntityEndpointPanel={destinationWorkspace.openAddEndpointEntityEndpointPanel}
              onEditEndpointEntityEndpoint={destinationWorkspace.openEditEndpointEntityEndpointPanel}
              onDeleteEndpointEntityEndpoint={destinationWorkspace.deleteEndpointEntityEndpoint}
              onEditDestinationEntityDeployment={destinationWorkspace.openEditDestinationEntityDeploymentPanel}
              onSaveDestinationEntityDeployment={destinationWorkspace.saveDestinationEntityDeploymentDetails}
              onDeleteDestinationEntityDeployment={destinationWorkspace.deleteDestinationEntityDeployment}
              onOpenDestinationEntityDeploymentSchedule={openDestinationScheduleForEntityDeployment}
              onManageDestinationEntityDeploymentEntity={openDestinationEntityDeploymentEntityManage}
              onMapDestinationEntityDeploymentEntity={openDestinationEntityDeploymentEntityMap}
              onSearchDestinationEntityDeploymentEntity={openDestinationEntityDeploymentEntitySearch}
              canBuyResourceAllowance={canAddMarketplaceResource && Boolean(selectedMarketplaceResource)}
              onBuyResourceAllowance={openResourcePurchaseDialog}
              importSupport={resourceConfigurationImportSupport}
              onImportResourceConfiguration={openResourceConfigurationImportDialog}
              eventManager={eventMgr}
            />
          ) : (
            <ResourceToolUnavailable toolKey={selectedToolKey} />
          )}
        </section>
      </div>
      <MarketplaceAllowancePurchaseDialog
        open={Boolean(purchaseDialogResource)}
        resource={purchaseDialogResource}
        product={purchaseDialogResource ? getProduct(purchaseDialogResource.productKey, marketplaceCatalog?.products || []) : null}
        environmentKey={marketplaceCatalog?.environmentKey || catalogModel.environmentKey}
        clientProductOffers={marketplaceCatalog?.clientProductOffers || []}
        initialOfferKey={purchaseDialogOfferKey}
        actionVerb="Buy"
        isSelfHostedResourceMode={marketplaceCatalog?.isSelfHostedResourceMode}
        onClose={closeResourcePurchaseDialog}
        onPurchased={handleResourcePurchased}
      />
      <ResourceConfigurationDeleteDialog
        configuration={deleteConfigurationTarget}
        confirmText={deleteConfigurationConfirmText}
        state={deleteConfigurationState}
        onChangeConfirmText={setDeleteConfigurationConfirmText}
        onCancel={closeDeleteConfigurationDialog}
        onConfirm={confirmDeleteConfiguration}
      />
      <ResourceConfigurationDeactivateDialog
        configuration={deactivateConfigurationTarget}
        state={configurationActionState}
        onCancel={closeDeactivateConfigurationDialog}
        onConfirm={confirmDeactivateConfiguration}
      />
      <ResourceConfigurationEditPanel
        open={editConfigurationPanelOpen}
        configuration={editConfigurationPanelData}
        state={configurationActionState}
        onSave={saveConfigurationDetails}
        onCancel={closeEditConfigurationPanel}
        onOpenChange={setEditConfigurationPanelOpen}
        onChangeConfiguration={setEditConfigurationPanelData}
      />
      <ResourceConfigurationImportDialog
        dialog={resourceConfigurationImportDialog}
        schemaOptions={buildSchemaImportOptions(resourceConfigurationImportDialog.schemas)}
        offerRows={resourceConfigurationImportOfferCardRows}
        onChange={updateResourceConfigurationImportDialog}
        onCancel={closeResourceConfigurationImportDialog}
        onConfirm={confirmResourceConfigurationImport}
      />
      <ResourceIntegrationEditPanel
        open={editIntegrationPanelOpen}
        integration={editIntegrationPanelData}
        metadata={integrationScreenState.metadata}
        state={configurationActionState}
        onSave={saveIntegrationDetails}
        onCancel={closeEditIntegrationPanel}
        onOpenChange={setEditIntegrationPanelOpen}
        onChangeIntegration={setEditIntegrationPanelData}
      />
      <ResourceSourceSystemEditPanel
        open={editSourceSystemPanelOpen}
        sourceSystem={editSourceSystemPanelData}
        metadata={sourceSystemScreenState.metadata}
        state={configurationActionState}
        onSave={saveSourceSystemDetails}
        onCancel={closeEditSourceSystemPanel}
        onOpenChange={setEditSourceSystemPanelOpen}
        onChangeSourceSystem={setEditSourceSystemPanelData}
      />
      <ResourceSchemaEditPanel
        open={editSchemaPanelOpen}
        schema={editSchemaPanelData}
        metadata={schemaScreenState.metadata}
        state={configurationActionState}
        onSave={saveSchemaDetails}
        onCancel={closeEditSchemaPanel}
        onOpenChange={setEditSchemaPanelOpen}
        onChangeSchema={setEditSchemaPanelData}
      />
      <ResourceSchemaVersionEditPanel
        open={editSchemaVersionPanelOpen}
        schemaVersion={editSchemaVersionPanelData}
        state={configurationActionState}
        onSave={saveSchemaVersionDetails}
        onCancel={closeEditSchemaVersionPanel}
        onOpenChange={setEditSchemaVersionPanelOpen}
        onChangeSchemaVersion={setEditSchemaVersionPanelData}
      />
      <SchemaVersionPublishDialog
        dialog={publishSchemaVersionDialog}
        onChangeMakePrimary={(makePrimary) => setPublishSchemaVersionDialog((previous) => ({
          ...previous,
          makePrimary: Boolean(makePrimary)
        }))}
        onCancel={closePublishSchemaVersionDialog}
        onConfirm={confirmPublishSchemaVersion}
      />
      <ResourceDestinationEditPanel
        open={destinationWorkspace.editDestinationPanelOpen}
        destination={destinationWorkspace.editDestinationPanelData}
        metadata={destinationWorkspace.editDestinationPanelMetadata}
        state={configurationActionState}
        onSave={destinationWorkspace.saveDestinationDetails}
        onCancel={destinationWorkspace.closeEditDestinationPanel}
        onOpenChange={destinationWorkspace.setEditDestinationPanelOpen}
        onChangeDestination={destinationWorkspace.setEditDestinationPanelData}
      />
      <ResourceDestinationDeploymentEditPanel
        open={destinationWorkspace.editDestinationDeploymentPanelOpen}
        deployment={destinationWorkspace.editDestinationDeploymentPanelData}
        metadata={destinationScreenState.deploymentMetadata}
        defaults={destinationScreenState.defaults}
        state={configurationActionState}
        onSave={destinationWorkspace.saveDestinationDeploymentDetails}
        onCancel={destinationWorkspace.closeEditDestinationDeploymentPanel}
        onOpenChange={destinationWorkspace.setEditDestinationDeploymentPanelOpen}
        onChangeDeployment={destinationWorkspace.setEditDestinationDeploymentPanelData}
      />
      <ResourceDestinationEntityDeploymentEditPanel
        open={destinationWorkspace.editDestinationEntityDeploymentPanelOpen}
        entityDeployment={destinationWorkspace.editDestinationEntityDeploymentPanelData}
        metadata={destinationScreenState.entityDeploymentMetadata}
        defaults={destinationScreenState.defaults}
        state={configurationActionState}
        onSave={destinationWorkspace.saveDestinationEntityDeploymentDetails}
        onCancel={destinationWorkspace.closeEditDestinationEntityDeploymentPanel}
        onOpenChange={destinationWorkspace.setEditDestinationEntityDeploymentPanelOpen}
        onChangeEntityDeployment={destinationWorkspace.setEditDestinationEntityDeploymentPanelData}
      />
      {destinationWorkspace.editEndpointEntityEndpointPanelOpen ? (() => {
        const endpointEntityPanelData = destinationWorkspace.editEndpointEntityEndpointPanelData;
        const endpointEntityPanelViewOnly = Boolean(endpointEntityPanelData?.__EndpointScopeViewOnly);
        const endpointEntityPanelAddMode = endpointEntityPanelData?.__EndpointEntityMode === "add";
        const endpointEntityPanelTitle = endpointEntityPanelAddMode
          ? "Add Endpoint Scope"
          : `${endpointEntityPanelViewOnly ? "View" : "Edit"} Endpoint Scope`;

        return (
          <SidePanelFormControl
            key={`${getString(endpointEntityPanelData, "UniqueId", "uniqueId", "_tempKey") || "endpoint-entity"}-${destinationWorkspace.editEndpointEntityEndpointPanelOpen ? "open" : "closed"}`}
            open={destinationWorkspace.editEndpointEntityEndpointPanelOpen}
            title={endpointEntityPanelTitle}
            metadata={endpointEntityEndpointMetadata}
            formFields={endpointEntityEndpointMetadata.Fields}
            itemData={endpointEntityPanelData}
            onChange={(_value, _fieldName, nextPanelData) => {
              const deploymentId = getString(
                nextPanelData,
                "__DeploymentId",
                "SchemaDestinationDeploymentId",
                "schemaDestinationDeploymentId"
              );
              const entityObjectOptions = destinationWorkspace.endpointEntityObjectSelectOptionsByDeployment?.[
                normalizeCompare(deploymentId)
              ] || [];
              destinationWorkspace.setEditEndpointEntityEndpointPanelData(
                normalizeEndpointEntityPanelExecutionMode(nextPanelData, entityObjectOptions)
              );
            }}
            onSave={destinationWorkspace.saveEndpointEntityEndpointDetails}
            onCancel={destinationWorkspace.closeEditEndpointEntityEndpointPanel}
            onUpdateSidePanel={() => {}}
            saveButtonText={configurationActionState?.busyAction?.startsWith("endpoint-entity-save:") ? "Saving..." : "Save"}
            setOpenEditSidePanel={(value) => {
              destinationWorkspace.setEditEndpointEntityEndpointPanelOpen(Boolean(value));
              if (!value) {
                destinationWorkspace.setEditEndpointEntityEndpointPanelData(null);
              }
            }}
            setItemData={(nextPanelData) => {
              const currentPanelData = destinationWorkspace.editEndpointEntityEndpointPanelData;
              const resolvedPanelData = typeof nextPanelData === "function"
                ? nextPanelData(currentPanelData)
                : nextPanelData;
              const deploymentId = getString(
                resolvedPanelData,
                "__DeploymentId",
                "SchemaDestinationDeploymentId",
                "schemaDestinationDeploymentId"
              );
              const entityObjectOptions = destinationWorkspace.endpointEntityObjectSelectOptionsByDeployment?.[
                normalizeCompare(deploymentId)
              ] || [];
              destinationWorkspace.setEditEndpointEntityEndpointPanelData(
                normalizeEndpointEntityPanelExecutionMode(resolvedPanelData, entityObjectOptions)
              );
            }}
            viewOnly={endpointEntityPanelViewOnly}
          />
        );
      })() : null}
      <DestinationDeploymentDeleteDialog
        deleteRequest={destinationWorkspace.deleteDestinationDeploymentRequest}
        busy={configurationActionState?.busyAction?.startsWith("destination-deployment-delete:")}
        error={configurationActionState?.error}
        onCancel={destinationWorkspace.closeDeleteDestinationDeploymentDialog}
        onConfirm={destinationWorkspace.confirmDeleteDestinationDeployment}
      />
      <DestinationEntityDeploymentDeleteDialog
        deleteRequest={destinationWorkspace.deleteDestinationEntityDeploymentRequest}
        busy={configurationActionState?.busyAction?.startsWith("destination-entity-deployment-delete:")}
        error={configurationActionState?.error}
        onCancel={destinationWorkspace.closeDeleteDestinationEntityDeploymentDialog}
        onConfirm={destinationWorkspace.confirmDeleteDestinationEntityDeployment}
      />
      <DialogControl
        controlOrigin="endpoint-entity-delete"
        title="Delete Entity Endpoint"
        openDialog={Boolean(destinationWorkspace.deleteEndpointEntityEndpointRequest?.endpointEntityEndpoint)}
        okText={configurationActionState?.busyAction?.startsWith("endpoint-entity-delete:") ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        onOk={destinationWorkspace.confirmDeleteEndpointEntityEndpoint}
        onCancel={destinationWorkspace.closeDeleteEndpointEntityEndpointDialog}
        onX={destinationWorkspace.closeDeleteEndpointEntityEndpointDialog}
        disableOk={configurationActionState?.busyAction?.startsWith("endpoint-entity-delete:")}
        disableCancel={configurationActionState?.busyAction?.startsWith("endpoint-entity-delete:")}
      >
        <div className="resource-workspace-linked-record-add-dialog">
          <p>
            Delete "{getString(destinationWorkspace.deleteEndpointEntityEndpointRequest?.endpointEntityEndpoint, "EntityName", "entityName", "DisplayName", "displayName") || "Entity Endpoint"}" from endpoint scope? This cannot be undone.
          </p>
          {configurationActionState?.error ? <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">{configurationActionState.error}</div> : null}
        </div>
      </DialogControl>
      <DestinationDeploymentManualRunDialog
        open={destinationWorkspace.destinationDeploymentManualRunDialog.open}
        destination={destinationScreenState.destination}
        deployment={destinationWorkspace.destinationDeploymentManualRunDialog.deployment}
        defaults={destinationScreenState.defaults}
        entityObjects={destinationScreenState.entityObjects}
        sourceObjects={destinationScreenState.sourceObjects}
        canEditSingleThreadMode={isSystemAdmin}
        values={destinationWorkspace.destinationDeploymentManualRunDialog.values}
        busy={configurationActionState?.busyAction?.startsWith("destination-deployment-manual-run:")}
        error={destinationWorkspace.destinationDeploymentManualRunDialog.error}
        feedback={destinationWorkspace.destinationDeploymentManualRunDialog.feedback}
        feedbackRef={destinationWorkspace.destinationDeploymentManualRunFeedbackRef}
        feedbackManager={destinationWorkspace.destinationDeploymentManualRunStream.manager}
        feedbackOperationName={destinationWorkspace.destinationDeploymentManualRunStream.operationName}
        feedbackRefId={destinationWorkspace.destinationDeploymentManualRunStream.refId}
        onChange={destinationWorkspace.setDestinationDeploymentManualRunValues}
        onCancel={destinationWorkspace.closeDestinationDeploymentManualRunDialog}
        onConfirm={destinationWorkspace.runDestinationDeploymentManualRun}
      />
      <WarehouseDeploymentDeployDialog
        open={destinationWorkspace.warehouseDeploymentDeployDialog.open}
        destination={destinationScreenState.destination}
        deployment={destinationWorkspace.warehouseDeploymentDeployDialog.deployment}
        entityObjects={destinationScreenState.entityObjects}
        sourceObjects={destinationScreenState.sourceObjects}
        canEditProcessorType={isSystemAdmin}
        values={destinationWorkspace.warehouseDeploymentDeployDialog.values}
        busy={configurationActionState?.busyAction?.startsWith("warehouse-deployment-deploy:")}
        error={destinationWorkspace.warehouseDeploymentDeployDialog.error}
        onChange={destinationWorkspace.setWarehouseDeploymentDeployValues}
        onCancel={destinationWorkspace.closeWarehouseDeploymentDeployDialog}
        onConfirm={destinationWorkspace.confirmWarehouseDeploymentDeploy}
      />
      <WarehouseDeploymentDropDialog
        open={destinationWorkspace.warehouseDeploymentDropDialog.open}
        destination={destinationScreenState.destination}
        deployment={destinationWorkspace.warehouseDeploymentDropDialog.deployment}
        busy={configurationActionState?.busyAction?.startsWith("warehouse-deployment-drop:")}
        error={destinationWorkspace.warehouseDeploymentDropDialog.error}
        onCancel={destinationWorkspace.closeWarehouseDeploymentDropDialog}
        onConfirm={destinationWorkspace.confirmWarehouseDeploymentDrop}
      />
      <WarehouseDeploymentCloneDialog
        open={destinationWorkspace.warehouseDeploymentCloneDialog.open}
        destination={destinationScreenState.destination}
        deployment={destinationWorkspace.warehouseDeploymentCloneDialog.deployment}
        schemaVersionOptions={getWarehouseSchemaVersionOptions(destinationScreenState.schema)}
        values={destinationWorkspace.warehouseDeploymentCloneDialog.values}
        busy={configurationActionState?.busyAction?.startsWith("warehouse-deployment-clone:")}
        error={destinationWorkspace.warehouseDeploymentCloneDialog.error}
        onChange={destinationWorkspace.setWarehouseDeploymentCloneValues}
        onCancel={destinationWorkspace.closeWarehouseDeploymentCloneDialog}
        onConfirm={destinationWorkspace.confirmWarehouseDeploymentClone}
      />
      <EndpointDeploymentLifecycleConfirmDialog
        request={destinationWorkspace.endpointDeploymentLifecycleConfirmDialog}
        onCancel={destinationWorkspace.closeEndpointDeploymentLifecycleConfirmDialog}
        onConfirm={destinationWorkspace.confirmEndpointDeploymentLifecycleAction}
      />
      <ResourceSourceTargetEditPanel
        open={editSourceTargetPanelOpen}
        target={editSourceTargetPanelData}
        metadata={sourceSystemScreenState.sourceTargetMetadata}
        state={editSourceTargetPanelState}
        onSave={saveSourceTargetDetails}
        onCancel={closeEditSourceTargetPanel}
        onOpenChange={setEditSourceTargetPanelOpen}
        onChangeTarget={setEditSourceTargetPanelData}
      />
      <SourceTargetStatusDialog
        target={sourceTargetStatusDialog.target}
        isActive={sourceTargetStatusDialog.isActive}
        busy={sourceTargetStatusDialog.busy}
        error={sourceTargetStatusDialog.error}
        onCancel={closeSourceTargetStatusDialog}
        onConfirm={confirmSourceTargetStatusChange}
      />
      <SourceObjectAddDialog
        open={addSourceObjectDialogOpen}
        operationDisplayName={getOperationDisplayName(sourceSystemLoadObjectsOperation)}
        sourceSystem={sourceSystemScreenState.sourceSystem}
        sourceObjects={sourceSystemScreenState.sourceObjects}
        targetFilterDefaults={selectedProductResourceDefaults}
        selectedTargetIds={addSourceObjectTargetIds}
        state={addSourceObjectState}
        onChangeSelectedTargetIds={setAddSourceObjectTargetIds}
        onCancel={closeAddSourceObjectDialog}
        onBack={returnToAddSourceObjectSelection}
        onConfirm={runLoadSourceObjectsForTargets}
      />
      <DestinationEntityDeploymentPickerDialog
        open={destinationWorkspace.destinationEntityPickerOpen}
        entityObjects={destinationScreenState.entityObjects}
        sourceObjects={destinationScreenState.sourceObjects}
        deployments={getDestinationDeployments(destinationScreenState.destination, destinationScreenState.defaults)}
        existingEntityDeployments={getDestinationEntityDeployments(destinationScreenState.destination)}
        selectedDeploymentId={destinationWorkspace.destinationEntityPickerDeploymentId}
        selectedEntityObjectIds={destinationWorkspace.destinationEntityPickerSelection}
        busy={configurationActionState.busyAction === "destination-entity-deployment-generate"}
        error={configurationActionState.error}
        onChangeSelectedDeploymentId={destinationWorkspace.setDestinationEntityPickerDeploymentId}
        onChangeSelectedEntityObjectIds={destinationWorkspace.setDestinationEntityPickerSelection}
        onCancel={destinationWorkspace.closeDestinationEntityPicker}
        onConfirm={destinationWorkspace.confirmDestinationEntityPicker}
      />
      <SourceObjectExportDialog
        open={sourceObjectExportDialog.open}
        sourceObject={sourceObjectExportDialog.sourceObject}
        selectedFormat={sourceObjectExportDialog.selectedFormat}
        busy={sourceObjectExportDialog.busy}
        error={sourceObjectExportDialog.error}
        resultMessage={sourceObjectExportDialog.resultMessage}
        onSelectFormat={selectSourceObjectExportFormat}
        onCancel={closeSourceObjectExportDialog}
        onConfirm={executeSourceObjectExport}
      />
      <DestinationEntitySearchDialog
        dialog={destinationEntitySearchDialog}
        onChange={updateDestinationEntitySearchDialog}
        onCancel={closeDestinationEntitySearchDialog}
        onConfirm={runDestinationEntityDeploymentEntitySearch}
      />
      <ResourceConnectionResultDialog
        result={connectionResultDialog}
        onClose={closeConnectionResultDialog}
      />
      <ResourceConnectionResultDialog
        result={configurationOperationResultDialog}
        onClose={() => setConfigurationOperationResultDialog(null)}
      />
      <DialogControl
        controlOrigin="source-system-execute-operation"
        openDialog={sourceSystemOperationDialog.openDialog}
        title={sourceSystemOperationDialog.activeOperation?.DisplayName ?? sourceSystemOperationDialog.activeOperation?.Name ?? "Operation Completed"}
        subTitle={sourceSystemOperationDialog.activeOperation?.Details}
        onCancel={cancelSourceSystemDialogOperation}
        onOk={executeSourceSystemDialogOperation}
        excludeCancel={!sourceSystemOperationDialog.activeOperation}
        okText={sourceSystemOperationDialog.activeOperation && sourceSystemOperationDialog.operationErrors ? "Retry" : "OK"}
        disableCancel={sourceSystemOperationDialog.operationSuccessMessage}
      >
        {sourceSystemOperationDialog.renderOperationContent?.()}
      </DialogControl>
      <DialogControl
        controlOrigin="source-object-execute-operation"
        openDialog={sourceObjectOperationDialog.openDialog}
        title={sourceObjectOperationDialog.activeOperation?.DisplayName ?? sourceObjectOperationDialog.activeOperation?.Name ?? "Operation Completed"}
        subTitle={sourceObjectOperationDialog.activeOperation?.Details}
        onCancel={sourceObjectOperationDialog.cancelOperation}
        onOk={executeSourceObjectDialogOperation}
        excludeCancel={!sourceObjectOperationDialog.activeOperation}
        okText={sourceObjectOperationDialog.activeOperation && sourceObjectOperationDialog.operationErrors ? "Retry" : "OK"}
        disableCancel={sourceObjectOperationDialog.operationSuccessMessage}
      >
        {sourceObjectOperationDialog.renderOperationContent?.()}
      </DialogControl>
      <DialogControl
        controlOrigin="schema-execute-operation"
        openDialog={schemaOperationDialog.openDialog}
        title={schemaOperationDialog.activeOperation?.DisplayName ?? schemaOperationDialog.activeOperation?.Name ?? "Operation Completed"}
        subTitle={schemaOperationDialog.activeOperation?.Details}
        onCancel={schemaOperationDialog.cancelOperation}
        onOk={executeSchemaDialogOperation}
        excludeCancel={!schemaOperationDialog.activeOperation}
        okText={schemaOperationDialog.activeOperation && schemaOperationDialog.operationErrors ? "Retry" : "OK"}
        disableCancel={schemaOperationDialog.operationSuccessMessage}
      >
        {schemaOperationDialog.renderOperationContent?.()}
      </DialogControl>
      <DialogControl
        controlOrigin="entity-object-execute-operation"
        openDialog={entityObjectOperationDialog.openDialog}
        title={entityObjectOperationDialog.activeOperation?.DisplayName ?? entityObjectOperationDialog.activeOperation?.Name ?? "Operation Completed"}
        subTitle={entityObjectOperationDialog.activeOperation?.Details}
        onCancel={entityObjectOperationDialog.cancelOperation}
        onOk={executeEntityObjectDialogOperation}
        excludeCancel={!entityObjectOperationDialog.activeOperation}
        okText={entityObjectOperationDialog.activeOperation && entityObjectOperationDialog.operationErrors ? "Retry" : "OK"}
        disableCancel={entityObjectOperationDialog.operationSuccessMessage}
      >
        {entityObjectOperationDialog.renderOperationContent?.()}
      </DialogControl>
    </main>
  );
};

const ResourceListScreen = ({
  title = "All Resources",
  eyebrow = "Resources",
  resources,
  resourceConfigurations = [],
  onBuy,
  onSelect,
  onAddConfiguration,
  onRemoveResource,
  canAddResource = false,
  canConfigureResource = false,
  canRemoveResource = false,
  showEdiGroup = false,
  onSelectEdiGroup,
  backAction = null,
  emptyTitle = "No Client Resources",
  emptyMessage = "Add a marketplace resource before managing resource tools."
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [pendingSetupResource, setPendingSetupResource] = useState(null);
  const [pendingRemoveResource, setPendingRemoveResource] = useState(null);
  const [removeResourceConfirmText, setRemoveResourceConfirmText] = useState("");
  const [removeResourceState, setRemoveResourceState] = useState({ busy: false, error: "" });

  const statusOptions = useMemo(
    () => getSortedUniqueOptions(resources.map(getClientResourceStatusLabel)),
    [resources]
  );
  const typeOptions = useMemo(
    () => getSortedUniqueOptions(resources.map(getResourceTypeLabel)),
    [resources]
  );
  const hasActiveFilters = !!searchTerm.trim() || !!statusFilter || !!typeFilter;
  const filteredResources = useMemo(() => {
    const normalizedSearch = normalizeCompare(searchTerm);

    return resources.filter((resource) => {
      const matchesSearch = !normalizedSearch || [
        getResourceCatalogDisplayName(resource),
        resource.displayName,
        resource.productName,
        resource.productResourceKey,
        isEdiResource(resource) ? EDI_RESOURCE_GROUP_TITLE : ""
      ].some((value) => normalizeCompare(value).includes(normalizedSearch));
      const matchesStatus = !statusFilter || getClientResourceStatusLabel(resource) === statusFilter;
      const matchesType = !typeFilter || getResourceTypeLabel(resource) === typeFilter;

      return matchesSearch && matchesStatus && matchesType;
    });
  }, [resources, searchTerm, statusFilter, typeFilter]);

  const displayRows = useMemo(() => {
    if (!showEdiGroup) return filteredResources.map((resource) => ({ kind: "resource", resource }));

    const rows = [];
    let ediGroupInserted = false;
    filteredResources.forEach((resource) => {
      if (!isEdiResource(resource)) {
        rows.push({ kind: "resource", resource });
        return;
      }

      if (!ediGroupInserted) {
        rows.push({
          kind: "edi-group",
          id: EDI_RESOURCE_GROUP_KEY,
          resources: filteredResources.filter(isEdiResource)
        });
        ediGroupInserted = true;
      }
    });
    return rows;
  }, [filteredResources, showEdiGroup]);

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setTypeFilter("");
  };
  const hasConfigurableResource = resources.some(isClientResourceSetupEnabled);
  const primaryActionLabel = hasConfigurableResource ? "Add" : "Buy";
  const primaryActionTitle = hasConfigurableResource ? "Add resources" : "Buy resources";
  const pendingSetupResourceName = pendingSetupResource
    ? getResourceCatalogDisplayName(pendingSetupResource)
    : "";
  const pendingRemoveResourceName = pendingRemoveResource
    ? getResourceCatalogDisplayName(pendingRemoveResource)
    : "";

  const openSetupConfirmation = (event, resource) => {
    event.stopPropagation();
    if (!canConfigureResource) return;
    setPendingSetupResource(resource);
  };

  const openRemoveConfirmation = (event, resource) => {
    event.stopPropagation();
    if (!canRemoveResource) return;
    setPendingRemoveResource(resource);
    setRemoveResourceConfirmText("");
    setRemoveResourceState({ busy: false, error: "" });
  };

  const cancelSetupConfirmation = () => {
    setPendingSetupResource(null);
  };

  const cancelRemoveConfirmation = () => {
    if (removeResourceState.busy) return;
    setPendingRemoveResource(null);
    setRemoveResourceConfirmText("");
    setRemoveResourceState({ busy: false, error: "" });
  };

  const confirmSetupConfiguration = () => {
    if (!canConfigureResource || !pendingSetupResource) return;
    const resource = pendingSetupResource;
    setPendingSetupResource(null);
    onAddConfiguration(resource);
  };

  const confirmRemoveResource = async () => {
    if (!canRemoveResource || !pendingRemoveResource || removeResourceConfirmText.trim() !== pendingRemoveResourceName) return;
    try {
      setRemoveResourceState({ busy: true, error: "" });
      await onRemoveResource?.(pendingRemoveResource);
      setPendingRemoveResource(null);
      setRemoveResourceConfirmText("");
      setRemoveResourceState({ busy: false, error: "" });
    } catch (removeError) {
      setRemoveResourceState({
        busy: false,
        error: removeError?.message || "Client Resource could not be removed."
      });
    }
  };

  return (
    <main className="resource-workspace" aria-label="Resources">
      <header className="resource-workspace-list-header">
        <div className="resource-workspace-list-header-title">
          <span className="resource-workspace-eyebrow">{eyebrow}</span>
          <h1>{title}</h1>
        </div>
        <span className="resource-workspace-list-header-actions">
          {backAction}
          {canAddResource ? (
            <ButtonControl
              type="ok"
              className="resource-workspace-buy-action resource-workspace-list-action"
              onClick={onBuy}
              title={primaryActionTitle}
              aria-label={primaryActionTitle}
            >
              {primaryActionLabel}
            </ButtonControl>
          ) : null}
        </span>
      </header>
      {resources.length > 0 ? (
        <>
          <div className="resource-workspace-filter-bar" aria-label="Filter resources">
            <label>
              <span>Search</span>
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search resources..."
                aria-label="Search resources"
              />
            </label>
            <label>
              <span>Status</span>
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                aria-label="Filter by status"
              >
                <option value="">All statuses</option>
                {statusOptions.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Type</span>
              <select
                value={typeFilter}
                onChange={(event) => setTypeFilter(event.target.value)}
                aria-label="Filter by type"
              >
                <option value="">All types</option>
                {typeOptions.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </label>
            <ButtonControl
              type="standard"
              className="resource-workspace-filter-clear"
              disabled={!hasActiveFilters}
              onClick={clearFilters}
            >
              Clear
            </ButtonControl>
          </div>
          {displayRows.length > 0 ? (
            <div className="resource-workspace-resource-list">
              {displayRows.map((row) => {
                if (row.kind === "edi-group") {
                  return (
                    <article
                      key={row.id}
                      className="resource-workspace-resource-row resource-workspace-resource-row--catalog"
                      role="button"
                      tabIndex={0}
                      onClick={onSelectEdiGroup}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onSelectEdiGroup?.();
                        }
                      }}
                    >
                      <span className="resource-workspace-resource-main">
                        <span className="resource-workspace-resource-icon">
                          <ResourceWorkspaceIcon
                            iconKey="marketplaceresourceedigroup"
                            iconPath={EDI_RESOURCE_GROUP_ICON_PATH}
                            label={EDI_RESOURCE_GROUP_TITLE}
                            preferImage
                            width="3.25rem"
                            height="3.25rem"
                          />
                        </span>
                        <span>
                          <strong>{EDI_RESOURCE_GROUP_TITLE}</strong>
                          <small>{buildEdiGroupSummary(row.resources)}</small>
                        </span>
                      </span>
                      {canAddResource ? (
                        <ButtonControl
                          type="ok-small"
                          className="resource-workspace-resource-add"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onBuy?.();
                          }}
                        >
                          Add More
                        </ButtonControl>
                      ) : null}
                    </article>
                  );
                }

                const resource = row.resource;
                const hasResourceAccess = resource.hasAccess !== false;
                const setupEnabled = canConfigureResource
                  && hasResourceAccess
                  && isClientResourceSetupEnabled(resource);
                const canRemoveThisResource = canRemoveResource
                  && hasResourceAccess
                  && canRemoveUnstartedClientResource(resource, resourceConfigurations);
                const isSystemManagedClientMcpServer = isClientMcpServerResource(resource);
                const hasSingleInstance = isSingleInstanceProductResource(resource)
                  && resourceConfigurations.some((configuration) =>
                    normalizeCompare(getString(configuration, "ClientResourceId", "clientResourceId")) === normalizeCompare(resource.id)
                    && !isConfigurationRetired(configuration)
                  );
                const catalogDisplayName = getResourceCatalogDisplayName(resource);
                return (
                  <article
                    key={resource.id}
                    className="resource-workspace-resource-row resource-workspace-resource-row--catalog"
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelect(resource)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onSelect(resource);
                      }
                    }}
                  >
                    <span className="resource-workspace-resource-main">
                      <span className="resource-workspace-resource-icon">
                        <ResourceWorkspaceIcon
                          iconKey={getString(resource.productResource, "Icon", "icon") || RESOURCE_FALLBACK_ICON}
                          iconPath={getResourceWorkspaceIconPath(resource)}
                          label={catalogDisplayName}
                          preferImage={resourceUsesProductIcon(resource)}
                          width="3.25rem"
                          height="3.25rem"
                        />
                      </span>
                      <span>
                        <strong>{catalogDisplayName}</strong>
                        {getResourceCatalogNote(resource) && (
                          <small>{getResourceCatalogNote(resource)}</small>
                        )}
                      </span>
                    </span>
                    {!hasResourceAccess ? (
                      <span className="resource-workspace-muted">Access required</span>
                    ) : isSystemManagedClientMcpServer ? (
                      <span className="resource-workspace-muted">System managed</span>
                    ) : hasSingleInstance ? (
                      <span className="resource-workspace-muted">Single instance</span>
                    ) : canRemoveThisResource ? (
                      <ButtonControl
                        type="delete-small"
                        className="resource-workspace-resource-remove"
                        title="Remove unstarted resource"
                        onClick={(event) => openRemoveConfirmation(event, resource)}
                      >
                        Remove
                      </ButtonControl>
                    ) : canConfigureResource ? (
                      <ButtonControl
                        type="ok-small"
                        className="resource-workspace-resource-add"
                        disabled={!setupEnabled}
                        title={!setupEnabled ? "Setup requires an Active and Approved Client Resource." : undefined}
                        onClick={(event) => openSetupConfirmation(event, resource)}
                      >
                        {setupEnabled ? "Add More" : "Buy More"}
                      </ButtonControl>
                    ) : <span className="resource-workspace-muted">View only</span>}
                  </article>
                );
              })}
            </div>
          ) : (
            <ResourceStateControl
              compact
              headingLevel={1}
              title="No matching resources"
              message="Adjust the search or filters to show resources."
              actions={<ButtonControl type="standard" onClick={clearFilters}>Clear</ButtonControl>}
            />
          )}
        </>
      ) : (
        <ResourceStateControl
          headingLevel={1}
          title={emptyTitle}
          message={emptyMessage}
        />
      )}
      <DialogControl
        openDialog={Boolean(pendingSetupResource)}
        title="Enter Setup Mode"
        onCancel={cancelSetupConfirmation}
        onOk={confirmSetupConfiguration}
        okText="Continue"
        cancelText="Cancel"
      >
        <div className="resource-workspace-setup-confirmation">
          <p>
            You are about to enter Setup mode for a new {pendingSetupResourceName}. Would you like to continue?
          </p>
        </div>
      </DialogControl>
      <ClientResourceRemoveDialog
        resource={pendingRemoveResource}
        confirmText={removeResourceConfirmText}
        state={removeResourceState}
        onChangeConfirmText={setRemoveResourceConfirmText}
        onCancel={cancelRemoveConfirmation}
        onConfirm={confirmRemoveResource}
      />
    </main>
  );
};

const ClientResourceRemoveDialog = ({
  resource,
  confirmText,
  state,
  onChangeConfirmText,
  onCancel,
  onConfirm
}) => {
  const resourceName = getDisplayName(resource?.clientResource, resource?.displayName || "Resource");
  const canConfirm = Boolean(resource) && confirmText.trim() === resourceName && !state.busy;

  return (
    <DialogControl
      openDialog={Boolean(resource)}
      title="Remove Resource"
      okText={state.busy ? "Removing..." : "Remove"}
      cancelText="Cancel"
      disableOk={!canConfirm}
      onOk={onConfirm}
      onCancel={onCancel}
      onX={onCancel}
    >
      <div
        className="resource-workspace-delete-dialog"
        onCopy={(event) => event.preventDefault()}
        onCut={(event) => event.preventDefault()}
        onPaste={(event) => event.preventDefault()}
      >
        <p>
          Removing <strong>{resourceName}</strong> will remove this unstarted resource from the Resources list.
        </p>
        <p>
          This resource has no configurations yet. Type <strong>"{resourceName}"</strong> to continue.
        </p>
        <FieldControl
          fieldMetadata={buildTextBoxField(
            "RemoveClientResourceConfirmation",
            "Confirmation",
            `Type "${resourceName}" to remove this unstarted resource.`
          )}
          groupId="clientResourceRemove"
          value={confirmText}
          disabled={state.busy}
          onChange={(nextValue) => onChangeConfirmText(nextValue ?? "")}
        />
        {state.error ? (
          <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">
            {state.error}
          </div>
        ) : null}
      </div>
    </DialogControl>
  );
};

const ResourceWorkspaceHeader = ({ selectedResource }) => (
  <header className="resource-workspace-header">
    <span className="resource-workspace-header-icon" aria-hidden="true">
      <ResourceWorkspaceIcon
        iconKey={getString(selectedResource.productResource, "Icon", "icon") || RESOURCE_FALLBACK_ICON}
        iconPath={getResourceWorkspaceIconPath(selectedResource)}
        label={selectedResource.displayName}
        preferImage={resourceUsesProductIcon(selectedResource)}
        width="3.25rem"
        height="3.25rem"
      />
    </span>
    <div className="resource-workspace-title-block">
      <div className="resource-workspace-title-line">
        <h1>{selectedResource.displayName}</h1>
        <ResourceMaturityBadge value={getValue(selectedResource.productResource, "MaturityStatus", "maturityStatus")} />
      </div>
    </div>
  </header>
);

const ResourceOverviewScreen = ({
  selectedResource,
  configurations,
  canOpenConfigurationSetup,
  onOpenConfigurationSetup,
  onManageConfiguration,
  onSelectConfigurationScreen,
  canBuyResourceAllowance,
  onBuyResourceAllowance,
  onAddResourceConfiguration,
  importSupport,
  onImportResourceConfiguration
}) => {
  const clientResource = selectedResource.clientResource;
  const productResource = selectedResource.productResource;
  const isSystemManagedClientMcpServer = isClientMcpServerResource(clientResource, productResource);
  const isSingleInstance = isSingleInstanceProductResource(productResource);
  const hasSingleInstance = isSingleInstance && configurations.some((configuration) => !isConfigurationRetired(configuration));
  const allowance = getValue(clientResource, "ResourceAllowance", "resourceAllowance") || {};
  const effectiveAllowance = selectedResource.effectiveAllowance || {};
  const isUnlimitedAllowance = Boolean(getValue(effectiveAllowance, "IsUnlimited", "isUnlimited"));
  const allowedCount = isUnlimitedAllowance
    ? Number.POSITIVE_INFINITY
    : getNumber(effectiveAllowance, "AllowedCount", "allowedCount")
      ?? getNumber(allowance, "AllowedResourceConfigurationCount", "allowedResourceConfigurationCount");
  const reservedCount = getNumber(effectiveAllowance, "ReservedCount", "reservedCount") ?? configurations.length;
  const remainingCount = isUnlimitedAllowance
    ? Number.POSITIVE_INFINITY
    : getNumber(effectiveAllowance, "RemainingCount", "remainingCount")
      ?? (allowedCount === null ? null : Math.max(0, allowedCount - reservedCount));
  const formatCapacityCount = (value) => {
    if (value === Number.POSITIVE_INFINITY) return "Unlimited";
    return value === null ? "Not set" : value;
  };
  const formatQuantity = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return "1";
    return Number.isInteger(parsed) ? String(parsed) : String(parsed).replace(/\.?0+$/, "");
  };
  const purchasedOffers = getArray(effectiveAllowance, "PurchasedOffers", "purchasedOffers", "Grants", "grants");
  const getPurchasedOfferName = (offer) =>
    getDisplayName(offer, formatDisplayLabel(getString(offer, "ProductOfferKey", "productOfferKey")) || "Purchased offer");
  const getOfferCapacityCount = (offer, ...keys) =>
    Boolean(getValue(offer, "IsUnlimited", "isUnlimited"))
      ? Number.POSITIVE_INFINITY
      : getNumber(offer, ...keys);
  const configurationUnit = getString(productResource, "ResourceConfigurationUnit", "resourceConfigurationUnit") || "configuration";
  const usagePercent = allowedCount === Number.POSITIVE_INFINITY
    ? (reservedCount > 0 ? 100 : 0)
    : allowedCount === null || allowedCount === 0
    ? (reservedCount > 0 ? 100 : 0)
    : Math.min(100, Math.round((reservedCount / allowedCount) * 100));
  const usageCountLabel = allowedCount === Number.POSITIVE_INFINITY
    ? "Unlimited"
    : allowedCount === null
      ? `${reservedCount} active`
      : `${reservedCount} of ${formatCapacityCount(allowedCount)}`;
  const usageDescription = remainingCount === Number.POSITIVE_INFINITY
    ? `${reservedCount} active ${reservedCount === 1 ? "configuration" : "configurations"}; unlimited capacity is available.`
    : remainingCount === null
      ? "Capacity is not set for this resource."
      : `${remainingCount} configuration slots remain.`;
  const hasRemainingConfigurationAllowance = remainingCount === Number.POSITIVE_INFINITY
    || (remainingCount !== null && remainingCount > 0);

  return (
    <div className="resource-workspace-overview">
      <section className="resource-workspace-panel">
        <div className="resource-workspace-section-title">
          <h2>Overview</h2>
          <p>{getString(productResource, "Description", "description") || "Resource details are managed from this workspace."}</p>
        </div>
        <div className="resource-workspace-metric-grid">
          <Metric label="Resource Status" value={getClientResourceStatusLabelFromItem(clientResource)} />
          <Metric label="Approval" value={getStatusLabel(APPROVAL_STATUS_LABELS, clientResource, "ApprovalStatus", "approvalStatus")} />
          <Metric label="Allowance" value={formatCapacityCount(allowedCount)} />
          <Metric label="Remaining" value={formatCapacityCount(remainingCount)} />
          <Metric label="Configurations" value={reservedCount} />
        </div>
        {!canOpenConfigurationSetup ? (
          <div className="resource-workspace-access-note" role="status">
            Setup and runtime access are disabled until this Client Resource is Active and Approved.
          </div>
        ) : null}
      </section>

      <section className="resource-workspace-two-column">
        <div className="resource-workspace-panel">
          <div className="resource-workspace-section-title">
            <h2>Resource Details</h2>
          </div>
          <dl className="resource-workspace-facts">
            <Fact label="Product" value={selectedResource.productName} />
            <Fact label="Product Resource" value={getDisplayName(productResource, selectedResource.productResourceKey)} />
            <Fact label="Configuration Unit" value={configurationUnit} />
          </dl>
        </div>

        <div className="resource-workspace-panel">
          <div className="resource-workspace-section-title">
            <h2>Product Details</h2>
          </div>
          {purchasedOffers.length > 0 ? (
            <div className="resource-workspace-offer-list" aria-label="Purchased offers">
              {purchasedOffers.map((offer, index) => {
                const offerAllowedCount = getOfferCapacityCount(offer, "AllowedCount", "allowedCount");
                const offerAssignedCount = getNumber(offer, "AssignedCount", "assignedCount") ?? 0;
                const offerRemainingCount = getOfferCapacityCount(offer, "RemainingCount", "remainingCount");
                const offerKey = getString(offer, "ClientProductOfferId", "clientProductOfferId")
                  || getString(offer, "ProductOfferKey", "productOfferKey")
                  || `${getPurchasedOfferName(offer)}-${index}`;

                return (
                  <article key={offerKey} className="resource-workspace-offer-row">
                    <div>
                      <strong>{getPurchasedOfferName(offer)}</strong>
                    </div>
                    <dl className="resource-workspace-offer-stats">
                      <div>
                        <dt>Quantity</dt>
                        <dd>{formatQuantity(getValue(offer, "Quantity", "quantity"))}</dd>
                      </div>
                      <div>
                        <dt>Allowance</dt>
                        <dd>{formatCapacityCount(offerAllowedCount)}</dd>
                      </div>
                      <div>
                        <dt>Used</dt>
                        <dd>{offerAssignedCount}</dd>
                      </div>
                      <div>
                        <dt>Remaining</dt>
                        <dd>{formatCapacityCount(offerRemainingCount)}</dd>
                      </div>
                    </dl>
                  </article>
                );
              })}
            </div>
          ) : (
            <p className="resource-workspace-muted">No purchased offers assigned.</p>
          )}
          <div className="resource-workspace-usage-chart" aria-label="Configuration usage">
            <div className="resource-workspace-usage-chart-header">
              <span>Configuration usage</span>
              <strong>{usageCountLabel}</strong>
            </div>
            <div className="resource-workspace-usage-track">
              <span style={{ width: `${usagePercent}%` }} />
            </div>
            <p>{usageDescription}</p>
          </div>
        </div>
      </section>

      <section className={`resource-workspace-panel resource-workspace-panel--configurations${configurations.length > 0 ? "" : " resource-workspace-panel--empty-configurations"}`}>
        <div className="resource-workspace-section-title resource-workspace-section-title--with-action">
          <h2>Configurations</h2>
          <div className="resource-workspace-section-actions">
            {isSystemManagedClientMcpServer ? null : hasRemainingConfigurationAllowance ? (
              <ButtonControl
                type="icon"
                className="resource-workspace-configuration-shell-action-button"
                disabled={!canOpenConfigurationSetup}
                onClick={onAddResourceConfiguration}
                title="Add configuration"
                aria-label="Add configuration"
              >
                <IconControl iconKey="add" title="Add configuration" width="1rem" height="1rem" />
              </ButtonControl>
            ) : hasSingleInstance ? (
              <span className="resource-workspace-muted">Single instance</span>
            ) : (
              <ButtonControl
                type="ok-small"
                disabled={!canBuyResourceAllowance}
                onClick={onBuyResourceAllowance}
              >
                Buy
              </ButtonControl>
            )}
            {importSupport ? (
              <ButtonControl
                type="ok-small"
                disabled={!canOpenConfigurationSetup}
                onClick={onImportResourceConfiguration}
              >
                Import
              </ButtonControl>
            ) : null}
          </div>
        </div>
        {configurations.length > 0 ? (
          <div className="resource-workspace-configuration-list">
            {configurations.map((configuration) => {
              const linkedRecords = getConfigurationLinkedRecords(configuration);
              const configurationKey = getId(configuration) || getDisplayName(configuration);

              return (
                <div
                  key={configurationKey}
                  className={`resource-workspace-configuration-group${linkedRecords.length > 0 ? "" : " resource-workspace-configuration-group--standalone"}`}
                >
                  <article className={`resource-workspace-configuration-row${linkedRecords.length > 0 ? "" : " resource-workspace-configuration-row--standalone"}`}>
                    <div>
                      <strong>{getDisplayName(configuration, "Resource configuration")}</strong>
                    </div>
                    <div className="resource-workspace-configuration-action">
                      <span className={getConfigurationStatusClassName(configuration)}>{getConfigurationStatusLabel(configuration)}</span>
                      {!isConfigurationComplete(configuration) ? (
                        <ButtonControl
                          type="ok-small"
                          disabled={!canOpenConfigurationSetup || !getId(configuration)}
                          title={!canOpenConfigurationSetup ? "Setup requires an Active and Approved Client Resource." : undefined}
                          onClick={() => onOpenConfigurationSetup(configuration)}
                        >
                          Setup
                        </ButtonControl>
                      ) : (
                        <ButtonControl
                          type="ok-small"
                          disabled={!canOpenConfigurationSetup || !getId(configuration)}
                          title={!canOpenConfigurationSetup ? "Management requires an Active and Approved Client Resource." : undefined}
                          onClick={() => onManageConfiguration(configuration)}
                        >
                          Manage
                        </ButtonControl>
                      )}
                    </div>
                  </article>
                  {linkedRecords.length > 0 ? (
                    <div className="resource-workspace-linked-record-grid" aria-label={`${getDisplayName(configuration, "Resource configuration")} linked setup records`}>
                      {linkedRecords.map((record, index) => {
                        const recordTypeLabel = getLinkedRecordTypeLabel(record, selectedResource);
                        const recordDisplayName = getLinkedRecordDisplayName(record);
                        const recordScreenKey = getLinkedRecordScreenKey(record, configuration);
                        const configurationInSetupMode = canContinueConfigurationSetup(configuration);
                        const recordScreenAvailable = Boolean(getId(configuration) && recordScreenKey);
                        const canOpenRecordScreen = recordScreenAvailable && !configurationInSetupMode;
                        const recordActionTitle = configurationInSetupMode
                          ? `Complete setup before opening ${recordTypeLabel} settings.`
                          : canOpenRecordScreen
                          ? `Open ${recordTypeLabel} screen`
                          : `No ${recordTypeLabel} screen is configured.`;

                        return (
                          <article
                            key={`${getString(record, "ItemName", "itemName")}-${getString(record, "ItemId", "itemId")}-${index}`}
                            className={`resource-workspace-linked-record-card${!canOpenRecordScreen ? " resource-workspace-linked-record-card--disabled" : ""}`}
                            aria-disabled={!canOpenRecordScreen ? "true" : undefined}
                          >
                            <div className="resource-workspace-linked-record-card__copy">
                              <span className="resource-workspace-linked-record-card__type">{recordTypeLabel}</span>
                              <strong>{recordDisplayName}</strong>
                            </div>
                            <ButtonControl
                              type="standard-small"
                              disabled={!canOpenRecordScreen}
                              title={recordActionTitle}
                              aria-label={recordActionTitle}
                              onClick={() => onSelectConfigurationScreen?.(getId(configuration), recordScreenKey, record)}
                            >
                              <IconControl iconKey="settings" title={recordActionTitle} width="0.95rem" height="0.95rem" />
                            </ButtonControl>
                          </article>
                        );
                      })}
                    </div>
                  ) : !isConfigurationComplete(configuration) ? (
                    <div className="resource-workspace-configuration-empty">
                      Click Setup to continue configuration.
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </section>
    </div>
  );
};

const ResourceConfigurationsScreen = ({
  selectedResource,
  configurations,
  resourceConfigurations = [],
  resourceLogs,
  resourceEvents,
  configurationNavigations,
  configurationRoute,
  selectedConfiguration,
  environmentKey,
  clientProductOffers = [],
  canOpenConfigurationSetup,
  onOpenConfigurationSetup,
  onAddResourceConfiguration,
  onAddLinkedEndpoint,
  onSelectConfigurationScreen,
  onDeleteConfiguration,
  onEditConfiguration,
  onValidateConfiguration,
  onActivateConfiguration,
  onDisableConfiguration,
  onReconfigureConfiguration,
  onRefreshConfigurations,
  onBackToDestinationDeployment,
  configurationActionState,
  integrationScreenState,
  sourceSystemScreenState,
  logManager,
  eventManager,
  recentLogsRefreshKey,
  onEditIntegration,
  onCheckIntegrationConnection,
  onSetIntegrationOnline,
  onEditSourceSystem,
  onSetSourceSystemActive,
  onSetSourceObjectActive,
  schemaScreenState,
  schemaOperations,
  onEditSchema,
  onRunSchemaOperation,
  onSelectSchemaVersion,
  onEditSchemaVersion,
  onPublishSchemaVersion,
  onRefreshSchemaWorkspace,
  onSetSourceObjectTargetActive,
  onLoadSourceSystemTargets,
  onOpenAddSourceObjects,
  onEditSourceObjectTarget,
  onAddQueryTarget,
  onCloneQueryTarget,
  onEditSourceObject,
  onEditCurrentSourceObject,
  sourceObjectEditRequestKey,
  sourceObjectManagerSourceObjectId,
  onCloseSourceObjectManager,
  sourceObjectOperations,
  onRunSourceObjectOperation,
  entityObjectOperations,
  onRunEntityObjectOperation,
  onOpenEntityObjectPanel,
  entityObjectManager,
  destinationScreenState,
  onRefreshDestinationWorkspace,
  destinationDeploymentRouteFilters,
  destinationEntityDeploymentRouteFilters,
  destinationScheduleActionState,
  onDestinationScheduleActionsChange,
  endpointDeploymentsSelectionState,
  onEndpointDeploymentsSelectionChange,
  destinationEntityScopeActionState,
  onDestinationEntityScopeActionsChange,
  warehouseViewActionState,
  onWarehouseViewActionsChange,
  onEditDestination,
  onSetDestinationActive,
  onCheckDestinationConnection,
  onManualRunDestinationDeployment,
  onDeployDestinationDeployment,
  onUndeployDestinationDeployment,
  onDeployWarehouseDeployment,
  onDropWarehouseDeployment,
  onCloneWarehouseDeployment,
  onAddDestinationDeployment,
  onManageDestinationDeployment,
  onEditDestinationDeployment,
  onSaveDestinationDeploymentSchemaScopes,
  onOpenEntitySchemaConfigurationFromScope,
  onSetDestinationDeploymentActive,
  onDeleteDestinationDeployment,
  onOpenDestinationDeploymentSchedule,
  onOpenDestinationDeploymentScope,
  onOpenDestinationDeploymentViews,
  onOpenDestinationEntityPicker,
  onOpenEndpointEntityEndpointPanel,
  onEditEndpointEntityEndpoint,
  onDeleteEndpointEntityEndpoint,
  onEditDestinationEntityDeployment,
  onSaveDestinationEntityDeployment,
  onDeleteDestinationEntityDeployment,
  onOpenDestinationEntityDeploymentSchedule,
  onManageDestinationEntityDeploymentEntity,
  onMapDestinationEntityDeploymentEntity,
  onSearchDestinationEntityDeploymentEntity,
  canBuyResourceAllowance,
  onBuyResourceAllowance,
  importSupport,
  onImportResourceConfiguration
}) => {
  const screenKey = configurationRoute.screenKey || CONFIGURATION_MANAGE_NAVIGATION.key;
  const sourceSystemAliasScreen = INBOUND_EDI_SOURCE_SYSTEM_SCREEN_KEYS.includes(screenKey)
    ? {
      ...(SOURCE_INTEGRATION_NAVIGATIONS.find((navigation) => navigation.key === SOURCE_SYSTEM_SCREEN_KEY) || {}),
      key: screenKey,
      displayName: screenKey === INBOUND_EDI_TARGETS_SCREEN_KEY
        ? "EDI Targets"
        : screenKey === INBOUND_EDI_SOURCE_OBJECTS_SCREEN_KEY
          ? "Source Objects"
          : "EDI Source System"
    }
    : null;
  const entitySchemaAliasScreen = [
    ...INBOUND_EDI_ENTITY_SCHEMA_SCREEN_KEYS,
    ...OUTBOUND_EDI_ENTITY_SCHEMA_SCREEN_KEYS
  ].includes(screenKey)
    ? {
      ...(ENTITY_SCHEMA_NAVIGATIONS.find((navigation) => navigation.key === ENTITY_SCHEMA_SCREEN_KEY) || {}),
      key: screenKey,
      displayName: screenKey === INBOUND_EDI_MAPPING_SCREEN_KEY
        ? "Mapping"
        : screenKey === OUTBOUND_EDI_ENTITY_OBJECTS_SCREEN_KEY
          ? "EDI Entity Objects"
          : "Entity Schema"
    }
    : null;
  const selectedScreenCandidate = configurationNavigations.find((navigation) => navigation.key === screenKey)
    || sourceSystemAliasScreen
    || entitySchemaAliasScreen
    || (screenKey === DESTINATION_DEPLOYMENTS_SCREEN_KEY ? DESTINATION_NAVIGATIONS.find((navigation) => navigation.key === DESTINATION_DEPLOYMENTS_SCREEN_KEY) : null)
    || (screenKey === DESTINATION_DEPLOYMENT_SCREEN_KEY ? DESTINATION_DEPLOYMENT_NAVIGATION : null)
    || (screenKey === DESTINATION_ENTITY_DEPLOYMENTS_SCREEN_KEY ? DESTINATION_ENTITY_DEPLOYMENTS_NAVIGATION : null)
    || (screenKey === DESTINATION_WAREHOUSE_VIEWS_SCREEN_KEY ? DESTINATION_WAREHOUSE_VIEWS_NAVIGATION : null)
    || (screenKey === DESTINATION_SCHEDULES_SCREEN_KEY ? DESTINATION_SCHEDULE_NAVIGATION : null)
    || (screenKey === ENDPOINT_AUTHENTICATION_SCREEN_KEY ? { key: ENDPOINT_AUTHENTICATION_SCREEN_KEY, displayName: "Authentication" } : null)
    || (screenKey === MCP_TOOLS_ACCESS_SCREEN_KEY
      ? configurationNavigations.find((navigation) => navigation.key === MCP_TOOLS_OVERVIEW_SCREEN_KEY)
      : null)
    || (screenKey === MCP_TOOLS_CAPABILITIES_SCREEN_KEY
      ? configurationNavigations.find((navigation) => navigation.key === MCP_TOOLS_OVERVIEW_SCREEN_KEY)
      : null);
  const selectedScreen = destinationScreenState?.defaults?.isEndpoint
    && [DESTINATION_ENTITY_DEPLOYMENTS_SCREEN_KEY, ENDPOINT_AUTHENTICATION_SCREEN_KEY].includes(selectedScreenCandidate?.key)
    ? configurationNavigations.find((navigation) => navigation.key === DESTINATION_DEPLOYMENTS_SCREEN_KEY) || selectedScreenCandidate
    : selectedScreenCandidate;
  const firstConfiguration = configurations.find((configuration) => getId(configuration)) || configurations[0] || null;
  const allowanceState = resolveResourceAllowanceState(selectedResource, configurations);
  const { hasRemainingConfigurationAllowance } = allowanceState;
  const reservedEndpointAllowanceUnits = configurations
    .filter((configuration) => !getBoolean(configuration, "IsDeleted", "isDeleted") && !isConfigurationRetired(configuration))
    .reduce((total, configuration) => {
      const endpointCount = getConfigurationLinkedRecords(configuration).filter((record) =>
        normalizeCompare(getString(record, "ItemName", "itemName")) === "schemaapi"
      ).length;
      return total + Math.max(1, endpointCount);
    }, 0);
  const hasRemainingLinkedEndpointAllowance = allowanceState.allowedCount === Number.POSITIVE_INFINITY
    || (allowanceState.allowedCount !== null && reservedEndpointAllowanceUnits < allowanceState.allowedCount);
  const effectiveSelectedConfiguration = !configurationRoute.resourceConfigurationId && screenKey !== CONFIGURATION_MANAGE_NAVIGATION.key
    ? firstConfiguration
    : selectedConfiguration;
  const sourceSystemTargetFilterDefaults = isInboundEdiResource(selectedResource?.productResource, selectedResource, effectiveSelectedConfiguration)
    ? parseJsonObject(getValue(selectedResource?.productResource, "SetupDefaultsJson", "setupDefaultsJson"))
    : {};
  const [clientMcpToolsScreenActions, setClientMcpToolsScreenActions] = useState([]);
  const [inboundEdiPolicyScreenActions, setInboundEdiPolicyScreenActions] = useState([]);
  const [endpointAuthenticationPanelOpen, setEndpointAuthenticationPanelOpen] = useState(false);

  useEffect(() => {
    if (screenKey !== DESTINATION_DEPLOYMENTS_SCREEN_KEY) {
      setEndpointAuthenticationPanelOpen(false);
    }
  }, [screenKey]);

  useEffect(() => {
    if (screenKey !== ENDPOINT_AUTHENTICATION_SCREEN_KEY || !destinationScreenState?.defaults?.isEndpoint) return;
    onSelectConfigurationScreen?.(getId(effectiveSelectedConfiguration), DESTINATION_DEPLOYMENTS_SCREEN_KEY);
  }, [destinationScreenState?.defaults?.isEndpoint, effectiveSelectedConfiguration, onSelectConfigurationScreen, screenKey]);

  useEffect(() => {
    if (![
      MCP_TOOLS_OVERVIEW_SCREEN_KEY,
      MCP_TOOLS_ACCESS_SCREEN_KEY,
      MCP_TOOLS_CAPABILITIES_SCREEN_KEY,
      MCP_TOOLS_AUTH_SCREEN_KEY,
      MCP_TOOLS_RULES_SCREEN_KEY,
      MCP_TOOLS_EXPOSURE_SCREEN_KEY
    ].includes(screenKey)) {
      setClientMcpToolsScreenActions([]);
    }
  }, [screenKey]);

  useEffect(() => {
    if (![
      INBOUND_EDI_PARTNERS_SCREEN_KEY
    ].includes(screenKey)) {
      setInboundEdiPolicyScreenActions([]);
    }
  }, [screenKey]);

  if (configurationRoute.resourceConfigurationId && !selectedConfiguration) {
    return (
      <ResourceConfigurationScreenShell
        selectedResource={selectedResource}
        configurations={configurations}
        selectedConfiguration={null}
        screenKey={CONFIGURATION_MANAGE_NAVIGATION.key}
        canOpenConfigurationSetup={canOpenConfigurationSetup}
        canBuyResourceAllowance={canBuyResourceAllowance}
        onAddResourceConfiguration={onAddResourceConfiguration}
        onBuyResourceAllowance={onBuyResourceAllowance}
        onSelectConfigurationScreen={onSelectConfigurationScreen}
        onOpenConfigurationSetup={onOpenConfigurationSetup}
        onValidateConfiguration={onValidateConfiguration}
        onActivateConfiguration={onActivateConfiguration}
        onDisableConfiguration={onDisableConfiguration}
        onReconfigureConfiguration={onReconfigureConfiguration}
        onDeleteConfiguration={onDeleteConfiguration}
        onEditConfiguration={onEditConfiguration}
        onRefreshConfigurations={onRefreshConfigurations}
        configurationActionState={configurationActionState}
        integrationScreenState={integrationScreenState}
        sourceSystemScreenState={sourceSystemScreenState}
        logManager={logManager}
        recentLogsRefreshKey={recentLogsRefreshKey}
        onEditIntegration={onEditIntegration}
        onCheckIntegrationConnection={onCheckIntegrationConnection}
        onSetIntegrationOnline={onSetIntegrationOnline}
        onEditSourceSystem={onEditSourceSystem}
        onSetSourceSystemActive={onSetSourceSystemActive}
        onLoadSourceSystemTargets={onLoadSourceSystemTargets}
        onOpenAddSourceObjects={onOpenAddSourceObjects}
        onEditSourceObjectTarget={onEditSourceObjectTarget}
        onEditSourceObject={onEditSourceObject}
        sourceObjectOperations={sourceObjectOperations}
        onRunSourceObjectOperation={onRunSourceObjectOperation}
      >
        <ResourceStateControl
          tone="warning"
          title="Configuration not found"
          message={`The selected Resource Configuration is not available for ${selectedResource.displayName}.`}
        />
      </ResourceConfigurationScreenShell>
    );
  }

  if (screenKey === CONFIGURATION_MANAGE_NAVIGATION.key) {
    return (
      <ResourceConfigurationScreenShell
        selectedResource={selectedResource}
        configurations={configurations}
        selectedConfiguration={effectiveSelectedConfiguration}
        screenKey={CONFIGURATION_MANAGE_NAVIGATION.key}
        canOpenConfigurationSetup={canOpenConfigurationSetup}
        canBuyResourceAllowance={canBuyResourceAllowance}
        onAddResourceConfiguration={onAddResourceConfiguration}
        onBuyResourceAllowance={onBuyResourceAllowance}
        onSelectConfigurationScreen={onSelectConfigurationScreen}
        onOpenConfigurationSetup={onOpenConfigurationSetup}
        onValidateConfiguration={onValidateConfiguration}
        onActivateConfiguration={onActivateConfiguration}
        onDisableConfiguration={onDisableConfiguration}
        onReconfigureConfiguration={onReconfigureConfiguration}
        onDeleteConfiguration={onDeleteConfiguration}
        onEditConfiguration={onEditConfiguration}
        onRefreshConfigurations={onRefreshConfigurations}
        configurationActionState={configurationActionState}
        integrationScreenState={integrationScreenState}
        sourceSystemScreenState={sourceSystemScreenState}
        logManager={logManager}
        recentLogsRefreshKey={recentLogsRefreshKey}
        onEditIntegration={onEditIntegration}
        onCheckIntegrationConnection={onCheckIntegrationConnection}
        onSetIntegrationOnline={onSetIntegrationOnline}
        onEditSourceSystem={onEditSourceSystem}
        onSetSourceSystemActive={onSetSourceSystemActive}
        onLoadSourceSystemTargets={onLoadSourceSystemTargets}
        onOpenAddSourceObjects={onOpenAddSourceObjects}
        onEditSourceObjectTarget={onEditSourceObjectTarget}
        onEditSourceObject={onEditSourceObject}
        sourceObjectOperations={sourceObjectOperations}
        onRunSourceObjectOperation={onRunSourceObjectOperation}
      >
        <ResourceConfigurationsManageScreen
          configurations={configurations}
          resourceLogs={resourceLogs}
          resourceEvents={resourceEvents}
          selectedConfiguration={selectedConfiguration}
          prompt={screenKey !== CONFIGURATION_MANAGE_NAVIGATION.key ? "Select a configuration to open this screen." : ""}
          resource={selectedResource}
          productIcon={getString(selectedResource.productResource, "Icon", "icon") || RESOURCE_FALLBACK_ICON}
          canOpenConfigurationSetup={canOpenConfigurationSetup}
          canBuyResourceAllowance={canBuyResourceAllowance}
          hasRemainingConfigurationAllowance={hasRemainingConfigurationAllowance}
          hasRemainingLinkedEndpointAllowance={hasRemainingLinkedEndpointAllowance}
          onOpenConfigurationSetup={onOpenConfigurationSetup}
          onAddResourceConfiguration={onAddResourceConfiguration}
          onAddLinkedEndpoint={onAddLinkedEndpoint}
          onBuyResourceAllowance={onBuyResourceAllowance}
          importSupport={importSupport}
          onImportResourceConfiguration={onImportResourceConfiguration}
          onSelectConfigurationScreen={onSelectConfigurationScreen}
          onDeleteConfiguration={onDeleteConfiguration}
          onRefreshConfigurations={onRefreshConfigurations}
          logManager={logManager}
          eventManager={eventManager}
        />
      </ResourceConfigurationScreenShell>
    );
  }

  if (selectedScreen && effectiveSelectedConfiguration && !isConfigurationActive(effectiveSelectedConfiguration)) {
    return (
      <ResourceConfigurationScreenShell
        selectedResource={selectedResource}
        configurations={configurations}
        selectedConfiguration={effectiveSelectedConfiguration}
        screenKey={CONFIGURATION_MANAGE_NAVIGATION.key}
        canOpenConfigurationSetup={canOpenConfigurationSetup}
        onSelectConfigurationScreen={onSelectConfigurationScreen}
        onOpenConfigurationSetup={onOpenConfigurationSetup}
        onValidateConfiguration={onValidateConfiguration}
        onActivateConfiguration={onActivateConfiguration}
        onDisableConfiguration={onDisableConfiguration}
        onReconfigureConfiguration={onReconfigureConfiguration}
        onDeleteConfiguration={onDeleteConfiguration}
        onEditConfiguration={onEditConfiguration}
        onRefreshConfigurations={onRefreshConfigurations}
        configurationActionState={configurationActionState}
      >
        <ResourceStateControl
          tone="warning"
          title="Resource is not active"
          message="Activate this Resource from Manage before opening its runtime features."
        />
      </ResourceConfigurationScreenShell>
    );
  }

  if (!configurationRoute.resourceConfigurationId && selectedScreen && !effectiveSelectedConfiguration) {
    return (
      <ResourceConfigurationScreenShell
        selectedResource={selectedResource}
        configurations={configurations}
        selectedConfiguration={null}
        screenKey={CONFIGURATION_MANAGE_NAVIGATION.key}
        canOpenConfigurationSetup={canOpenConfigurationSetup}
        canBuyResourceAllowance={canBuyResourceAllowance}
        onAddResourceConfiguration={onAddResourceConfiguration}
        onBuyResourceAllowance={onBuyResourceAllowance}
        onSelectConfigurationScreen={onSelectConfigurationScreen}
        onOpenConfigurationSetup={onOpenConfigurationSetup}
        onValidateConfiguration={onValidateConfiguration}
        onActivateConfiguration={onActivateConfiguration}
        onDisableConfiguration={onDisableConfiguration}
        onReconfigureConfiguration={onReconfigureConfiguration}
        onDeleteConfiguration={onDeleteConfiguration}
        onEditConfiguration={onEditConfiguration}
        onRefreshConfigurations={onRefreshConfigurations}
        configurationActionState={configurationActionState}
        integrationScreenState={integrationScreenState}
        sourceSystemScreenState={sourceSystemScreenState}
        logManager={logManager}
        recentLogsRefreshKey={recentLogsRefreshKey}
        onEditIntegration={onEditIntegration}
        onCheckIntegrationConnection={onCheckIntegrationConnection}
        onSetIntegrationOnline={onSetIntegrationOnline}
        onEditSourceSystem={onEditSourceSystem}
        onSetSourceSystemActive={onSetSourceSystemActive}
        onLoadSourceSystemTargets={onLoadSourceSystemTargets}
        onOpenAddSourceObjects={onOpenAddSourceObjects}
        onEditSourceObjectTarget={onEditSourceObjectTarget}
        onEditSourceObject={onEditSourceObject}
        sourceObjectOperations={sourceObjectOperations}
        onRunSourceObjectOperation={onRunSourceObjectOperation}
      >
        <ResourceConfigurationsManageScreen
          configurations={configurations}
          resourceLogs={resourceLogs}
          resourceEvents={resourceEvents}
          selectedConfiguration={null}
          prompt="Select a configuration to open this screen."
          resource={selectedResource}
          productIcon={getString(selectedResource.productResource, "Icon", "icon") || RESOURCE_FALLBACK_ICON}
          canOpenConfigurationSetup={canOpenConfigurationSetup}
          canBuyResourceAllowance={canBuyResourceAllowance}
          hasRemainingConfigurationAllowance={hasRemainingConfigurationAllowance}
          onOpenConfigurationSetup={onOpenConfigurationSetup}
          onAddResourceConfiguration={onAddResourceConfiguration}
          onBuyResourceAllowance={onBuyResourceAllowance}
          importSupport={importSupport}
          onImportResourceConfiguration={onImportResourceConfiguration}
          onSelectConfigurationScreen={onSelectConfigurationScreen}
          onDeleteConfiguration={onDeleteConfiguration}
          onRefreshConfigurations={onRefreshConfigurations}
          logManager={logManager}
          eventManager={eventManager}
        />
      </ResourceConfigurationScreenShell>
    );
  }

  if (!selectedScreen || selectedScreen.key === CONFIGURATION_MANAGE_NAVIGATION.key) {
    return (
      <ResourceConfigurationScreenShell
        selectedResource={selectedResource}
        configurations={configurations}
        selectedConfiguration={effectiveSelectedConfiguration}
        screenKey={CONFIGURATION_MANAGE_NAVIGATION.key}
        canOpenConfigurationSetup={canOpenConfigurationSetup}
        canBuyResourceAllowance={canBuyResourceAllowance}
        onAddResourceConfiguration={onAddResourceConfiguration}
        onBuyResourceAllowance={onBuyResourceAllowance}
        onSelectConfigurationScreen={onSelectConfigurationScreen}
        onOpenConfigurationSetup={onOpenConfigurationSetup}
        onValidateConfiguration={onValidateConfiguration}
        onActivateConfiguration={onActivateConfiguration}
        onDisableConfiguration={onDisableConfiguration}
        onReconfigureConfiguration={onReconfigureConfiguration}
        onDeleteConfiguration={onDeleteConfiguration}
        onEditConfiguration={onEditConfiguration}
        onRefreshConfigurations={onRefreshConfigurations}
        configurationActionState={configurationActionState}
        integrationScreenState={integrationScreenState}
        sourceSystemScreenState={sourceSystemScreenState}
        logManager={logManager}
        recentLogsRefreshKey={recentLogsRefreshKey}
        onEditIntegration={onEditIntegration}
        onCheckIntegrationConnection={onCheckIntegrationConnection}
        onSetIntegrationOnline={onSetIntegrationOnline}
        onEditSourceSystem={onEditSourceSystem}
        onSetSourceSystemActive={onSetSourceSystemActive}
        onLoadSourceSystemTargets={onLoadSourceSystemTargets}
        onOpenAddSourceObjects={onOpenAddSourceObjects}
        onEditSourceObjectTarget={onEditSourceObjectTarget}
        onEditSourceObject={onEditSourceObject}
        sourceObjectOperations={sourceObjectOperations}
        onRunSourceObjectOperation={onRunSourceObjectOperation}
      >
        <ResourceStateControl
          tone="warning"
          title="Configuration screen unavailable"
          message={`The configuration screen key \`${screenKey}\` is not configured for this Product Resource.`}
        />
      </ResourceConfigurationScreenShell>
    );
  }

  const isIntegrationScreen = selectedScreen.key === SOURCE_INTEGRATION_NAVIGATIONS[0].key;
  const isSourceSystemScreen = INBOUND_EDI_SOURCE_SYSTEM_SCREEN_KEYS.includes(selectedScreen.key);
  const isEntitySchemaScreen = [
    ...INBOUND_EDI_ENTITY_SCHEMA_SCREEN_KEYS,
    ...OUTBOUND_EDI_ENTITY_SCHEMA_SCREEN_KEYS
  ].includes(selectedScreen.key);
  const isInboundEdiTargetsScreen = selectedScreen.key === INBOUND_EDI_TARGETS_SCREEN_KEY;
  const isInboundEdiSourceObjectsScreen = selectedScreen.key === INBOUND_EDI_SOURCE_OBJECTS_SCREEN_KEY;
  const isInboundEdiMappingScreen = selectedScreen.key === INBOUND_EDI_MAPPING_SCREEN_KEY;
  const isEdiPolicyScreen = selectedScreen.key === INBOUND_EDI_PARTNERS_SCREEN_KEY
    && isEdiResourceConfiguration(selectedResource?.productResource, selectedResource, effectiveSelectedConfiguration);
  const isOutboundEdiConfigurationScreen = isOutboundEdiResource(selectedResource?.productResource, selectedResource, effectiveSelectedConfiguration);
  const isDestinationScreen = selectedScreen.key === DESTINATION_SCREEN_KEY;
  const isDestinationDeploymentsScreen = selectedScreen.key === DESTINATION_DEPLOYMENTS_SCREEN_KEY;
  const isDestinationDeploymentScreen = selectedScreen.key === DESTINATION_DEPLOYMENT_SCREEN_KEY;
  const isDestinationWarehouseViewsScreen = selectedScreen.key === DESTINATION_WAREHOUSE_VIEWS_SCREEN_KEY;
  const isDestinationEntityDeploymentsScreen = selectedScreen.key === DESTINATION_ENTITY_DEPLOYMENTS_SCREEN_KEY;
  const isDestinationScheduleScreen = selectedScreen.key === DESTINATION_SCHEDULES_SCREEN_KEY;
  const isClientMcpToolsScreen = [
    MCP_TOOLS_OVERVIEW_SCREEN_KEY,
    MCP_TOOLS_CAPABILITIES_SCREEN_KEY,
    MCP_TOOLS_AUTH_SCREEN_KEY,
    MCP_TOOLS_ACTIVE_SESSIONS_SCREEN_KEY,
    MCP_TOOLS_RULES_SCREEN_KEY,
    MCP_TOOLS_EXPOSURE_SCREEN_KEY
  ].includes(selectedScreen.key);
  const selectedDestinationDeployment = isDestinationDeploymentScreen || isDestinationWarehouseViewsScreen
    ? findDestinationDeploymentById(
      destinationScreenState?.destination,
      destinationScreenState?.defaults,
      destinationDeploymentRouteFilters?.deploymentId
    )
    : null;
  const endpointDeploymentSelection = endpointDeploymentsSelectionState?.selectedDeployment || null;
  const endpointDeploymentSelectionId = endpointDeploymentsSelectionState?.selectedDeploymentId || getDeploymentId(endpointDeploymentSelection);
  const endpointDeploymentSelectionOnline = isSchemaDestinationOnline(endpointDeploymentSelection);
  const endpointDeploymentSelectionEditable = isSchemaDestinationConfigEditable(endpointDeploymentSelection);
  const endpointDeploymentMetadataUnavailable = !destinationScreenState?.deploymentMetadata;
  const endpointDeploymentLifecycleBusy = Boolean(configurationActionState?.busyAction);
  const endpointDestinationActive = isRecordActive(destinationScreenState?.destination);
  const endpointDeploymentsScreenActions = isDestinationDeploymentsScreen && destinationScreenState?.defaults?.isEndpoint
    ? [{
      key: "endpoint-deployment-edit",
      label: endpointDeploymentSelectionEditable ? "Edit" : "View",
      iconKey: endpointDeploymentSelectionEditable ? "edit" : "view",
      disabled: endpointDeploymentLifecycleBusy || !endpointDeploymentSelection || endpointDeploymentMetadataUnavailable,
      title: endpointDeploymentSelection && !endpointDeploymentMetadataUnavailable
        ? endpointDeploymentSelectionEditable
          ? "Edit the selected deployment."
          : "View the selected online deployment."
        : "Select a deployment with available metadata before editing it.",
      onClick: () => onEditDestinationDeployment?.(endpointDeploymentSelection)
    }, {
      key: endpointDeploymentSelectionOnline ? "endpoint-deployment-undeploy" : "endpoint-deployment-deploy",
      label: endpointDeploymentSelectionOnline ? "Undeploy" : "Deploy",
      iconKey: endpointDeploymentSelectionOnline ? "undo" : "play",
      disabled: endpointDeploymentLifecycleBusy
        || !endpointDeploymentSelection
        || !endpointDestinationActive
        || !isRecordActive(endpointDeploymentSelection)
        || !(endpointDeploymentSelectionOnline
          ? destinationScreenState?.defaults?.dropOperationName
          : destinationScreenState?.defaults?.deployOperationName),
      title: endpointDeploymentSelection
        ? endpointDeploymentSelectionOnline
          ? "Undeploy the selected endpoint deployment so its routes are no longer available."
          : "Validate and deploy the selected endpoint deployment."
        : "Select a deployment before changing its online status.",
      onClick: () => endpointDeploymentSelectionOnline
        ? onUndeployDestinationDeployment?.(endpointDeploymentSelection)
        : onDeployDestinationDeployment?.(endpointDeploymentSelection)
    }, {
      key: "endpoint-add-entity-endpoint",
      label: "Add Endpoint Scope",
      iconKey: "add",
      disabled: !endpointDeploymentSelection || !isSchemaDestinationConfigEditable(endpointDeploymentSelection),
      title: endpointDeploymentSelection
        ? isSchemaDestinationConfigEditable(endpointDeploymentSelection)
          ? "Add an Endpoint Scope row to the selected deployment."
          : "Endpoint Scope settings are view-only while this deployment is online."
        : "Select a deployment before adding an Endpoint Scope row.",
      onClick: () => onOpenEndpointEntityEndpointPanel?.(endpointDeploymentSelectionId)
    }, {
      key: "endpoint-deployment-authentication",
      label: "Access",
      iconKey: "key",
      disabled: !endpointDeploymentSelection,
      title: endpointDeploymentSelection
        ? "Configure and test access for the selected deployment."
        : "Select a deployment before configuring access.",
      onClick: () => setEndpointAuthenticationPanelOpen(true)
    }, {
      key: "endpoint-deployment-routes",
      label: "MCP URLs",
      iconElement: <EndpointRouteIcon />,
      disabled: !endpointDeploymentSelection || !endpointDeploymentsSelectionState?.openRoutes,
      title: endpointDeploymentSelection ? "View MCP connection URLs for the selected deployment." : "Select a deployment to view its MCP URLs.",
      onClick: () => endpointDeploymentsSelectionState?.openRoutes?.()
    }]
    : [];
  const configurationScreenActions = isClientMcpToolsScreen
    ? clientMcpToolsScreenActions
    : isEdiPolicyScreen
      ? inboundEdiPolicyScreenActions
      : endpointDeploymentsScreenActions;

  return (
    <ResourceConfigurationScreenShell
      selectedResource={selectedResource}
      configurations={configurations}
      selectedConfiguration={effectiveSelectedConfiguration}
      screenKey={selectedScreen.key}
      screenDisplayName={selectedScreen.screenDisplayName || selectedScreen.displayName}
      canOpenConfigurationSetup={canOpenConfigurationSetup}
      canBuyResourceAllowance={canBuyResourceAllowance}
      onAddResourceConfiguration={onAddResourceConfiguration}
      onBuyResourceAllowance={onBuyResourceAllowance}
      onSelectConfigurationScreen={onSelectConfigurationScreen}
      onOpenConfigurationSetup={onOpenConfigurationSetup}
      onValidateConfiguration={onValidateConfiguration}
      onActivateConfiguration={onActivateConfiguration}
      onDisableConfiguration={onDisableConfiguration}
      onReconfigureConfiguration={onReconfigureConfiguration}
      onDeleteConfiguration={onDeleteConfiguration}
      onEditConfiguration={onEditConfiguration}
      onRefreshConfigurations={onRefreshConfigurations}
      onBackToDestinationDeployment={onBackToDestinationDeployment}
      configurationActionState={configurationActionState}
      integrationScreenState={integrationScreenState}
      sourceSystemScreenState={sourceSystemScreenState}
      logManager={logManager}
      recentLogsRefreshKey={recentLogsRefreshKey}
      onEditIntegration={onEditIntegration}
      onCheckIntegrationConnection={onCheckIntegrationConnection}
      onSetIntegrationOnline={onSetIntegrationOnline}
      onEditSourceSystem={onEditSourceSystem}
      onSetSourceSystemActive={onSetSourceSystemActive}
      onSetSourceObjectActive={onSetSourceObjectActive}
      schemaScreenState={schemaScreenState}
      schemaOperations={schemaOperations}
      onEditSchema={onEditSchema}
      onRunSchemaOperation={onRunSchemaOperation}
      onPublishSchemaVersion={onPublishSchemaVersion}
      onSetSourceObjectTargetActive={onSetSourceObjectTargetActive}
      onLoadSourceSystemTargets={onLoadSourceSystemTargets}
      onOpenAddSourceObjects={onOpenAddSourceObjects}
      sourceObjectManagerSourceObjectId={sourceObjectManagerSourceObjectId}
      onCloseSourceObjectManager={onCloseSourceObjectManager}
      onEditSourceObjectTarget={onEditSourceObjectTarget}
      onEditSourceObject={onEditSourceObject}
      onEditCurrentSourceObject={onEditCurrentSourceObject}
      sourceObjectEditRequestKey={sourceObjectEditRequestKey}
      sourceObjectOperations={sourceObjectOperations}
      onRunSourceObjectOperation={onRunSourceObjectOperation}
      entityObjectOperations={entityObjectOperations}
      onRunEntityObjectOperation={onRunEntityObjectOperation}
      onOpenEntityObjectPanel={onOpenEntityObjectPanel}
      destinationScreenState={destinationScreenState}
      selectedDestinationDeployment={selectedDestinationDeployment}
      destinationDeploymentRouteFilters={destinationDeploymentRouteFilters}
      destinationEntityDeploymentRouteFilters={destinationEntityDeploymentRouteFilters}
      destinationScheduleActionState={destinationScheduleActionState}
      destinationEntityScopeActionState={destinationEntityScopeActionState}
      warehouseViewActionState={warehouseViewActionState}
      onEditDestination={onEditDestination}
      onSetDestinationActive={onSetDestinationActive}
      onCheckDestinationConnection={onCheckDestinationConnection}
      onManualRunDestinationDeployment={onManualRunDestinationDeployment}
      onDeployDestinationDeployment={onDeployDestinationDeployment}
      onUndeployDestinationDeployment={onUndeployDestinationDeployment}
      onDeployWarehouseDeployment={onDeployWarehouseDeployment}
      onDropWarehouseDeployment={onDropWarehouseDeployment}
      onCloneWarehouseDeployment={onCloneWarehouseDeployment}
      onAddDestinationDeployment={onAddDestinationDeployment}
      onEditDestinationDeployment={onEditDestinationDeployment}
      onSetDestinationDeploymentActive={onSetDestinationDeploymentActive}
      onDeleteDestinationDeployment={onDeleteDestinationDeployment}
      onOpenDestinationDeploymentSchedule={onOpenDestinationDeploymentSchedule}
      onOpenDestinationDeploymentScope={onOpenDestinationDeploymentScope}
      onOpenDestinationDeploymentViews={onOpenDestinationDeploymentViews}
      onOpenEndpointEntityEndpointPanel={onOpenEndpointEntityEndpointPanel}
      screenActions={configurationScreenActions}
    >
      {isIntegrationScreen ? (
        <ResourceConfigurationIntegrationScreen
          configuration={effectiveSelectedConfiguration}
          integrationRecord={getLinkedIntegrationRecord(effectiveSelectedConfiguration)}
          integrationState={integrationScreenState}
          logManager={logManager}
          recentLogsRefreshKey={recentLogsRefreshKey}
          resourceLogs={resourceLogs}
        />
      ) : null}
      {isSourceSystemScreen ? (
        sourceObjectManagerSourceObjectId ? (
          <ResourceConfigurationSourceObjectManagerScreen
            sourceObjectId={sourceObjectManagerSourceObjectId}
            sourceSystemId={getId(sourceSystemScreenState?.sourceSystem) || getLinkedRecordItemId(getLinkedSourceSystemRecord(effectiveSelectedConfiguration))}
            clientResourceId={selectedResource?.id}
            resourceConfigurationId={getId(effectiveSelectedConfiguration)}
            editObjectRequestKey={sourceObjectEditRequestKey}
            onClose={onCloseSourceObjectManager}
          />
        ) : (
          <ResourceConfigurationSourceSystemScreen
            configuration={effectiveSelectedConfiguration}
            sourceSystemRecord={getLinkedSourceSystemRecord(effectiveSelectedConfiguration)}
            sourceSystemState={sourceSystemScreenState}
            initialCatalogTab={isInboundEdiTargetsScreen ? SOURCE_SYSTEM_CATALOG_TAB_TARGETS : isInboundEdiSourceObjectsScreen ? SOURCE_SYSTEM_CATALOG_TAB_SOURCE_OBJECTS : undefined}
            targetFilterDefaults={sourceSystemTargetFilterDefaults}
            logManager={logManager}
            recentLogsRefreshKey={recentLogsRefreshKey}
            onEditSourceObjectTarget={onEditSourceObjectTarget}
            onAddQueryTarget={onAddQueryTarget}
            onCloneQueryTarget={onCloneQueryTarget}
            onSetSourceObjectTargetActive={onSetSourceObjectTargetActive}
            onEditSourceObject={onEditSourceObject}
            sourceObjectOperations={sourceObjectOperations}
            onRunSourceObjectOperation={onRunSourceObjectOperation}
          />
        )
      ) : null}
      {isEntitySchemaScreen ? (
        <ResourceConfigurationEntitySchemaScreen
          configuration={effectiveSelectedConfiguration}
          schemaRecord={getLinkedEntitySchemaRecord(effectiveSelectedConfiguration)}
          schemaWorkspaceState={schemaScreenState}
          entityObjectManager={entityObjectManager}
          entityObjectOperations={entityObjectOperations}
          onRunEntityObjectOperation={onRunEntityObjectOperation}
          onManageEntityObject={onOpenEntityObjectPanel}
          onManageSourceObject={onEditSourceObject}
          onRefreshSchemaWorkspace={onRefreshSchemaWorkspace}
          onSelectSchemaVersion={onSelectSchemaVersion}
          onEditSchemaVersion={onEditSchemaVersion}
        />
      ) : null}
      {isInboundEdiMappingScreen ? (
        <InboundEdiMappingScreen
          configuration={effectiveSelectedConfiguration}
          schemaWorkspaceState={schemaScreenState}
          entityObjectOperations={entityObjectOperations}
          onRunEntityObjectOperation={onRunEntityObjectOperation}
          onManageSourceObject={onEditSourceObject}
        />
      ) : null}
      {isEdiPolicyScreen ? (
        <InboundEdiOperationalPolicyScreen
          configuration={effectiveSelectedConfiguration}
          selectedResource={selectedResource}
          screenKey={selectedScreen.key}
          onSaved={onRefreshConfigurations}
          onRegisterScreenActions={setInboundEdiPolicyScreenActions}
        />
      ) : null}
      {isClientMcpToolsScreen ? (
        <ResourceConfigurationClientMcpToolsScreen
          selectedResource={selectedResource}
          configuration={effectiveSelectedConfiguration}
          resourceConfigurations={resourceConfigurations}
          screenKey={selectedScreen.key}
          onRefreshConfigurations={onRefreshConfigurations}
          onScreenActionsChange={setClientMcpToolsScreenActions}
        />
      ) : null}
      {isDestinationScreen ? (
        <ResourceConfigurationDestinationScreen
          destinationRecord={getLinkedDestinationRecord(effectiveSelectedConfiguration)}
          destinationState={destinationScreenState}
          endpointTypeLabel={getSchemaApiLinkedRecordTypeLabel(
            getLinkedDestinationRecord(effectiveSelectedConfiguration),
            selectedResource
          )}
          showDeploymentsInline={isOutboundEdiConfigurationScreen}
          onManageSchema={onOpenEntitySchemaConfigurationFromScope}
          onManageDeployment={onManageDestinationDeployment}
          onDeleteDeployment={onDeleteDestinationDeployment}
          onCheckDeployment={onCheckDestinationConnection}
          onOpenDeploymentSchedule={onOpenDestinationDeploymentSchedule}
          onOpenDeploymentScope={onOpenDestinationDeploymentScope}
          onOpenDeploymentViews={onOpenDestinationDeploymentViews}
        />
      ) : null}
      {isDestinationDeploymentsScreen ? (
        <ResourceConfigurationDestinationDeploymentsScreen
          destinationRecord={getLinkedDestinationRecord(effectiveSelectedConfiguration)}
          destinationState={destinationScreenState}
          initialDeploymentId={destinationEntityDeploymentRouteFilters?.deploymentId}
          onAddDeployment={onAddDestinationDeployment}
          onManageDeployment={onManageDestinationDeployment}
          onDeleteDeployment={onDeleteDestinationDeployment}
          onCheckDeployment={onCheckDestinationConnection}
          onOpenDeploymentSchedule={onOpenDestinationDeploymentSchedule}
          onOpenDeploymentScope={onOpenDestinationDeploymentScope}
          onOpenDeploymentViews={onOpenDestinationDeploymentViews}
          onEditEntityEndpoint={onEditEndpointEntityEndpoint}
          onDeleteEntityEndpoint={onDeleteEndpointEntityEndpoint}
          onSelectedDeploymentActionsChange={onEndpointDeploymentsSelectionChange}
        />
      ) : null}
      {isDestinationDeploymentScreen ? (
        <ResourceConfigurationDestinationDeploymentScreen
          configuration={effectiveSelectedConfiguration}
          destinationRecord={getLinkedDestinationRecord(effectiveSelectedConfiguration)}
          destinationState={destinationScreenState}
          deployment={selectedDestinationDeployment}
          resourceEvents={resourceEvents}
          onCheckDeployment={onCheckDestinationConnection}
          onConfigureEntitySchema={onOpenEntitySchemaConfigurationFromScope}
          onSaveSchemaScopes={onSaveDestinationDeploymentSchemaScopes}
          schemaScopeActionState={configurationActionState}
          onDeployDeployment={onDeployDestinationDeployment}
          onUndeployDeployment={onUndeployDestinationDeployment}
          onOpenDeploymentSchedule={onOpenDestinationDeploymentSchedule}
          onOpenDeploymentScope={onOpenDestinationDeploymentScope}
        />
      ) : null}
      {isDestinationWarehouseViewsScreen ? (
        <ResourceConfigurationWarehouseViewDesignerScreen
          destinationState={destinationScreenState}
          deployment={selectedDestinationDeployment}
          onWarehouseViewActionsChange={onWarehouseViewActionsChange}
        />
      ) : null}
      {isDestinationEntityDeploymentsScreen ? (
        destinationScreenState?.defaults?.isEndpoint ? (
          <ResourceConfigurationEndpointScopeScreen
            destinationState={destinationScreenState}
            initialDeploymentFilter={destinationEntityDeploymentRouteFilters?.deploymentId}
            onEditEntityEndpoint={onEditEndpointEntityEndpoint}
            onDeleteEntityEndpoint={onDeleteEndpointEntityEndpoint}
          />
        ) : (
          <ResourceConfigurationDestinationEntityDeploymentsScreen
            destinationState={destinationScreenState}
            initialDeploymentFilter={destinationEntityDeploymentRouteFilters?.deploymentId}
            onSaveEntityDeployment={onSaveDestinationEntityDeployment}
            onDeleteEntityDeployment={onDeleteDestinationEntityDeployment}
            onOpenEntityDeploymentSchedule={onOpenDestinationEntityDeploymentSchedule}
            onManageEntityDeploymentEntity={onManageDestinationEntityDeploymentEntity}
            onMapEntityDeploymentEntity={onMapDestinationEntityDeploymentEntity}
            onSearchEntityDeploymentEntity={onSearchDestinationEntityDeploymentEntity}
            onOpenEntityDeploymentPicker={onOpenDestinationEntityPicker}
            onEntityScopeActionsChange={onDestinationEntityScopeActionsChange}
          />
        )
      ) : null}
      {isDestinationDeploymentsScreen && destinationScreenState?.defaults?.isEndpoint ? (
        <ResourceConfigurationEndpointAuthenticationPanel
          open={endpointAuthenticationPanelOpen}
          deployment={endpointDeploymentSelection}
          destinationRecord={getLinkedDestinationRecord(effectiveSelectedConfiguration)}
          destinationState={destinationScreenState}
          configuration={effectiveSelectedConfiguration}
          selectedResource={selectedResource}
          onSaved={onRefreshDestinationWorkspace}
          onClose={() => setEndpointAuthenticationPanelOpen(false)}
        />
      ) : null}
      {isDestinationScheduleScreen ? (
        <ResourceConfigurationDestinationScheduleScreen
          selectedResource={selectedResource}
          configuration={effectiveSelectedConfiguration}
          environmentKey={environmentKey}
          clientProductOffers={clientProductOffers}
          onManagementActionsChange={onDestinationScheduleActionsChange}
        />
      ) : null}
    </ResourceConfigurationScreenShell>
  );
};

const getSchemaVersionId = (schemaVersion) =>
  getString(schemaVersion, "SchemaVersionId", "schemaVersionId", "UniqueId", "uniqueId", "_id", "Id", "id");

const findSchemaVersionById = (schema, schemaVersionId) => {
  const normalizedSchemaVersionId = normalizeCompare(schemaVersionId);
  if (!normalizedSchemaVersionId) return null;
  return getArray(schema, "SchemaVersions", "schemaVersions").find((version) =>
    normalizeCompare(getSchemaVersionId(version)) === normalizedSchemaVersionId
  ) || null;
};

const getStoredSchemaPrimaryVersionId = (schema) =>
  getString(schema, "ActiveSchemaVersionId", "activeSchemaVersionId");

const getSchemaActiveVersionId = (schema) => {
  const primaryVersionId = getStoredSchemaPrimaryVersionId(schema);
  if (!primaryVersionId) return "";
  const primaryVersion = findSchemaVersionById(schema, primaryVersionId);
  return primaryVersion
    && getBoolean(primaryVersion, "IsPublished", "isPublished")
    && !getBoolean(primaryVersion, "IsDeleted", "isDeleted")
    && getValue(primaryVersion, "IsActive", "isActive") !== false
    ? primaryVersionId
    : "";
};

const getDefaultSchemaVersion = (schema) => {
  const versions = getArray(schema, "SchemaVersions", "schemaVersions");
  if (versions.length === 0) return null;

  const primaryVersion = findSchemaVersionById(schema, getSchemaActiveVersionId(schema));
  if (primaryVersion && getBoolean(primaryVersion, "IsPublished", "isPublished")) return primaryVersion;

  const draftVersions = versions.filter((version) =>
    !getBoolean(version, "IsPublished", "isPublished")
    && !getBoolean(version, "IsDeleted", "isDeleted")
    && getValue(version, "IsActive", "isActive") !== false
  );
  if (draftVersions.length > 0) return draftVersions[draftVersions.length - 1];
  const publishedVersions = versions.filter((version) =>
    getBoolean(version, "IsPublished", "isPublished")
    && !getBoolean(version, "IsDeleted", "isDeleted")
    && getValue(version, "IsActive", "isActive") !== false
  );
  return publishedVersions[publishedVersions.length - 1] || versions[versions.length - 1] || null;
};

const getSchemaVersionStatusLabel = (schemaVersion) =>
  getBoolean(schemaVersion, "IsPublished", "isPublished") ? "Published" : "Draft";

const isPrimarySchemaVersion = (schemaVersion, primaryVersionId) =>
  Boolean(schemaVersion)
  && Boolean(primaryVersionId)
  && normalizeCompare(getSchemaVersionId(schemaVersion)) === normalizeCompare(primaryVersionId);

const clearInvalidPrimarySchemaVersion = (schema) => {
  const primaryVersionId = getStoredSchemaPrimaryVersionId(schema);
  if (!primaryVersionId) return schema;
  const primaryVersion = findSchemaVersionById(schema, primaryVersionId);
  if (primaryVersion && getBoolean(primaryVersion, "IsPublished", "isPublished")) return schema;
  return {
    ...schema,
    ActiveSchemaVersionId: null
  };
};

const updateSchemaVersionOnSchema = (schema, updatedVersion) => {
  const updatedVersionId = getSchemaVersionId(updatedVersion);
  return clearInvalidPrimarySchemaVersion({
    ...schema,
    SchemaVersions: getArray(schema, "SchemaVersions", "schemaVersions").map((version) =>
      normalizeCompare(getSchemaVersionId(version)) === normalizeCompare(updatedVersionId)
        ? { ...version, ...updatedVersion, IsNew: false }
        : version
    ),
    IsNew: false
  });
};

const publishSchemaVersionOnSchema = (schema, schemaVersionId, makePrimary = false) =>
  ({
    ...schema,
    ActiveSchemaVersionId: makePrimary ? schemaVersionId : (getSchemaActiveVersionId(schema) || null),
    SchemaVersions: getArray(schema, "SchemaVersions", "schemaVersions").map((version) =>
      normalizeCompare(getSchemaVersionId(version)) === normalizeCompare(schemaVersionId)
        ? { ...version, IsActive: true, IsPublished: true }
        : version
    ),
    IsNew: false
  });

const makePrimarySchemaVersionOnSchema = (schema, schemaVersionId) => {
  const selectedVersion = findSchemaVersionById(schema, schemaVersionId);
  if (!selectedVersion || !getBoolean(selectedVersion, "IsPublished", "isPublished")) {
    throw new Error("Only a Published Schema Version can be selected as Primary.");
  }
  return {
    ...schema,
    ActiveSchemaVersionId: schemaVersionId,
    SchemaVersions: getArray(schema, "SchemaVersions", "schemaVersions").map((version) =>
      normalizeCompare(getSchemaVersionId(version)) === normalizeCompare(schemaVersionId)
        ? { ...version, IsActive: true }
        : version
    ),
    IsNew: false
  };
};

const unpublishSchemaVersionOnSchema = (schema, schemaVersionId) =>
  clearInvalidPrimarySchemaVersion({
    ...schema,
    SchemaVersions: getArray(schema, "SchemaVersions", "schemaVersions").map((version) =>
      normalizeCompare(getSchemaVersionId(version)) === normalizeCompare(schemaVersionId)
        ? { ...version, IsPublished: false }
        : version
    ),
    IsNew: false
  });

const resolveSelectedSchemaVersion = (schema, requestedSchemaVersionId) => {
  const versions = getArray(schema, "SchemaVersions", "schemaVersions");
  if (versions.length === 0) return null;
  return findSchemaVersionById(schema, requestedSchemaVersionId)
    || getDefaultSchemaVersion(schema)
    || versions[0];
};

const loadLinkedEntitySchemaWorkspaceState = async ({ schemaRecord, schemaManager, entityObjectManager, sourceObjectManager, selectedSchemaVersionId }) => {
  const schemaId = getLinkedRecordItemId(schemaRecord);
  if (!schemaId) {
    return { schema: null, selectedSchemaVersionId: "", activeSchemaVersionId: "", entityObjects: [], sourceObjects: [], sourceObjectError: "", error: "No Entity Schema is linked to this Resource Configuration." };
  }
  if (!schemaManager?.Query || !entityObjectManager?.Query) {
    return { schema: null, selectedSchemaVersionId: "", activeSchemaVersionId: "", entityObjects: [], sourceObjects: [], sourceObjectError: "", error: "Entity Schema workspace services are not available." };
  }

  const schemaResult = await schemaManager.Query({ model: "x => x.IsDeleted == false" });
  if (getOperationResult(schemaResult)?.Success === false || schemaResult?.Success === false) {
    return { schema: null, selectedSchemaVersionId: "", activeSchemaVersionId: "", entityObjects: [], sourceObjects: [], sourceObjectError: "", error: getOperationResultMessage(schemaResult, "Entity Schema could not be loaded.") };
  }

  const schema = getOperationItems(schemaResult).find((item) =>
    normalizeCompare(getId(item)) === normalizeCompare(schemaId)
    || normalizeCompare(getString(item, "SchemaId", "schemaId", "UniqueId", "uniqueId")) === normalizeCompare(schemaId)
  ) || null;
  if (!schema) {
    return { schema: null, selectedSchemaVersionId: "", activeSchemaVersionId: "", entityObjects: [], sourceObjects: [], sourceObjectError: "", error: "The linked Entity Schema could not be loaded." };
  }

  const activeSchemaVersionId = getSchemaActiveVersionId(schema);
  const selectedSchemaVersion = resolveSelectedSchemaVersion(schema, selectedSchemaVersionId);
  const schemaVersionId = getSchemaVersionId(selectedSchemaVersion);
  if (!schemaVersionId) {
    return { schema, selectedSchemaVersionId: "", activeSchemaVersionId, entityObjects: [], sourceObjects: [], sourceObjectError: "", error: "" };
  }

  const entityObjectResult = await entityObjectManager.Query({
    model: "x => x.IsDeleted == false && x.SchemaVersionId == schemaVersionId",
    params: { schemaVersionId }
  });
  if (getOperationResult(entityObjectResult)?.Success === false || entityObjectResult?.Success === false) {
    return { schema, selectedSchemaVersionId: schemaVersionId, activeSchemaVersionId, entityObjects: [], sourceObjects: [], sourceObjectError: "", error: getOperationResultMessage(entityObjectResult, "Entity Objects could not be loaded.") };
  }

  const entityObjects = getUniqueRowsByKey(
    getOperationItems(entityObjectResult).filter((item) => !isRecordDeleted(item)),
    (entityObject, index) => getId(entityObject) || getString(entityObject, "EntityObjectId", "entityObjectId", "ObjectName", "objectName") || `entity-object-${index}`
  )
    .sort((left, right) => getDisplayName(left, getString(left, "ObjectName", "objectName")).localeCompare(getDisplayName(right, getString(right, "ObjectName", "objectName"))));

  const sourceObjectIds = new Set(entityObjects.map(getEntityObjectSourceObjectId).map(normalizeCompare).filter(Boolean));
  let sourceObjects = [];
  let sourceObjectError = "";
  if (sourceObjectIds.size > 0) {
    if (sourceObjectManager?.Query) {
      const sourceObjectResult = await sourceObjectManager.Query({ model: "x => x.IsDeleted == false" });
      if (getOperationResult(sourceObjectResult)?.Success === false || sourceObjectResult?.Success === false) {
        sourceObjectError = getOperationResultMessage(sourceObjectResult, "Selected Source Objects could not be loaded.");
      } else {
        sourceObjects = getSelectedSourceObjectsForEntityObjects(entityObjects, getOperationItems(sourceObjectResult))
          .sort((left, right) =>
            getDisplayName(left, getString(left, "SystemName", "systemName", "ObjectName", "objectName"))
              .localeCompare(getDisplayName(right, getString(right, "SystemName", "systemName", "ObjectName", "objectName")))
          );
      }
    } else {
      sourceObjectError = "Source Object service is not available.";
    }
  }

  return { schema, selectedSchemaVersionId: schemaVersionId, activeSchemaVersionId, entityObjects, sourceObjects, sourceObjectError, error: "" };
};

const ResourceConfigurationEntitySchemaScreen = ({
  schemaRecord,
  schemaWorkspaceState,
  entityObjectManager,
  entityObjectOperations = [],
  onRunEntityObjectOperation,
  onManageEntityObject,
  onManageSourceObject,
  onRefreshSchemaWorkspace,
  onSelectSchemaVersion,
  onEditSchemaVersion
}) => {
  const {
    schema,
    entityObjects = [],
    sourceObjects = [],
    selectedSchemaVersionId = "",
    activeSchemaVersionId = "",
    loading = false,
    error = ""
  } = schemaWorkspaceState || {};
  const expectedSchemaId = getLinkedRecordItemId(schemaRecord);
  const loadedSchemaId = getString(schemaWorkspaceState, "linkedRecordId", "LinkedRecordId");
  const awaitingLinkedSchemaLoad = Boolean(expectedSchemaId && !error && (!loadedSchemaId || normalizeCompare(loadedSchemaId) !== normalizeCompare(expectedSchemaId)));
  const refresh = onRefreshSchemaWorkspace || (() => {});
  const [actionError, setActionError] = useState("");
  const [actionBusyKey, setActionBusyKey] = useState("");
  const [openOperationMenu, setOpenOperationMenu] = useState("");
  const [operationMenuStyle, setOperationMenuStyle] = useState(null);
  const [entityObjectSearch, setEntityObjectSearch] = useState("");

  if (loading || awaitingLinkedSchemaLoad) return <ResourceStateControl loading loadingText="Loading Entity Schema..." />;
  if (error && !schema) return <ResourceStateControl tone="warning" title="Entity Schema unavailable" message={error} actions={<ButtonControl type="standard" onClick={refresh}>Retry</ButtonControl>} />;

  const schemaName = getDisplayName(schema, getLinkedRecordDisplayName(schemaRecord) || "Entity Schema");
  const versions = getArray(schema, "SchemaVersions", "schemaVersions");
  const primaryVersionId = activeSchemaVersionId || getSchemaActiveVersionId(schema);
  const selectedVersionId = selectedSchemaVersionId || primaryVersionId;
  const primaryVersion = versions.find((version) =>
    normalizeCompare(getString(version, "SchemaVersionId", "schemaVersionId", "UniqueId", "uniqueId")) === normalizeCompare(primaryVersionId)
      && getBoolean(version, "IsPublished", "isPublished")
  ) || null;
  const selectedVersion = resolveSelectedSchemaVersion(schema, selectedVersionId);
  const selectedVersionLabel = getString(selectedVersion, "Version", "version") || "selected version";
  const schemaDescription = getString(schema, "Description", "description");
  const sourceBackedCount = entityObjects.filter((entityObject) => getString(entityObject, "SourceObjectOfRecordId", "sourceObjectOfRecordId")).length;
  const normalizedEntityObjectSearch = normalizeCompare(entityObjectSearch);
  const filteredEntityObjects = normalizedEntityObjectSearch
    ? entityObjects.filter((entityObject) => {
      const objectName = getString(entityObject, "ObjectName", "objectName");
      const entityObjectName = getDisplayName(entityObject, objectName || "Entity Object");
      const updatedDate = formatDateTime(getValue(entityObject, "UpdatedDate", "updatedDate", "ModifiedDate", "modifiedDate", "LastUpdatedDate", "lastUpdatedDate", "CreatedDate", "createdDate"));
      const searchableText = [
        entityObjectName,
        objectName,
        getString(entityObject, "Description", "description"),
        getString(entityObject, "SourceObjectOfRecordId", "sourceObjectOfRecordId"),
        isRecordActive(entityObject) ? "Active" : "Inactive",
        updatedDate
      ].filter(Boolean).join(" ");
      return normalizeCompare(searchableText).includes(normalizedEntityObjectSearch);
    })
    : entityObjects;
  const openOperations = (event, operationMenuKey) => {
    const buttonBox = event.currentTarget?.getBoundingClientRect?.();
    setOpenOperationMenu((current) => current === operationMenuKey ? "" : operationMenuKey);
    setOperationMenuStyle({
      left: Math.max(8, (buttonBox?.right ?? 0) - 190),
      top: (buttonBox?.bottom ?? 0) + 6
    });
  };
  const runEntityObjectAction = async (operationName, entityObject) => {
    const entityObjectId = getId(entityObject);
    const busyKey = `${operationName}:${entityObjectId || ""}`;
    setActionBusyKey(busyKey);
    setActionError("");
    try {
      await runEntityObjectRedirectOperation(entityObjectManager, entityObject, setActionError, operationName);
    } finally {
      setActionBusyKey("");
    }
  };
  const runMenuOperation = (entityObject, operation) => {
    setOpenOperationMenu("");
    onRunEntityObjectOperation?.(entityObject, operation);
  };
  const openSourceManager = (entityObject) => {
    const sourceObjectId = getEntityObjectSourceObjectId(entityObject);
    if (!sourceObjectId) return;
    const sourceObject = findSourceObjectById(sourceObjects, sourceObjectId) || {
      _id: sourceObjectId,
      SourceObjectId: sourceObjectId,
      SourceSystemId: getString(entityObject, "SourceSystemId", "sourceSystemId")
    };
    setOpenOperationMenu("");
    onManageSourceObject?.(sourceObject);
  };

  return (
    <div className="resource-workspace-linked-item-layout resource-workspace-linked-item-layout--summary-sidebar resource-workspace-linked-item-layout--entity-schema-detail" aria-label={`${schemaName} Entity Schema details`}>
      <section className="resource-workspace-panel resource-workspace-linked-item-section">
        <div className="resource-workspace-section-title">
          <h2>Information</h2>
          <p>View-only Entity Schema settings used by this configuration.</p>
        </div>
        <dl className="resource-workspace-facts resource-workspace-linked-item-metrics">
          <Fact label="Display Name" value={schemaName} />
          <Fact label="Primary Version" value={getString(primaryVersion, "Version", "version") || "Not set"} />
          <Fact label="Entity Objects" value={entityObjects.length.toLocaleString()} />
          <Fact label="Source-backed" value={sourceBackedCount.toLocaleString()} />
          <Fact label="Availability" value={isRecordActive(schema) ? "Enabled" : "Disabled"} />
        </dl>
        <div className="resource-workspace-linked-item-section__divider" />
        <div className="resource-workspace-section-title resource-workspace-section-title--embedded">
          <h3>Schema Versions</h3>
          <p>Draft Schema Versions are editable. Published Schema Versions can be selected as Primary.</p>
        </div>
        {schemaDescription ? <p className="resource-workspace-linked-item-summary">{schemaDescription}</p> : null}
        <div className="resource-workspace-record-list resource-workspace-schema-version-list resource-workspace-schema-version-list--embedded">
          {versions.length === 0 ? (
            <ResourceStateControl compact tone="warning" title="No schema versions" message="This Schema does not have a schema version yet." />
          ) : versions.map((version, index) => {
            const versionId = getSchemaVersionId(version);
            const versionLabel = getString(version, "Version", "version") || "Version";
            const versionTitle = getString(version, "Title", "title") || "Schema version";
            const versionComment = getString(version, "Comment", "comment");
            const versionCommentHtml = sanitizeHtmlFragment(versionComment);
            const selected = normalizeCompare(versionId) === normalizeCompare(selectedVersionId || primaryVersionId);
            const isPublished = getBoolean(version, "IsPublished", "isPublished");
            const isPrimary = isPrimarySchemaVersion(version, primaryVersionId);
            return (
              <article
                key={versionId || `schema-version-${index}`}
                className={`resource-workspace-record-row resource-workspace-schema-version-row${selected ? " resource-workspace-schema-version-row--selected" : ""}`}
                role="button"
                tabIndex={0}
                aria-label={`Select schema version ${versionLabel}`}
                onClick={() => onSelectSchemaVersion?.(version)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelectSchemaVersion?.(version);
                  }
                }}
              >
                <div className="resource-workspace-schema-version-row__copy">
                  <div className="resource-workspace-schema-version-row__heading">
                    <strong>{versionLabel}</strong>
                    <strong>{versionTitle}</strong>
                  </div>
                  {versionCommentHtml ? (
                    <div
                      className="resource-workspace-schema-version-row__comment"
                      dangerouslySetInnerHTML={{ __html: versionCommentHtml }}
                    />
                  ) : null}
                </div>
                <span className="resource-workspace-record-row-actions resource-workspace-schema-version-row__actions">
                  <span className="resource-workspace-schema-version-row__status">
                    {isPrimary ? "Published / Primary" : getSchemaVersionStatusLabel(version)}
                  </span>
                  <ButtonControl
                    type="standard-small"
                    title={isPublished ? `${versionLabel} is Published and locked.` : `Edit schema version ${versionLabel}`}
                    aria-label={`Edit schema version ${versionLabel}`}
                    disabled={isPublished}
                    onClick={(event) => {
                      event.stopPropagation();
                      onEditSchemaVersion?.(version);
                    }}
                  >
                    <IconControl iconKey="edit" title={`Edit schema version ${versionLabel}`} width="0.95rem" height="0.95rem" />
                  </ButtonControl>
                </span>
              </article>
            );
          })}
        </div>
      </section>
      <section className="resource-workspace-panel resource-workspace-linked-item-section resource-workspace-entity-object-list-section">
        <div className="resource-workspace-section-title resource-workspace-section-title--with-action">
          <div>
            <h2>Entity Objects</h2>
            <p>Defined for schema version {selectedVersionLabel}.</p>
          </div>
          {entityObjects.length > 0 ? (
            <div className="resource-workspace-entity-object-search">
              <FieldControl
                value={entityObjectSearch}
                onChange={(value) => setEntityObjectSearch(value ?? "")}
                fieldMetadata={{
                  ...buildTextBoxField("entityObjectSearch", "Search", "Search by name, object name, status, source, or updated date."),
                  DisplayName: "",
                  Description: "",
                  IsRequired: false,
                  PlaceHolder: "Search entity objects...",
                  AriaLabel: "Search entity objects"
                }}
              />
            </div>
          ) : null}
        </div>
        {actionError ? <p className="resource-workspace-inline-warning">{actionError}</p> : null}
        <div className="resource-workspace-record-list">
          {entityObjects.length === 0 ? (
            <ResourceStateControl compact tone="warning" title="No Entity Objects" message="No Entity Objects have been created for this Schema yet." />
          ) : filteredEntityObjects.length === 0 ? (
            <ResourceStateControl compact tone="warning" title="No matching objects" message="No objects match the current search." />
          ) : filteredEntityObjects.map((entityObject, index) => {
            const fieldCount = getEntityObjectFieldCount(entityObject);
            const objectName = getString(entityObject, "ObjectName", "objectName");
            const entityObjectId = getId(entityObject);
            const entityObjectName = getDisplayName(entityObject, objectName || "Entity Object");
            const sourceObjectId = getEntityObjectSourceObjectId(entityObject);
            const hasSourceObject = Boolean(sourceObjectId && onManageSourceObject);
            const operationMenuKey = entityObjectId || `entity-object-${index}`;
            const operationMenuOpen = openOperationMenu === operationMenuKey;
            const updatedDate = formatDateTime(getValue(entityObject, "UpdatedDate", "updatedDate", "ModifiedDate", "modifiedDate", "LastUpdatedDate", "lastUpdatedDate", "CreatedDate", "createdDate"));
            const mapBusyKey = `${ENTITY_OBJECT_FIELD_MAPPING_OPERATION}:${entityObjectId || ""}`;
            const hasOperationMenu = entityObjectOperations.length > 0;
            return (
              <article
                key={entityObjectId || `entity-object-${index}`}
                className="resource-workspace-configuration-linked-row resource-workspace-entity-object-card"
                aria-label={`Entity Object ${entityObjectName}`}
              >
                <span className="resource-workspace-configuration-linked-copy resource-workspace-entity-object-card__copy">
                  <strong>{entityObjectName}</strong>
                  <small>{objectName || "No object name"} - {fieldCount.toLocaleString()} field{fieldCount === 1 ? "" : "s"}</small>
                </span>
                <dl className="resource-workspace-entity-object-card__facts">
                  <div>
                    <dt>Version</dt>
                    <dd>{getEntityObjectVersionLabel(entityObject, selectedVersionLabel)}</dd>
                  </div>
                  <div>
                    <dt>Status</dt>
                    <dd>{isRecordActive(entityObject) ? "Active" : "Inactive"}</dd>
                  </div>
                  <div>
                    <dt>Updated</dt>
                    <dd>{updatedDate || "No updated date"}</dd>
                  </div>
                </dl>
                <div className="resource-workspace-record-row-actions resource-workspace-entity-object-card__actions">
                  {entityObjectId ? (
                    <>
                      <ButtonControl
                        type="standard-small"
                        title={`Manage ${entityObjectName}`}
                        aria-label={`Manage ${entityObjectName}`}
                        onClick={() => onManageEntityObject?.(entityObject)}
                      >
                        <IconControl iconKey="settings" title={`Manage ${entityObjectName}`} width="0.95rem" height="0.95rem" />
                      </ButtonControl>
                      <ButtonControl
                        type="standard-small"
                        title={`Map ${entityObjectName}`}
                        aria-label={`Map ${entityObjectName}`}
                        onClick={() => runEntityObjectAction(ENTITY_OBJECT_FIELD_MAPPING_OPERATION, entityObject)}
                        disabled={actionBusyKey === mapBusyKey}
                      >
                        <IconControl iconKey="mapping" title={`Map ${entityObjectName}`} width="0.95rem" height="0.95rem" />
                      </ButtonControl>
                      {hasSourceObject ? (
                        <ButtonControl
                          type="standard-small"
                          title={`Open source object for ${entityObjectName}`}
                          aria-label={`Open source object for ${entityObjectName}`}
                          onClick={() => openSourceManager(entityObject)}
                        >
                          <IconControl iconKey="sourcedata" title={`Open source object for ${entityObjectName}`} width="0.95rem" height="0.95rem" />
                        </ButtonControl>
                      ) : null}
                    </>
                  ) : null}
                  {hasOperationMenu ? (
                    <span className="resource-workspace-record-row-operation-menu">
                      <ButtonControl
                        type="standard-small"
                        title={`Open entity object operations for ${entityObjectName}`}
                        aria-label={`Open entity object operations for ${entityObjectName}`}
                        onClick={(event) => openOperations(event, operationMenuKey)}
                      >
                        <IconControl iconKey="viewmore" title={`Open entity object operations for ${entityObjectName}`} width="0.95rem" height="0.95rem" />
                      </ButtonControl>
                      {operationMenuOpen ? (
                        <ContextMenuControl
                          openContextMenu={operationMenuOpen}
                          setOpenContextMenu={(nextOpen) => setOpenOperationMenu(nextOpen ? operationMenuKey : "")}
                          contextMenuStyle={operationMenuStyle}
                        >
                          <div className="resource-workspace-command-bar-menu" role="menu" aria-label={`Entity object operations for ${entityObjectName}`}>
                            {entityObjectOperations.map((operation) => (
                              <button
                                key={getOperationName(operation)}
                                type="button"
                                role="menuitem"
                                onClick={() => runMenuOperation(entityObject, operation)}
                              >
                                {getOperationDisplayName(operation)}
                              </button>
                            ))}
                          </div>
                        </ContextMenuControl>
                      ) : null}
                    </span>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
};

const ResourceConfigurationSourceObjectManagerScreen = ({
  sourceObjectId,
  sourceSystemId,
  clientResourceId,
  resourceConfigurationId,
  editObjectRequestKey,
  onClose
}) => (
  <section className="resource-workspace-configuration-source-object-manager" aria-label="Source Object Manager">
    <SourceColumnSetManage
      embeddedMode
      panelId={sourceObjectId}
      panelParams={{
        sourceObjectId,
        sourceSystemId,
        clientResourceId,
        resourceConfigurationId
      }}
      editObjectRequestKey={editObjectRequestKey}
      onEmbeddedClose={onClose}
    />
  </section>
);

const ResourceConfigurationScreenShell = ({
  selectedResource,
  configurations,
  selectedConfiguration,
  screenKey,
  screenDisplayName,
  canOpenConfigurationSetup,
  onSelectConfigurationScreen,
  onOpenConfigurationSetup,
  onValidateConfiguration,
  onActivateConfiguration,
  onDisableConfiguration,
  onReconfigureConfiguration,
  onDeleteConfiguration,
  onEditConfiguration,
  onRefreshConfigurations,
  onBackToDestinationDeployment,
  configurationActionState,
  integrationScreenState,
  sourceSystemScreenState,
  logManager,
  recentLogsRefreshKey,
  onEditIntegration,
  onCheckIntegrationConnection,
  onSetIntegrationOnline,
  onEditSourceSystem,
  onSetSourceSystemActive,
  onSetSourceObjectActive,
  schemaScreenState,
  schemaOperations,
  onEditSchema,
  onRunSchemaOperation,
  onPublishSchemaVersion,
  onLoadSourceSystemTargets,
  onOpenAddSourceObjects,
  sourceObjectManagerSourceObjectId,
  onCloseSourceObjectManager,
  onEditCurrentSourceObject,
  sourceObjectOperations,
  onRunSourceObjectOperation,
  entityObjectOperations,
  onRunEntityObjectOperation,
  onOpenEntityObjectPanel,
  destinationScreenState,
  selectedDestinationDeployment: selectedDestinationDeploymentOverride,
  destinationDeploymentRouteFilters,
  destinationEntityDeploymentRouteFilters,
  destinationScheduleActionState,
  destinationEntityScopeActionState,
  warehouseViewActionState,
  onEditDestination,
  onSetDestinationActive,
  onCheckDestinationConnection,
  onManualRunDestinationDeployment,
  onDeployDestinationDeployment,
  onUndeployDestinationDeployment,
  onDeployWarehouseDeployment,
  onDropWarehouseDeployment,
  onCloneWarehouseDeployment,
  onAddDestinationDeployment,
  onManageDestinationDeployment,
  onEditDestinationDeployment,
  onSetDestinationDeploymentActive,
  onDeleteDestinationDeployment,
  onOpenDestinationDeploymentSchedule,
  onOpenDestinationDeploymentScope,
  onOpenDestinationDeploymentViews,
  onOpenEndpointEntityEndpointPanel,
  screenActions = [],
  children
}) => {
  const selectedConfigurationId = getId(selectedConfiguration);
  const isManageScreen = screenKey === CONFIGURATION_MANAGE_NAVIGATION.key;
  const showConfigurationSelector = !isManageScreen && configurations.length > 0;
  const configurationOptions = useMemo(
    () =>
      configurations.map((configuration) => ({
        Text: getDisplayName(configuration, "Resource configuration"),
        Value: getId(configuration)
      })).filter((option) => option.Value),
    [configurations]
  );
  const resourceTitle = getDisplayName(
    selectedResource.productResource,
    selectedResource.productResourceKey || selectedResource.displayName
  );
  const isSingleInstance = isSingleInstanceProductResource(selectedResource.productResource);
  const selectedConfigurationTitle = getDisplayName(selectedConfiguration, "Resource configuration");
  const selectedScreenTitle = screenDisplayName
    || (isManageScreen ? CONFIGURATION_MANAGE_NAVIGATION.displayName : formatDisplayLabel(screenKey))
    || "Configuration";
  const title = selectedConfigurationId
    ? `${selectedConfigurationTitle}: ${selectedScreenTitle}`
    : `${resourceTitle}: Configurations`;
  const showConfigurationActionBar = Boolean(selectedConfigurationId);
  const isSourceObjectManagerScreen = Boolean(sourceObjectManagerSourceObjectId);
  const isDestinationDeploymentScreen = screenKey === DESTINATION_DEPLOYMENT_SCREEN_KEY;
  const isDestinationWarehouseViewsScreen = screenKey === DESTINATION_WAREHOUSE_VIEWS_SCREEN_KEY;
  const isDestinationEntityDeploymentsScreen = screenKey === DESTINATION_ENTITY_DEPLOYMENTS_SCREEN_KEY;
  const configurationSelectorDisabled = isSourceObjectManagerScreen
    || isDestinationDeploymentScreen
    || isDestinationWarehouseViewsScreen
    || isDestinationEntityDeploymentsScreen;

  return (
    <div className="resource-workspace-overview resource-workspace-configuration-screen">
      <header className="resource-workspace-configuration-shell-header">
        <div className="resource-workspace-configuration-shell-title">
          <span className="resource-workspace-header-icon" aria-hidden="true">
            <ResourceWorkspaceIcon
              iconKey={getString(selectedResource.productResource, "Icon", "icon") || RESOURCE_FALLBACK_ICON}
              iconPath={getResourceWorkspaceIconPath(selectedResource)}
              label={selectedResource.displayName}
              preferImage={resourceUsesProductIcon(selectedResource)}
              width="3.25rem"
              height="3.25rem"
            />
          </span>
          <div className="resource-workspace-title-line">
            <h1>{title}</h1>
            <ResourceMaturityBadge value={getValue(selectedResource.productResource, "MaturityStatus", "maturityStatus")} />
          </div>
        </div>
        <div className="resource-workspace-configuration-shell-actions">
          {showConfigurationSelector ? (
            <div className="resource-workspace-configuration-selector">
              <FieldControl
                fieldMetadata={buildSelectListField("ResourceConfigurationId", "", configurationOptions, "Select configuration")}
                value={selectedConfigurationId}
                disabled={configurationSelectorDisabled}
                onChange={(nextValue) => onSelectConfigurationScreen(nextValue || "", screenKey)}
              />
            </div>
          ) : null}
        </div>
      </header>
      {showConfigurationActionBar ? (
        <ResourceConfigurationActionBar
          configuration={selectedConfiguration}
          screenKey={screenKey}
          canOpenConfigurationSetup={canOpenConfigurationSetup}
          actionState={configurationActionState}
          onOpenConfigurationSetup={onOpenConfigurationSetup}
          onValidateConfiguration={onValidateConfiguration}
          onActivateConfiguration={onActivateConfiguration}
          onDisableConfiguration={onDisableConfiguration}
          onReconfigureConfiguration={onReconfigureConfiguration}
          onDeleteConfiguration={onDeleteConfiguration}
          onEditConfiguration={onEditConfiguration}
          onRefreshConfigurations={onRefreshConfigurations}
          onBackToConfigurations={isSingleInstance
            ? undefined
            : () => onSelectConfigurationScreen("", CONFIGURATION_MANAGE_NAVIGATION.key)}
          onBackToManage={() => onSelectConfigurationScreen(selectedConfigurationId, CONFIGURATION_MANAGE_NAVIGATION.key)}
          onBackToDestination={() => onSelectConfigurationScreen(selectedConfigurationId, DESTINATION_SCREEN_KEY)}
          onBackToDeployment={onBackToDestinationDeployment || (() => onSelectConfigurationScreen(selectedConfigurationId, DESTINATION_DEPLOYMENT_SCREEN_KEY))}
          onOpenDestinationDeployments={() => onSelectConfigurationScreen(selectedConfigurationId, DESTINATION_DEPLOYMENTS_SCREEN_KEY)}
          integrationScreenState={integrationScreenState}
          onEditIntegration={onEditIntegration}
          onCheckIntegrationConnection={onCheckIntegrationConnection}
          onSetIntegrationOnline={onSetIntegrationOnline}
          sourceSystemScreenState={sourceSystemScreenState}
          onEditSourceSystem={onEditSourceSystem}
          onSetSourceSystemActive={onSetSourceSystemActive}
          onSetSourceObjectActive={onSetSourceObjectActive}
          schemaScreenState={schemaScreenState}
          schemaOperations={schemaOperations}
          onEditSchema={onEditSchema}
          onRunSchemaOperation={onRunSchemaOperation}
          onPublishSchemaVersion={onPublishSchemaVersion}
          onLoadSourceSystemTargets={onLoadSourceSystemTargets}
          onOpenAddSourceObjects={onOpenAddSourceObjects}
          sourceObjectManagerSourceObjectId={sourceObjectManagerSourceObjectId}
          onCloseSourceObjectManager={onCloseSourceObjectManager}
          onEditCurrentSourceObject={onEditCurrentSourceObject}
          sourceObjectOperations={sourceObjectOperations}
          onRunSourceObjectOperation={onRunSourceObjectOperation}
          destinationScreenState={destinationScreenState}
          selectedDestinationDeployment={selectedDestinationDeploymentOverride}
          destinationDeploymentRouteFilters={destinationDeploymentRouteFilters}
          destinationEntityDeploymentRouteFilters={destinationEntityDeploymentRouteFilters}
          destinationScheduleActionState={destinationScheduleActionState}
          destinationEntityScopeActionState={destinationEntityScopeActionState}
          warehouseViewActionState={warehouseViewActionState}
          onEditDestination={onEditDestination}
          onSetDestinationActive={onSetDestinationActive}
          onCheckDestinationConnection={onCheckDestinationConnection}
          onManualRunDestinationDeployment={onManualRunDestinationDeployment}
          onDeployDestinationDeployment={onDeployDestinationDeployment}
          onUndeployDestinationDeployment={onUndeployDestinationDeployment}
          onDeployWarehouseDeployment={onDeployWarehouseDeployment}
          onDropWarehouseDeployment={onDropWarehouseDeployment}
          onCloneWarehouseDeployment={onCloneWarehouseDeployment}
          onAddDestinationDeployment={onAddDestinationDeployment}
          onManageDestinationDeployment={onManageDestinationDeployment}
          onEditDestinationDeployment={onEditDestinationDeployment}
          onSetDestinationDeploymentActive={onSetDestinationDeploymentActive}
          onDeleteDestinationDeployment={onDeleteDestinationDeployment}
          onOpenDestinationDeploymentSchedule={onOpenDestinationDeploymentSchedule}
          onOpenDestinationDeploymentScope={onOpenDestinationDeploymentScope}
          onOpenDestinationDeploymentViews={onOpenDestinationDeploymentViews}
          onOpenEndpointEntityEndpointPanel={onOpenEndpointEntityEndpointPanel}
          screenActions={screenActions}
        />
      ) : null}
      {children}
    </div>
  );
};

const ResourceConfigurationActionBar = ({
  configuration,
  screenKey,
  canOpenConfigurationSetup,
  actionState,
  onOpenConfigurationSetup,
  onValidateConfiguration,
  onActivateConfiguration,
  onDisableConfiguration,
  onReconfigureConfiguration,
  onDeleteConfiguration,
  onEditConfiguration,
  onRefreshConfigurations,
  onBackToConfigurations,
  onBackToManage,
  onBackToDestination,
  onBackToDeployment,
  onOpenDestinationDeployments,
  integrationScreenState,
  onEditIntegration,
  onCheckIntegrationConnection,
  onSetIntegrationOnline,
  sourceSystemScreenState,
  onEditSourceSystem,
  onSetSourceSystemActive,
  onSetSourceObjectActive,
  schemaScreenState,
  schemaOperations = [],
  onEditSchema,
  onRunSchemaOperation,
  onPublishSchemaVersion,
  onLoadSourceSystemTargets,
  onOpenAddSourceObjects,
  sourceObjectManagerSourceObjectId,
  onCloseSourceObjectManager,
  onEditCurrentSourceObject,
  sourceObjectOperations = [],
  onRunSourceObjectOperation,
  destinationScreenState,
  selectedDestinationDeployment: selectedDestinationDeploymentOverride,
  destinationDeploymentRouteFilters,
  destinationEntityDeploymentRouteFilters,
  destinationScheduleActionState,
  destinationEntityScopeActionState,
  warehouseViewActionState,
  onEditDestination,
  onSetDestinationActive,
  onCheckDestinationConnection,
  onManualRunDestinationDeployment,
  onDeployDestinationDeployment,
  onUndeployDestinationDeployment,
  onDeployWarehouseDeployment,
  onDropWarehouseDeployment,
  onCloneWarehouseDeployment,
  onAddDestinationDeployment,
  onManageDestinationDeployment,
  onEditDestinationDeployment,
  onSetDestinationDeploymentActive,
  onDeleteDestinationDeployment,
  onOpenDestinationDeploymentSchedule,
  onOpenDestinationDeploymentScope,
  onOpenDestinationDeploymentViews,
  onOpenEndpointEntityEndpointPanel,
  screenActions = []
}) => {
  const [deploymentActiveConfirmRequest, setDeploymentActiveConfirmRequest] = useState({
    open: false,
    isActive: false,
    deployment: null
  });
  const closeDeploymentActiveConfirmDialog = useCallback(() => {
    setDeploymentActiveConfirmRequest({
      open: false,
      isActive: false,
      deployment: null
    });
  }, []);
  const confirmDeploymentActiveChange = useCallback(() => {
    const request = deploymentActiveConfirmRequest;
    closeDeploymentActiveConfirmDialog();
    if (!request?.deployment) return;
    onSetDestinationDeploymentActive?.(request.deployment, request.isActive);
  }, [closeDeploymentActiveConfirmDialog, deploymentActiveConfirmRequest, onSetDestinationDeploymentActive]);
  const configurationName = getDisplayName(configuration, "Resource configuration");
  const isManageScreen = screenKey === CONFIGURATION_MANAGE_NAVIGATION.key;
  const isSystemManagedClientMcpServer = isClientMcpServerResource(configuration);
  const isIntegrationScreen = screenKey === SOURCE_INTEGRATION_NAVIGATIONS[0].key;
  const isSourceSystemScreen = INBOUND_EDI_SOURCE_SYSTEM_SCREEN_KEYS.includes(screenKey);
  const isEntitySchemaScreen = [
    ...INBOUND_EDI_ENTITY_SCHEMA_SCREEN_KEYS,
    ...OUTBOUND_EDI_ENTITY_SCHEMA_SCREEN_KEYS
  ].includes(screenKey);
  const isDestinationScreen = screenKey === DESTINATION_SCREEN_KEY;
  const isDestinationDeploymentsScreen = screenKey === DESTINATION_DEPLOYMENTS_SCREEN_KEY;
  const isDestinationDeploymentScreen = screenKey === DESTINATION_DEPLOYMENT_SCREEN_KEY;
  const isDestinationWarehouseViewsScreen = screenKey === DESTINATION_WAREHOUSE_VIEWS_SCREEN_KEY;
  const isDestinationEntityDeploymentsScreen = screenKey === DESTINATION_ENTITY_DEPLOYMENTS_SCREEN_KEY;
  const isDestinationScheduleScreen = screenKey === DESTINATION_SCHEDULES_SCREEN_KEY;
  const isInboundEdiConfiguration = isInboundEdiResource(null, null, configuration);
  const isOutboundEdiConfiguration = isOutboundEdiResource(null, null, configuration);
  const isEdiConfiguration = isEdiResourceConfiguration(null, null, configuration);
  const integration = integrationScreenState?.integration;
  const hasIntegration = Boolean(getId(integration));
  const integrationOnline = getBoolean(integration, "IsOnline", "isOnline");
  const integrationUnavailable = integrationScreenState?.loading || !hasIntegration;
  const integrationMetadataUnavailable = !integrationScreenState?.metadata;
  const sourceSystem = sourceSystemScreenState?.sourceSystem;
  const hasSourceSystem = Boolean(getId(sourceSystem));
  const sourceSystemActive = isRecordActive(sourceSystem);
  const sourceSystemUnavailable = sourceSystemScreenState?.loading || !hasSourceSystem;
  const sourceSystemMetadataUnavailable = !sourceSystemScreenState?.metadata;
  const schema = schemaScreenState?.schema;
  const hasSchema = Boolean(getId(schema));
  const schemaUnavailable = schemaScreenState?.loading || !hasSchema;
  const schemaMetadataUnavailable = !schemaScreenState?.metadata;
  const selectedSchemaVersion = resolveSelectedSchemaVersion(
    schema,
    schemaScreenState?.selectedSchemaVersionId || schemaScreenState?.activeSchemaVersionId
  );
  const selectedSchemaVersionLabel = getString(selectedSchemaVersion, "Version", "version") || "selected version";
  const selectedSchemaVersionPublished = getBoolean(selectedSchemaVersion, "IsPublished", "isPublished");
  const selectedSchemaVersionPrimary = isPrimarySchemaVersion(
    selectedSchemaVersion,
    getSchemaActiveVersionId(schema)
  );
  const destination = destinationScreenState?.destination;
  const hasDestination = Boolean(getId(destination));
  const destinationActive = isRecordActive(destination);
  const destinationUnavailable = destinationScreenState?.loading || !hasDestination;
  const destinationMetadataUnavailable = !destinationScreenState?.metadata;
  const destinationDefaults = destinationScreenState?.defaults;
  const isEndpointDestination = Boolean(destinationDefaults?.isEndpoint);
  const isWarehouseDestination = normalizeCompare(destinationDefaults?.managerKey) === "warehouse";
  const destinationDeployments = getDestinationDeployments(destination, destinationDefaults);
  const selectedDestinationDeployment = selectedDestinationDeploymentOverride || (isDestinationDeploymentScreen || isDestinationWarehouseViewsScreen
    ? findDestinationDeploymentById(destination, destinationDefaults, destinationDeploymentRouteFilters?.deploymentId)
    : null);
  const selectedDestinationDeploymentActive = isRecordActive(selectedDestinationDeployment);
  const destinationDeploymentUnavailable = destinationUnavailable || !selectedDestinationDeployment;
  const destinationDeploymentManualRunUnavailable = destinationDeploymentUnavailable
    || !destinationActive
    || !isRecordActive(selectedDestinationDeployment);
  const destinationDeploymentMetadataUnavailable = !destinationScreenState?.deploymentMetadata;
  const selectedEndpointDeploymentOnline = isEndpointDestination && isSchemaDestinationOnline(selectedDestinationDeployment);
  const selectedEndpointDeploymentConfigLocked = isEndpointDestination
    && selectedDestinationDeployment
    && !isSchemaDestinationConfigEditable(selectedDestinationDeployment);
  const editableEndpointDeployments = isEndpointDestination
    ? destinationDeployments.filter(isSchemaDestinationConfigEditable)
    : destinationDeployments;
  const scopedEndpointDeployment = isEndpointDestination && destinationEntityDeploymentRouteFilters?.deploymentId
    ? findDestinationDeploymentById(destination, destinationDefaults, destinationEntityDeploymentRouteFilters.deploymentId)
    : null;
  const endpointScopeConfigLocked = isEndpointDestination
    && (
      scopedEndpointDeployment
        ? !isSchemaDestinationConfigEditable(scopedEndpointDeployment)
        : editableEndpointDeployments.length === 0
    );
  const isSourceObjectManagerScreen = Boolean(sourceObjectManagerSourceObjectId);
  const currentManagedSourceObject = isSourceObjectManagerScreen
    ? findSourceObjectById(sourceSystemScreenState?.sourceObjects, sourceObjectManagerSourceObjectId)
    : null;
  const currentManagedSourceObjectActive = currentManagedSourceObject
    ? isRecordActive(currentManagedSourceObject)
    : true;
  const loadSourceObjectsOperation = sourceSystemScreenState?.loadSourceObjectsOperation;
  const loadSourceObjectsLabel = isInboundEdiConfiguration
    ? "Generate Source Objects"
    : getOperationDisplayName(loadSourceObjectsOperation) || "Load Source Objects";
  const connectionCheckIcon = getOperationIcon(
    integrationScreenState?.metadata,
    Operations.IntegrationItem.ConnectionCheck,
    "settings_ethernet"
  );
  const isBusy = Boolean(
    actionState?.busyAction
    || (isDestinationScheduleScreen && destinationScheduleActionState?.busyAction)
    || (isDestinationEntityDeploymentsScreen && destinationEntityScopeActionState?.busyAction)
    || (isDestinationWarehouseViewsScreen && warehouseViewActionState?.busyAction)
  );
  const runConfigurationGuardedAction = useCallback((action, label) => {
    if (isDestinationWarehouseViewsScreen && warehouseViewActionState?.confirmDiscardChanges) {
      warehouseViewActionState.confirmDiscardChanges(action, label);
      return;
    }
    if (isDestinationEntityDeploymentsScreen && !isEndpointDestination && destinationEntityScopeActionState?.confirmDiscardChanges) {
      destinationEntityScopeActionState.confirmDiscardChanges(action, label);
      return;
    }
    action?.();
  }, [
    destinationEntityScopeActionState,
    isDestinationEntityDeploymentsScreen,
    isDestinationWarehouseViewsScreen,
    isEndpointDestination,
    warehouseViewActionState
  ]);
  const screenActionList = Array.isArray(screenActions) ? screenActions.filter((action) => action?.key) : [];
  const refreshActionOverride = screenActionList.find((action) => action.key === "refresh");
  const actions = [{
    key: "refresh",
    label: refreshActionOverride?.label || "Refresh",
    iconKey: refreshActionOverride?.iconKey || "refresh",
    disabled: refreshActionOverride ? Boolean(refreshActionOverride.disabled) : isBusy,
    loading: Boolean(refreshActionOverride?.loading),
    title: refreshActionOverride?.title || "Refresh configuration data.",
    onClick: refreshActionOverride?.onClick || (() => runConfigurationGuardedAction(() => onRefreshConfigurations?.(), "refresh configuration data"))
  }];

  screenActionList.forEach((action) => {
    if (!action || !action.key) return;
    if (action.key === "refresh") return;
    actions.push({
      ...action,
      disabled: Boolean(action.disabled)
    });
  });

  if (isManageScreen && !isSystemManagedClientMcpServer) {
    actions.push({
      key: "edit",
      label: "Edit",
      iconKey: "edit",
      disabled: isBusy,
      title: `Edit ${configurationName}.`,
      onClick: () => onEditConfiguration?.(configuration)
    });
  }

  if (isIntegrationScreen) {
    actions.push({
      key: "integration-edit",
      label: "Edit",
      iconKey: "edit",
      disabled: isBusy || integrationUnavailable || integrationMetadataUnavailable,
      title: hasIntegration && !integrationMetadataUnavailable
        ? "Edit this Integration."
        : "No linked Integration metadata is available to edit.",
      onClick: () => onEditIntegration?.()
    });
    actions.push({
      key: "integration-check",
      label: "Check Connection",
      iconKey: connectionCheckIcon,
      disabled: isBusy || integrationUnavailable,
      title: hasIntegration ? "Check the Integration connection." : "No linked Integration is available to check.",
      onClick: () => onCheckIntegrationConnection?.()
    });
    actions.push({
      key: integrationOnline ? "integration-disable" : "integration-enable",
      label: integrationOnline ? "Disable" : "Enable",
      iconKey: integrationOnline ? "undo" : "success",
      disabled: isBusy || integrationUnavailable,
      title: integrationOnline
        ? "Disable this Integration for runtime use."
        : "Enable this Integration for runtime use.",
      onClick: () => onSetIntegrationOnline?.(!integrationOnline)
    });
  }

  if (isSourceSystemScreen) {
    actions.push({
      key: isSourceObjectManagerScreen ? "source-object-manager-edit" : "source-system-edit",
      label: "Edit",
      iconKey: "edit",
      disabled: isSourceObjectManagerScreen
        ? isBusy || !sourceObjectManagerSourceObjectId
        : isBusy || sourceSystemUnavailable || sourceSystemMetadataUnavailable,
      title: isSourceObjectManagerScreen
        ? "Edit this Source Object."
        : hasSourceSystem && !sourceSystemMetadataUnavailable
        ? "Edit this Source System."
        : "No linked Source System metadata is available to edit.",
      onClick: () => {
        if (isSourceObjectManagerScreen) {
          onEditCurrentSourceObject?.();
          return;
        }
        onEditSourceSystem?.();
      }
    });
    actions.push({
      key: isSourceObjectManagerScreen
        ? currentManagedSourceObjectActive ? "source-object-disable" : "source-object-enable"
        : sourceSystemActive ? "source-system-disable" : "source-system-enable",
      label: isSourceObjectManagerScreen
        ? currentManagedSourceObjectActive ? "Disable" : "Enable"
        : sourceSystemActive ? "Disable" : "Enable",
      iconKey: isSourceObjectManagerScreen
        ? currentManagedSourceObjectActive ? "undo" : "success"
        : sourceSystemActive ? "undo" : "success",
      disabled: isSourceObjectManagerScreen
        ? !currentManagedSourceObject
        : isBusy || sourceSystemUnavailable,
      title: isSourceObjectManagerScreen
        ? currentManagedSourceObject
          ? currentManagedSourceObjectActive
            ? "Disable this Source Object for runtime use."
            : "Enable this Source Object for runtime use."
          : "Current Source Object is not available to update."
        : sourceSystemActive
          ? "Disable this Source System for runtime use."
          : "Enable this Source System for runtime use.",
      onClick: () => {
        if (isSourceObjectManagerScreen) {
          onSetSourceObjectActive?.(!currentManagedSourceObjectActive);
          return;
        }
        onSetSourceSystemActive?.(!sourceSystemActive);
      }
    });
    if (isSourceObjectManagerScreen && currentManagedSourceObject && sourceObjectOperations.length > 0) {
      sourceObjectOperations.forEach((operation, index) => {
        const operationName = getOperationName(operation) || `source-object-operation-${index}`;
        const operationLabel = getOperationDisplayName(operation) || operationName;
        actions.push({
          key: `source-object-operation-${operationName}-${index}`,
          label: operationLabel,
          iconKey: getOperationIcon(
            sourceSystemScreenState?.sourceObjectMetadata,
            operationName,
            getString(operation, "Icon", "icon", "IconKey", "iconKey") || "settings"
          ),
          disabled: isBusy || !currentManagedSourceObject,
          title: operationLabel,
          onClick: () => onRunSourceObjectOperation?.(currentManagedSourceObject, operation)
        });
      });
    }
    if (!isSourceObjectManagerScreen) {
      actions.push({
        key: "source-system-add-target",
        label: loadSourceObjectsLabel,
        iconKey: "add",
        disabled: isBusy || sourceSystemUnavailable,
        title: hasSourceSystem ? loadSourceObjectsLabel : "No linked Source System is available to load Source Objects.",
        onClick: () => onOpenAddSourceObjects?.()
      });
    }
    if (!isSourceObjectManagerScreen) {
      actions.push({
        key: "source-system-load-targets",
        label: "Load Targets",
        iconKey: "target",
        disabled: isBusy || sourceSystemUnavailable,
        title: hasSourceSystem ? "Load Source System targets." : "No linked Source System is available to load targets.",
        onClick: () => onLoadSourceSystemTargets?.()
      });
    }
  }

  if (isEntitySchemaScreen) {
    const appendSchemaOperationAction = (operation, index) => {
      const operationName = getOperationName(operation) || `schema-operation-${index}`;
      const operationLabel = getOperationDisplayName(operation) || operationName;
      actions.push({
        key: `schema-operation-${operationName}-${index}`,
        label: operationLabel,
        iconKey: getOperationIcon(
          schemaScreenState?.metadata,
          operationName,
          getString(operation, "Icon", "icon", "IconKey", "iconKey") || "settings"
        ),
        disabled: isBusy || schemaUnavailable,
        title: operationLabel,
        onClick: () => onRunSchemaOperation?.(schema, operation)
      });
    };
    const indexedSchemaOperations = schemaOperations.map((operation, index) => ({ operation, index }));
    indexedSchemaOperations
      .filter(({ operation }) => isEntityObjectImportFromSchemaOperation(operation))
      .forEach(({ operation, index }) => appendSchemaOperationAction(operation, index));
    actions.push({
      key: "schema-edit",
      label: "Edit",
      iconKey: "edit",
      disabled: isBusy || schemaUnavailable || schemaMetadataUnavailable,
      title: hasSchema && !schemaMetadataUnavailable
        ? "Edit this Entity Schema."
        : "No linked Entity Schema metadata is available to edit.",
      onClick: () => onEditSchema?.(schema, schemaScreenState?.metadata)
    });
    indexedSchemaOperations
      .filter(({ operation }) => !isEntityObjectImportFromSchemaOperation(operation))
      .forEach(({ operation, index }) => appendSchemaOperationAction(operation, index));
    if (!selectedSchemaVersionPublished) {
      actions.push({
        key: "schema-version-publish",
        label: "Publish",
        iconKey: "publish",
        disabled: isBusy || schemaUnavailable || !selectedSchemaVersion,
        title: selectedSchemaVersion
          ? `Publish selected Draft Schema Version ${selectedSchemaVersionLabel}.`
          : "Select a Draft Schema Version to publish.",
        onClick: () => onPublishSchemaVersion?.(selectedSchemaVersion, "publish")
      });
    } else {
      if (!selectedSchemaVersionPrimary) {
        actions.push({
          key: "schema-version-make-primary",
          label: "Make Primary",
          iconKey: "success",
          disabled: isBusy || schemaUnavailable || !selectedSchemaVersion,
          title: `Use Published Schema Version ${selectedSchemaVersionLabel} as the Primary Version.`,
          onClick: () => onPublishSchemaVersion?.(selectedSchemaVersion, "make-primary")
        });
      }
      actions.push({
        key: "schema-version-unpublish",
        label: "Unpublish",
        iconKey: "undo",
        disabled: isBusy || schemaUnavailable || !selectedSchemaVersion,
        title: selectedSchemaVersionPrimary
          ? `Return Primary Schema Version ${selectedSchemaVersionLabel} to Draft and clear Primary.`
          : `Return Published Schema Version ${selectedSchemaVersionLabel} to Draft.`,
        onClick: () => onPublishSchemaVersion?.(selectedSchemaVersion, "unpublish")
      });
    }
  }

  if (isDestinationScreen) {
    actions.push({
      key: "destination-edit",
      label: "Edit",
      iconKey: "edit",
      disabled: isBusy || destinationUnavailable || destinationMetadataUnavailable,
      title: hasDestination && !destinationMetadataUnavailable
        ? `Edit this ${isEndpointDestination ? "Endpoint" : "Destination"}.`
        : `No linked ${isEndpointDestination ? "Endpoint" : "Destination"} metadata is available to edit.`,
      onClick: () => onEditDestination?.()
    });
    if (!isEndpointDestination) {
      actions.push({
        key: "destination-check",
        label: "Check Connection",
        iconKey: "settings_ethernet",
        disabled: isBusy || destinationUnavailable || !destinationDefaults?.checkOperationName,
        title: hasDestination
          ? "Check the Destination connection."
          : "No linked Destination is available to check.",
        onClick: () => onCheckDestinationConnection?.()
      });
    }
    if (isOutboundEdiConfiguration) {
      actions.push({
        key: "destination-add-deployment",
        label: "Add Deployment",
        iconKey: "add",
        disabled: isBusy || destinationUnavailable,
        title: hasDestination
          ? "Add a destination deployment."
          : "No linked Destination is available.",
        onClick: () => onAddDestinationDeployment?.()
      });
    } else {
      actions.push({
        key: "destination-deployments",
        label: "Deployments",
        iconKey: "target",
        disabled: isBusy || destinationUnavailable,
        title: hasDestination
          ? "Open Destination deployments."
          : "No linked Destination is available.",
        onClick: () => onOpenDestinationDeployments?.()
      });
    }
    actions.push({
      key: destinationActive ? "destination-disable" : "destination-enable",
      label: destinationActive ? "Disable" : "Enable",
      iconKey: destinationActive ? "undo" : "success",
      disabled: isBusy || destinationUnavailable,
      title: destinationActive
        ? "Disable this Destination for runtime use."
        : "Enable this Destination for runtime use.",
      onClick: () => onSetDestinationActive?.(!destinationActive)
    });
  }

  if (isDestinationDeploymentsScreen && !isEndpointDestination) {
    actions.push({
      key: "destination-add-deployment",
      label: "Add Deployment",
      iconKey: "add",
      disabled: isBusy || destinationUnavailable,
      title: hasDestination
        ? `Add ${isEndpointDestination ? "an endpoint" : "a destination"} deployment.`
        : `No linked ${isEndpointDestination ? "Endpoint" : "Destination"} is available.`,
      onClick: () => onAddDestinationDeployment?.()
    });
  }

  if (isDestinationDeploymentScreen) {
    actions.push({
      key: "destination-deployment-edit",
      label: selectedEndpointDeploymentConfigLocked ? "View" : "Edit",
      iconKey: selectedEndpointDeploymentConfigLocked ? "view" : "edit",
      disabled: isBusy || destinationDeploymentUnavailable || destinationDeploymentMetadataUnavailable,
      title: selectedDestinationDeployment && !destinationDeploymentMetadataUnavailable
        ? selectedEndpointDeploymentConfigLocked ? "View this online deployment." : "Edit this deployment."
        : "No deployment metadata is available to edit.",
      onClick: () => onEditDestinationDeployment?.(selectedDestinationDeployment)
    });
    if (isWarehouseDestination) {
      actions.push({
        key: "warehouse-deployment-deploy",
        label: "Deploy",
        iconKey: "publish",
        disabled: isBusy || destinationDeploymentUnavailable,
        title: selectedDestinationDeployment ? "Deploy physical warehouse objects for this deployment." : "No deployment is selected.",
        onClick: () => onDeployWarehouseDeployment?.(selectedDestinationDeployment)
      });
    }
    if (isEndpointDestination) {
      actions.push({
        key: selectedEndpointDeploymentOnline ? "endpoint-deployment-undeploy" : "endpoint-deployment-deploy",
        label: selectedEndpointDeploymentOnline ? "Undeploy" : "Deploy",
        iconKey: selectedEndpointDeploymentOnline ? "undo" : "play",
        disabled: isBusy
          || destinationDeploymentUnavailable
          || !destinationActive
          || !isRecordActive(selectedDestinationDeployment)
          || !(selectedEndpointDeploymentOnline ? destinationDefaults?.dropOperationName : destinationDefaults?.deployOperationName),
        title: selectedDestinationDeployment
          ? selectedEndpointDeploymentOnline
            ? "Undeploy this endpoint deployment so routes are no longer available."
            : "Validate and deploy this endpoint deployment."
          : "No deployment is selected.",
        onClick: () => selectedEndpointDeploymentOnline
          ? onUndeployDestinationDeployment?.(selectedDestinationDeployment)
          : onDeployDestinationDeployment?.(selectedDestinationDeployment)
      });
    }
    if (!isEndpointDestination) {
      actions.push({
        key: "destination-deployment-manual-run",
        label: "Manual Run",
        iconKey: "play",
        disabled: isBusy || destinationDeploymentManualRunUnavailable,
        title: selectedDestinationDeployment
          ? "Run this deployment once with manual options."
          : "No deployment is selected.",
        onClick: () => onManualRunDestinationDeployment?.(selectedDestinationDeployment)
      });
    }
    if (isWarehouseDestination) {
      actions.push({
        key: "warehouse-deployment-clone",
        label: "Clone",
        iconKey: "contenttab",
        disabled: isBusy || destinationDeploymentUnavailable,
        title: selectedDestinationDeployment ? "Clone this warehouse deployment." : "No deployment is selected.",
        onClick: () => onCloneWarehouseDeployment?.(selectedDestinationDeployment)
      });
      actions.push({
        key: "warehouse-deployment-views",
        label: "Views",
        iconKey: "view",
        disabled: isBusy || destinationDeploymentUnavailable,
        title: selectedDestinationDeployment ? "Open warehouse views for this deployment." : "No deployment is selected.",
        onClick: () => onOpenDestinationDeploymentViews?.(selectedDestinationDeployment)
      });
    }
    actions.push({
      key: "destination-deployment-scope",
      label: isEndpointDestination ? "Endpoint Scope" : "Entity Scope",
      iconKey: "target",
      disabled: isBusy || destinationDeploymentUnavailable,
      title: selectedDestinationDeployment
        ? isEndpointDestination ? "Open endpoint scope filtered to this deployment." : "Open Entity Scope filtered to this deployment."
        : "No deployment is selected.",
      onClick: () => onOpenDestinationDeploymentScope?.(selectedDestinationDeployment)
    });
    if (!isEndpointDestination) {
      actions.push({
        key: "destination-deployment-schedule",
        label: "Schedule",
        iconKey: "clock",
        disabled: isBusy || destinationDeploymentUnavailable,
        title: selectedDestinationDeployment ? "Open Schedule filtered to this deployment." : "No deployment is selected.",
        onClick: () => onOpenDestinationDeploymentSchedule?.(selectedDestinationDeployment)
      });
    }
    actions.push({
      key: selectedDestinationDeploymentActive ? "destination-deployment-disable" : "destination-deployment-activate",
      label: selectedDestinationDeploymentActive ? "Disable" : "Enable",
      iconKey: selectedDestinationDeploymentActive ? "undo" : "success",
      disabled: isBusy || destinationDeploymentUnavailable,
      title: selectedDestinationDeploymentActive
        ? "Disable this deployment for runtime use."
        : "Enable this deployment for runtime use.",
      onClick: () => setDeploymentActiveConfirmRequest({
        open: true,
        isActive: !selectedDestinationDeploymentActive,
        deployment: selectedDestinationDeployment
      })
    });
    if (isWarehouseDestination) {
      actions.push({
        key: "warehouse-deployment-drop",
        label: "Drop",
        iconKey: "delete",
        danger: true,
        disabled: isBusy || destinationDeploymentUnavailable || selectedDestinationDeploymentActive,
        title: selectedDestinationDeployment
          ? selectedDestinationDeploymentActive
            ? "Disable this deployment before dropping physical warehouse objects."
            : "Drop physical warehouse objects for this deployment."
          : "No deployment is selected.",
        onClick: () => onDropWarehouseDeployment?.(selectedDestinationDeployment)
      });
    }
    actions.push({
      key: "destination-deployment-delete",
      label: "Delete",
      iconKey: "delete",
      danger: true,
      disabled: isBusy || destinationDeploymentUnavailable || destinationDeployments.length <= 1 || selectedEndpointDeploymentConfigLocked,
      title: selectedEndpointDeploymentConfigLocked
        ? "Endpoint deployments are view-only while online."
        : destinationDeployments.length <= 1
        ? "At least one deployment is required for this Destination."
        : "Delete this deployment.",
      onClick: () => onDeleteDestinationDeployment?.(selectedDestinationDeployment)
    });
  }

  if (isDestinationEntityDeploymentsScreen) {
    if (isEndpointDestination) {
      actions.push({
        key: "endpoint-add-entity-endpoint",
        label: "Add Entity Endpoint",
        iconKey: "add",
        disabled: isBusy || destinationUnavailable || endpointScopeConfigLocked,
        title: endpointScopeConfigLocked
          ? "Endpoint scope settings are view-only while online."
          : hasDestination ? "Add an endpoint entity scope row." : "No linked Endpoint is available.",
        onClick: () => onOpenEndpointEntityEndpointPanel?.(destinationEntityDeploymentRouteFilters?.deploymentId)
      });
    }
  }

  if (isDestinationScheduleScreen) {
    (destinationScheduleActionState?.actions || []).forEach((action) => {
      actions.push({
        ...action,
        disabled: Boolean(action.disabled)
      });
    });
  }

  if (isDestinationWarehouseViewsScreen) {
    (warehouseViewActionState?.actions || []).forEach((action) => {
      actions.push({
        ...action,
        disabled: Boolean(action.disabled)
      });
    });
  }

  if (isDestinationEntityDeploymentsScreen && !isEndpointDestination) {
    (destinationEntityScopeActionState?.actions || []).forEach((action) => {
      actions.push({
        ...action,
        disabled: Boolean(action.disabled)
      });
    });
  }

  if (isSourceSystemScreen && sourceObjectManagerSourceObjectId) {
    actions.push({
      key: "source-object-manager-back",
      label: "Back to Source Objects",
      iconKey: "undo",
      alignRight: true,
      disabled: isBusy,
      title: "Return to the Source Objects list.",
      onClick: () => onCloseSourceObjectManager?.()
    });
  }

  if (isDestinationWarehouseViewsScreen && onBackToDeployment) {
    actions.push({
      key: "destination-warehouse-views-back-deployment",
      label: "Back to Deployment",
      iconKey: "undo",
      alignRight: true,
      disabled: isBusy,
      title: selectedDestinationDeployment ? "Return to the selected deployment." : "No deployment is selected.",
      onClick: () => runConfigurationGuardedAction(() => onBackToDeployment?.(), "return to the selected deployment")
    });
  } else if (isDestinationEntityDeploymentsScreen && onOpenDestinationDeployments && !isOutboundEdiConfiguration) {
    actions.push({
      key: "destination-entity-deployments-back-deployments",
      label: "Back to Deployments",
      iconKey: "undo",
      alignRight: true,
      disabled: isBusy,
      title: "Return to the Deployments screen.",
      onClick: () => runConfigurationGuardedAction(() => onOpenDestinationDeployments?.(), "return to the Deployments screen")
    });
  } else if (isDestinationDeploymentScreen && onOpenDestinationDeployments && !isOutboundEdiConfiguration) {
    actions.push({
      key: "destination-deployment-back-deployments",
      label: "Back to Deployments",
      iconKey: "undo",
      alignRight: true,
      disabled: isBusy,
      title: "Return to the Deployments screen.",
      onClick: () => onOpenDestinationDeployments?.()
    });
  } else if ((isDestinationDeploymentScreen || isDestinationEntityDeploymentsScreen || isDestinationScheduleScreen) && onBackToDestination) {
    actions.push({
      key: "destination-child-back-destination",
      label: "Back to Destination",
      iconKey: "undo",
      alignRight: true,
      disabled: isBusy,
      title: "Return to the Destination screen.",
      onClick: () => onBackToDestination?.()
    });
  } else if ((isIntegrationScreen || (isSourceSystemScreen && !isSourceObjectManagerScreen) || isDestinationScreen) && onBackToManage) {
    actions.push({
      key: "configuration-linked-back-manage",
      label: "Back to Manage",
      iconKey: "undo",
      alignRight: true,
      disabled: isBusy,
      title: "Return to the configuration Manage screen.",
      onClick: () => onBackToManage?.()
    });
  }

  if (isManageScreen && onBackToConfigurations) {
    actions.push({
      key: "configuration-manage-back",
      label: "Back to Configurations",
      iconKey: "undo",
      alignRight: true,
      disabled: isBusy,
      title: "Return to the configurations list.",
      onClick: () => onBackToConfigurations?.()
    });
  }

  if (isManageScreen && canContinueConfigurationSetup(configuration)) {
    actions.push({
      key: "setup",
      label: "Continue setup",
      iconKey: "settings",
      disabled: isBusy || !canOpenConfigurationSetup,
      title: canOpenConfigurationSetup ? `Continue setup for ${configurationName}` : "Setup requires an Active and Approved Client Resource.",
      onClick: () => onOpenConfigurationSetup?.(configuration)
    });
  }

  if (isManageScreen && isEdiConfiguration && isConfigurationComplete(configuration)) {
    actions.push({
      key: "validate",
      label: "Validate",
      iconKey: "refresh",
      disabled: isBusy,
      title: `Re-run readiness validation for ${configurationName}.`,
      onClick: () => onValidateConfiguration?.(configuration)
    });
  }

  if (isManageScreen && (isConfigurationConfigured(configuration) || isConfigurationReady(configuration) || isConfigurationDisabled(configuration))) {
    actions.push({
      key: "activate",
      label: "Activate",
      iconKey: "success",
      disabled: isBusy,
      title: `Activate ${configurationName} for runtime use.`,
      onClick: () => onActivateConfiguration?.(configuration)
    });
  }

  if (isManageScreen && isConfigurationActive(configuration)) {
    actions.push({
      key: "deactivate",
      label: "Deactivate",
      iconKey: "undo",
      danger: true,
      disabled: isBusy,
      title: `Deactivate ${configurationName} and make all of its runtime features unavailable.`,
      onClick: () => onDisableConfiguration?.(configuration)
    });
  }

  if (isManageScreen && (isConfigurationReady(configuration) || isConfigurationDisabled(configuration))) {
    actions.push({
      key: "reconfigure",
      label: "Reconfigure",
      iconKey: "settings",
      disabled: isBusy,
      title: `Return ${configurationName} to configured state for management changes.`,
      onClick: () => onReconfigureConfiguration?.(configuration)
    });
  }

  if (isManageScreen && !isSystemManagedClientMcpServer) {
    actions.push({
      key: "retire",
      label: "Retire",
      iconKey: "delete",
      danger: true,
      disabled: isBusy || isConfigurationActive(configuration),
      title: isConfigurationActive(configuration)
        ? "Deactivate this configuration before retiring it."
        : `Retire ${configurationName}.`,
      onClick: () => onDeleteConfiguration?.(configuration)
    });
  }
  return (
    <>
      <ConvokeCommandBar
        actions={actions}
        ariaLabel="Configuration actions"
        className="resource-workspace-configuration-command-bar"
        actionClassName="resource-workspace-command-bar-action"
        actionRightClassName="resource-workspace-command-bar-action--right"
        actionDangerClassName="resource-workspace-command-bar-action--danger"
        actionSeparatorAfterClassName="resource-workspace-command-bar-action--separator-after"
        overflowWrapperClassName="resource-workspace-command-bar-overflow"
        measureClassName="resource-workspace-command-bar-measure"
        menuClassName="resource-workspace-command-bar-menu"
        menuActionDangerClassName="resource-workspace-command-bar-menu-action--danger"
        loadingClassName="resource-workspace-command-bar-action__loading"
      />
      <DestinationDeploymentActiveConfirmDialog
        request={deploymentActiveConfirmRequest}
        onCancel={closeDeploymentActiveConfirmDialog}
        onConfirm={confirmDeploymentActiveChange}
      />
    </>
  );
};

const ResourceConfigurationsManageScreen = ({
  configurations,
  resourceLogs,
  resourceEvents,
  selectedConfiguration,
  prompt,
  resource,
  productIcon,
  canOpenConfigurationSetup,
  canBuyResourceAllowance,
  hasRemainingConfigurationAllowance,
  hasRemainingLinkedEndpointAllowance,
  onOpenConfigurationSetup,
  onAddResourceConfiguration,
  onAddLinkedEndpoint,
  onBuyResourceAllowance,
  importSupport,
  onImportResourceConfiguration,
  onSelectConfigurationScreen,
  onDeleteConfiguration,
  onRefreshConfigurations,
  logManager,
  eventManager
}) => {
  const isSystemManagedClientMcpServer = isClientMcpServerResource(resource);
  const isSingleInstance = isSingleInstanceProductResource(resource);
  const hasSingleInstance = isSingleInstance && configurations.some((configuration) => !isConfigurationRetired(configuration));
  if (selectedConfiguration) {
    return (
      <SelectedResourceConfigurationManageScreen
        configuration={selectedConfiguration}
        resource={resource}
        resourceLogs={resourceLogs}
        resourceEvents={resourceEvents}
        productIcon={productIcon}
        canOpenConfigurationSetup={canOpenConfigurationSetup}
        hasRemainingLinkedEndpointAllowance={hasRemainingLinkedEndpointAllowance}
        onAddLinkedEndpoint={onAddLinkedEndpoint}
        onSelectConfigurationScreen={onSelectConfigurationScreen}
        onRefreshConfigurations={onRefreshConfigurations}
        logManager={logManager}
        eventManager={eventManager}
      />
    );
  }

  return (
    <section
      className="resource-workspace-panel resource-workspace-configuration-selection"
      aria-label="Resource configurations"
    >
      <div className="resource-workspace-section-title resource-workspace-section-title--with-action">
        <div>
          <h2>Manage Configurations</h2>
          <p>Select a configuration to open setup, manage its details, or choose a configuration screen.</p>
        </div>
        <div className="resource-workspace-section-actions">
          {isSystemManagedClientMcpServer ? null : hasRemainingConfigurationAllowance ? (
            <ButtonControl
              type="icon"
              className="resource-workspace-configuration-shell-action-button"
              disabled={!canOpenConfigurationSetup}
              onClick={onAddResourceConfiguration}
              title="Add configuration"
              aria-label="Add configuration"
            >
              <IconControl iconKey="create" title="Add configuration" width="1rem" height="1rem" />
            </ButtonControl>
          ) : hasSingleInstance ? (
            <span className="resource-workspace-muted">Single instance</span>
          ) : (
            <ButtonControl
              type="icon"
              className="resource-workspace-configuration-shell-action-button"
              disabled={!canBuyResourceAllowance}
              onClick={onBuyResourceAllowance}
              title="Buy allowance"
              aria-label="Buy allowance"
            >
              <IconControl iconKey="storefront" title="Buy allowance" width="1rem" height="1rem" />
            </ButtonControl>
          )}
          {importSupport ? (
            <ButtonControl
              type="icon"
              className="resource-workspace-configuration-shell-action-button"
              disabled={!canOpenConfigurationSetup}
              onClick={onImportResourceConfiguration}
              title="Import configuration"
              aria-label="Import configuration"
            >
              <IconControl iconKey="import" title="Import configuration" width="1rem" height="1rem" />
            </ButtonControl>
          ) : null}
        </div>
      </div>
      {prompt ? <div className="resource-workspace-access-note" role="status">{prompt}</div> : null}
      {configurations.length > 0 ? (
        <ConfigurationSelectionRows
          configurations={configurations}
          resource={resource}
          productIcon={productIcon}
          canOpenConfigurationSetup={canOpenConfigurationSetup}
          onOpenConfigurationSetup={onOpenConfigurationSetup}
          onSelectConfigurationScreen={onSelectConfigurationScreen}
          onDeleteConfiguration={onDeleteConfiguration}
        />
      ) : (
        <ResourceStateControl
          compact
          headingLevel={1}
          title="No configurations"
          message="Add a configuration before opening resource-specific screens."
        />
      )}
    </section>
  );
};

const ResourceConfigurationIntegrationScreen = ({
  configuration,
  integrationRecord,
  integrationState,
  logManager,
  recentLogsRefreshKey,
  resourceLogs
}) => {
  const linkedIntegrationId = getLinkedRecordItemId(integrationRecord);
  const stateLinkedIntegrationId = getString(integrationState, "linkedRecordId", "LinkedRecordId");
  const isResolvingLinkedIntegration = Boolean(linkedIntegrationId)
    && normalizeCompare(stateLinkedIntegrationId) !== normalizeCompare(linkedIntegrationId);
  const integration = integrationState?.integration;
  const metadata = integrationState?.metadata;
  const integrationName = getDisplayName(integration, getLinkedRecordDisplayName(integrationRecord) || "Integration");
  const integrationDescription = getString(integration, "Description", "description");
  const integrationActive = getBoolean(integration, "IsActive", "isActive");
  const dbName = getString(integration, "DBName", "dbName", "DatabaseName", "databaseName", "ApiName", "apiName", "RootFolder", "rootFolder");
  const logLevels = getIntegrationLogLevelLabels(integration, metadata);
  const connectionDetailRows = getIntegrationConnectionDetailRows(integration, metadata);
  const connectionStringClipboardValue = buildIntegrationConnectionStringClipboardValue(connectionDetailRows);
  const [connectionCopyState, setConnectionCopyState] = useState("idle");
  const copyConnectionString = useCallback(async () => {
    if (!connectionStringClipboardValue) return;
    try {
      await copyTextToClipboard(connectionStringClipboardValue);
      setConnectionCopyState("copied");
      window.setTimeout(() => setConnectionCopyState("idle"), 1600);
    } catch {
      setConnectionCopyState("error");
      window.setTimeout(() => setConnectionCopyState("idle"), 2200);
    }
  }, [connectionStringClipboardValue]);

  if (integrationState?.loading || isResolvingLinkedIntegration) {
    return (
      <ResourceStateControl
        loading
        title="Loading Integration"
        loadingText="Loading linked Integration details."
      />
    );
  }

  if (!linkedIntegrationId) {
    return (
      <ResourceStateControl
        tone="warning"
        title="No Integration linked"
        message="Continue setup to select or create the Integration for this configuration."
      />
    );
  }

  if (integrationState?.error || !integration) {
    return (
      <ResourceStateControl
        tone="error"
        title="Integration unavailable"
        message={integrationState?.error || "The linked Integration could not be loaded."}
      />
    );
  }

  return (
    <div className="resource-workspace-linked-item-layout resource-workspace-linked-item-layout--summary-sidebar" aria-label={`${integrationName} Integration details`}>
      <section className="resource-workspace-panel resource-workspace-linked-item-section">
        <div className="resource-workspace-section-title">
          <h2>Information</h2>
          <p>View-only Integration settings used by this configuration.</p>
        </div>
        <div className="resource-workspace-linked-item-summary">
          <dl className="resource-workspace-facts resource-workspace-linked-item-metrics">
            <Fact label="Display Name" value={integrationName} />
            <Fact label="Runtime Status" value={getIntegrationRuntimeStatus(integration)} />
            <Fact label="Integration Type" value={getIntegrationTypeLabel(integration, metadata)} />
            <Fact label="Active" value={integrationActive ? "Active" : "Inactive"} />
          </dl>
        </div>
        <div className="resource-workspace-linked-item-detail-row">
          <dl className="resource-workspace-facts resource-workspace-linked-item-facts">
            {dbName ? <Fact label="Database / API / Root Folder" value={dbName} /> : null}
            {logLevels ? <Fact label="Log Levels" value={logLevels} /> : null}
          </dl>
          <div className="resource-workspace-configuration-notes resource-workspace-linked-item-description">
            <span>Description</span>
            <p>{integrationDescription || "No Integration description has been added."}</p>
          </div>
        </div>
        <div className="resource-workspace-configuration-connection-details">
          <div className="resource-workspace-configuration-connection-header">
            <span>Connection Fields</span>
            <button
              type="button"
              className="resource-workspace-configuration-copy-action"
              disabled={!connectionStringClipboardValue}
              title={connectionCopyState === "copied" ? "Connection string copied." : "Copy connection string with password placeholder."}
              aria-label={connectionCopyState === "copied" ? "Connection string copied" : "Copy connection string"}
              onClick={copyConnectionString}
            >
              <IconControl
                iconKey={connectionCopyState === "copied" ? "success" : connectionCopyState === "error" ? "warning" : "contenttab"}
                width="1rem"
                height="1rem"
              />
            </button>
          </div>
          {connectionDetailRows.length > 0 ? (
            <dl className="resource-workspace-configuration-connection-grid">
              {connectionDetailRows.map((row) => (
                <div key={`${row.label}-${row.value}`}>
                  <dt>{row.label}</dt>
                  <dd>{row.value}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p>No connection field details are available.</p>
          )}
        </div>
      </section>

      <section className="resource-workspace-panel resource-workspace-linked-item-section">
        <div className="resource-workspace-section-title">
          <h2>Recent Logs</h2>
          <p>Latest Integration activity and connection checks for this configuration.</p>
        </div>
        <RecentLogsControl
          logManager={logManager}
          logReferenceId={linkedIntegrationId}
          take={5}
          refreshKey={recentLogsRefreshKey}
          emptyMessage="No recent Integration logs yet."
        />
      </section>
    </div>
  );
};

const ResourceConfigurationSourceSystemScreen = ({
  configuration,
  sourceSystemRecord,
  sourceSystemState,
  initialCatalogTab,
  targetFilterDefaults,
  logManager,
  recentLogsRefreshKey,
  onEditSourceObjectTarget,
  onAddQueryTarget,
  onCloneQueryTarget,
  onSetSourceObjectTargetActive,
  onEditSourceObject,
  sourceObjectOperations,
  onRunSourceObjectOperation
}) => {
  const linkedSourceSystemId = getLinkedRecordItemId(sourceSystemRecord);
  const stateLinkedSourceSystemId = getString(sourceSystemState, "linkedRecordId", "LinkedRecordId");
  const isResolvingLinkedSourceSystem = Boolean(linkedSourceSystemId)
    && normalizeCompare(stateLinkedSourceSystemId) !== normalizeCompare(linkedSourceSystemId);
  const sourceSystem = sourceSystemState?.sourceSystem;
  const metadata = sourceSystemState?.metadata;
  const sourceSystemName = getDisplayName(sourceSystem, getLinkedRecordDisplayName(sourceSystemRecord) || "Source System");
  const sourceSystemDescription = getString(sourceSystem, "Description", "description");
  const sourceSystemActive = isRecordActive(sourceSystem);
  const sourceObjectStats = getSourceObjectStats(sourceSystemState?.sourceObjects || []);
  const systemName = getString(sourceSystem, "SystemName", "systemName");
  const timezone = getString(sourceSystem, "TimeZone", "timeZone");
  const sourceSystemUpdatedDate = formatDateTime(getValue(sourceSystem, "UpdatedDate", "updatedDate", "ModifiedDate", "modifiedDate", "LastUpdatedDate", "lastUpdatedDate"));

  if (sourceSystemState?.loading || isResolvingLinkedSourceSystem) {
    return (
      <ResourceStateControl
        loading
        title="Loading Source System"
        loadingText="Loading linked Source System details."
      />
    );
  }

  if (!linkedSourceSystemId) {
    return (
      <ResourceStateControl
        tone="warning"
        title="No Source System linked"
        message="Continue setup to create the Source System for this configuration."
      />
    );
  }

  if (sourceSystemState?.error || !sourceSystem) {
    return (
      <ResourceStateControl
        tone="error"
        title="Source System unavailable"
        message={sourceSystemState?.error || "The linked Source System could not be loaded."}
      />
    );
  }

  return (
    <div className="resource-workspace-linked-item-layout resource-workspace-linked-item-layout--catalog" aria-label={`${sourceSystemName} Source System details`}>
      <div className="resource-workspace-linked-item-main">
        <section className="resource-workspace-panel resource-workspace-linked-item-section">
          <div className="resource-workspace-section-title">
            <h2>Information</h2>
            <p>View-only Source System settings used by this configuration.</p>
          </div>
          <dl className="resource-workspace-facts resource-workspace-linked-item-metrics">
            <Fact label="Display Name" value={sourceSystemName} />
            <Fact label="Runtime Status" value={sourceSystemActive ? "Active" : "Inactive"} />
            <Fact label="Source System Type" value={getSourceSystemTypeLabel(sourceSystem, metadata)} />
            <Fact label="Source Objects" value={`${sourceObjectStats.active.toLocaleString()} active / ${sourceObjectStats.total.toLocaleString()} total`} />
          </dl>
          <div className="resource-workspace-linked-item-detail-row">
            <dl className="resource-workspace-facts resource-workspace-linked-item-facts">
              {systemName ? <Fact label="System Name" value={systemName} /> : null}
              {timezone ? <Fact label="Time Zone" value={timezone} /> : null}
            </dl>
            <div className="resource-workspace-configuration-notes resource-workspace-linked-item-description">
              <span>Description</span>
              <p>{sourceSystemDescription || "No Source System description has been added."}</p>
              {sourceSystemUpdatedDate ? (
                <>
                  <span>Updated Date</span>
                  <p>{sourceSystemUpdatedDate}</p>
                </>
              ) : null}
            </div>
          </div>
        </section>

        <section className="resource-workspace-panel resource-workspace-linked-item-section">
          <div className="resource-workspace-section-title">
            <h2>Recent Logs</h2>
            <p>Latest Source System activity for this configuration.</p>
          </div>
          <RecentLogsControl
            logManager={logManager}
            logReferenceId={linkedSourceSystemId}
            take={5}
            refreshKey={recentLogsRefreshKey}
            emptyMessage="No recent Source System logs yet."
          />
        </section>
      </div>

      <SourceSystemCatalogTabsPanel
        sourceSystem={sourceSystem}
        sourceObjects={sourceSystemState?.sourceObjects || []}
        initialActiveTab={initialCatalogTab}
        targetFilterDefaults={targetFilterDefaults}
        sourceObjectOperations={sourceObjectOperations}
        onEditSourceObjectTarget={onEditSourceObjectTarget}
        onAddQueryTarget={onAddQueryTarget}
        onCloneQueryTarget={onCloneQueryTarget}
        onSetSourceObjectTargetActive={onSetSourceObjectTargetActive}
        onEditSourceObject={onEditSourceObject}
        onRunSourceObjectOperation={onRunSourceObjectOperation}
      />
    </div>
  );
};

const SourceSystemCatalogTabsPanel = ({
  sourceSystem,
  sourceObjects,
  initialActiveTab = SOURCE_SYSTEM_CATALOG_TAB_SOURCE_OBJECTS,
  targetFilterDefaults,
  sourceObjectOperations,
  onEditSourceObjectTarget,
  onAddQueryTarget,
  onCloneQueryTarget,
  onSetSourceObjectTargetActive,
  onEditSourceObject,
  onRunSourceObjectOperation
}) => {
  const [activeTab, setActiveTab] = useState(initialActiveTab || SOURCE_SYSTEM_CATALOG_TAB_SOURCE_OBJECTS);
  const targets = useMemo(
    () => filterSourceTargetsForProductDefaults(getSourceSystemTargets(sourceSystem), targetFilterDefaults),
    [sourceSystem, targetFilterDefaults]
  );
  const targetStats = getSourceSystemTargetStats(targets);
  const tabs = [
    {
      key: SOURCE_SYSTEM_CATALOG_TAB_SOURCE_OBJECTS,
      title: "Source Objects",
      content: (
        <SourceObjectListPanel
          sourceSystem={sourceSystem}
          sourceObjects={sourceObjects}
          sourceObjectOperations={sourceObjectOperations}
          onEditSourceObject={onEditSourceObject}
          onRunSourceObjectOperation={onRunSourceObjectOperation}
        />
      )
    },
    {
      key: SOURCE_SYSTEM_CATALOG_TAB_TARGETS,
      title: "Targets",
      content: (
        <SourceTargetListPanel
          targets={targets}
          canAddQueryTarget={isSqlQueryTargetSourceSystem(sourceSystem)}
          onAddQueryTarget={onAddQueryTarget}
          onCloneQueryTarget={onCloneQueryTarget}
          onEditSourceObjectTarget={onEditSourceObjectTarget}
          onSetSourceObjectTargetActive={onSetSourceObjectTargetActive}
        />
      )
    }
  ];
  const sourceObjectStats = getSourceObjectStats(sourceObjects || []);
  const catalogHeader = activeTab === SOURCE_SYSTEM_CATALOG_TAB_TARGETS
    ? {
      title: "Targets",
      summary: `${targetStats.active.toLocaleString()} active / ${targetStats.total.toLocaleString()} total`
    }
    : {
      title: "Source Objects",
      summary: `${sourceObjectStats.active.toLocaleString()} active / ${sourceObjectStats.total.toLocaleString()} total`
    };

  const handleTabChange = (nextKey) => {
    setActiveTab(nextKey || SOURCE_SYSTEM_CATALOG_TAB_SOURCE_OBJECTS);
  };

  useEffect(() => {
    setActiveTab(initialActiveTab || SOURCE_SYSTEM_CATALOG_TAB_SOURCE_OBJECTS);
  }, [initialActiveTab]);

  return (
    <ResourceCatalogPanel
      title={catalogHeader.title}
      summary={catalogHeader.summary}
      tabs={tabs}
      activeTabKey={activeTab}
      onTabChange={handleTabChange}
      idPrefix="resource-source-system-catalog-tabs"
      ariaLabel="Source System catalog"
    />
  );
};

const getSourceTargetUpdatedLabel = (target) => {
  const updatedDate = formatDateTime(getValue(target, "UpdatedDate", "updatedDate", "ModifiedDate", "modifiedDate", "LastUpdatedDate", "lastUpdatedDate"));
  return updatedDate ? `Last updated ${updatedDate}` : "";
};

const SourceTargetListPanel = ({ targets, canAddQueryTarget, onAddQueryTarget, onCloneQueryTarget, onEditSourceObjectTarget, onSetSourceObjectTargetActive }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const targetRows = useMemo(
    () => getArray({ targets }, "targets").filter((target) => !isRecordDeleted(target)),
    [targets]
  );

  return (
    <>
      {canAddQueryTarget ? (
        <div className="resource-catalog-list-tab__actions">
          <ButtonControl
            type="standard-small"
            className="resource-catalog-list-tab__action-button"
            aria-label="Add Query Target"
            onClick={() => onAddQueryTarget?.()}
          >
            <IconControl iconKey="add" title="Add Query Target" width="1rem" height="1rem" />
            <span>Add Query Target</span>
          </ButtonControl>
        </div>
      ) : null}
      <ResourceCatalogListTab
        items={targetRows}
        getItemKey={getSourceTargetIdentityKey}
        ariaLabel="Source targets"
        emptyMessage="No Source System targets have been loaded yet."
        emptyFilteredMessage="No Source System targets match these filters."
        search={{
          controlType: "field",
          fieldMetadata: buildFilterTextBoxField("SourceTargetSearch", "Search targets...", "Search source targets"),
          value: searchTerm,
          onChange: (nextValue) => setSearchTerm(nextValue || ""),
          matchesItem: (target, value) => {
            const normalizedSearch = normalizeCompare(value);
            return !normalizedSearch || buildSourceTargetSearchText(target).includes(normalizedSearch);
          }
        }}
        statusFilter={{
          fieldMetadata: buildSelectListField("SourceTargetStatus", "", SOURCE_CATALOG_STATUS_OPTIONS, "Please Select", "Filter source targets"),
          value: statusFilter,
          onChange: (nextValue) => setStatusFilter(nextValue || "active"),
          matchesItem: (target, value) => matchesSourceCatalogStatusFilter(target, value)
        }}
        renderItem={(target) => {
          const targetName = getDisplayName(target, getString(target, "ObjectName", "objectName") || "Source target");
          const updatedLabel = getSourceTargetUpdatedLabel(target);
          const targetActive = isRecordActive(target);
          const statusActionLabel = targetActive
            ? `Deactivate target ${targetName}`
            : `Activate target ${targetName}`;
          const canCloneTarget = canAddQueryTarget && isSourceObjectTypeQuery(target) && !isCustomQueryTarget(target);

          return (
            <ResourceCatalogRow
              title={targetName}
              subtitle={getString(target, "ObjectName", "objectName") || "No object name"}
              titleAttribute={targetName}
              metaItems={[
                {
                  key: "status",
                  className: "resource-catalog-row__meta-item--status",
                  content: isRecordActive(target) ? "Active" : "Inactive"
                },
                updatedLabel ? {
                  key: "updated",
                  className: "resource-catalog-row__meta-item--detail",
                  content: updatedLabel
                } : null
              ]}
              actions={(
                <>
                  <ButtonControl
                    type="standard-small"
                    title={statusActionLabel}
                    aria-label={statusActionLabel}
                    onClick={() => onSetSourceObjectTargetActive?.(target, !targetActive)}
                  >
                    <IconControl iconKey={targetActive ? "undo" : "success"} title={statusActionLabel} width="0.95rem" height="0.95rem" />
                  </ButtonControl>
                  {canCloneTarget ? (
                    <ButtonControl
                      type="standard-small"
                      title={`Clone query target ${targetName}`}
                      aria-label={`Clone query target ${targetName}`}
                      onClick={() => onCloneQueryTarget?.(target)}
                    >
                      <IconControl iconKey="clone" title={`Clone query target ${targetName}`} width="0.95rem" height="0.95rem" />
                    </ButtonControl>
                  ) : null}
                  <ButtonControl
                    type="standard-small"
                    title={`Edit target ${targetName}`}
                    aria-label={`Edit target ${targetName}`}
                    onClick={() => onEditSourceObjectTarget?.(target)}
                  >
                    <IconControl iconKey="edit" title={`Edit target ${targetName}`} width="0.95rem" height="0.95rem" />
                  </ButtonControl>
                </>
              )}
            />
          );
        }}
      />
    </>
  );
};

const SourceObjectListPanel = ({
  sourceSystem,
  sourceObjects,
  sourceObjectOperations,
  onEditSourceObject,
  onRunSourceObjectOperation
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [openOperationMenu, setOpenOperationMenu] = useState("");
  const [operationMenuStyle, setOperationMenuStyle] = useState(null);
  const targets = useMemo(() => getSourceSystemTargets(sourceSystem), [sourceSystem]);
  const sourceObjectRows = useMemo(
    () => getUniqueRowsByKey(sourceObjects, getSourceObjectIdentityKey)
      .filter((sourceObject) => !isRecordDeleted(sourceObject)),
    [sourceObjects]
  );
  const openOperations = useCallback((event, sourceObjectKey) => {
    const buttonBox = event.currentTarget?.getBoundingClientRect?.();
    setOpenOperationMenu((current) => current === sourceObjectKey ? "" : sourceObjectKey);
    setOperationMenuStyle({
      left: Math.max(8, (buttonBox?.right ?? 0) - 190),
      top: (buttonBox?.bottom ?? 0) + 6
    });
  }, []);

  const runOperation = useCallback((sourceObject, operation) => {
    setOpenOperationMenu("");
    onRunSourceObjectOperation?.(sourceObject, operation);
  }, [onRunSourceObjectOperation]);

  return (
    <ResourceCatalogListTab
      items={sourceObjectRows}
      getItemKey={getSourceObjectIdentityKey}
      ariaLabel="Created source objects"
      emptyMessage="No Source Objects have been created yet."
      emptyFilteredMessage="No Source Objects match the current filters."
      search={{
        controlType: "field",
        fieldMetadata: buildFilterTextBoxField("SourceObjectSearch", "Search objects...", "Search source objects"),
        value: searchTerm,
        onChange: (nextValue) => setSearchTerm(nextValue || ""),
        matchesItem: (sourceObject, value) => {
          const normalizedSearch = normalizeCompare(value);
          if (!normalizedSearch) return true;
          const target = findSourceObjectTarget(targets, sourceObject);
          return buildSourceObjectSearchText(sourceObject, target).includes(normalizedSearch);
        }
      }}
      statusFilter={{
        fieldMetadata: buildSelectListField("SourceObjectStatus", "", SOURCE_CATALOG_STATUS_OPTIONS, "Please Select", "Filter source objects"),
        value: statusFilter,
        onChange: (nextValue) => setStatusFilter(nextValue || "active"),
        matchesItem: (sourceObject, value) => matchesSourceCatalogStatusFilter(sourceObject, value)
      }}
      renderItem={(sourceObject, index) => {
        const sourceObjectKey = getSourceObjectIdentityKey(sourceObject, index);
        const sourceObjectName = getDisplayName(sourceObject, getString(sourceObject, "SystemName", "systemName", "InternalName", "internalName") || "Source Object");
        const operationMenuOpen = openOperationMenu === sourceObjectKey;

        return (
          <ResourceCatalogRow
            title={sourceObjectName}
            subtitle={`Version ${getSourceObjectVersionLabel(sourceObject)}`}
            titleAttribute={sourceObjectName}
            metaItems={[
              {
                key: "status",
                className: "resource-catalog-row__meta-item--status",
                content: isRecordActive(sourceObject) ? "Active" : "Inactive"
              },
              {
                key: "fields",
                className: "resource-catalog-row__meta-item--detail",
                content: `${getSourceObjectFieldCount(sourceObject).toLocaleString()} fields`
              }
            ]}
            actions={(
              <>
                <ButtonControl
                  type="standard-small"
                  title={`Edit source object ${sourceObjectName}`}
                  aria-label={`Edit source object ${sourceObjectName}`}
                  onClick={() => onEditSourceObject?.(sourceObject)}
                >
                  <IconControl iconKey="settings" title={`Edit source object ${sourceObjectName}`} width="0.95rem" height="0.95rem" />
                </ButtonControl>
                {sourceObjectOperations?.length > 0 ? (
                  <span className="resource-catalog-row__operation-menu">
                    <ButtonControl
                      type="standard-small"
                      title={`Open source object operations for ${sourceObjectName}`}
                      aria-label={`Open source object operations for ${sourceObjectName}`}
                      onClick={(event) => openOperations(event, sourceObjectKey)}
                    >
                      <IconControl iconKey="viewmore" title={`Open source object operations for ${sourceObjectName}`} width="0.95rem" height="0.95rem" />
                    </ButtonControl>
                    {operationMenuOpen ? (
                      <ContextMenuControl
                        openContextMenu={operationMenuOpen}
                        setOpenContextMenu={(nextOpen) => setOpenOperationMenu(nextOpen ? sourceObjectKey : "")}
                        contextMenuStyle={operationMenuStyle}
                      >
                        <div className="resource-workspace-command-bar-menu" role="menu" aria-label={`Source object operations for ${sourceObjectName}`}>
                          {sourceObjectOperations.map((operation) => (
                            <button
                              key={getOperationName(operation)}
                              type="button"
                              role="menuitem"
                              onClick={() => runOperation(sourceObject, operation)}
                            >
                              {getOperationDisplayName(operation)}
                            </button>
                          ))}
                        </div>
                      </ContextMenuControl>
                    ) : null}
                  </span>
                ) : null}
              </>
            )}
          />
        );
      }}
    />
  );
};

const loadInboundEdiManagementSummary = async ({ manager, configuration, selectedResource }) => {
  if (!manager?.RunOperation || !configuration) {
    return { summary: null, error: "Resource Configuration management service is not available." };
  }

  let response;
  try {
    response = await manager.RunOperation({
      operationName: INBOUND_EDI_SUMMARY_OPERATION,
      item: configuration,
      queryParams: buildResourceOperationQueryParams({
        item: configuration,
        resourceConfiguration: configuration,
        clientResourceId: selectedResource?.id || getString(configuration, "ClientResourceId", "clientResourceId"),
        resourceConfigurationId: getId(configuration)
      }),
      isItem: true,
      isWarning: true,
      supressAllErrors: true
    });
  } catch (loadError) {
    return { summary: null, error: loadError?.message || "Inbound EDI management summary could not be loaded." };
  }

  const result = getOperationResult(response);
  if (result?.Success === false || result?.success === false) {
    return { summary: null, error: getOperationResultMessage(response, "Inbound EDI management summary could not be loaded.") };
  }

  return { summary: getOperationItem(response), error: "" };
};

const useInboundEdiManagementSummary = ({ configuration, selectedResource, refreshKey = 0 }) => {
  const resourceDomainContext = useResourceDomainContext();
  const manager = resourceDomainContext?.managers?.resourceConfiguration;
  const configurationId = getId(configuration);
  const selectedResourceId = selectedResource?.id || getId(selectedResource) || getString(configuration, "ClientResourceId", "clientResourceId");
  const managerReady = Boolean(manager?.RunOperation);
  const loadContextRef = useRef({ manager, configuration, selectedResource });
  const [state, setState] = useState({ loading: Boolean(configurationId), error: "", summary: null });

  loadContextRef.current = { manager, configuration, selectedResource };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!configurationId) {
        setState({ loading: false, error: "", summary: null });
        return;
      }

      setState((previous) => ({ ...previous, loading: true, error: "" }));
      const {
        manager: currentManager,
        configuration: currentConfiguration,
        selectedResource: currentSelectedResource
      } = loadContextRef.current;
      const result = await loadInboundEdiManagementSummary({
        manager: currentManager,
        configuration: currentConfiguration,
        selectedResource: currentSelectedResource
      });
      if (!mounted) return;
      setState({ loading: false, error: result.error, summary: result.summary });
    };

    load();

    return () => {
      mounted = false;
    };
  }, [configurationId, managerReady, refreshKey, selectedResourceId]);

  return state;
};

const READINESS_FILTER_NOT_READY = "notReady";
const READINESS_FILTER_READY = "ready";

const isReadinessRowReady = (row) =>
  getSeverityIconKey(getIssuePresentationSeverity(row?.issue)) === "check";

const filterReadinessRowsByStatus = (rows, filterMode = READINESS_FILTER_NOT_READY) =>
  rows.filter((row) => (filterMode === READINESS_FILTER_READY ? isReadinessRowReady(row) : !isReadinessRowReady(row)));

const getReadinessRowFilterCounts = (rows) =>
  rows.reduce((counts, row) => {
    if (isReadinessRowReady(row)) {
      counts.ready += 1;
    } else {
      counts.notReady += 1;
    }
    return counts;
  }, { ready: 0, notReady: 0 });

const ReadinessStatusFilter = ({ value, onChange, counts }) => (
  <div className="resource-workspace-readiness-filter" role="group" aria-label="Readiness filter">
    <ButtonControl
      type="standard"
      className={`resource-workspace-readiness-filter__option${value === READINESS_FILTER_NOT_READY ? " resource-workspace-readiness-filter__option--active" : ""}`}
      aria-pressed={value === READINESS_FILTER_NOT_READY}
      onClick={() => onChange?.(READINESS_FILTER_NOT_READY)}
    >
      <span className="resource-workspace-readiness-filter__label">Not Ready</span>
      <span className="resource-workspace-readiness-filter__count">{counts?.notReady || 0}</span>
    </ButtonControl>
    <ButtonControl
      type="standard"
      className={`resource-workspace-readiness-filter__option${value === READINESS_FILTER_READY ? " resource-workspace-readiness-filter__option--active" : ""}`}
      aria-pressed={value === READINESS_FILTER_READY}
      onClick={() => onChange?.(READINESS_FILTER_READY)}
    >
      <span className="resource-workspace-readiness-filter__label">Ready</span>
      <span className="resource-workspace-readiness-filter__count">{counts?.ready || 0}</span>
    </ButtonControl>
  </div>
);

const InboundEdiReadinessDashboard = ({
  configuration,
  selectedResource,
  productIcon,
  onSelectConfigurationScreen,
  onRefresh
}) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [activationRequirementFilter, setActivationRequirementFilter] = useState(READINESS_FILTER_NOT_READY);
  const { loading, error, summary } = useInboundEdiManagementSummary({ configuration, selectedResource, refreshKey });
  const configurationId = getId(configuration);
  const ediDirection = getEdiConfigurationDirection(selectedResource?.productResource, selectedResource, configuration);
  const ediDirectionLabel = `${ediDirection} EDI`;
  const issues = getArray(summary, "Issues", "issues");
  const blockers = issues.filter((issue) => normalizeCompare(getString(issue, "Severity", "severity")) === "error");
  const warnings = issues.filter((issue) => normalizeCompare(getString(issue, "Severity", "severity")) === "warning");
  const linkedRecords = getArray(summary, "LinkedRecords", "linkedRecords").length > 0
    ? getArray(summary, "LinkedRecords", "linkedRecords")
    : getConfigurationLinkedRecords(configuration);
  const sections = getArray(summary, "Sections", "sections");
  const latestReadinessUtc = formatDateTime(getValue(summary, "LatestReadinessUtc", "latestReadinessUtc"));
  const readinessStatus = getString(summary, "ReadinessStatus", "readinessStatus") || "Not run";
  const activationRequirementRows = sortPolicyReadinessRows(issues.map((issue, index) => ({
    key: getIssueIdentity(issue, index),
    issue,
    synthetic: false
  })));
  const activationRequirementCounts = getReadinessRowFilterCounts(activationRequirementRows);
  const filteredActivationRequirementRows = filterReadinessRowsByStatus(activationRequirementRows, activationRequirementFilter);

  const refreshSummary = useCallback(() => {
    setRefreshKey((current) => current + 1);
    onRefresh?.();
  }, [onRefresh]);

  if (loading && !summary) {
    return <ResourceStateControl loading title="Loading readiness" loadingText={`Loading ${ediDirectionLabel} readiness.`} />;
  }

  if (error && !summary) {
    return <ResourceStateControl tone="error" title="Readiness unavailable" message={error} />;
  }

  return (
    <div className="resource-workspace-configuration-manage-grid resource-workspace-inbound-edi-readiness" aria-label={`${ediDirectionLabel} readiness management`}>
      <div className="resource-workspace-configuration-manage-column">
        <section className="resource-workspace-panel resource-workspace-configuration-manage-section">
          <div className="resource-workspace-section-title resource-workspace-section-title--with-action">
            <div>
              <h2>Overview / Readiness</h2>
              <p>Operational state after setup scaffold completion.</p>
            </div>
            <div className="resource-workspace-section-actions">
              <ButtonControl
                type="standard-small"
                disabled={loading}
                title="Refresh readiness summary"
                aria-label="Refresh readiness summary"
                onClick={refreshSummary}
              >
                <IconControl iconKey="refresh" title="Refresh readiness summary" width="1rem" height="1rem" />
                <span>Refresh</span>
              </ButtonControl>
            </div>
          </div>
          <div className="resource-workspace-metric-grid resource-workspace-configuration-analytics-grid">
            <Metric label="Configuration" value={getConfigurationStatusLabel(configuration)} />
            <Metric label="Readiness" value={readinessStatus} />
            <Metric label="Blockers" value={String(blockers.length)} />
            <Metric label="Warnings" value={String(warnings.length)} />
          </div>
          <dl className="resource-workspace-facts resource-workspace-configuration-summary-facts resource-workspace-inbound-edi-overview-facts">
            <Fact label="Latest Validation" value={latestReadinessUtc || "Not run"} />
            <Fact label="Setup Result" value={getString(configuration, "SetupStatusSummary", "setupStatusSummary") || "Configured scaffold"} />
          </dl>
        </section>

        <section className="resource-workspace-panel resource-workspace-configuration-manage-section">
          <div className="resource-workspace-section-title resource-workspace-section-title--with-action resource-workspace-inbound-edi-activation-title">
            <div>
              <h2>Activation Requirements</h2>
              <p>Blockers prevent activation; warnings are cleanup or production-readiness signals.</p>
            </div>
            <div className="resource-workspace-section-actions">
              <ReadinessStatusFilter
                value={activationRequirementFilter}
                onChange={setActivationRequirementFilter}
                counts={activationRequirementCounts}
              />
            </div>
          </div>
          {filteredActivationRequirementRows.length > 0 ? (
            <div className="resource-workspace-record-list">
              {filteredActivationRequirementRows.map((row, index) => {
                const issue = row.issue;
                const presentationSeverity = getIssuePresentationSeverity(issue);
                const section = getString(issue, "SectionStepKey", "sectionStepKey");
                const isDone = getSeverityIconKey(presentationSeverity) === "check";
                const canRepairIssue = Boolean(!isDone && getPolicyRepairDefinitionForIssue(issue));
                const targetScreenKey = getEdiReadinessIssueScreenKey(issue, configuration);
                const actionLabel = !isDone && targetScreenKey && !canRepairIssue && !isPolicyTestParseIssue(issue)
                  ? formatDisplayLabel(targetScreenKey)
                  : getPolicyReadinessActionLabel({ issue, isDone, canRepairIssue, section });
                return (
                  <ReadinessIssueRow
                    key={row.key || `${getString(issue, "Code", "code")}-${index}`}
                    issue={issue}
                    actionLabel={actionLabel}
                    isActionable={Boolean(!isDone && targetScreenKey)}
                    actionTitle={targetScreenKey ? `Open ${formatDisplayLabel(targetScreenKey)} to resolve this requirement` : undefined}
                    onAction={() => onSelectConfigurationScreen(configurationId, targetScreenKey)}
                  />
                );
              })}
            </div>
          ) : (
            <ResourceStateControl compact message={activationRequirementFilter === READINESS_FILTER_READY ? "No ready activation requirements are currently reported." : "No not-ready activation requirements are currently reported."} />
          )}
        </section>
      </div>

      <div className="resource-workspace-configuration-manage-column">
        <section className="resource-workspace-panel resource-workspace-configuration-manage-section">
          <div className="resource-workspace-section-title">
            <h2>Linked Resources</h2>
            <p>Setup-created links that management validates before activation.</p>
          </div>
          {linkedRecords.length > 0 ? (
            <div className="resource-workspace-configuration-linked-list">
              {linkedRecords.map((record, index) => {
                const recordScreenKey = getLinkedRecordScreenKey(record, configuration);
                const recordType = getLinkedRecordTypeLabel(record, selectedResource);
                const recordDisplayName = getLinkedRecordDisplayName(record);
                const recordIconKey = getLinkedRecordIconKey(record, configuration, productIcon);
                return (
                  <article key={`${getString(record, "ItemName", "itemName")}-${getString(record, "ItemId", "itemId")}-${index}`} className="resource-workspace-configuration-linked-row">
                    <span className="resource-workspace-resource-icon" aria-hidden="true">
                      <IconControl iconKey={recordIconKey} width="2.75rem" height="2.75rem" />
                    </span>
                    <span className="resource-workspace-configuration-linked-copy">
                      <span>{recordType}</span>
                      <strong>{recordDisplayName}</strong>
                      {getLinkedRecordRelationshipLabel(record) ? <small>{getLinkedRecordRelationshipLabel(record)}</small> : null}
                    </span>
                    <ButtonControl
                      type="standard-small"
                      disabled={!recordScreenKey}
                      title={recordScreenKey ? `Open ${recordType}` : "No linked screen available"}
                      aria-label={recordScreenKey ? `Open ${recordType}` : "No linked screen available"}
                      onClick={() => onSelectConfigurationScreen(configurationId, recordScreenKey, record)}
                    >
                      <IconControl iconKey="settings" title={recordScreenKey ? `Open ${recordType}` : "No linked screen available"} width="0.95rem" height="0.95rem" />
                    </ButtonControl>
                  </article>
                );
              })}
            </div>
          ) : (
            <ResourceStateControl compact message="No linked resources are available." />
          )}
        </section>

        <section className="resource-workspace-panel resource-workspace-configuration-manage-section">
          <div className="resource-workspace-section-title">
            <h2>Readiness Sections</h2>
            <p>Current operational checkpoints grouped by management area.</p>
          </div>
          {sections.length > 0 ? (
            <div className="resource-workspace-record-list">
              {sections.map((section) => (
                <article key={getString(section, "SectionKey", "sectionKey", "Title", "title")} className="resource-workspace-record-row resource-workspace-record-row--stacked">
                  <div className="resource-workspace-inbound-edi-section-summary">
                    <strong>{getString(section, "Title", "title")}</strong>
                    {getString(section, "Description", "description") ? <small>{getString(section, "Description", "description")}</small> : null}
                  </div>
                  <div className="resource-workspace-inbound-edi-section-records">
                    {getArray(section, "Records", "records").map((record, recordIndex) => (
                      <span key={`${getString(record, "DisplayName", "displayName")}-${recordIndex}`} className="resource-workspace-inbound-edi-section-record">
                        <strong>{getString(record, "DisplayName", "displayName")}</strong>
                        <small>{getString(record, "Status", "status") || getString(record, "SecondaryText", "secondaryText")}</small>
                      </span>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <ResourceStateControl compact message="No readiness sections have been reported." />
          )}
        </section>
      </div>
    </div>
  );
};

const getInboundEdiPartnerScopePolicies = (summary) => getArray(summary, "EdiPartnerScopePolicies", "ediPartnerScopePolicies");
const getInboundEdiTradingPartners = (summary) => getArray(summary, "EdiTradingPartners", "ediTradingPartners");
const getInboundEdiTradingPartnerGroups = (summary) => getArray(summary, "EdiTradingPartnerGroups", "ediTradingPartnerGroups");

const getInboundEdiPartnerScopeTypeLabel = (mode) => mode === "PartnerGroup" ? "Partner Group" : "Partner";

const getInboundEdiPartnerId = (partner) => getId(partner) || getString(partner, "UniqueId", "uniqueId");
const getInboundEdiPartnerName = (partner) => getString(partner, "PartnerName", "partnerName", "PartnerCode", "partnerCode") || getDisplayName(partner, "Trading partner");
const getInboundEdiPartnerGroupId = (group) => getId(group) || getString(group, "UniqueId", "uniqueId");
const getInboundEdiPartnerGroupName = (group) => getString(group, "GroupName", "groupName", "GroupCode", "groupCode") || getDisplayName(group, "Trading partner group");

const INBOUND_EDI_PARTNER_SCOPE_SELECTOR_CARD_FIELDS = [
  { FieldName: "PartnerScopeName", DisplayName: "Scope", ShowOnCard: true, GridSequence: 1 },
  { FieldName: "PartnerScopeCode", DisplayName: "Code", ShowOnCard: true, GridSequence: 2 },
  { FieldName: "PartnerScopeDescription", DisplayName: "Notes", ShowOnCard: true, GridSequence: 3 }
];

const INBOUND_EDI_PARTNER_SCOPE_SELECTOR_METADATA = {
  Name: "InboundEdiPartnerScopeSelector",
  DisplayName: "Partner Scope",
  Fields: INBOUND_EDI_PARTNER_SCOPE_SELECTOR_CARD_FIELDS
};

const buildInboundEdiPartnerPickerRows = (mode, partners, groups) => {
  const isGroupMode = mode === "PartnerGroup";
  const rows = isGroupMode ? groups : partners;
  return rows
    .map((row) => {
      const scopeId = isGroupMode ? getInboundEdiPartnerGroupId(row) : getInboundEdiPartnerId(row);
      if (!scopeId) return null;
      return {
        ...row,
        PartnerScopeId: scopeId,
        PartnerScopeName: isGroupMode ? getInboundEdiPartnerGroupName(row) : getInboundEdiPartnerName(row),
        PartnerScopeCode: isGroupMode
          ? getString(row, "GroupCode", "groupCode")
          : getString(row, "PartnerCode", "partnerCode"),
        PartnerScopeDescription: getString(row, "Description", "description", "Notes", "notes"),
        PartnerScopeType: isGroupMode ? "PartnerGroup" : "Partner"
      };
    })
    .filter(Boolean);
};

const getInboundEdiPolicyScopeId = (policy) => getString(policy, "TradingPartnerId", "tradingPartnerId")
  || getString(policy, "TradingPartnerGroupId", "tradingPartnerGroupId");

const getInboundEdiPolicyScopeName = (policy, partners, groups) => {
  const scopeType = getString(policy, "ScopeType", "scopeType") || "Partner";
  const scopeId = getInboundEdiPolicyScopeId(policy);
  if (scopeType === "PartnerGroup") {
    const group = groups.find((item) => getInboundEdiPartnerGroupId(item) === scopeId);
    return group ? getInboundEdiPartnerGroupName(group) : scopeId || "Partner group";
  }
  const partner = partners.find((item) => getInboundEdiPartnerId(item) === scopeId);
  return partner ? getInboundEdiPartnerName(partner) : scopeId || "Trading partner";
};

const createInboundEdiPartnerPolicyDraft = ({ configuration, scopeMode, scopeId }) => {
  const direction = getEdiConfigurationDirection(null, null, configuration);
  return {
    ClientId: getValue(configuration, "ClientId", "clientId"),
    ResourceConfigurationId: getId(configuration),
    Direction: direction,
    ScopeType: scopeMode || "Partner",
    TradingPartnerId: scopeMode === "PartnerGroup" ? "" : scopeId,
    TradingPartnerGroupId: scopeMode === "PartnerGroup" ? scopeId : "",
    OutputMode: direction === "Outbound" ? "" : "NormalizeToEntitySchema",
    DeliveryResourceConfigurationId: "",
    AcknowledgementMode: "",
    DuplicatePolicy: "",
    RejectionPolicy: "",
    ErrorQueuePolicy: "",
    DiagnosticsEnabled: direction === "Outbound" ? false : true,
    RequireTestParseForActivation: false,
    IsActive: true
  };
};

const POLICY_REPAIR_FIELD_BY_ISSUE_CODE = {
  "acknowledgement-policy-missing": {
    fieldName: "AcknowledgementMode",
    title: "Fix Acknowledgement Policy",
    description: "Choose the acknowledgement behavior for this partner scope."
  },
  "duplicate-policy-missing": {
    fieldName: "DuplicatePolicy",
    title: "Fix Duplicate Handling",
    description: "Choose how duplicate inbound documents are handled for this partner scope."
  },
  "rejection-policy-missing": {
    fieldName: "RejectionPolicy",
    title: "Fix Rejection Rules",
    description: "Choose how invalid documents are rejected for this partner scope."
  },
  "error-queue-policy-missing": {
    fieldName: "ErrorQueuePolicy",
    title: "Fix Error Queue",
    description: "Choose where failed document processing should be queued for this partner scope."
  },
  "inbound-destination-routing-missing": {
    fieldName: "DestinationResourceConfigurationId",
    title: "Fix Destination Routing",
    description: "Select the destination Resource Configuration used when this partner scope sends normalized output to a destination."
  },
  "inbound-destination-routing-invalid": {
    fieldName: "DestinationResourceConfigurationId",
    title: "Fix Destination Routing",
    description: "Select an active compatible destination Resource Configuration for this partner scope."
  },
  "outbound-delivery-route-missing": {
    fieldName: "DeliveryResourceConfigurationId",
    title: "Fix Delivery Route",
    description: "Select the delivery Resource Configuration used for outbound documents for this partner scope."
  },
  "outbound-delivery-route-invalid": {
    fieldName: "DeliveryResourceConfigurationId",
    title: "Fix Delivery Route",
    description: "Select an active compatible delivery Resource Configuration for this partner scope."
  },
  "outbound-acknowledgement-policy-missing": {
    fieldName: "AcknowledgementMode",
    title: "Fix Acknowledgement Policy",
    description: "Choose the acknowledgement behavior for this outbound partner scope."
  },
  "outbound-diagnostics-policy-missing": {
    fieldName: "DiagnosticsEnabled",
    title: "Fix Diagnostics",
    description: "Enable diagnostics for this outbound partner scope."
  },
  "test-parse-required": {
    fieldName: "RequireTestParseForActivation",
    title: "Fix Test Parse Requirement",
    description: "Update whether this partner scope requires a successful test parse before activation."
  }
};

const getPolicyRepairDefinitionForIssue = (issue) =>
  POLICY_REPAIR_FIELD_BY_ISSUE_CODE[normalizeCompare(getString(issue, "Code", "code"))] || null;

const getIssueScopeLabel = (issue) => {
  const message = getString(issue, "Message", "message");
  const match = message.match(/'([^']+)'/);
  return match?.[1] || "";
};

const POLICY_READINESS_CHECKS = [
  {
    key: "routing",
    label: "Routing",
    issueCodes: [
      "inbound-destination-routing-missing",
      "inbound-destination-routing-invalid",
      "outbound-delivery-route-missing",
      "outbound-delivery-route-invalid"
    ],
    successMessage: (policy, scopeName) => {
      if (getString(policy, "Direction", "direction") === "Outbound") {
        return getString(policy, "DeliveryResourceConfigurationId", "deliveryResourceConfigurationId")
          ? `Delivery route is configured for '${scopeName}'.`
          : `Delivery route is not configured for '${scopeName}'.`;
      }
      const outputMode = getString(policy, "OutputMode", "outputMode") === "DestinationRouting" ? "Send To Destination" : "Normalize Only";
      return outputMode === "Send To Destination"
        ? `Destination routing is configured for '${scopeName}'.`
        : `Normalize Only is configured for '${scopeName}'.`;
    }
  },
  {
    key: "acknowledgement",
    label: "Acknowledgements",
    issueCodes: [
      "acknowledgement-policy-missing",
      "outbound-acknowledgement-policy-missing",
      "acknowledgement-disabled"
    ],
    successMessage: (policy, scopeName) => {
      const acknowledgementMode = getString(policy, "AcknowledgementMode", "acknowledgementMode") || "NotConfigured";
      return `Acknowledgement policy is ${formatDisplayLabel(acknowledgementMode)} for '${scopeName}'.`;
    }
  },
  {
    key: "duplicate",
    label: "Duplicate Handling",
    directions: ["Inbound"],
    issueCodes: ["duplicate-policy-missing"],
    successMessage: (policy, scopeName) => `Duplicate handling is ${formatDisplayLabel(getString(policy, "DuplicatePolicy", "duplicatePolicy"))} for '${scopeName}'.`
  },
  {
    key: "rejection",
    label: "Rejection Rules",
    directions: ["Inbound"],
    issueCodes: ["rejection-policy-missing"],
    successMessage: (policy, scopeName) => `Rejection rules are ${formatDisplayLabel(getString(policy, "RejectionPolicy", "rejectionPolicy"))} for '${scopeName}'.`
  },
  {
    key: "error-queue",
    label: "Error Queue",
    directions: ["Inbound"],
    issueCodes: ["error-queue-policy-missing"],
    successMessage: (policy, scopeName) => `Error queue policy is ${formatDisplayLabel(getString(policy, "ErrorQueuePolicy", "errorQueuePolicy"))} for '${scopeName}'.`
  },
  {
    key: "diagnostics",
    label: "Diagnostics",
    directions: ["Outbound"],
    issueCodes: ["outbound-diagnostics-policy-missing"],
    successMessage: (policy, scopeName) =>
      getValue(policy, "DiagnosticsEnabled", "diagnosticsEnabled") === false
        ? `Diagnostics are disabled for '${scopeName}'.`
        : `Diagnostics are enabled for '${scopeName}'.`
  },
  {
    key: "test-parse",
    label: "Test Parse",
    directions: ["Inbound"],
    issueCodes: ["test-parse-required", "test-parse-not-run"],
    successMessage: (policy, scopeName) => {
      const latestSuccessfulParse = getString(policy, "LastSuccessfulTestParseUtc", "lastSuccessfulTestParseUtc");
      return latestSuccessfulParse
        ? `A successful test parse is recorded for '${scopeName}'.`
        : `Test parse is not required for '${scopeName}'.`;
    }
  }
];

const getIssueIdentity = (issue, index = 0) =>
  `${getString(issue, "Code", "code")}|${getString(issue, "Message", "message")}|${index}`;

const issueMatchesPolicyScope = (issue, policy, scopeName, policies) => {
  const issueScope = normalizeCompare(getIssueScopeLabel(issue));
  if (!issueScope) return policies.length === 1;
  if (issueScope === normalizeCompare(scopeName)) return true;
  return issueScope === normalizeCompare(getInboundEdiPolicyScopeId(policy));
};

const buildPolicyReadinessSuccessIssue = ({ policy, check, scopeName }) => ({
  Code: `${check.key}-configured`,
  Label: check.label,
  Message: check.successMessage(policy, scopeName),
  Severity: "Success",
  SectionStepKey: "partners",
  _policyReadinessSynthetic: true
});

const getPolicyReadinessSortPriority = (row) => {
  const severity = normalizeCompare(getIssuePresentationSeverity(row.issue));
  if (["error", "blocker", "failed", "failure"].includes(severity)) return 0;
  if (["warning", "warn"].includes(severity)) return 1;
  if (["info"].includes(severity)) return 2;
  return 3;
};

const sortPolicyReadinessRows = (rows) =>
  rows
    .map((row, index) => ({ row, index, priority: getPolicyReadinessSortPriority(row) }))
    .sort((left, right) => left.priority - right.priority || left.index - right.index)
    .map(({ row }) => row);

const buildPolicyReadinessRows = ({ policies, issues, tradingPartners, tradingPartnerGroups, direction = "Inbound" }) => {
  const indexedIssues = issues.map((issue, index) => ({ issue, identity: getIssueIdentity(issue, index) }));
  const matchedIssueIdentities = new Set();
  const rows = [];
  const checks = POLICY_READINESS_CHECKS.filter((check) => !check.directions || check.directions.includes(direction));

  policies.forEach((policy) => {
    const scopeName = getInboundEdiPolicyScopeName(policy, tradingPartners, tradingPartnerGroups);
    checks.forEach((check) => {
      const matchingIssue = indexedIssues.find(({ issue, identity }) =>
        !matchedIssueIdentities.has(identity)
        && check.issueCodes.includes(normalizeCompare(getString(issue, "Code", "code")))
        && issueMatchesPolicyScope(issue, policy, scopeName, policies)
      );
      if (matchingIssue) {
        matchedIssueIdentities.add(matchingIssue.identity);
        rows.push({
          key: `${getId(policy) || scopeName}-${check.key}-${matchingIssue.identity}`,
          issue: matchingIssue.issue,
          policy,
          check,
          synthetic: false
        });
        return;
      }

      rows.push({
        key: `${getId(policy) || scopeName}-${check.key}`,
        issue: buildPolicyReadinessSuccessIssue({ policy, check, scopeName }),
        policy,
        check,
        synthetic: true
      });
    });
  });

  indexedIssues
    .filter(({ identity }) => !matchedIssueIdentities.has(identity))
    .forEach(({ issue, identity }) => {
      rows.push({
        key: identity,
        issue,
        policy: null,
        check: null,
        synthetic: false
      });
    });

  return sortPolicyReadinessRows(rows);
};

const getPolicyReadinessActionLabel = ({ issue, isDone, canRepairIssue, section }) => {
  const code = normalizeCompare(getString(issue, "Code", "code"));
  if (!isDone && code.startsWith("test-parse")) return "Test";
  if (isDone) return "Ready";
  if (canRepairIssue) return "Resolve";
  return section ? formatDisplayLabel(section) : "";
};

const isPolicyTestParseIssue = (issue) =>
  normalizeCompare(getString(issue, "Code", "code")).startsWith("test-parse");

const getEdiReadinessIssueScreenKey = (issue, configuration = null) => {
  const section = normalizeCompare(getString(issue, "SectionStepKey", "sectionStepKey"));
  const code = normalizeCompare(getString(issue, "Code", "code"));
  const label = normalizeCompare(getString(issue, "Label", "label"));
  const token = `${section} ${code} ${label}`;
  const isOutbound = isOutboundEdiResource(null, null, configuration);

  if (token.includes("partner") || token.includes("polic") || token.includes("acknowledgement") || token.includes("duplicate") || token.includes("rejection") || token.includes("errorqueue") || token.includes("testparse")) {
    return INBOUND_EDI_PARTNERS_SCREEN_KEY;
  }

  if (isOutbound && (code.startsWith("outbound-delivery-route") || code.startsWith("outbound-acknowledgement") || code.startsWith("outbound-diagnostics"))) {
    return INBOUND_EDI_PARTNERS_SCREEN_KEY;
  }

  if (isOutbound && (token.includes("schedule") || token.includes("outbound-schedule"))) {
    return DESTINATION_SCHEDULES_SCREEN_KEY;
  }

  if (isOutbound && (token.includes("entity-deployments") || token.includes("entity deployment") || token.includes("entity deployments") || token.includes("delivery scope") || token.includes("delivery-scope"))) {
    return DESTINATION_SCREEN_KEY;
  }

  if (isOutbound && (token.includes("deployment") || token.includes("deployments"))) {
    return DESTINATION_SCREEN_KEY;
  }

  if (isOutbound && (token.includes("file destination") || token.includes("file-destination") || token.includes("file storage") || token.includes("filestorage") || token.includes("destination"))) {
    return DESTINATION_SCREEN_KEY;
  }

  if (isOutbound && (token.includes("edi entity objects") || token.includes("edi-entity-objects") || token.includes("entity objects") || token.includes("entity-object") || token.includes("entityobject"))) {
    return OUTBOUND_EDI_ENTITY_OBJECTS_SCREEN_KEY;
  }

  if (
    token.includes("sourceobjectofrecord")
    || token.includes("source-object-of-record")
    || token.includes("source object of record")
    || token.includes("sourceobjectfield")
    || token.includes("source-object-field")
    || code.startsWith("source-object-")
  ) {
    return INBOUND_EDI_SOURCE_OBJECTS_SCREEN_KEY;
  }

  if (token.includes("mapping") || token.includes("fieldmap")) {
    return INBOUND_EDI_MAPPING_SCREEN_KEY;
  }

  if (token.includes("integration") || token.includes("intake") || token.includes("connection")) {
    return SOURCE_INTEGRATION_NAVIGATIONS[0].key;
  }

  if (token.includes("sourcesystem") || token.includes("target") || token.includes("sourceobject")) {
    return SOURCE_SYSTEM_SCREEN_KEY;
  }

  if (token.includes("entityschema") || token.includes("directschema") || token.includes("schema")) {
    return isOutbound ? OUTBOUND_EDI_ENTITY_OBJECTS_SCREEN_KEY : ENTITY_SCHEMA_SCREEN_KEY;
  }

  return "";
};

const ReadinessIssueRow = ({
  issue,
  actionLabel,
  isActionable = false,
  actionTitle,
  onAction
}) => {
  const presentationSeverity = getIssuePresentationSeverity(issue);
  const openRowAction = () => {
    if (isActionable) onAction?.();
  };

  return (
    <article
      className={`resource-workspace-record-row resource-workspace-inbound-edi-issue-row${isActionable ? " resource-workspace-inbound-edi-issue-row--repairable" : ""}`}
      role={isActionable ? "button" : undefined}
      tabIndex={isActionable ? 0 : undefined}
      title={actionTitle}
      onClick={openRowAction}
      onKeyDown={(event) => {
        if (!isActionable || (event.key !== "Enter" && event.key !== " ")) return;
        event.preventDefault();
        openRowAction();
      }}
    >
      <span
        className={`resource-workspace-status-pill resource-workspace-status-pill--icon resource-workspace-status-pill--${normalizeCompare(presentationSeverity)}`}
        aria-label={presentationSeverity}
        title={presentationSeverity}
      >
        <span className="material-symbols-rounded" aria-hidden="true">{getSeverityIconKey(presentationSeverity)}</span>
      </span>
      <span>
        <strong>{getString(issue, "Label", "label")}</strong>
        <small>{getString(issue, "Message", "message")}</small>
      </span>
      {actionLabel ? <small>{actionLabel}</small> : null}
    </article>
  );
};

const InboundEdiOperationalPolicyScreen = ({ configuration, selectedResource, onSaved, onRegisterScreenActions }) => {
  const resourceDomainContext = useResourceDomainContext();
  const navigate = useNavigate();
  const location = useLocation();
  const policyManager = resourceDomainContext?.managers?.ediPartnerScopePolicy;
  const resourceConfigurationManager = resourceDomainContext?.managers?.resourceConfiguration;
  const [refreshKey, setRefreshKey] = useState(0);
  const { loading, error, summary } = useInboundEdiManagementSummary({ configuration, selectedResource, refreshKey });
  const [partnerScopeDraft, setPartnerScopeDraft] = useState({ Mode: "Partner", Id: "" });
  const [partnerScopeSelectedIds, setPartnerScopeSelectedIds] = useState([]);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [policyRepair, setPolicyRepair] = useState(null);
  const [testParseDialog, setTestParseDialog] = useState(null);
  const [testParseState, setTestParseState] = useState({ busy: false, error: "" });
  const [policyMetadata, setPolicyMetadata] = useState(null);
  const [policyMetadataState, setPolicyMetadataState] = useState({ loading: false, error: "" });
  const [partnerInfoDialogOpen, setPartnerInfoDialogOpen] = useState(false);
  const [partnerScopeDialogOpen, setPartnerScopeDialogOpen] = useState(false);
  const [policyReadinessFilter, setPolicyReadinessFilter] = useState(READINESS_FILTER_NOT_READY);
  const [saveState, setSaveState] = useState({ busy: false, error: "" });
  const policies = getInboundEdiPartnerScopePolicies(summary);
  const tradingPartners = getInboundEdiTradingPartners(summary);
  const tradingPartnerGroups = getInboundEdiTradingPartnerGroups(summary);
  const ediDirection = getEdiConfigurationDirection(selectedResource?.productResource, selectedResource, configuration);
  const title = `${ediDirection} Partner Scope Assignments`;
  const description = ediDirection === "Outbound"
    ? "Assign configured trading partners or partner groups to this outbound EDI document flow. Delivery route, acknowledgements, and diagnostics are edited per partner scope."
    : "Assign configured trading partners or partner groups to this inbound EDI document flow. Routing, acknowledgements, diagnostics, duplicate handling, rejection, error queue, and test-parse rules are edited per partner scope.";
  const pickerRows = useMemo(() => {
    const rows = buildInboundEdiPartnerPickerRows(partnerScopeDraft.Mode, tradingPartners, tradingPartnerGroups);
    const assignedScopeIds = new Set(policies
      .filter((policy) => (getString(policy, "ScopeType", "scopeType") || "Partner") === (partnerScopeDraft.Mode || "Partner"))
      .map(getInboundEdiPolicyScopeId)
      .filter(Boolean));

    return rows.filter((row) => !assignedScopeIds.has(row.PartnerScopeId));
  }, [partnerScopeDraft.Mode, policies, tradingPartnerGroups, tradingPartners]);
  const pickerOptions = useMemo(() => pickerRows.map((row) => ({
    Text: row.PartnerScopeName,
    Value: row.PartnerScopeId
  })), [pickerRows]);

  useEffect(() => {
    setPartnerScopeSelectedIds((previous) => {
      const availableIds = new Set(pickerRows.map((row) => row.PartnerScopeId));
      const retainedIds = previous.filter((id) => availableIds.has(id));
      if (retainedIds.length) return retainedIds;
      if (pickerRows.length === 1) return [pickerRows[0].PartnerScopeId];
      return [];
    });
    setPartnerScopeDraft((previous) => {
      if (previous.Id && pickerRows.some((row) => row.PartnerScopeId === previous.Id)) return previous;
      if (pickerRows.length === 1) return { ...previous, Id: pickerRows[0].PartnerScopeId };
      if (previous.Id) return { ...previous, Id: "" };
      return previous;
    });
  }, [pickerRows]);

  const loadPolicyMetadata = useCallback(async () => {
    if (policyMetadata) return policyMetadata;
    if (policyMetadataState.loading) return null;
    if (!policyManager?.GetMetadata) {
      setPolicyMetadataState({ loading: false, error: "EDI partner policy metadata is not available." });
      return null;
    }

    setPolicyMetadataState({ loading: true, error: "" });
    try {
      const response = await policyManager.GetMetadata({});
      const metadata = firstItem(response?.Items) || firstItem(response) || firstItem(policyManager.uiMetadata) || null;
      setPolicyMetadata(metadata);
      setPolicyMetadataState({
        loading: false,
        error: metadata ? "" : "EDI partner policy metadata is not available."
      });
      return metadata;
    } catch (metadataError) {
      setPolicyMetadataState({
        loading: false,
        error: metadataError?.message || "EDI partner policy metadata could not be loaded."
      });
      return null;
    }
  }, [policyManager, policyMetadata, policyMetadataState.loading]);

  const openPolicyEditor = useCallback((policy) => {
    setEditingPolicy(policy);
    loadPolicyMetadata();
  }, [loadPolicyMetadata]);

  const savePolicy = useCallback(async (policyDraft) => {
    if (!policyManager?.saveItem) {
      setSaveState({ busy: false, error: "EDI partner policy service is not available." });
      return null;
    }

    setSaveState({ busy: true, error: "" });
    try {
      const response = await policyManager.saveItem({
        ...policyDraft,
        ResourceConfigurationId: getId(configuration),
        Direction: ediDirection,
        IsNew: !getId(policyDraft),
        IsActive: getValue(policyDraft, "IsActive", "isActive") !== false
      });
      const result = getOperationResult(response);
      if (result?.Success === false || result?.success === false) {
        throw new Error(getOperationResultMessage(response, "EDI partner scope policy could not be saved."));
      }

      setSaveState({ busy: false, error: "" });
      setEditingPolicy(null);
      setPolicyRepair(null);
      setRefreshKey((current) => current + 1);
      onSaved?.();
      return result;
    } catch (saveError) {
      setSaveState({ busy: false, error: saveError?.message || "EDI partner scope policy could not be saved." });
      return null;
    }
  }, [configuration, ediDirection, onSaved, policyManager]);

  const addPartnerScope = useCallback(async () => {
    const mode = partnerScopeDraft.Mode || "Partner";
    const existingScopeIds = new Set(policies
      .filter((policy) => (getString(policy, "ScopeType", "scopeType") || "Partner") === mode)
      .map(getInboundEdiPolicyScopeId)
      .filter(Boolean));
    const scopeIds = partnerScopeSelectedIds
      .map((scopeId) => String(scopeId || "").trim())
      .filter((scopeId) => scopeId && !existingScopeIds.has(scopeId));

    if (!scopeIds.length) {
      setPartnerScopeSelectedIds([]);
      setPartnerScopeDraft((previous) => ({ ...previous, Id: "" }));
      return;
    }

    for (const scopeId of scopeIds) {
      const result = await savePolicy(createInboundEdiPartnerPolicyDraft({ configuration, scopeMode: mode, scopeId }));
      if (!result) return;
    }

    setPartnerScopeDialogOpen(false);
    setPartnerScopeSelectedIds([]);
    setPartnerScopeDraft((previous) => ({ ...previous, Id: "" }));
  }, [configuration, partnerScopeDraft.Mode, partnerScopeSelectedIds, policies, savePolicy]);

  const openPartnerScopeDialog = useCallback(() => {
    if (saveState.busy || pickerRows.length === 0) return;
    setPartnerScopeDialogOpen(true);
  }, [pickerRows.length, saveState.busy]);

  const removePartnerScope = useCallback(async (policy) => {
    if (!policyManager?.saveItem) {
      setSaveState({ busy: false, error: "EDI partner policy service is not available." });
      return;
    }
    setSaveState({ busy: true, error: "" });
    try {
      const response = await policyManager.saveItem({ ...policy, IsActive: false, IsDeleted: true, IsNew: false });
      const result = getOperationResult(response);
      if (result?.Success === false || result?.success === false) {
        throw new Error(getOperationResultMessage(response, "Partner scope could not be removed."));
      }
      setSaveState({ busy: false, error: "" });
      setRefreshKey((current) => current + 1);
      onSaved?.();
    } catch (removeError) {
      setSaveState({ busy: false, error: removeError?.message || "Partner scope could not be removed." });
    }
  }, [onSaved, policyManager]);

  const openPartnerScopes = useCallback(() => {
    const backUrl = `${location.pathname}${location.search}`;
    navigate(`${RESOURCES_TRADING_PARTNERS_PATH}?backUrl=${encodeURIComponent(backUrl)}`);
  }, [location.pathname, location.search, navigate]);

  const getPolicyForIssue = useCallback((issue) => {
    const scopeLabel = normalizeCompare(getIssueScopeLabel(issue));
    if (scopeLabel) {
      const matchingPolicy = policies.find((policy) =>
        normalizeCompare(getInboundEdiPolicyScopeName(policy, tradingPartners, tradingPartnerGroups)) === scopeLabel
      );
      if (matchingPolicy) return matchingPolicy;
    }

    return policies.length === 1 ? policies[0] : null;
  }, [policies, tradingPartnerGroups, tradingPartners]);

  const openTestParseDialog = useCallback((issue) => {
    const policy = getPolicyForIssue(issue);
    if (!policy) return;

    setTestParseDialog({ issue, policy });
    setTestParseState({ busy: false, error: "" });
  }, [getPolicyForIssue]);

  const closeTestParseDialog = useCallback(() => {
    if (testParseState.busy) return;
    setTestParseDialog(null);
    setTestParseState({ busy: false, error: "" });
  }, [testParseState.busy]);

  const runTestParseValidation = useCallback(async () => {
    const policy = testParseDialog?.policy;
    const policyId = getId(policy);
    if (!policyId) {
      setTestParseState({ busy: false, error: "Partner scope policy is missing an id." });
      return;
    }
    if (!resourceConfigurationManager?.RunOperation) {
      setTestParseState({ busy: false, error: "Resource Configuration operation service is not available." });
      return;
    }

    setTestParseState({ busy: true, error: "" });
    try {
      const response = await resourceConfigurationManager.RunOperation({
        operationName: INBOUND_EDI_TEST_PARSE_OPERATION,
        item: configuration,
        queryParams: buildResourceOperationQueryParams({
          queryParams: {
            partnerPolicyId: policyId,
            policyId
          },
          item: configuration,
          resourceConfiguration: configuration,
          clientResourceId: selectedResource?.id || getString(configuration, "ClientResourceId", "clientResourceId"),
          resourceConfigurationId: getId(configuration)
        }),
        isItem: true,
        isWarning: true,
        supressAllErrors: true
      });
      const result = getOperationResult(response);
      if (result?.Success === false || result?.success === false) {
        throw new Error(getOperationResultMessage(response, "Test parse validation could not be completed."));
      }

      setTestParseDialog(null);
      setTestParseState({ busy: false, error: "" });
      setRefreshKey((current) => current + 1);
      onSaved?.();
    } catch (testParseError) {
      setTestParseState({
        busy: false,
        error: testParseError?.message || "Test parse validation could not be completed."
      });
    }
  }, [configuration, onSaved, resourceConfigurationManager, selectedResource?.id, testParseDialog?.policy]);

  const openPolicyRepairDialog = useCallback(async (issue) => {
    const repairDefinition = getPolicyRepairDefinitionForIssue(issue);
    const policy = getPolicyForIssue(issue);
    if (!repairDefinition || !policy) return;

    const metadata = policyMetadata || await loadPolicyMetadata();
    const fieldMetadata = getMetadataField(metadata, repairDefinition.fieldName);
    const scopeName = getInboundEdiPolicyScopeName(policy, tradingPartners, tradingPartnerGroups);
    setPolicyRepair({
      issue,
      policy,
      fieldName: repairDefinition.fieldName,
      fieldMetadata,
      title: `${repairDefinition.title}: ${scopeName}`,
      description: repairDefinition.description
    });
  }, [getPolicyForIssue, loadPolicyMetadata, policyMetadata, tradingPartnerGroups, tradingPartners]);

  const closePolicyRepairDialog = useCallback(() => {
    setPolicyRepair(null);
  }, []);

  useEffect(() => {
    const actions = [
      {
        key: "inbound-edi-add-partner-scope",
        label: "Add Partner Scope",
        iconKey: "add",
        disabled: saveState.busy || pickerRows.length === 0,
        loading: saveState.busy,
        title: "Add partner scope.",
        onClick: openPartnerScopeDialog
      },
      {
        key: "inbound-edi-partner-scopes",
        label: "Trading Partners",
        iconKey: "settings",
        disabled: false,
        title: "Open Trading Partners.",
        onClick: openPartnerScopes
      }
    ].filter(Boolean);

    onRegisterScreenActions?.(actions);
    return () => onRegisterScreenActions?.([]);
  }, [onRegisterScreenActions, openPartnerScopeDialog, openPartnerScopes, pickerRows.length, saveState.busy]);

  if (loading && !summary) {
    return <ResourceStateControl loading title={`Loading ${title}`} loadingText="Loading EDI partner scope policies." />;
  }

  if (error && !summary) {
    return <ResourceStateControl tone="error" title={`${title} unavailable`} message={error} />;
  }

  const diagnosticIssues = getArray(summary, "Issues", "issues").filter((issue) =>
    ["partners", "policies"].includes(normalizeCompare(getString(issue, "SectionStepKey", "sectionStepKey")))
  );
  const policyReadinessRows = buildPolicyReadinessRows({
    policies,
    issues: diagnosticIssues,
    tradingPartners,
    tradingPartnerGroups,
    direction: ediDirection
  });
  const policyReadinessCounts = getReadinessRowFilterCounts(policyReadinessRows);
  const filteredPolicyReadinessRows = filterReadinessRowsByStatus(policyReadinessRows, policyReadinessFilter);

  return (
    <div className="resource-workspace-linked-item-layout resource-workspace-linked-item-layout--summary-sidebar" aria-label={title}>
      <section className="resource-workspace-panel resource-workspace-linked-item-section">
        <div className="resource-workspace-section-title">
          <div className="resource-workspace-section-title__row">
            <h2>{title}</h2>
            <ButtonControl
              type="standard-small"
              title="About partner scope assignments"
              aria-label="About partner scope assignments"
              onClick={() => setPartnerInfoDialogOpen(true)}
            >
              <IconControl iconKey="info" title="About partner scope assignments" width="0.95rem" height="0.95rem" />
            </ButtonControl>
          </div>
          <p>{description}</p>
        </div>
        {saveState.error ? <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">{saveState.error}</div> : null}
        <div className="resource-workspace-inbound-edi-partner-assignments">
          <div className="resource-workspace-inbound-edi-partner-add">
            <FieldControl
              value={partnerScopeDraft.Mode}
              onChange={(value) => {
                setPartnerScopeSelectedIds([]);
                setPartnerScopeDraft({ Mode: value || "Partner", Id: "" });
              }}
              fieldMetadata={buildSelectListField("PartnerScopeMode", "", INBOUND_EDI_PARTNER_MODE_OPTIONS.filter((option) => option.Value !== "Unassigned"), "Scope type", "Scope type")}
            />
            <FieldControl
              value={partnerScopeDraft.Id}
              onChange={(value) => {
                const nextValue = value ?? "";
                setPartnerScopeSelectedIds(nextValue ? [nextValue] : []);
                setPartnerScopeDraft((previous) => ({ ...previous, Id: nextValue }));
              }}
              fieldMetadata={{
                ...buildSelectListField("PartnerScopeId", "", pickerOptions, partnerScopeDraft.Mode === "PartnerGroup" ? "Select partner group" : "Select trading partner", partnerScopeDraft.Mode === "PartnerGroup" ? "Partner group" : "Trading partner"),
                IsRequired: false,
                Description: ""
              }}
            />
          </div>
          {policies.length > 0 ? (
            <div className="resource-workspace-record-list">
              {policies.map((policy) => {
                const scopeType = getString(policy, "ScopeType", "scopeType") || "Partner";
                const scopeName = getInboundEdiPolicyScopeName(policy, tradingPartners, tradingPartnerGroups);
                const outputMode = getString(policy, "OutputMode", "outputMode") === "DestinationRouting" ? "Send To Destination" : "Normalize Only";
                const ackMode = getString(policy, "AcknowledgementMode", "acknowledgementMode") || "NotConfigured";
                return (
                  <article key={getId(policy) || `${scopeType}-${getInboundEdiPolicyScopeId(policy)}`} className="resource-workspace-configuration-linked-row resource-workspace-entity-object-card resource-workspace-inbound-edi-partner-scope-row">
                    <span className="resource-workspace-configuration-linked-copy resource-workspace-entity-object-card__copy">
                      <strong>{scopeName}</strong>
                      <small>{getInboundEdiPartnerScopeTypeLabel(scopeType)} scope policy</small>
                    </span>
                    <dl className="resource-workspace-entity-object-card__facts">
                      <div>
                        <dt>Routing</dt>
                        <dd>{outputMode}</dd>
                      </div>
                      <div>
                        <dt>Acknowledgement</dt>
                        <dd>{formatDisplayLabel(ackMode)}</dd>
                      </div>
                    </dl>
                    <span className="resource-workspace-entity-object-card__actions">
                      <ButtonControl
                        type="standard-small"
                        title={`Edit policy for ${scopeName}`}
                        aria-label={`Edit policy for ${scopeName}`}
                        disabled={saveState.busy}
                        onClick={() => openPolicyEditor(policy)}
                      >
                        <IconControl iconKey="edit" title={`Edit policy for ${scopeName}`} width="0.95rem" height="0.95rem" />
                      </ButtonControl>
                      <ButtonControl
                        className="resource-workspace-row-action--danger"
                        type="standard-small"
                        title={`Remove ${scopeName}`}
                        aria-label={`Remove ${scopeName}`}
                        disabled={saveState.busy}
                        onClick={() => removePartnerScope(policy)}
                      >
                        <IconControl iconKey="delete" title={`Remove ${scopeName}`} width="0.95rem" height="0.95rem" />
                      </ButtonControl>
                    </span>
                  </article>
                );
              })}
            </div>
          ) : (
            <ResourceStateControl compact tone="warning" title="No partner scopes assigned" message="Assign one or more configured trading partners or partner groups before production activation." />
          )}
        </div>
        <DialogControl
          controlOrigin="inbound-edi-partner-scope-info"
          title={`${ediDirection} Partner Scope Assignments`}
          openDialog={partnerInfoDialogOpen}
          okText="Ok"
          hideCancel={true}
          onOk={() => setPartnerInfoDialogOpen(false)}
          onCancel={() => setPartnerInfoDialogOpen(false)}
          onX={() => setPartnerInfoDialogOpen(false)}
        >
          <div className="resource-workspace-inbound-edi-partner-info-dialog">
            <p>
              Trading partners are configured in Trading Partner Connectivity. This page assigns existing partners or groups to this EDI document configuration.
            </p>
            <p>
              {ediDirection === "Outbound"
                ? "Open a partner scope policy to configure delivery route, acknowledgements, and diagnostics requirements for that partner or group."
                : "Open a partner scope policy to configure destination routing, acknowledgements, diagnostics, duplicate handling, rejection, error queue, and test-parse requirements for that partner or group."}
            </p>
          </div>
        </DialogControl>
        <DialogControl
          controlOrigin="inbound-edi-add-partner-scope"
          title="Add Partner Scope"
          openDialog={partnerScopeDialogOpen}
          okText="Add Partner Scope"
          cancelText="Cancel"
          disableOk={saveState.busy || partnerScopeSelectedIds.length === 0}
          loadingOk={saveState.busy}
          onOk={addPartnerScope}
          onCancel={() => setPartnerScopeDialogOpen(false)}
          onX={() => setPartnerScopeDialogOpen(false)}
        >
          <div className="resource-workspace-inbound-edi-partner-info-dialog">
            <p>Select the configured trading partner or partner group to assign to this EDI document flow.</p>
            <FieldControl
              value={partnerScopeDraft.Mode}
              onChange={(value) => {
                setPartnerScopeSelectedIds([]);
                setPartnerScopeDraft({ Mode: value || "Partner", Id: "" });
              }}
              fieldMetadata={buildSelectListField("PartnerScopeDialogMode", "Scope Type", INBOUND_EDI_PARTNER_MODE_OPTIONS.filter((option) => option.Value !== "Unassigned"), "Scope type", "Scope type")}
            />
            <CardGridControl
              rows={pickerRows}
              metadata={INBOUND_EDI_PARTNER_SCOPE_SELECTOR_METADATA}
              cardFields={INBOUND_EDI_PARTNER_SCOPE_SELECTOR_CARD_FIELDS}
              rowKey={(row) => row.PartnerScopeId}
              selectedRowKeys={partnerScopeSelectedIds}
              onSelectedRowKeysChange={(nextKeys) => {
                const normalizedKeys = (Array.isArray(nextKeys) ? nextKeys : [])
                  .map((key) => String(key || ""))
                  .filter(Boolean);
                setPartnerScopeSelectedIds(normalizedKeys);
                setPartnerScopeDraft((previous) => ({ ...previous, Id: normalizedKeys[0] || "" }));
              }}
              selectable
              multiSelect
              searchable
              activeFilterEnabled={false}
              disableInlineEdit
              disableCardClickEdit
              minCardWidth="14rem"
              emptyContent={partnerScopeDraft.Mode === "PartnerGroup" ? "No unassigned trading partner groups are available." : "No unassigned trading partners are available."}
              cardTitleResolver={(row) => row.PartnerScopeName}
            />
          </div>
        </DialogControl>
        {editingPolicy && policyMetadataState.error ? (
          <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">
            {policyMetadataState.error}
          </div>
        ) : null}
        <PolicyFieldRepairDialog
          open={Boolean(policyRepair)}
          item={policyRepair?.policy}
          itemManager={policyManager}
          fieldName={policyRepair?.fieldName}
          fieldMetadata={policyRepair?.fieldMetadata}
          title={policyRepair?.title}
          description={policyRepair?.description}
          saveButtonText="Save Fix"
          onSave={savePolicy}
          onClose={closePolicyRepairDialog}
        />
        <DialogControl
          controlOrigin="inbound-edi-test-parse"
          title={`Run Test Parse${testParseDialog?.policy ? `: ${getInboundEdiPolicyScopeName(testParseDialog.policy, tradingPartners, tradingPartnerGroups)}` : ""}`}
          openDialog={Boolean(testParseDialog)}
          okText={testParseState.busy ? "Testing..." : "Run Test"}
          cancelText="Cancel"
          disableOk={testParseState.busy || !testParseDialog?.policy}
          loadingOk={testParseState.busy}
          onOk={runTestParseValidation}
          onCancel={closeTestParseDialog}
          onX={closeTestParseDialog}
        >
          <div className="resource-workspace-inbound-edi-partner-info-dialog">
            <p>
              Run the partner-scope test parse validation for this EDI document configuration. A successful run records the latest test result and refreshes Policy Readiness.
            </p>
            {testParseState.error ? (
              <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">
                {testParseState.error}
              </div>
            ) : null}
          </div>
        </DialogControl>
        <SidePanelFormControl
          key={`${getId(editingPolicy) || "edi-partner-policy"}-${editingPolicy ? "open" : "closed"}`}
          open={Boolean(editingPolicy)}
          title={`Edit Partner Policy: ${editingPolicy ? getInboundEdiPolicyScopeName(editingPolicy, tradingPartners, tradingPartnerGroups) : ""}`}
          metadata={policyMetadata || { Name: "EdiPartnerScopePolicy", DisplayName: "EDI Partner Scope Policy", Fields: [] }}
          formFields={getArray(policyMetadata, "Fields", "fields")}
          itemData={editingPolicy}
          setItemData={setEditingPolicy}
          setOpenEditSidePanel={(isOpen) => {
            if (!isOpen) setEditingPolicy(null);
          }}
          onUpdateSidePanel={() => {}}
          onCancel={() => setEditingPolicy(null)}
          onSave={savePolicy}
          saveButtonText={saveState.busy ? "Saving..." : policyMetadataState.loading ? "Loading..." : "Save"}
          viewOnly={!policyMetadata || policyMetadataState.loading || Boolean(policyMetadataState.error)}
        />
      </section>

      <section className="resource-workspace-panel resource-workspace-linked-item-section">
        <div className="resource-workspace-section-title resource-workspace-section-title--with-action">
          <div>
            <h2>Policy Readiness</h2>
            <p>Current blockers and warnings related to this policy area.</p>
          </div>
          <div className="resource-workspace-section-actions">
            <ReadinessStatusFilter
              value={policyReadinessFilter}
              onChange={setPolicyReadinessFilter}
              counts={policyReadinessCounts}
            />
          </div>
        </div>
        {filteredPolicyReadinessRows.length > 0 ? (
          <div className="resource-workspace-record-list">
            {filteredPolicyReadinessRows.map((row, index) => {
              const issue = row.issue;
              const presentationSeverity = getIssuePresentationSeverity(issue);
              const section = getString(issue, "SectionStepKey", "sectionStepKey");
              const isDone = getSeverityIconKey(presentationSeverity) === "check";
              const canRunTestParse = Boolean(
                !row.synthetic
                && !isDone
                && isPolicyTestParseIssue(issue)
                && (row.policy || getPolicyForIssue(issue))
              );
              const canRepairIssue = Boolean(
                !row.synthetic
                &&
                !isDone
                && !isPolicyTestParseIssue(issue)
                && getPolicyRepairDefinitionForIssue(issue)
                && (row.policy || getPolicyForIssue(issue))
              );
              const isActionable = canRunTestParse || canRepairIssue;
              const actionLabel = getPolicyReadinessActionLabel({ issue, isDone, canRepairIssue, section });
              const openRowAction = () => {
                if (canRunTestParse) {
                  openTestParseDialog(issue);
                  return;
                }
                if (canRepairIssue) openPolicyRepairDialog(issue);
              };
              return (
                <ReadinessIssueRow
                  key={row.key || `${getString(issue, "Code", "code")}-${index}`}
                  issue={issue}
                  actionLabel={actionLabel}
                  isActionable={isActionable}
                  actionTitle={canRunTestParse ? "Run test parse validation" : canRepairIssue ? "Open quick policy fix" : undefined}
                  onAction={openRowAction}
                />
              );
            })}
          </div>
        ) : (
          <ResourceStateControl compact message={policyReadinessFilter === READINESS_FILTER_READY ? "No ready policy checks are currently reported." : "No not-ready policy checks are currently reported."} />
        )}
      </section>
    </div>
  );
};

const InboundEdiMappingScreen = ({
  configuration,
  schemaWorkspaceState,
  entityObjectOperations = [],
  onRunEntityObjectOperation,
  onManageSourceObject
}) => {
  const entityObjects = getArray(schemaWorkspaceState, "entityObjects");
  const sourceObjects = getArray(schemaWorkspaceState, "sourceObjects");
  const loading = schemaWorkspaceState?.loading;
  const error = schemaWorkspaceState?.error;
  const schema = schemaWorkspaceState?.schema;
  const sourceObjectById = useMemo(() => {
    const lookup = new Map();
    sourceObjects.forEach((sourceObject) => {
      [getId(sourceObject), getString(sourceObject, "SourceObjectId", "sourceObjectId", "UniqueId", "uniqueId")]
        .map(normalizeCompare)
        .filter(Boolean)
        .forEach((key) => lookup.set(key, sourceObject));
    });
    return lookup;
  }, [sourceObjects]);

  if (loading) {
    return <ResourceStateControl loading title="Loading Mapping" loadingText="Loading Entity Schema mapping details." />;
  }

  if (error) {
    return <ResourceStateControl tone="error" title="Mapping unavailable" message={error} />;
  }

  return (
    <div className="resource-workspace-linked-item-layout resource-workspace-linked-item-layout--catalog" aria-label="Inbound EDI mapping">
      <div className="resource-workspace-linked-item-main">
        <section className="resource-workspace-panel resource-workspace-linked-item-section">
          <div className="resource-workspace-section-title">
            <h2>Mapping</h2>
            <p>Map inbound EDI Source Objects to Entity Schema objects and source-object-of-record assignments.</p>
          </div>
          <dl className="resource-workspace-facts resource-workspace-linked-item-metrics">
            <Fact label="Entity Schema" value={getDisplayName(schema, "Entity Schema")} />
            <Fact label="Entity Objects" value={entityObjects.length.toLocaleString()} />
            <Fact label="Mapped Source Objects" value={sourceObjects.length.toLocaleString()} />
            <Fact label="Configuration" value={getDisplayName(configuration, "Resource configuration")} />
          </dl>
        </section>
      </div>

      <ResourceCatalogPanel
        title="Entity Object Mapping"
        summary={`${entityObjects.length.toLocaleString()} objects`}
        activeTabKey="entity-objects"
        tabs={[
          {
            key: "entity-objects",
            title: "Entity Objects",
            content: (
              <ResourceCatalogListTab
                items={entityObjects}
                getItemKey={(entityObject, index) => getString(entityObject, "EntityObjectId", "entityObjectId", "_id", "id") || `entity-object-${index}`}
                ariaLabel="Entity object mappings"
                emptyMessage="No Entity Objects exist for the linked schema."
                emptyFilteredMessage="No Entity Objects match these filters."
                renderItem={(entityObject) => {
                  const entityName = getDisplayName(entityObject, getString(entityObject, "ObjectName", "objectName") || "Entity Object");
                  const sourceObjectId = getEntityObjectSourceObjectId(entityObject);
                  const sourceObject = sourceObjectById.get(normalizeCompare(sourceObjectId));
                  const sourceObjectName = sourceObject
                    ? getDisplayName(sourceObject, getString(sourceObject, "SystemName", "systemName") || "Source Object")
                    : "No source object of record";
                  return (
                    <ResourceCatalogRow
                      title={entityName}
                      subtitle={sourceObjectName}
                      titleAttribute={entityName}
                      metaItems={[
                        { key: "status", className: "resource-catalog-row__meta-item--status", content: sourceObject ? "SOR assigned" : "SOR missing" },
                        { key: "fields", content: `${getArray(entityObject, "Fields", "fields").length.toLocaleString()} fields` }
                      ]}
                      actions={(
                        <>
                          {sourceObject ? (
                            <ButtonControl
                              type="standard-small"
                              title={`Open source object ${sourceObjectName}`}
                              aria-label={`Open source object ${sourceObjectName}`}
                              onClick={() => onManageSourceObject?.(sourceObject)}
                            >
                              <IconControl iconKey="sourcedata" title={`Open source object ${sourceObjectName}`} width="0.95rem" height="0.95rem" />
                            </ButtonControl>
                          ) : null}
                          {entityObjectOperations.length > 0 ? (
                            <ButtonControl
                              type="standard-small"
                              title={`Open mapping for ${entityName}`}
                              aria-label={`Open mapping for ${entityName}`}
                              onClick={() => onRunEntityObjectOperation?.(entityObject, entityObjectOperations[0])}
                            >
                              <IconControl iconKey="settings" title={`Open mapping for ${entityName}`} width="0.95rem" height="0.95rem" />
                            </ButtonControl>
                          ) : null}
                        </>
                      )}
                    />
                  );
                }}
              />
            )
          }
        ]}
        onTabChange={() => {}}
        idPrefix="resource-inbound-edi-mapping-tabs"
        ariaLabel="Inbound EDI mapping"
      />
    </div>
  );
};

const SelectedResourceConfigurationManageScreen = ({
  configuration,
  resource,
  resourceLogs,
  resourceEvents,
  productIcon,
  canOpenConfigurationSetup,
  hasRemainingLinkedEndpointAllowance,
  onAddLinkedEndpoint,
  onSelectConfigurationScreen,
  onRefreshConfigurations,
  logManager,
  eventManager
}) => {
  const configurationId = getId(configuration);
  const configurationName = getDisplayName(configuration, "Resource configuration");
  const configurationDescription = getString(configuration, "Description", "description");
  const configurationNotes = getString(configuration, "Notes", "notes");
  const formattedConfigurationNotes = formatConfigurationNotes(configurationNotes);
  const linkedRecords = [...getConfigurationLinkedRecords(configuration)].sort((left, right) =>
    Number(isClientMcpServerLinkedRecord(right)) - Number(isClientMcpServerLinkedRecord(left)));
  const usageSnapshot = getConfigurationUsageSnapshot(configuration);
  const usageIsUnlimited = getBoolean(usageSnapshot, "IsUnlimited", "isUnlimited");
  const usageRemaining = usageIsUnlimited
    ? "Unlimited"
    : formatNumber(getNumber(usageSnapshot, "RemainingUnits", "remainingUnits"));
  const usageApplied = formatNumber(getNumber(usageSnapshot, "AppliedUnits", "appliedUnits"));
  const usageBillable = formatNumber(getNumber(usageSnapshot, "BillableUnits", "billableUnits"));
  const usageTotalAmount = formatNumber(getNumber(usageSnapshot, "TotalAmount", "totalAmount"));
  const usageCurrency = getString(usageSnapshot, "CurrencyCode", "currencyCode") || "USD";
  const endpointDefaults = getDestinationResourceDefaults(resource?.productResource);
  const linkedEndpointTypeLabel = getSchemaApiLinkedRecordTypeLabel({}, resource);
  const showAddLinkedEndpoint = Boolean(endpointDefaults?.isEndpoint && linkedEndpointTypeLabel !== "Endpoint");
  const isSystemManagedClientMcpServer = isClientMcpServerResource(resource?.productResource, resource, configuration);

  if (isEdiResourceConfiguration(resource?.productResource, resource, configuration)) {
    return (
      <InboundEdiReadinessDashboard
        configuration={configuration}
        selectedResource={resource}
        productIcon={productIcon}
        onSelectConfigurationScreen={onSelectConfigurationScreen}
        onRefresh={onRefreshConfigurations}
      />
    );
  }

  return (
    <div className="resource-workspace-configuration-manage-grid" aria-label={`${configurationName} management`}>
      <div className="resource-workspace-configuration-manage-column">
        <section className="resource-workspace-panel resource-workspace-configuration-manage-section">
          <div className="resource-workspace-section-title">
            <h2>Information</h2>
          </div>
          <div className="resource-workspace-configuration-information-grid">
            <dl className="resource-workspace-facts resource-workspace-configuration-summary-facts">
              <Fact label="Display Name" value={configurationName} />
              <Fact label="Status" value={getConfigurationStatusLabel(configuration)} />
            </dl>
            <div className="resource-workspace-configuration-notes">
              <span>Notes / Description</span>
              <div
                className="resource-workspace-configuration-notes-scroll"
                role="region"
                tabIndex={0}
                aria-label={`${configurationName} notes and description`}
              >
                {configurationDescription
                  ? <p>{configurationDescription}</p>
                  : renderConfigurationNoteParagraphs(formattedConfigurationNotes, "No notes or description have been added.")}
                {configurationDescription && formattedConfigurationNotes.length > 0 ? (
                  <>
                    <span>Notes</span>
                    {renderConfigurationNoteParagraphs(formattedConfigurationNotes, "")}
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </section>

        {!isSystemManagedClientMcpServer ? <section className="resource-workspace-panel resource-workspace-configuration-manage-section">
          <div className={`resource-workspace-section-title${showAddLinkedEndpoint ? " resource-workspace-section-title--with-action" : ""}`}>
            <div>
              <h2>Linked Resources</h2>
              <p>Resources created or connected by this configuration.</p>
            </div>
            {showAddLinkedEndpoint ? (
              <div className="resource-workspace-section-actions">
                <ButtonControl
                  type="icon"
                  className="resource-workspace-configuration-shell-action-button"
                  disabled={!canOpenConfigurationSetup || !hasRemainingLinkedEndpointAllowance}
                  onClick={onAddLinkedEndpoint}
                  title={hasRemainingLinkedEndpointAllowance ? `Add linked ${linkedEndpointTypeLabel}` : `${linkedEndpointTypeLabel} allowance exhausted`}
                  aria-label={`Add linked ${linkedEndpointTypeLabel}`}
                >
                  <IconControl iconKey="add" title={`Add linked ${linkedEndpointTypeLabel}`} width="1rem" height="1rem" />
                </ButtonControl>
              </div>
            ) : null}
          </div>
          {linkedRecords.length > 0 ? (
            <div className="resource-workspace-configuration-linked-list">
              {linkedRecords.map((record, index) => {
                const recordKey = `${getString(record, "ItemName", "itemName")}-${getString(record, "ItemId", "itemId")}-${index}`;
                const recordScreenKey = getLinkedRecordScreenKey(record, configuration);
                const recordIconKey = getLinkedRecordIconKey(record, configuration, productIcon);
                const recordTypeLabel = getLinkedRecordTypeLabel(record, resource);
                const recordDescription = getLinkedRecordDescription(record);
                const recordTitle = recordScreenKey
                  ? `Open ${recordTypeLabel} screen`
                  : "No management screen is configured for this linked resource.";

                return (
                  <article key={recordKey} className="resource-workspace-configuration-linked-row">
                    <span className="resource-workspace-resource-icon" aria-hidden="true">
                      <IconControl iconKey={recordIconKey} width="2.75rem" height="2.75rem" />
                    </span>
                    <span className="resource-workspace-configuration-linked-copy">
                      <span>{recordTypeLabel}</span>
                      <strong>{getLinkedRecordDisplayName(record)}</strong>
                      {recordDescription ? (
                        <small>{recordDescription}</small>
                      ) : null}
                    </span>
                    {isClientMcpServerLinkedRecord(record) ? (
                      <span className="resource-workspace-configuration-linked-actions">
                        <ButtonControl type="standard-small" title="Open Client MCP Server overview" aria-label="Open Client MCP Server overview" onClick={() => onSelectConfigurationScreen(configurationId, MCP_TOOLS_OVERVIEW_SCREEN_KEY, record)}>
                          <IconControl iconKey="settings" title="Open Client MCP Server overview" width="1.5rem" height="1.5rem" />
                        </ButtonControl>
                      </span>
                    ) : (
                      <ButtonControl
                        type="standard-small"
                        disabled={!recordScreenKey}
                        title={recordTitle}
                        aria-label={recordTitle}
                        onClick={() => onSelectConfigurationScreen(configurationId, recordScreenKey, record)}
                      >
                        <IconControl iconKey="settings" title={recordTitle} width="0.95rem" height="0.95rem" />
                      </ButtonControl>
                    )}
                  </article>
                );
              })}
            </div>
          ) : (
            <ResourceStateControl
              compact
              message="Linked resources will appear here after setup creates them."
            />
          )}
        </section> : null}
      </div>

      <div className="resource-workspace-configuration-manage-column">
        {!isSystemManagedClientMcpServer ? <section className="resource-workspace-panel resource-workspace-configuration-manage-section">
          <div className="resource-workspace-section-title">
            <h2>Analytics</h2>
            <p>Usage and configuration signals for this configuration.</p>
          </div>
          <div className="resource-workspace-metric-grid resource-workspace-configuration-analytics-grid">
            <Metric label="Linked Resources" value={linkedRecords.length.toLocaleString()} />
            <Metric label="Applied Usage" value={usageApplied} />
            <Metric label="Remaining" value={usageRemaining} />
            <Metric label="Billable Units" value={usageBillable} />
          </div>
          {getString(usageSnapshot, "PriceSummary", "priceSummary") ? (
            <p className="resource-workspace-configuration-analytics-summary">
              {getString(usageSnapshot, "PriceSummary", "priceSummary")}
            </p>
          ) : (
            <p className="resource-workspace-configuration-analytics-summary">
              Estimated total: {usageTotalAmount} {usageCurrency}
            </p>
          )}
        </section> : null}

        <section className="resource-workspace-panel resource-workspace-configuration-manage-section">
          <div className="resource-workspace-section-title">
            <h2>Events / Logs</h2>
            <p>Latest five logs and five events for this configuration.</p>
          </div>
          <ResourceConfigurationActivityFeed
            configuration={configuration}
            resourceLogs={resourceLogs}
            resourceEvents={resourceEvents}
            logManager={logManager}
            eventManager={eventManager}
          />
        </section>
      </div>
    </div>
  );
};

const ResourceConfigurationActivityFeed = ({
  configuration,
  resourceLogs,
  resourceEvents,
  logManager,
  eventManager
}) => {
  const configurationId = getId(configuration);
  const selectedClientResourceId = getString(configuration, "ClientResourceId", "clientResourceId");
  const logManagerRef = useRef(logManager);
  const eventManagerRef = useRef(eventManager);
  const hasActivityManagers = Boolean(logManager?.Query || eventManager?.Query);
  const fallbackLogs = useMemo(
    () => getConfigurationScopedLogs(resourceLogs, configuration).slice(0, CONFIGURATION_ACTIVITY_ITEM_LIMIT),
    [configuration, resourceLogs]
  );
  const fallbackEvents = useMemo(
    () => getConfigurationScopedEvents(resourceEvents, configuration).slice(0, CONFIGURATION_ACTIVITY_ITEM_LIMIT),
    [configuration, resourceEvents]
  );
  const fallbackEntries = useMemo(
    () => getConfigurationActivityEntries(configuration, fallbackLogs, fallbackEvents),
    [configuration, fallbackEvents, fallbackLogs]
  );
  const [state, setState] = useState({
    loading: Boolean(configurationId && (logManager?.Query || eventManager?.Query)),
    error: "",
    entries: fallbackEntries
  });

  useEffect(() => {
    logManagerRef.current = logManager;
  }, [logManager]);

  useEffect(() => {
    eventManagerRef.current = eventManager;
  }, [eventManager]);

  useEffect(() => {
    let mounted = true;

    const loadActivity = async () => {
      const activeLogManager = logManagerRef.current;
      const activeEventManager = eventManagerRef.current;

      if (!configurationId) {
        setState({ loading: false, error: "", entries: fallbackEntries });
        return;
      }

      if (!activeLogManager?.Query && !activeEventManager?.Query) {
        setState({ loading: false, error: "", entries: fallbackEntries });
        return;
      }

      setState((previous) => ({
        ...previous,
        loading: true,
        error: "",
        entries: previous.entries.length > 0 ? previous.entries : fallbackEntries
      }));

      const [logsResult, eventsResult] = await Promise.allSettled([
        activeLogManager?.Query
          ? queryConfigurationScopedRecords(activeLogManager, selectedClientResourceId, configurationId)
          : Promise.resolve(fallbackLogs),
        activeEventManager?.Query
          ? queryConfigurationScopedRecords(activeEventManager, selectedClientResourceId, configurationId)
          : Promise.resolve(fallbackEvents)
      ]);

      if (!mounted) return;

      const nextLogs = logsResult.status === "fulfilled" ? logsResult.value : fallbackLogs;
      const nextEvents = eventsResult.status === "fulfilled" ? eventsResult.value : fallbackEvents;
      const nextEntries = getConfigurationActivityEntries(configuration, nextLogs, nextEvents);
      const loadError = [logsResult, eventsResult]
        .find((result) => result.status === "rejected")
        ?.reason;

      setState({
        loading: false,
        error: nextEntries.length > 0
          ? ""
          : getResultMessageText(loadError?.message || loadError),
        entries: nextEntries
      });
    };

    loadActivity();

    return () => {
      mounted = false;
    };
  }, [
    configuration,
    configurationId,
    fallbackEntries,
    fallbackEvents,
    fallbackLogs,
    hasActivityManagers,
    selectedClientResourceId
  ]);

  if (state.loading && state.entries.length === 0) {
    return <ResourceStateControl compact loading loadingText="Loading configuration activity..." />;
  }

  if (state.error && state.entries.length === 0) {
    return (
      <ResourceStateControl
        compact
        tone="error"
        title="Activity could not be loaded"
        message={state.error}
      />
    );
  }

  if (state.entries.length === 0) {
    return (
      <ResourceStateControl
        compact
        message="No events or logs are available for this configuration yet."
      />
    );
  }

  return (
    <div className="resource-workspace-configuration-event-list" aria-label="Configuration events and logs">
      {state.entries.map((entry) => (
        <article key={entry.id} className="resource-workspace-configuration-event-row">
          <span>{entry.type}</span>
          <strong>{entry.title}</strong>
          {entry.description ? <p>{entry.description}</p> : null}
          {entry.timestamp ? <small>{formatDateTime(entry.timestamp)}</small> : null}
        </article>
      ))}
    </div>
  );
};

const ConfigurationSelectionRows = ({
  configurations,
  resource,
  productIcon,
  canOpenConfigurationSetup,
  onOpenConfigurationSetup,
  onSelectConfigurationScreen,
  onDeleteConfiguration
}) => {
  const [openScreenMenu, setOpenScreenMenu] = useState(false);
  const [screenMenuStyle, setScreenMenuStyle] = useState(null);

  const openConfigurationScreenMenu = useCallback((event, configurationKey) => {
    const buttonBox = event.currentTarget?.getBoundingClientRect?.();
    setOpenScreenMenu((current) => current === configurationKey ? false : configurationKey);
    setScreenMenuStyle({
      left: Math.max(8, (buttonBox?.right ?? 0) - 112),
      top: (buttonBox?.bottom ?? 0) + 6
    });
  }, []);

  const selectScreenFromMenu = useCallback((configurationId, screenKey) => {
    setOpenScreenMenu(false);
    onSelectConfigurationScreen(configurationId, screenKey);
  }, [onSelectConfigurationScreen]);

  const openPrimaryConfigurationAction = useCallback((configuration) => {
    const configurationId = getId(configuration);
    if (!configurationId) return;

    if (isConfigurationConfiguring(configuration)) {
      if (canOpenConfigurationSetup) {
        onOpenConfigurationSetup(configuration);
      }
      return;
    }

    onSelectConfigurationScreen(configurationId, "manage");
  }, [canOpenConfigurationSetup, onOpenConfigurationSetup, onSelectConfigurationScreen]);

  const handleConfigurationRowKeyDown = useCallback((event, configuration) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    openPrimaryConfigurationAction(configuration);
  }, [openPrimaryConfigurationAction]);

  return (
    <div className="resource-workspace-resource-list resource-workspace-configuration-select-list">
      {configurations.map((configuration) => {
        const configurationKey = getId(configuration) || getDisplayName(configuration);
        const configurationId = getId(configuration);
        const configurationStatus = getConfigurationStatusLabel(configuration);
        const configurationName = getDisplayName(configuration, "Resource configuration");
        const configurationIsConfiguring = isConfigurationConfiguring(configuration);
        const manageActionTitle = configurationIsConfiguring
          ? `Continue setup for ${configurationName}`
          : `Manage ${configurationName}`;
        const rowActionTitle = configurationIsConfiguring
          ? `Open setup for ${configurationName}`
          : `Open ${configurationName} management`;
        const rowActionDisabled = !configurationId || (configurationIsConfiguring && !canOpenConfigurationSetup);
        const isSystemManagedClientMcpServer = isClientMcpServerResource(configuration, resource);

        return (
          <article
            key={configurationKey}
            className="resource-workspace-resource-row resource-workspace-configuration-select-row"
            role="button"
            tabIndex={rowActionDisabled ? -1 : 0}
            aria-label={rowActionTitle}
            aria-disabled={rowActionDisabled ? "true" : undefined}
            onClick={() => {
              if (!rowActionDisabled) {
                openPrimaryConfigurationAction(configuration);
              }
            }}
            onKeyDown={(event) => handleConfigurationRowKeyDown(event, configuration)}
          >
            <span className="resource-workspace-resource-icon" aria-hidden="true">
              <ResourceWorkspaceIcon
                iconKey={productIcon || RESOURCE_FALLBACK_ICON}
                iconPath={getResourceWorkspaceIconPath(resource)}
                label={configurationName}
                preferImage={resourceUsesProductIcon(resource)}
                width="3.25rem"
                height="3.25rem"
              />
            </span>
            <span>
              <strong>{configurationName}</strong>
              <span className={getConfigurationStatusClassName(configuration)}>{configurationStatus}</span>
            </span>
            <span
              className="resource-workspace-configuration-select-actions"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <ButtonControl
                type="standard-small"
                disabled={!configurationId || (configurationIsConfiguring && !canOpenConfigurationSetup)}
                title={manageActionTitle}
                aria-label={manageActionTitle}
                onClick={() => openPrimaryConfigurationAction(configuration)}
              >
                <IconControl iconKey="settings" title={manageActionTitle} width="0.95rem" height="0.95rem" />
              </ButtonControl>
              {!isSystemManagedClientMcpServer ? (
                <ButtonControl
                  type="standard-small"
                  className="resource-workspace-configuration-delete-button"
                  disabled={!configurationId}
                  title={`Delete ${configurationName}`}
                  aria-label={`Delete ${configurationName}`}
                  onClick={() => onDeleteConfiguration(configuration)}
                >
                  <IconControl iconKey="delete" title={`Delete ${configurationName}`} width="0.95rem" height="0.95rem" />
                </ButtonControl>
              ) : null}
              <ButtonControl
                type="standard-small"
                disabled={!configurationId || configurationIsConfiguring}
                title={`Open configuration screens for ${configurationName}`}
                aria-label={`Open configuration screens for ${configurationName}`}
                onClick={(event) => openConfigurationScreenMenu(event, configurationKey)}
              >
                <IconControl iconKey="viewmore" title={`Open configuration screens for ${configurationName}`} width="0.95rem" height="0.95rem" />
              </ButtonControl>
              {!configurationIsConfiguring && openScreenMenu === configurationKey ? (
                <ContextMenuControl
                  openContextMenu={openScreenMenu === configurationKey}
                  setOpenContextMenu={(nextOpen) => setOpenScreenMenu(nextOpen ? configurationKey : false)}
                  contextMenuStyle={screenMenuStyle}
                >
                  <div
                    className="resource-workspace-configuration-screen-menu"
                    role="menu"
                    aria-label={`Configuration screens for ${configurationName}`}
                  >
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => selectScreenFromMenu(configurationId, "integration")}
                    >
                      Integration
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => selectScreenFromMenu(configurationId, "source-system")}
                    >
                      Source System
                    </button>
                  </div>
                </ContextMenuControl>
              ) : null}
            </span>
          </article>
        );
      })}
    </div>
  );
};

const ResourceConfigurationEditPanel = ({
  open,
  configuration,
  state,
  onSave,
  onCancel,
  onOpenChange,
  onChangeConfiguration
}) => {
  const configurationName = getDisplayName(configuration, "Resource configuration");

  return (
    <SidePanelFormControl
      key={`${getId(configuration) || "resource-configuration"}-${open ? "open" : "closed"}`}
      open={open}
      title={`Edit: ${configurationName}`}
      metadata={RESOURCE_CONFIGURATION_EDIT_PANEL_METADATA}
      formFields={RESOURCE_CONFIGURATION_EDIT_PANEL_METADATA.Fields}
      itemData={configuration}
      onSave={onSave}
      onCancel={onCancel}
      onUpdateSidePanel={() => {}}
      saveButtonText={state?.busyAction?.startsWith("edit:") ? "Saving..." : "Save"}
      setOpenEditSidePanel={(value) => {
        onOpenChange(Boolean(value));
        if (!value) {
          onChangeConfiguration(null);
        }
      }}
      setItemData={onChangeConfiguration}
      viewOnly={false}
    />
  );
};

const ResourceConfigurationImportDialog = ({
  dialog,
  schemaOptions,
  offerRows = [],
  onChange,
  onCancel,
  onConfirm
}) => {
  const open = Boolean(dialog?.open);
  const busy = Boolean(dialog?.busy);
  const loadingSchemas = Boolean(dialog?.loadingSchemas);
  const hasSchemaOptions = schemaOptions.some((option) => option.Value);
  const hasOfferOptions = offerRows.some((offer) => offer.ClientProductOfferId);
  const disableOk = busy
    || loadingSchemas
    || !dialog?.schemaId
    || !dialog?.clientProductOfferId
    || !hasSchemaOptions
    || !hasOfferOptions;

  return (
    <DialogControl
      title="Import Resource Configuration"
      openDialog={open}
      okText={busy ? "Importing..." : "Import"}
      cancelText="Cancel"
      disableOk={disableOk}
      loadingOk={busy}
      onCancel={onCancel}
      onOk={onConfirm}
      className="resource-workspace-import-dialog-control"
    >
      <div className="resource-workspace-linked-record-add-dialog">
        {dialog?.error ? (
          <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">
            {dialog.error}
          </div>
        ) : null}
        <FieldControl
          fieldMetadata={buildSearchSelectListField("schemaId", "Entity Schema", schemaOptions, "Select Entity Schema")}
          value={dialog?.schemaId || ""}
          disabled={busy || loadingSchemas || !hasSchemaOptions}
          onChange={(value) => onChange({ schemaId: value })}
        />
        <ResourceImportOfferAllowanceSelector
          rows={offerRows}
          value={dialog?.clientProductOfferId || ""}
          disabled={busy || !hasOfferOptions}
          onChange={(value) => onChange({ clientProductOfferId: value })}
        />
        {loadingSchemas ? (
          <div className="resource-workspace-access-note" role="status">Loading Entity Schemas...</div>
        ) : null}
        {!loadingSchemas && !hasSchemaOptions ? (
          <div className="resource-workspace-access-note" role="status">No enabled Entity Schemas with a Published Primary Version are available.</div>
        ) : null}
        {!hasOfferOptions ? (
          <div className="resource-workspace-access-note" role="status">No Product Offer allowance with remaining capacity is available.</div>
        ) : null}
      </div>
    </DialogControl>
  );
};

const ResourceImportOfferAllowanceSelector = ({
  rows = [],
  value,
  disabled,
  onChange
}) => (
  <div className="field-control__wrapper error-after resource-workspace-import-offer-selector" role="group" aria-label="Product Offer allowance" field-name="clientProductOfferId">
    <div className="title-header">
      <div className="title">
        <div>Product Offer allowance</div>
      </div>
    </div>
    <div className="content child">
      <CardGridControl
        className="resource-workspace-import-offer-selector__grid"
        rows={rows}
        cardFields={RESOURCE_IMPORT_OFFER_CARD_FIELDS}
        rowKey={(row) => row.ClientProductOfferId}
        selectedRowKeys={value ? [value] : []}
        onSelectedRowKeysChange={(nextKeys) => onChange?.(nextKeys[0] || value || "")}
        selectable
        searchable={rows.length > 3}
        activeFilterEnabled={false}
        disabled={disabled}
        emptyContent="No Product Offer allowance with remaining capacity is available."
        minCardWidth="100%"
        maxCardWidth="100%"
        maxColumns={1}
      />
    </div>
  </div>
);

const ResourceIntegrationEditPanel = ({
  open,
  integration,
  metadata,
  state,
  onSave,
  onCancel,
  onOpenChange,
  onChangeIntegration
}) => {
  const integrationName = getDisplayName(integration, "Integration");
  const formFields = Array.isArray(metadata?.Fields) ? metadata.Fields : [];

  if (!open) {
    return null;
  }

  return (
    <SidePanelFormControl
      key={`${getId(integration) || "integration"}-${open ? "open" : "closed"}`}
      open={open}
      title={`Edit: ${integrationName}`}
      metadata={metadata}
      formFields={formFields}
      itemData={integration}
      onSave={onSave}
      onCancel={onCancel}
      onUpdateSidePanel={() => {}}
      saveButtonText={state?.busyAction?.startsWith("integration-edit:") ? "Saving..." : "Save"}
      setOpenEditSidePanel={(value) => {
        onOpenChange(Boolean(value));
        if (!value) {
          onChangeIntegration(null);
        }
      }}
      setItemData={onChangeIntegration}
      viewOnly={!metadata}
    />
  );
};

const ResourceSourceSystemEditPanel = ({
  open,
  sourceSystem,
  metadata,
  state,
  onSave,
  onCancel,
  onOpenChange,
  onChangeSourceSystem
}) => {
  const sourceSystemName = getDisplayName(sourceSystem, "Source System");
  const formFields = Array.isArray(metadata?.Fields) ? metadata.Fields : [];

  if (!open) {
    return null;
  }

  return (
    <SidePanelFormControl
      key={`${getId(sourceSystem) || "source-system"}-${open ? "open" : "closed"}`}
      open={open}
      title={`Edit: ${sourceSystemName}`}
      metadata={metadata}
      formFields={formFields}
      itemData={sourceSystem}
      onSave={onSave}
      onCancel={onCancel}
      onUpdateSidePanel={() => {}}
      saveButtonText={state?.busyAction?.startsWith("source-system-edit:") ? "Saving..." : "Save"}
      setOpenEditSidePanel={(value) => {
        onOpenChange(Boolean(value));
        if (!value) {
          onChangeSourceSystem(null);
        }
      }}
      setItemData={onChangeSourceSystem}
      viewOnly={!metadata}
    />
  );
};

const ResourceSchemaEditPanel = ({
  open,
  schema,
  metadata,
  state,
  onSave,
  onCancel,
  onOpenChange,
  onChangeSchema
}) => {
  const schemaName = getDisplayName(schema, "Entity Schema");
  const formFields = Array.isArray(metadata?.Fields) ? metadata.Fields : [];

  if (!open) {
    return null;
  }

  return (
    <SidePanelFormControl
      key={`${getId(schema) || "schema"}-${open ? "open" : "closed"}`}
      open={open}
      title={`Edit: ${schemaName}`}
      metadata={metadata}
      formFields={formFields}
      itemData={schema}
      onSave={onSave}
      onCancel={onCancel}
      onUpdateSidePanel={() => {}}
      saveButtonText={state?.busyAction?.startsWith("schema-edit:") ? "Saving..." : "Save"}
      setOpenEditSidePanel={(value) => {
        onOpenChange(Boolean(value));
        if (!value) {
          onChangeSchema(null);
        }
      }}
      setItemData={onChangeSchema}
      viewOnly={!metadata}
    />
  );
};

const ResourceSchemaVersionEditPanel = ({
  open,
  schemaVersion,
  state,
  onSave,
  onCancel,
  onOpenChange,
  onChangeSchemaVersion
}) => {
  const versionLabel = getString(schemaVersion, "Version", "version") || "Schema Version";
  const formFields = Array.isArray(SCHEMA_VERSION_EDIT_PANEL_METADATA.Fields)
    ? SCHEMA_VERSION_EDIT_PANEL_METADATA.Fields
    : [];

  if (!open) {
    return null;
  }

  return (
    <SidePanelFormControl
      key={`${getSchemaVersionId(schemaVersion) || "schema-version"}-${open ? "open" : "closed"}`}
      open={open}
      title={`Edit Schema Version: ${versionLabel}`}
      metadata={SCHEMA_VERSION_EDIT_PANEL_METADATA}
      formFields={formFields}
      itemData={schemaVersion}
      onSave={onSave}
      onCancel={onCancel}
      onUpdateSidePanel={() => {}}
      saveButtonText={state?.busyAction?.startsWith("schema-version-edit:") ? "Saving..." : "Save"}
      setOpenEditSidePanel={(value) => {
        onOpenChange(Boolean(value));
        if (!value) {
          onChangeSchemaVersion(null);
        }
      }}
      setItemData={onChangeSchemaVersion}
      viewOnly={false}
    />
  );
};

const SchemaVersionPublishDialog = ({
  dialog,
  onChangeMakePrimary,
  onCancel,
  onConfirm
}) => {
  const schemaVersion = dialog?.version;
  const versionLabel = getString(schemaVersion, "Version", "version") || "Schema Version";
  const versionTitle = getString(schemaVersion, "Title", "title") || "schema version";
  const action = dialog?.action || "publish";
  const primary = isPrimarySchemaVersion(schemaVersion, getSchemaActiveVersionId(dialog?.schema));
  const actionLabel = action === "unpublish"
    ? "Unpublish"
    : action === "make-primary"
      ? "Make Primary"
      : "Publish";
  const actionProgressLabel = action === "unpublish"
    ? "Unpublishing..."
    : action === "make-primary"
      ? "Setting Primary..."
      : "Publishing...";
  const actionCopy = action === "unpublish"
    ? primary
      ? "This is the Primary Schema Version. Unpublishing returns it to Draft and clears the Primary Version selection. The Schema will have no Primary Version until another Published Schema Version is selected."
      : "This Published Schema Version is not Primary. Unpublishing returns it to Draft so it can be edited again."
    : action === "make-primary"
      ? "This will make the selected Published Schema Version the Primary Version used by default at runtime. Other Published versions remain Published."
      : "Publishing changes this Draft to Published and locks it from editing. It becomes the Primary Version only if you select Make Primary after publishing.";

  return (
    <DialogControl
      openDialog={Boolean(schemaVersion)}
      title={`${actionLabel} Schema Version`}
      okText={dialog?.busy ? actionProgressLabel : actionLabel}
      cancelText="Cancel"
      disableOk={dialog?.busy}
      disableCancel={dialog?.busy}
      onOk={onConfirm}
      onCancel={onCancel}
      onX={onCancel}
    >
      <div className="resource-workspace-delete-dialog">
        <p>
          {actionLabel} <strong>{versionLabel}</strong> - {versionTitle}?
        </p>
        <p>{actionCopy}</p>
        {action === "publish" ? (
          <FieldControl
            fieldMetadata={{
              FieldName: "MakePrimaryOnPublish",
              DisplayName: "Make Primary after publishing",
              Description: "Use this Schema Version as the Primary Version immediately after it is Published.",
              FieldType: SystemTypes.UIFieldType.CheckBox,
              DataTypeName: "Boolean",
              Format: "BOOL",
              IsRequired: false
            }}
            value={Boolean(dialog?.makePrimary)}
            disabled={dialog?.busy}
            onChange={(value) => onChangeMakePrimary?.(Boolean(value))}
          />
        ) : null}
        {dialog?.error ? (
          <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">
            {dialog.error}
          </div>
        ) : null}
      </div>
    </DialogControl>
  );
};

const ResourceSourceTargetEditPanel = ({
  open,
  target,
  metadata,
  state,
  onSave,
  onCancel,
  onOpenChange,
  onChangeTarget
}) => {
  const targetName = getDisplayName(target, getString(target, "ObjectName", "objectName") || "Source Object Target");
  const isCreateMode = target?.__SourceTargetMode === "create";
  const formFields = Array.isArray(metadata?.Fields) ? metadata.Fields : [];

  if (!open) {
    return null;
  }

  return (
    <>
      {state?.error ? <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">{state.error}</div> : null}
      <SidePanelFormControl
        key={`${getSourceTargetIdentityKey(target || {}) || "source-target"}-${open ? "open" : "closed"}`}
        open={open}
        title={isCreateMode ? "Add Query Target" : `Edit Target: ${targetName}`}
        metadata={metadata}
        formFields={formFields}
        itemData={target}
        onSave={onSave}
        onCancel={onCancel}
        onUpdateSidePanel={() => {}}
        saveButtonText={state?.busy ? "Saving..." : "Save"}
        setOpenEditSidePanel={(value) => {
          onOpenChange(Boolean(value));
          if (!value) {
            onChangeTarget(null);
          }
        }}
        setItemData={onChangeTarget}
        viewOnly={!metadata}
      />
    </>
  );
};

const SourceTargetStatusDialog = ({
  target,
  isActive,
  busy,
  error,
  onCancel,
  onConfirm
}) => {
  const targetName = getDisplayName(target, getString(target, "ObjectName", "objectName") || "Source Object Target");
  const actionLabel = isActive ? "Activate" : "Deactivate";
  const actionVerb = isActive ? "activate" : "deactivate";
  const availabilityText = isActive
    ? "available for this Source System."
    : "unavailable for this Source System.";

  return (
    <DialogControl
      openDialog={Boolean(target)}
      title={`${actionLabel} Target`}
      okText={busy ? `${actionLabel}...` : actionLabel}
      cancelText="Cancel"
      disableOk={busy}
      disableCancel={busy}
      onOk={onConfirm}
      onCancel={onCancel}
      onX={onCancel}
    >
      <div className="resource-workspace-delete-dialog">
        <p>
          Are you sure you want to {actionVerb} <strong>{targetName}</strong>?
        </p>
        <p>
          This target will be {availabilityText}
        </p>
        {error ? (
          <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">
            {error}
          </div>
        ) : null}
      </div>
    </DialogControl>
  );
};

const SourceObjectAddDialog = ({
  open,
  operationDisplayName,
  sourceSystem,
  sourceObjects,
  targetFilterDefaults,
  selectedTargetIds,
  state,
  onChangeSelectedTargetIds,
  onCancel,
  onBack,
  onConfirm
}) => {
  const filteredTargets = useMemo(
    () => filterSourceTargetsForProductDefaults(getSourceSystemTargets(sourceSystem), targetFilterDefaults),
    [sourceSystem, targetFilterDefaults]
  );
  const availableTargets = useMemo(
    () => getAvailableSourceObjectTargets(filteredTargets, sourceObjects, { includeRepresentedTargets: true }),
    [filteredTargets, sourceObjects]
  );
  const targetRows = useMemo(
    () => availableTargets.map((target, index) => ({
      target,
      targetId: getSourceTargetOperationId(target),
      sourceObject: findSourceObjectForTarget(sourceObjects, target, index)
    })),
    [availableTargets, sourceObjects]
  );
  const selectedReloadRows = targetRows.filter((row) =>
    row.sourceObject && selectedTargetIds.includes(row.targetId)
  );
  const selectedNewCount = selectedTargetIds.length - selectedReloadRows.length;
  const confirmReload = Boolean(state?.confirmReload);
  const emptyMessage = filteredTargets.length > 0
    ? "No active Source System targets are available to load."
    : "No Source System targets match this product transaction filter.";
  const canConfirm = selectedTargetIds.length > 0 && !state?.busy;
  const toggleTarget = useCallback((targetId) => {
    onChangeSelectedTargetIds((current) =>
      current.includes(targetId)
        ? current.filter((item) => item !== targetId)
        : [...current, targetId]
    );
  }, [onChangeSelectedTargetIds]);

  return (
    <DialogControl
      title={operationDisplayName || "Load Source Objects"}
      openDialog={open}
      okText={state?.busy
        ? "Reloading..."
        : confirmReload
          ? selectedNewCount > 0 ? "Load and Reload" : "Reload"
          : "Load"}
      cancelText={confirmReload ? "Back" : "Cancel"}
      onOk={() => onConfirm(confirmReload)}
      onCancel={confirmReload ? onBack : onCancel}
      onX={onCancel}
      disableOk={!canConfirm}
    >
      <div className="resource-workspace-linked-record-add-dialog">
        {state?.error ? <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">{state.error}</div> : null}
        {confirmReload ? (
          <div className="resource-workspace-reload-confirmation">
            <p>
              {selectedReloadRows.length === 1 ? "This Source Object is" : "These Source Objects are"} already loaded and will be regenerated:
            </p>
            <ul>
              {selectedReloadRows.map(({ target, targetId, sourceObject }) => (
                <li key={targetId}>
                  <strong>{getDisplayName(sourceObject, getDisplayName(target, "Source Object"))}</strong>
                </li>
              ))}
            </ul>
            <div className="resource-workspace-access-note resource-workspace-access-note--warning" role="alert">
              Reloading replaces the generated field definition. Custom field selections, ordering, data types, and other Source Object customizations may be lost.
            </div>
            <p>Do you want to continue?</p>
          </div>
        ) : availableTargets.length > 0 ? (
          <div className="resource-workspace-linked-record-add-list" aria-label="Available source targets">
            {targetRows.map(({ target, targetId, sourceObject }, index) => {
              const targetName = getDisplayName(target, getString(target, "ObjectName", "objectName") || "Source target");
              const targetSelected = selectedTargetIds.includes(targetId);
              const targetDisabled = !targetId || state?.busy;
              return (
                <label
                  key={targetId || getSourceTargetIdentityKey(target, index)}
                  className={`resource-workspace-linked-record-add-row resource-workspace-linked-record-add-row--source-target${targetSelected ? " resource-workspace-linked-record-add-row--selected" : ""}${targetDisabled ? " resource-workspace-linked-record-add-row--disabled" : ""}`}
                >
                  <input
                    className="resource-workspace-linked-record-add-row__input"
                    type="checkbox"
                    checked={targetSelected}
                    onChange={() => toggleTarget(targetId)}
                    disabled={targetDisabled}
                  />
                  <span className="resource-workspace-linked-record-add-row__content">
                    <span className="resource-workspace-linked-record-add-row__title">
                      <strong>{targetName}</strong>
                      {sourceObject ? (
                        <span className="resource-workspace-status-pill resource-workspace-status-pill--warning resource-workspace-source-target-loaded-tag">
                          Loaded
                        </span>
                      ) : null}
                    </span>
                    <small>{getString(target, "ObjectName", "objectName") || "No object name"}</small>
                  </span>
                </label>
              );
            })}
          </div>
        ) : (
          <ResourceStateControl
            compact
            message={emptyMessage}
          />
        )}
      </div>
    </DialogControl>
  );
};

const SourceObjectExportDialog = ({
  open,
  sourceObject,
  selectedFormat,
  busy,
  error,
  resultMessage,
  onSelectFormat,
  onCancel,
  onConfirm
}) => {
  const sourceObjectName = getDisplayName(sourceObject, "this Source Object");
  const hasResult = Boolean(resultMessage);
  const sanitizedResultMessage = useMemo(
    () => sanitizeHtmlFragment(resultMessage || ""),
    [resultMessage]
  );

  return (
    <DialogControl
      title="Export Source"
      openDialog={open}
      okText={hasResult ? "OK" : busy ? "Exporting..." : "Export"}
      cancelText="Cancel"
      onOk={hasResult ? onCancel : onConfirm}
      onCancel={onCancel}
      onX={onCancel}
      disableOk={!hasResult && (busy || !selectedFormat)}
      excludeCancel={hasResult}
    >
      <div className="resource-workspace-export-dialog">
        {hasResult ? (
          <div
            className="resource-workspace-export-dialog__result"
            dangerouslySetInnerHTML={{ __html: sanitizedResultMessage }}
          />
        ) : (
          <>
            <p>Choose the export format for {sourceObjectName}.</p>
            <div className="resource-workspace-export-dialog__options" role="radiogroup" aria-label="Export format">
              {SOURCE_OBJECT_EXPORT_FORMATS.map((format) => {
                const selected = selectedFormat === format.value;
                return (
                  <button
                    key={format.value}
                    type="button"
                    className={`resource-workspace-export-dialog__option${selected ? " resource-workspace-export-dialog__option--selected" : ""}`}
                    role="radio"
                    aria-checked={selected ? "true" : "false"}
                    onClick={() => onSelectFormat(format.value)}
                    disabled={busy}
                  >
                    <IconControl iconKey={format.iconKey} title={`${format.label} export`} width="2rem" height="2rem" />
                    <span className="resource-workspace-export-dialog__option-title">{format.label}</span>
                    <span className="resource-workspace-export-dialog__option-description">{format.description}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
        {error ? (
          <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">
            {error}
          </div>
        ) : null}
      </div>
    </DialogControl>
  );
};

const DestinationEntitySearchDialog = ({
  dialog,
  onChange,
  onCancel,
  onConfirm
}) => {
  const open = Boolean(dialog?.open);
  const busy = Boolean(dialog?.busy);
  const executionMode = dialog?.executionMode || DESTINATION_ENTITY_SEARCH_EXECUTION_SOURCE;
  const exportFormat = dialog?.exportFormat || "none";
  const useWarehouse = executionMode === DESTINATION_ENTITY_SEARCH_EXECUTION_WAREHOUSE;
  const warehouseContext = dialog?.warehouseContext || {};
  const warehouseReady = Boolean(warehouseContext.warehouseId && warehouseContext.warehouseDeploymentId);
  const entityObjectName = getDisplayName(dialog?.entityObject, "selected Entity Object");
  const disableOk = busy || !executionMode || (useWarehouse && !warehouseReady);
  const isExport = exportFormat !== "none";

  return (
    <DialogControl
      title={`Search/Export ${entityObjectName}`}
      openDialog={open}
      okText={busy ? (isExport ? "Exporting..." : "Opening...") : (isExport ? "Export" : "Open")}
      cancelText="Cancel"
      disableOk={disableOk}
      loadingOk={busy}
      onCancel={onCancel}
      onX={onCancel}
      onOk={onConfirm}
    >
      <div className="resource-workspace-linked-record-add-dialog resource-workspace-destination-entity-search-dialog">
        <p>
          Choose how to search or export <strong>{entityObjectName}</strong>.
        </p>
        <FieldControl
          fieldMetadata={buildSelectListField(
            "DestinationEntitySearchExecutionMode",
            "Execution Mode",
            DESTINATION_ENTITY_SEARCH_EXECUTION_OPTIONS,
            "Select execution mode"
          )}
          value={executionMode}
          disabled={busy}
          onChange={(value) => onChange({ executionMode: value || DESTINATION_ENTITY_SEARCH_EXECUTION_SOURCE })}
        />
        <FieldControl
          fieldMetadata={buildSelectListField(
            "DestinationEntitySearchExportFormat",
            "Export",
            DESTINATION_ENTITY_SEARCH_EXPORT_OPTIONS,
            "Select export option"
          )}
          value={exportFormat}
          disabled={busy}
          onChange={(value) => onChange({ exportFormat: value || "none" })}
        />
        {useWarehouse && !warehouseReady ? (
          <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">
            Warehouse mode requires a selected Warehouse and Warehouse Deployment on this destination.
          </div>
        ) : null}
        {dialog?.error ? (
          <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">
            {dialog.error}
          </div>
        ) : null}
      </div>
    </DialogControl>
  );
};

const ResourceConnectionResultDialog = ({ result, onClose }) => {
  const statusClass = result?.status === "success"
    ? "resource-workspace-access-note--success"
    : "resource-workspace-access-note--error";
  const message = result?.message || "Connection check completed.";
  const sanitizedMessage = useMemo(
    () => sanitizeHtmlFragment(message),
    [message]
  );
  const endpointStatus = result?.endpointStatus;
  const details = (result?.details || [])
    .filter((detail) => normalizeCompare(detail) !== normalizeCompare(message))
    .filter((detail, index, allDetails) =>
      allDetails.findIndex((candidate) => normalizeCompare(candidate) === normalizeCompare(detail)) === index
    );

  return (
    <DialogControl
      title={result?.title || "Connection Check"}
      openDialog={Boolean(result)}
      okText="OK"
      onOk={onClose}
      onCancel={onClose}
      onX={onClose}
      excludeCancel
    >
      <div className="resource-workspace-connection-result-dialog">
        <div
          className={`resource-workspace-access-note ${statusClass}`}
          role={result?.status === "success" ? "status" : "alert"}
          dangerouslySetInnerHTML={{ __html: sanitizedMessage }}
        />
        {endpointStatus ? (
          <EndpointStatusResultHtml status={endpointStatus} />
        ) : details.length > 0 ? (
          <ul className="resource-workspace-connection-result-details">
            {details.map((detail, index) => (
              <li key={`${index}-${detail}`}>{detail}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </DialogControl>
  );
};

const EndpointDeploymentLifecycleConfirmDialog = ({ request, onCancel, onConfirm }) => {
  const action = normalizeCompare(request?.action);
  const isUndeploy = action === "undeploy";
  const deployment = request?.deployment;
  const deploymentName = getString(
    deployment,
    "DisplayName",
    "displayName",
    "DeploymentName",
    "deploymentName",
    "Name",
    "name",
    "DeploymentKey",
    "deploymentKey"
  ) || "selected deployment";
  const deploymentKey = getString(deployment, "DeploymentKey", "deploymentKey", "ApiRouteKey", "apiRouteKey");
  const title = isUndeploy ? "Confirm Endpoint Undeploy" : "Confirm Endpoint Deploy";
  const okText = isUndeploy ? "Undeploy" : "Deploy";
  const isMcpOnly = isEndpointDeploymentMcpOnly(deployment);

  return (
    <DialogControl
      controlOrigin="endpoint-deployment-lifecycle-confirm"
      openDialog={Boolean(request?.open)}
      title={title}
      okText={okText}
      cancelText="Cancel"
      onOk={onConfirm}
      onCancel={onCancel}
      onX={onCancel}
    >
      <div className="resource-workspace-connection-result-dialog">
        <p>
          {isUndeploy
            ? "Undeploy this endpoint deployment? This sets the deployment Offline so routes are no longer routable. Configuration, credentials, and route setup are preserved."
            : isMcpOnly
              ? "Deploy this MCP endpoint deployment? This validates MCP readiness and marks the deployment Online. Hosted API secure-status verification is skipped."
              : "Deploy this endpoint deployment? This validates readiness, marks the deployment Online, and verifies the secure-status route before leaving it deployed."}
        </p>
        <ul className="resource-workspace-connection-result-details">
          <li><strong>Deployment:</strong> {deploymentName}</li>
          {deploymentKey ? <li><strong>Deployment key:</strong> {deploymentKey}</li> : null}
        </ul>
      </div>
    </DialogControl>
  );
};

const getPayloadObject = (value, ...keys) => {
  for (const key of keys) {
    const item = value?.[key];
    if (item && typeof item === "object" && !Array.isArray(item)) return item;
  }
  return {};
};

const getPayloadValue = (value, ...keys) => {
  for (const key of keys) {
    const item = value?.[key];
    if (item !== undefined && item !== null && item !== "") return item;
  }
  return undefined;
};

const getReadableValue = (value, fallback = "Not set") => {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.map((item) => getReadableValue(item, fallback)).join(", ") : fallback;
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const getEndpointStatusDeploymentRoute = (statusUrl, api) => {
  const route = getPayloadValue(api, "route", "Route", "baseRoute", "BaseRoute");
  if (route) return route;

  const clientShortName = getPayloadValue(api, "clientShortName", "ClientShortName");
  const deploymentKey = getPayloadValue(api, "deploymentKey", "DeploymentKey", "deploymentName", "DeploymentName");
  if (clientShortName && deploymentKey) return `/api/${clientShortName}/V1/${deploymentKey}`;

  const statusUrlText = String(statusUrl || "");
  if (!statusUrlText) return undefined;

  const routeMatch = statusUrlText.match(/\/api\/[^/?#]+\/V1\/[^/?#]+/i);
  if (routeMatch) return routeMatch[0];

  try {
    const parsedUrl = new URL(statusUrlText, "http://localhost");
    const segments = parsedUrl.pathname.split("/").filter(Boolean);
    const apiIndex = segments.findIndex((segment) => segment.toLowerCase() === "api");
    if (apiIndex >= 0 && segments.length >= apiIndex + 4) {
      return `/${segments.slice(apiIndex, apiIndex + 4).join("/")}`;
    }
  } catch {
    return undefined;
  }

  return undefined;
};

const getEndpointStatusDeploymentKeyFromRoute = (route) => {
  const routeText = String(route || "");
  if (!routeText) return undefined;
  const routeMatch = routeText.match(/\/api\/[^/?#]+\/V1\/([^/?#]+)/i);
  return routeMatch?.[1];
};

const formatEndpointStatusLabel = (value) =>
  String(value || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());

const EndpointStatusObjectValue = ({ value }) => {
  const entries = Object.entries(value || {}).filter(([, entryValue]) => entryValue !== undefined && entryValue !== null && entryValue !== "");
  if (!entries.length) return <span>Not set</span>;
  return (
    <dl className="resource-workspace-endpoint-status-object">
      {entries.map(([key, entryValue]) => (
        <div key={key}>
          <dt>{formatEndpointStatusLabel(key)}</dt>
          <dd><EndpointStatusValue value={entryValue} /></dd>
        </div>
      ))}
    </dl>
  );
};

const EndpointStatusValue = ({ value }) => {
  if (Array.isArray(value)) {
    if (!value.length) return <span>Not set</span>;
    return (
      <ul className="resource-workspace-endpoint-status-list">
        {value.map((item, index) => (
          <li key={`${index}-${typeof item === "object" ? JSON.stringify(item) : String(item)}`}>
            <EndpointStatusValue value={item} />
          </li>
        ))}
      </ul>
    );
  }
  if (value && typeof value === "object") {
    return <EndpointStatusObjectValue value={value} />;
  }
  return <span>{getReadableValue(value)}</span>;
};

const EndpointStatusFact = ({ label, value }) => (
  <div className="resource-workspace-endpoint-status-fact">
    <dt>{label}</dt>
    <dd><EndpointStatusValue value={value} /></dd>
  </div>
);

const EndpointStatusFactGroup = ({ title, children }) => (
  <section className="resource-workspace-endpoint-status-group">
    <h3>{title}</h3>
    <dl>{children}</dl>
  </section>
);

const hasEndpointStatusValue = (value) => value !== undefined && value !== null && value !== "";

const hasEndpointCredentialSetupStatus = (authentication = {}) => [
  authentication.apiPatEnabled,
  authentication.ApiPatEnabled,
  authentication.activeApiPatCount,
  authentication.ActiveApiPatCount,
  authentication.basicAuthEnabled,
  authentication.BasicAuthEnabled,
  authentication.basicClientAccessConfigured,
  authentication.BasicClientAccessConfigured,
  authentication.basicAuthScope,
  authentication.BasicAuthScope
].some(hasEndpointStatusValue);

const isEndpointStatusFailure = (status, payload) => {
  const httpStatus = Number(status?.httpStatus);
  if (Number.isFinite(httpStatus) && (httpStatus < 200 || httpStatus >= 300)) return true;

  const success = payload?.success ?? payload?.Success;
  if (success === false) return true;
  if (typeof success === "string" && normalizeCompare(success) === "false") return true;

  return false;
};

export const EndpointStatusResultHtml = ({ status }) => {
  const payload = status?.payload || {};
  const api = getPayloadObject(payload, "api", "Api");
  const authentication = getPayloadObject(payload, "authentication", "Authentication");
  const hasCredentialSetupStatus = hasEndpointCredentialSetupStatus(authentication);
  const deploymentRoute = getEndpointStatusDeploymentRoute(status?.statusUrl, api);
  const deploymentKey = getPayloadValue(api, "deploymentKey", "DeploymentKey", "deploymentName", "DeploymentName")
    || getEndpointStatusDeploymentKeyFromRoute(deploymentRoute);
  const statusResponse = (
    <EndpointStatusFactGroup title="Status Response">
      <EndpointStatusFact label="Status URL" value={status?.statusUrl} />
      <EndpointStatusFact label="HTTP Status" value={`${status?.httpStatus || "Not set"} ${status?.httpStatusText || ""}`.trim()} />
      <EndpointStatusFact label="Endpoint Online" value={payload.endpointOnline ?? payload.EndpointOnline} />
      <EndpointStatusFact label="Checked" value={payload.checkedAtUtc || payload.CheckedAtUtc} />
    </EndpointStatusFactGroup>
  );

  return (
    <div className="resource-workspace-endpoint-status-result">
      {statusResponse}
      {isEndpointStatusFailure(status, payload) ? null : (
        <>
          <EndpointStatusFactGroup title="API Route">
            <EndpointStatusFact label="Client" value={api.clientShortName || api.ClientShortName} />
            <EndpointStatusFact label="Endpoint" value={api.endpointName || api.EndpointName} />
            <EndpointStatusFact label="Deployment" value={deploymentKey} />
            <EndpointStatusFact label="Base Route" value={deploymentRoute} />
            <EndpointStatusFact label="Hosted API" value={api.hostedApiEnabled ?? api.HostedApiEnabled} />
            <EndpointStatusFact label="MCP" value={api.mcpEnabled ?? api.McpEnabled} />
          </EndpointStatusFactGroup>
          <EndpointStatusFactGroup title="Authentication">
            <EndpointStatusFact label="Mode" value={authentication.authenticationMode || authentication.AuthenticationMode} />
            <EndpointStatusFact label="Required" value={authentication.authenticationRequired ?? authentication.AuthenticationRequired} />
            <EndpointStatusFact label="Supported Schemes" value={authentication.supportedSchemes || authentication.SupportedSchemes} />
            <EndpointStatusFact label="Permission" value={authentication.endpointPermissionKey || authentication.EndpointPermissionKey} />
            <EndpointStatusFact label="Runtime Role" value={authentication.runtimeRole || authentication.RuntimeRole} />
            <EndpointStatusFact label="Anonymous" value={authentication.anonymousAccessEnabled ?? authentication.AnonymousAccessEnabled} />
          </EndpointStatusFactGroup>
          {hasCredentialSetupStatus ? (
            <EndpointStatusFactGroup title="Credential Setup">
              <EndpointStatusFact label="PAT Enabled" value={authentication.apiPatEnabled ?? authentication.ApiPatEnabled} />
              <EndpointStatusFact label="Active PAT Tokens" value={authentication.activeApiPatCount ?? authentication.ActiveApiPatCount} />
              <EndpointStatusFact label="Basic Enabled" value={authentication.basicAuthEnabled ?? authentication.BasicAuthEnabled} />
              <EndpointStatusFact label="Basic Client Access" value={authentication.basicClientAccessConfigured ?? authentication.BasicClientAccessConfigured} />
              <EndpointStatusFact label="Basic Scope" value={authentication.basicAuthScope || authentication.BasicAuthScope} />
            </EndpointStatusFactGroup>
          ) : null}
          {status?.formatted ? (
            <details className="resource-workspace-endpoint-status-json">
              <summary>Raw status JSON</summary>
              <pre>{status.formatted}</pre>
            </details>
          ) : null}
        </>
      )}
    </div>
  );
};

const ResourceConfigurationDeleteDialog = ({
  configuration,
  confirmText,
  state,
  onChangeConfirmText,
  onCancel,
  onConfirm
}) => {
  const configurationName = getDisplayName(configuration, "Resource configuration");
  const canConfirm = Boolean(configuration) && confirmText.trim() === configurationName && !state.busy;

  return (
    <DialogControl
      openDialog={Boolean(configuration)}
      title="Delete Configuration"
      okText={state.busy ? "Deleting..." : "Delete"}
      cancelText="Cancel"
      disableOk={!canConfirm}
      onOk={onConfirm}
      onCancel={onCancel}
      onX={onCancel}
    >
      <div
        className="resource-workspace-delete-dialog"
        onCopy={(event) => event.preventDefault()}
        onCut={(event) => event.preventDefault()}
        onPaste={(event) => event.preventDefault()}
      >
        <p>
          Deleting <strong>{configurationName}</strong> will remove this configuration from management.
        </p>
        <p>
          This is unrecoverable. Once deleted, this information is permanently deleted from the system.
          Type <strong>"{configurationName}"</strong> to continue.
        </p>
        <FieldControl
          fieldMetadata={buildTextBoxField(
            "DeleteConfigurationConfirmation",
            "Confirmation",
            `This is unrecoverable. Once deleted, this information is permanently deleted from the system. Type "${configurationName}" to continue.`
          )}
          groupId="resourceConfigurationDelete"
          value={confirmText}
          disabled={state.busy}
          onChange={(nextValue) => onChangeConfirmText(nextValue ?? "")}
        />
        {state.error ? (
          <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">
            {state.error}
          </div>
        ) : null}
      </div>
    </DialogControl>
  );
};

const ResourceConfigurationDeactivateDialog = ({
  configuration,
  state,
  onCancel,
  onConfirm
}) => {
  const configurationName = getDisplayName(configuration, "Resource configuration");
  const busy = Boolean(configuration) && state?.busyAction === `deactivate:${getId(configuration)}`;

  return (
    <DialogControl
      openDialog={Boolean(configuration)}
      title="Deactivate Resource"
      okText={busy ? "Deactivating..." : "Deactivate"}
      cancelText="Cancel"
      disableOk={!configuration || busy}
      disableCancel={busy}
      onOk={onConfirm}
      onCancel={onCancel}
      onX={onCancel}
    >
      <div className="resource-workspace-delete-dialog">
        <div className="resource-workspace-access-note resource-workspace-access-note--warning" role="alert">
          Deactivating <strong>{configurationName}</strong> will make all features for this Resource unavailable.
        </div>
        <p>
          The configuration and its child settings will be preserved. Activating the Resource later restores only its
          top-level runtime controls; no child records are deleted or reset.
        </p>
        {state?.error ? (
          <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">
            {state.error}
          </div>
        ) : null}
      </div>
    </DialogControl>
  );
};

const LOG_TABLE_METADATA_FALLBACK = {
  Name: "Log",
  DisplayName: "Logs",
  Description: "Operational logs for the selected Client Resource.",
  ServerFilterSort: true,
  HasPaging: [50],
  DisableCanCreate: true,
  DisableCanDelete: true,
  DisableCanClone: true,
  Fields: [
    { FieldName: "CreatedDate", DisplayName: "Created", FieldType: "DateTime", ShowOnGrid: true, GridSequence: 10, CanFilter: true },
    { FieldName: "LogLevel", DisplayName: "Level", FieldType: "Number", ShowOnGrid: true, GridSequence: 20, CanFilter: true },
    { FieldName: "DisplayMessage", DisplayName: "Message", FieldType: "TextBox", ShowOnGrid: true, GridSequence: 30, CanFilter: true },
    { FieldName: "EventName", DisplayName: "Event", FieldType: "TextBox", ShowOnGrid: true, GridSequence: 40, CanFilter: true },
    { FieldName: "LogReferenceId", DisplayName: "Reference", FieldType: "TextBox", ShowOnGrid: true, GridSequence: 50, CanFilter: true }
  ]
};

const LOG_LEVEL_SELECT_DATA = [
  { Text: "Debug", Value: 0 },
  { Text: "Info", Value: 1 },
  { Text: "Timing", Value: 2 },
  { Text: "Warning", Value: 3 },
  { Text: "Error", Value: 4 },
  { Text: "Deploy Info", Value: 5 }
];

const MESSAGE_TYPE_SELECT_DATA = [
  { Text: "System Error", Value: 0 },
  { Text: "Access Denied", Value: 1 },
  { Text: "Access Error Groups Not Found", Value: 2 },
  { Text: "Access Error Invalid Header", Value: 3 },
  { Text: "Access Denied Invalid Client", Value: 4 },
  { Text: "Permission Denied Read", Value: 5 },
  { Text: "Permission Denied Write", Value: 6 },
  { Text: "Permission Denied Delete", Value: 7 },
  { Text: "Permission Denied Hard Delete", Value: 8 },
  { Text: "Permission Denied Execute", Value: 9 },
  { Text: "Invalid Token", Value: 10 },
  { Text: "Permission Denied Create", Value: 11 },
  { Text: "Access Denied Invalid Profile", Value: 12 },
  { Text: "Access Denied Inactive", Value: 13 },
  { Text: "Loaded From Cache", Value: 1000 },
  { Text: "Timing Log", Value: 1001 },
  { Text: "Frontend Log", Value: 1002 },
  { Text: "Frontend Debug Log", Value: 1003 },
  { Text: "File Accessed", Value: 1004 },
  { Text: "Deploy Log", Value: 1005 },
  { Text: "Info Log", Value: 1006 },
  { Text: "Event Processor Log", Value: 1007 },
  { Text: "Backend Debug Log", Value: 1008 },
  { Text: "EDI Info", Value: 1900 },
  { Text: "Action Success", Value: 2000 },
  { Text: "Results Success", Value: 2002 },
  { Text: "Validation Success", Value: 2003 },
  { Text: "Action In Progress", Value: 2004 },
  { Text: "API Connection Success", Value: 2005 },
  { Text: "Operation Success", Value: 2006 },
  { Text: "Deploy Success", Value: 2007 },
  { Text: "Authenticated", Value: 2008 },
  { Text: "Authorized", Value: 2009 },
  { Text: "Event Processor Success", Value: 2010 },
  { Text: "Operation Scheduled", Value: 2011 },
  { Text: "Source System Targets Loaded", Value: 2020 },
  { Text: "Warehouse Connection Success", Value: 2551 },
  { Text: "Warehouse DB Success", Value: 2552 },
  { Text: "Warehouse Sync Success", Value: 2553 },
  { Text: "Warehouse Query Success", Value: 2554 },
  { Text: "Warehouse Action Success", Value: 2555 },
  { Text: "Warehouse Object Deleted", Value: 2556 },
  { Text: "Warehouse DB Deleted", Value: 2557 },
  { Text: "Warehouse Load Success", Value: 2558 },
  { Text: "Save Error", Value: 3000 },
  { Text: "Results Error", Value: 3001 },
  { Text: "Results Error Cache", Value: 3002 },
  { Text: "Delete Error", Value: 3003 },
  { Text: "Request Error Invalid Delete", Value: 3004 },
  { Text: "Hard Delete Error", Value: 3005 },
  { Text: "Event Duplicate Error", Value: 3007 },
  { Text: "Event Processor Error", Value: 3008 },
  { Text: "Event Processor Single Unique Id Error", Value: 3009 },
  { Text: "Event Processor Ops Invalid Error", Value: 3010 },
  { Text: "Event Processor Non Ops Invalid Error", Value: 3011 },
  { Text: "Delete Event Error", Value: 3012 },
  { Text: "Request Error Unsupported Verb", Value: 3013 },
  { Text: "Request Error Invalid Object", Value: 3014 },
  { Text: "Request Error Invalid Get", Value: 3015 },
  { Text: "Request Error Invalid Post", Value: 3016 },
  { Text: "Request Error Invalid Put", Value: 3017 },
  { Text: "Results Error Invalid Id", Value: 3018 },
  { Text: "Request Error Ops Unsupported", Value: 3019 },
  { Text: "Event Processor Invalid Error", Value: 3020 },
  { Text: "User Integration Error", Value: 3021 },
  { Text: "Action Error", Value: 3022 },
  { Text: "Event Processor Invalid Status", Value: 3023 },
  { Text: "Invalid Action View Only Item", Value: 3024 },
  { Text: "Frontend Error", Value: 3025 },
  { Text: "File Error Failed To Delete", Value: 3026 },
  { Text: "File Error Failed To Upload", Value: 3027 },
  { Text: "File Error Failed To Download", Value: 3028 },
  { Text: "File Error Failed To Get Item", Value: 3029 },
  { Text: "File Error Failed To Generate", Value: 3030 },
  { Text: "Select Data Operation Not Found", Value: 3031 },
  { Text: "Select Tree Data Operation Not Found", Value: 3032 },
  { Text: "Invalid API", Value: 3033 },
  { Text: "Email Send Error", Value: 3034 },
  { Text: "Email Finalize Error", Value: 3035 },
  { Text: "Email Resend Error", Value: 3036 },
  { Text: "Email Merge Error", Value: 3037 },
  { Text: "Email Send Skipped Inactive", Value: 3038 },
  { Text: "Init Error", Value: 3039 },
  { Text: "Action Error Failed Validation", Value: 3040 },
  { Text: "Request Error Get Call", Value: 3041 },
  { Text: "Request Error Post Call", Value: 3042 },
  { Text: "Request Error Put Call", Value: 3043 },
  { Text: "Source System Error", Value: 3044 },
  { Text: "API Connection Error", Value: 3045 },
  { Text: "Source Data Connection Error", Value: 3046 },
  { Text: "Source Object Partial Load Error", Value: 3047 },
  { Text: "Source Object Load Error", Value: 3048 },
  { Text: "Source Object Search Error", Value: 3049 },
  { Text: "Action Partial Error", Value: 3050 },
  { Text: "Operation Error", Value: 3051 },
  { Text: "Deploy Error", Value: 3052 },
  { Text: "Action Error Serialization", Value: 3053 },
  { Text: "Deploy In Progress", Value: 3054 },
  { Text: "Operation Not Implemented", Value: 3055 },
  { Text: "Warehouse Connection Failed", Value: 3550 },
  { Text: "Warehouse DB Missing", Value: 3552 },
  { Text: "Warehouse Invalid Object", Value: 3553 },
  { Text: "Warehouse DB Created Failed", Value: 3554 },
  { Text: "Warehouse Sync Failed", Value: 3555 },
  { Text: "Warehouse Query Failed", Value: 3556 },
  { Text: "Warehouse Offline", Value: 3557 },
  { Text: "Warehouse DB Error", Value: 3558 },
  { Text: "Data Connection Error", Value: 3559 },
  { Text: "Data Connection Unavailable", Value: 3560 },
  { Text: "Warehouse Action Failed", Value: 3561 },
  { Text: "Warehouse Inactive", Value: 3562 },
  { Text: "Warehouse Load Failed", Value: 3563 },
  { Text: "Warehouse Sync Warning", Value: 3564 },
  { Text: "Data Runtime Not Loaded", Value: 3565 },
  { Text: "Report Viewer Handoff Invalid", Value: 3569 },
  { Text: "Report Viewer Handoff Expired", Value: 3570 },
  { Text: "Report Viewer Handoff Already Used", Value: 3571 },
  { Text: "Report Viewer Handoff Configuration Error", Value: 3572 },
  { Text: "Storage Connection Failed", Value: 3650 },
  { Text: "Storage Root Missing", Value: 3652 },
  { Text: "Storage Invalid Object", Value: 3653 },
  { Text: "Storage Root Create Failed", Value: 3654 },
  { Text: "Storage Sync Failed", Value: 3655 },
  { Text: "Storage Query Failed", Value: 3656 },
  { Text: "Storage Root Delete Failed", Value: 3657 },
  { Text: "Storage Offline", Value: 3658 },
  { Text: "Storage Error", Value: 3659 },
  { Text: "Storage Deployment In Progress", Value: 3660 },
  { Text: "Storage Inactive", Value: 3662 },
  { Text: "Storage Sync In Progress", Value: 3663 },
  { Text: "Storage Sync Success", Value: 3664 },
  { Text: "EDI Error", Value: 3900 },
  { Text: "EDI Warn", Value: 3901 },
  { Text: "ADO Error", Value: 3950 },
  { Text: "Hard Delete Warning Item Not Found", Value: 4001 },
  { Text: "Email Send Skipped", Value: 4002 },
  { Text: "Email Partial Send", Value: 4003 },
  { Text: "SMS Send Skipped", Value: 4004 },
  { Text: "SMS Partial Send", Value: 4005 },
  { Text: "Deploy Found", Value: 4006 },
  { Text: "Deploy Partial Success", Value: 4007 },
  { Text: "Warehouse Sync In Progress", Value: 4500 },
  { Text: "Validation", Value: 5000 },
  { Text: "Validation Error", Value: 5001 },
  { Text: "Validation Is Required", Value: 5002 },
  { Text: "Validation Auth Invalid Answer", Value: 5003 },
  { Text: "Validation Template Cannot Generate", Value: 5004 },
  { Text: "Validation Generate Invalid File Type", Value: 5005 },
  { Text: "Validation Permission Setting Invalid Ids", Value: 5006 },
  { Text: "Validation System Data", Value: 5007 },
  { Text: "Validation Unique", Value: 5008 }
];

const normalizeLogFieldMetadata = (field) => {
  const fieldName = getString(field, "FieldName", "fieldName", "Name", "name");

  if (fieldName === "LogLevel") {
    return {
      ...field,
      FieldType: SystemTypes.UIFieldType.SelectList,
      DataJson: LOG_LEVEL_SELECT_DATA
    };
  }

  if (["MessageType", "MessageTypeId"].includes(fieldName)) {
    return {
      ...field,
      FieldType: SystemTypes.UIFieldType.SelectList,
      DataJson: MESSAGE_TYPE_SELECT_DATA
    };
  }

  return field;
};

const normalizeLogTableMetadata = (metadataItems) => {
  const metadata = firstItem(metadataItems) || LOG_TABLE_METADATA_FALLBACK;
  const metadataFields = getArray(metadata, "Fields", "fields");
  return [{
    ...LOG_TABLE_METADATA_FALLBACK,
    ...metadata,
    Fields: (metadataFields.length > 0 ? metadataFields : LOG_TABLE_METADATA_FALLBACK.Fields).map(normalizeLogFieldMetadata),
    ServerFilterSort: metadata?.ServerFilterSort ?? true,
    HasPaging: Array.isArray(metadata?.HasPaging) && metadata.HasPaging.length > 0 ? metadata.HasPaging : [50],
    DisableCanCreate: true,
    DisableCanDelete: true,
    DisableCanClone: true
  }];
};

const getResultMessage = (result, fallback) => {
  const message = getOperationResultMessage(result, fallback);
  const details = getOperationResultDetails(result)
    .filter((detail) => normalizeCompare(detail) !== normalizeCompare(message));

  return [message, ...details].filter(Boolean).join("\n");
};

const getLogResultItems = (response) => {
  const firstResponse = firstItem(response);
  if (firstResponse && (firstResponse.Items || firstResponse.items || firstResponse.Success !== undefined)) {
    return getArray(firstResponse, "Items", "items");
  }
  if (Array.isArray(response)) return response;
  return getArray(response, "Items", "items");
};

const normalizeScheduleTableMetadata = (metadataItems) => {
  const metadata = firstItem(metadataItems);
  if (!metadata) return [];

  return [{
    ...metadata,
    ServerFilterSort: true,
    HasPaging: Array.isArray(metadata?.HasPaging) && metadata.HasPaging.length > 0 ? metadata.HasPaging : [50]
  }];
};

const ResourceSchedulesScreen = ({
  destinationScheduleManager,
  environmentKey = "",
  clientProductOffers = [],
  isSelfHostedResourceMode = false,
  onRuntimeCapacityPurchased
}) => {
  const scheduleManagerRef = useRef(destinationScheduleManager);
  const [runtimeCapacityPurchaseOpen, setRuntimeCapacityPurchaseOpen] = useState(false);
  const [state, setState] = useState({
    loading: true,
    error: "",
    data: [],
    metadata: null
  });

  useEffect(() => {
    scheduleManagerRef.current = destinationScheduleManager;
  }, [destinationScheduleManager]);

  const runScheduleQuery = useCallback(async (tableQuery) => {
    const activeScheduleManager = scheduleManagerRef.current;
    if (!activeScheduleManager?.Query) {
      throw new Error("Destination Schedule service is not available.");
    }

    const scheduleQuery = buildScheduleTableQuery(tableQuery);
    const result = await activeScheduleManager.Query({
      model: scheduleQuery,
      skip: scheduleQuery.Skip,
      take: scheduleQuery.Take,
      sortBy: scheduleQuery.SortBy,
      sortOrder: scheduleQuery.SortOrder
    });
    const items = getLogResultItems(result);
    return {
      ...result,
      Items: items,
      TotalItemCount: Number.isFinite(result?.TotalItemCount) ? result.TotalItemCount : items.length
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadSchedules = async () => {
      const activeScheduleManager = scheduleManagerRef.current;
      if (!activeScheduleManager?.Query) {
        setState({
          loading: false,
          error: "Destination Schedule service is not available.",
          data: [],
          metadata: normalizeScheduleTableMetadata(activeScheduleManager?.uiMetadata)
        });
        return;
      }

      setState((previous) => ({
        ...previous,
        loading: true,
        error: ""
      }));

      try {
        const [metadataResult, queryResult] = await Promise.all([
          activeScheduleManager.GetMetadata
            ? activeScheduleManager.GetMetadata({ forceReload: true })
            : Promise.resolve({ Success: true, Items: activeScheduleManager.uiMetadata || [] }),
          runScheduleQuery({ Take: 50 })
        ]);

        if (!mounted) return;

        if (metadataResult && metadataResult.Success === false) {
          setState({
            loading: false,
            error: getResultMessage(metadataResult, "Destination Schedule metadata could not be loaded."),
            data: [],
            metadata: null
          });
          return;
        }

        if (queryResult && queryResult.Success === false) {
          setState({
            loading: false,
            error: getResultMessage(queryResult, "Destination Schedules could not be loaded."),
            data: [],
            metadata: normalizeScheduleTableMetadata(getLogResultItems(metadataResult))
          });
          return;
        }

        setState({
          loading: false,
          error: "",
          data: getLogResultItems(queryResult),
          metadata: normalizeScheduleTableMetadata(getLogResultItems(metadataResult))
        });
      } catch (loadError) {
        if (!mounted) return;
        setState({
          loading: false,
          error: loadError?.message || "Destination Schedules could not be loaded.",
          data: [],
          metadata: null
        });
      }
    };

    loadSchedules();

    return () => {
      mounted = false;
    };
  }, [runScheduleQuery]);

  const handleServerSideQuery = useCallback(async (tableQuery) => {
    try {
      const result = await runScheduleQuery(tableQuery);
      const items = getLogResultItems(result);
      setState((previous) => ({
        ...previous,
        loading: false,
        error: "",
        data: items
      }));
      return {
        ...result,
        Items: items,
        TotalItemCount: Number.isFinite(result?.TotalItemCount) ? result.TotalItemCount : items.length
      };
    } catch (queryError) {
      setState((previous) => ({
        ...previous,
        loading: false,
        error: queryError?.message || "Destination Schedules could not be loaded."
      }));
      return { Success: false, Items: [], TotalItemCount: 0 };
    }
  }, [runScheduleQuery]);

  const hasSchedules = state.data.length > 0;
  const runtimeCapacityActionVerb = "Add";

  return (
    <main className="resource-workspace resource-workspace--all-schedules" aria-label="All Schedules">
      <header className="resource-workspace-header resource-workspace-all-schedules-header">
        <span className="resource-workspace-header-icon" aria-hidden="true">
          <ResourceWorkspaceIcon
            iconKey="clock"
            label="All Schedules"
            width="3.25rem"
            height="3.25rem"
          />
        </span>
        <div className="resource-workspace-title-block">
          <h1>All Schedules</h1>
        </div>
        <div className="resource-workspace-header-actions">
          <ButtonControl
            type="ok-small"
            onClick={() => setRuntimeCapacityPurchaseOpen(true)}
          >
            {runtimeCapacityActionVerb} Grant
          </ButtonControl>
        </div>
      </header>
      <div className="resource-workspace-body">
        <section className="resource-workspace-screen">
          <div className="resource-workspace-panel resource-workspace-linked-item-section resource-workspace-all-schedules-panel">
            <div className="resource-workspace-section-title resource-workspace-section-title--grid">
              <h2>Schedules</h2>
              <p>Destination schedule templates across Resources.</p>
            </div>
            {state.loading && !state.metadata ? (
              <ResourceStateControl loading loadingText="Loading schedules..." gridState />
            ) : state.error ? (
              <ResourceStateControl
                tone="error"
                title="Schedules could not be loaded"
                message={state.error}
                gridState
              />
            ) : (
              <div className="resource-workspace-log-table resource-workspace-all-schedules-table">
                {!state.loading && !hasSchedules ? (
                  <ResourceStateControl
                    className="resource-workspace-log-table-empty"
                    compact
                    iconKey="clock"
                    message="No schedules are available yet."
                  />
                ) : (
                  <TableControl
                    data={state.data}
                    metadata={state.metadata || normalizeScheduleTableMetadata(null)}
                    filters={destinationScheduleManager?.filters}
                    loadingState={state.loading}
                    mgr={destinationScheduleManager}
                    enableMultiSelect={false}
                    defaultNumRows={50}
                    disableTableTitle
                    headerFeatures={["search", "filter", "tableStyles", "columnSettings"]}
                    onServerSideQuery={handleServerSideQuery}
                  />
                )}
              </div>
            )}
          </div>
        </section>
      </div>
      <MarketplaceRuntimeCapacityPurchaseDialog
        open={runtimeCapacityPurchaseOpen}
        product={{ name: "Processing Capacity" }}
        environmentKey={environmentKey}
        clientProductOffers={clientProductOffers}
        isSelfHostedResourceMode={isSelfHostedResourceMode}
        actionVerb={runtimeCapacityActionVerb}
        onClose={() => setRuntimeCapacityPurchaseOpen(false)}
        onPurchased={onRuntimeCapacityPurchased}
      />
    </main>
  );
};

const ResourceLogsScreen = ({ selectedResource, resourceLogs, logManager }) => {
  const selectedClientResourceId = getSelectedClientResourceId(selectedResource);
  const logManagerRef = useRef(logManager);
  const [state, setState] = useState({
    loading: Boolean(selectedClientResourceId),
    error: "",
    data: getResourceScopedLogs(resourceLogs, selectedResource),
    metadata: null
  });

  useEffect(() => {
    logManagerRef.current = logManager;
  }, [logManager]);

  const runLockedLogQuery = useCallback(async (tableQuery) => {
    if (!selectedClientResourceId) {
      return { Items: [], TotalItemCount: 0 };
    }

    const activeLogManager = logManagerRef.current;
    if (!activeLogManager?.Query) {
      throw new Error("Log service is not available.");
    }

    const lockedQuery = buildLockedClientResourceLogQuery(tableQuery, selectedClientResourceId);
    const result = await activeLogManager.Query({
      model: lockedQuery,
      skip: lockedQuery.Skip,
      take: lockedQuery.Take,
      sortBy: lockedQuery.SortBy,
      sortOrder: lockedQuery.SortOrder
    });
    const items = getResourceScopedLogs(getLogResultItems(result), selectedResource);
    return {
      ...result,
      Items: items,
      TotalItemCount: Number.isFinite(result?.TotalItemCount) ? result.TotalItemCount : items.length
    };
  }, [selectedClientResourceId, selectedResource]);

  useEffect(() => {
    let mounted = true;

    const loadLogs = async () => {
      if (!selectedClientResourceId) {
        setState({
          loading: false,
          error: "",
          data: [],
          metadata: normalizeLogTableMetadata(null)
        });
        return;
      }

      const activeLogManager = logManagerRef.current;
      if (!activeLogManager?.Query) {
        setState({
          loading: false,
          error: "Log service is not available.",
          data: getResourceScopedLogs(resourceLogs, selectedResource),
          metadata: normalizeLogTableMetadata(activeLogManager?.uiMetadata)
        });
        return;
      }

      setState((previous) => ({
        ...previous,
        loading: true,
        error: ""
      }));

      try {
        const [metadataResult, queryResult] = await Promise.all([
          activeLogManager.GetMetadata
            ? activeLogManager.GetMetadata({ forceReload: true })
            : Promise.resolve({ Success: true, Items: activeLogManager.uiMetadata || [] }),
          runLockedLogQuery({ Take: 50 })
        ]);

        if (!mounted) return;

        if (metadataResult && metadataResult.Success === false) {
          setState({
            loading: false,
            error: getResultMessage(metadataResult, "Log metadata could not be loaded."),
            data: [],
            metadata: null
          });
          return;
        }

        if (queryResult && queryResult.Success === false) {
          setState({
            loading: false,
            error: getResultMessage(queryResult, "Logs could not be loaded."),
            data: [],
            metadata: normalizeLogTableMetadata(getLogResultItems(metadataResult))
          });
          return;
        }

        const queryItems = getLogResultItems(queryResult);
        setState({
          loading: false,
          error: "",
          data: getResourceScopedLogs(queryItems, selectedResource),
          metadata: normalizeLogTableMetadata(getLogResultItems(metadataResult))
        });
      } catch (loadError) {
        if (!mounted) return;
        setState({
          loading: false,
          error: loadError?.message || "Logs could not be loaded.",
          data: [],
          metadata: null
        });
      }
    };

    loadLogs();

    return () => {
      mounted = false;
    };
  }, [resourceLogs, runLockedLogQuery, selectedClientResourceId, selectedResource]);

  const handleServerSideQuery = useCallback(async (tableQuery) => {
    try {
      const result = await runLockedLogQuery(tableQuery);
      const items = getResourceScopedLogs(result?.Items, selectedResource);
      setState((previous) => ({
        ...previous,
        loading: false,
        error: "",
        data: items
      }));
      return {
        ...result,
        Items: items,
        TotalItemCount: Number.isFinite(result?.TotalItemCount) ? result.TotalItemCount : items.length
      };
    } catch (queryError) {
      setState((previous) => ({
        ...previous,
        loading: false,
        error: queryError?.message || "Logs could not be loaded."
      }));
      return { Success: false, Items: [], TotalItemCount: 0 };
    }
  }, [runLockedLogQuery, selectedResource]);

  const scopedLogs = getResourceScopedLogs(state.data, selectedResource);
  const hasLogs = scopedLogs.length > 0;

  return (
    <div className="resource-workspace-panel resource-workspace-linked-item-section">
      <div className="resource-workspace-section-title resource-workspace-section-title--grid">
        <h2>Logs</h2>
        <p>Operational logs for {selectedResource.displayName}.</p>
      </div>
      {state.loading && !state.metadata ? (
        <ResourceStateControl loading loadingText="Loading logs..." gridState />
      ) : state.error ? (
        <ResourceStateControl
          tone="error"
          title="Logs could not be loaded"
          message={state.error}
          gridState
        />
      ) : (
        <div className="resource-workspace-log-table">
          {!state.loading && !hasLogs ? (
            <ResourceStateControl
              className="resource-workspace-log-table-empty"
              compact
              iconKey="logscreen"
              message="No logs are available for this resource yet."
            />
          ) : (
            <TableControl
              data={scopedLogs}
              metadata={state.metadata || normalizeLogTableMetadata(null)}
              filters={logManager?.filters}
              loadingState={state.loading}
              mgr={logManager}
              enableMultiSelect={false}
              defaultNumRows={50}
              disableTableTitle
              headerFeatures={["search", "filter", "tableStyles", "columnSettings"]}
              onServerSideQuery={handleServerSideQuery}
            />
          )}
        </div>
      )}
    </div>
  );
};

const normalizeEventTableMetadata = (metadataItems) => {
  const metadata = firstItem(metadataItems);
  if (!metadata) return [];

  return [{
    ...metadata,
    ServerFilterSort: metadata?.ServerFilterSort ?? true,
    HasPaging: Array.isArray(metadata?.HasPaging) && metadata.HasPaging.length > 0 ? metadata.HasPaging : [50],
    DisableCanCreate: true,
    DisableCanDelete: true,
    DisableCanClone: true
  }];
};

const ResourceEventsScreen = ({ selectedResource, resourceEvents, eventManager }) => {
  const selectedClientResourceId = getSelectedClientResourceId(selectedResource);
  const eventManagerRef = useRef(eventManager);
  const [state, setState] = useState({
    loading: Boolean(selectedClientResourceId),
    error: "",
    data: getResourceScopedEvents(resourceEvents, selectedResource),
    metadata: null
  });

  useEffect(() => {
    eventManagerRef.current = eventManager;
  }, [eventManager]);

  const runLockedEventQuery = useCallback(async (tableQuery) => {
    if (!selectedClientResourceId) {
      return { Items: [], TotalItemCount: 0 };
    }

    const activeEventManager = eventManagerRef.current;
    if (!activeEventManager?.Query) {
      throw new Error("Event service is not available.");
    }

    const lockedQuery = buildLockedClientResourceEventQuery(tableQuery, selectedClientResourceId);
    const result = await activeEventManager.Query({
      model: lockedQuery,
      skip: lockedQuery.Skip,
      take: lockedQuery.Take,
      sortBy: lockedQuery.SortBy,
      sortOrder: lockedQuery.SortOrder
    });
    const items = getResourceScopedEvents(getLogResultItems(result), selectedResource);
    return {
      ...result,
      Items: items,
      TotalItemCount: Number.isFinite(result?.TotalItemCount) ? result.TotalItemCount : items.length
    };
  }, [selectedClientResourceId, selectedResource]);

  useEffect(() => {
    let mounted = true;

    const loadEvents = async () => {
      if (!selectedClientResourceId) {
        setState({
          loading: false,
          error: "",
          data: [],
          metadata: normalizeEventTableMetadata(null)
        });
        return;
      }

      const activeEventManager = eventManagerRef.current;
      if (!activeEventManager?.Query) {
        setState({
          loading: false,
          error: "Event service is not available.",
          data: getResourceScopedEvents(resourceEvents, selectedResource),
          metadata: normalizeEventTableMetadata(activeEventManager?.uiMetadata)
        });
        return;
      }

      setState((previous) => ({
        ...previous,
        loading: true,
        error: ""
      }));

      try {
        const [metadataResult, queryResult] = await Promise.all([
          activeEventManager.GetMetadata
            ? activeEventManager.GetMetadata({ forceReload: true })
            : Promise.resolve({ Success: true, Items: activeEventManager.uiMetadata || [] }),
          runLockedEventQuery({ Take: 50 })
        ]);

        if (!mounted) return;

        if (metadataResult && metadataResult.Success === false) {
          setState({
            loading: false,
            error: getResultMessage(metadataResult, "Event metadata could not be loaded."),
            data: [],
            metadata: null
          });
          return;
        }

        if (queryResult && queryResult.Success === false) {
          setState({
            loading: false,
            error: getResultMessage(queryResult, "Events could not be loaded."),
            data: [],
            metadata: normalizeEventTableMetadata(getLogResultItems(metadataResult))
          });
          return;
        }

        const queryItems = getLogResultItems(queryResult);
        setState({
          loading: false,
          error: "",
          data: getResourceScopedEvents(queryItems, selectedResource),
          metadata: normalizeEventTableMetadata(getLogResultItems(metadataResult))
        });
      } catch (loadError) {
        if (!mounted) return;
        setState({
          loading: false,
          error: loadError?.message || "Events could not be loaded.",
          data: [],
          metadata: null
        });
      }
    };

    loadEvents();

    return () => {
      mounted = false;
    };
  }, [resourceEvents, runLockedEventQuery, selectedClientResourceId, selectedResource]);

  const handleServerSideQuery = useCallback(async (tableQuery) => {
    try {
      const result = await runLockedEventQuery(tableQuery);
      const items = getResourceScopedEvents(result?.Items, selectedResource);
      setState((previous) => ({
        ...previous,
        loading: false,
        error: "",
        data: items
      }));
      return {
        ...result,
        Items: items,
        TotalItemCount: Number.isFinite(result?.TotalItemCount) ? result.TotalItemCount : items.length
      };
    } catch (queryError) {
      setState((previous) => ({
        ...previous,
        loading: false,
        error: queryError?.message || "Events could not be loaded."
      }));
      return { Success: false, Items: [], TotalItemCount: 0 };
    }
  }, [runLockedEventQuery, selectedResource]);

  const scopedEvents = getResourceScopedEvents(state.data, selectedResource);
  const hasEvents = scopedEvents.length > 0;

  return (
    <div className="resource-workspace-panel resource-workspace-linked-item-section">
      <div className="resource-workspace-section-title resource-workspace-section-title--grid">
        <h2>Events</h2>
        <p>Operational events for {selectedResource.displayName}.</p>
      </div>
      {state.loading && !state.metadata ? (
        <ResourceStateControl loading loadingText="Loading events..." gridState />
      ) : state.error ? (
        <ResourceStateControl
          tone="error"
          title="Events could not be loaded"
          message={state.error}
          gridState
        />
      ) : (
        <div className="resource-workspace-log-table">
          {!state.loading && !hasEvents ? (
            <ResourceStateControl
              className="resource-workspace-log-table-empty"
              compact
              iconKey="eventscreen"
              message="No events are available for this resource yet."
            />
          ) : (
            <TableControl
              data={scopedEvents}
              metadata={state.metadata || []}
              filters={eventManager?.filters}
              loadingState={state.loading}
              mgr={eventManager}
              enableMultiSelect={false}
              defaultNumRows={50}
              disableTableTitle
              headerFeatures={["search", "filter", "tableStyles", "columnSettings"]}
              onServerSideQuery={handleServerSideQuery}
            />
          )}
        </div>
      )}
    </div>
  );
};

const ResourceToolUnavailable = ({ toolKey }) => (
  <ResourceStateControl
    tone="warning"
    title="Tool unavailable"
    message={`The resource navigation key \`${toolKey}\` does not map to an active phase-1 screen.`}
  />
);

const Metric = ({ label, value }) => (
  <div className="resource-workspace-metric">
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const Fact = ({ label, value }) => (
  <div>
    <dt>{label}</dt>
    <dd>{value}</dd>
  </div>
);

export default ResourceWorkspace;
